import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { adminApiService, CoinPrediction, RegimeStatusResult, RegimeStatusStrategyRow } from '../../services/adminApiService';
import { adminAuthService } from '../../services/adminAuthService';
import { useAppSelector } from '../../store/hooks';
import TradingStrategyReportsModal from './TradingStrategyReportsModal';
import StrategyLogsModal from './StrategyLogsModal';
import KucoinBalances from './KucoinBalances';
import MemeBlacklistModal from './MemeBlacklistModal';
import { KucoinSubaccountProvisioningWizard } from './KucoinSubaccountProvisioning';
import './AdminComponents.css';
import StrategyExchangeInfo from '../trading/StrategyExchangeInfo';
import { useSagemasterEnabled } from '../../hooks/useSagemasterEnabled';
import AiTuneChangeTable from './AiTuneChangeTable';
import AiTunePerformanceSnapshot from './AiTunePerformanceSnapshot';
import AiTunePerformanceMetaPanels from './AiTunePerformanceMetaPanels';
import AiTuneRegimeReview from './AiTuneRegimeReview';
import { featureFlags } from '../../config/featureFlags';

interface TradingStrategy {
  id: number;
  name: string;
  strategyType: 'MEME_COIN_PAIRS' | 'CRYPTO_PAIRS' | 'FOREX_PAIRS';
  status: 'ACTIVE' | 'PAUSED' | 'STOPPED';
  published: boolean; // Whether this strategy is visible to retail users for enrollment
  requiredPlan?: string | null; // Minimum retail subscription plan name (null = no restriction)
  config: string;
  filterProfileId?: number | null;
  createdAt: string;
  updatedAt: string;
  lastError?: string; // Last error message from backend (e.g., "INSUFFICIENT_MARGIN")
}

interface StrategyConfig {
  fixedOrderAmount?: number;
  leverage?: number;
  trailingStopLoss?: number;
  longTrailingStopLoss?: number;  // Per-direction SL override for LONG positions
  shortTrailingStopLoss?: number; // Per-direction SL override for SHORT positions
  stopLoss?: number; // For backward compatibility
  takeProfit?: number;
  basketTakeProfit?: number;
  enableUniversalBasketTakeProfit?: boolean;
  universalBasketTakeProfit?: number;
  universalBasketMinOpenLegs?: number;
  exchange?: string;
  selectedCoins?: string[];
  coinSelectionMode?: 'TOP_N' | 'TOP_100' | 'MANUAL' | 'ALL_COINS' | 'STOCK_INDEX';
  includeStockIndexPerps?: boolean;
  coinLimit?: number;
  stopLossType?: 'CLASSIC' | 'TRAILING';
  enableStopLossPerCoin?: boolean;
  enableTakeProfitPerCoin?: boolean;
  enableBasketTakeProfit?: boolean;
  marketType?: 'SPOT' | 'FUTURES';
  maxOpenPositions?: number;
  paperTrading?: boolean; // Paper trading mode (local simulator)
  enableVolatilityFilter?: boolean; // Enable/disable volatility filter
  minMarketVolatility?: number; // Minimum market volatility %
  maxMarketVolatility?: number; // Maximum market volatility %
  volatilityCalculationCoins?: number; // Number of coins to use for volatility calculation (default: 10)
  volatilityCalculationMethod?: 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO'; // Volatility calculation method
  marginMode?: 'CROSS' | 'ISOLATED'; // Margin mode for Futures trading (default: CROSS)
  orderType?: 'MARKET' | 'LIMIT'; // Order type for opening positions (default: MARKET)
  limitOffset?: number; // Price offset % for limit orders (default: 0.1)
  limitTimeoutSeconds?: number; // Timeout in seconds before canceling limit order (default: 30)
  minMarketActivityThreshold?: number; // Minimum average 24h change % for "market alive" signal (default: 4.4)
  // NEW: micro trailing stop config
  enableMicroTsl?: boolean;
  microTslActivationPercent?: number;  // e.g. 4
  microTslDistancePercent?: number;    // e.g. 1
  // Personal KuCoin account selection (strategy-level)
  kucoinConfigId?: number | null;
  kucoinConfigName?: string;
  // BTC correlation / market filter (mirrors backend MemeCoinStrategyConfig)
  btcCorrelationEnabled?: boolean;
  btcEmaLength?: number;
  btcEmaTimeframe?: string;
  btcMomentumLongThreshold?: number;
  btcMomentumShortThreshold?: number;
  btcRangeNoTradeThreshold?: number;
  btcDumpProtectionEnabled?: boolean;
  btcDumpThreshold?: number;
  btcCooldownMinutes?: number;
  // BTC Higher-Timeframe Trend Confirmation
  btcTrendTimeframe?: string;
  btcTrendEmaPeriod?: number;
  // Entry Filters
  entryFiltersEnabled?: boolean;
  fundingRateFilterEnabled?: boolean;
  maxFundingRate?: number;
  rsiFilterEnabled?: boolean;
  rsiLongMin?: number;
  rsiShortMax?: number;
  emaFilterEnabled?: boolean;
  emaPeriod?: number;
  // Minimum Profit Exit
  minProfitExitPercent?: number;
  // Meme Participation Filter
  memeParticipationFilterEnabled?: boolean;
  minParticipationRate?: number; // 0.0–1.0 (e.g. 0.25 = 25 %)
  microReversalFilterEnabled?: boolean;
  microReversalMinDropPercent?: number;
  microReversalMaxDropPercent?: number;
  microReversalLookbackCandles?: number;
  // Regime-gated entry behaviour (US-REB-01/03)
  lowParticipationMode?: 'REDUCED_EXPOSURE' | 'ENTRY_BLOCK';
  lowParticipationAutoBlockDays?: number;
  // BEARISH_BTC long-block (US-RLB-01)
  aiRegimeLongBlockEnabled?: boolean;
  aiRegimeLongBlockList?: string;
  // Scalper mode (US-SCL-01)
  scalperModeEnabled?: boolean;
  scalperTakeProfit?: number;
  scalperTrailingStopLoss?: number;
  scalperMaxOpenPositions?: number;
  scalperRegimeList?: string;
  // Coin Selection Oracle
  oracleEnabled?: boolean;
  oracleConfidenceThreshold?: number;
  oracleWeightMomentum?: number;
  oracleWeightRsi?: number;
  oracleWeightVolume?: number;
  oracleWeightFunding?: number;
  oracleWeightEma?: number;
  // AI auto-tune (Phase 2)
  aiAutoTuneEnabled?: boolean;
  aiAutoTuneRequireApproval?: boolean;
  aiAutoTuneLastRegime?: string;
  aiAutoTuneLastAppliedAt?: number;
  aiAutoTunePendingJson?: string;
  aiPerformanceTuneEnabled?: boolean;
  aiPerformanceTuneWindow?: number;
  aiPerformanceTuneMinTrades?: number;
  aiPerformanceCoinTuneEnabled?: boolean;
  aiPerformanceSideBlockEnabled?: boolean;
  aiPerformanceTuneLlmEnabled?: boolean;
  aiPerformanceTuneHistoryLearningEnabled?: boolean;
  coinSideBlocks?: Record<string, string[]>;
}

interface ActiveTradingPair {
  id: number;
  strategyId: number;
  longCoin: string;
  shortCoin: string;
  longPositionId?: string;
  shortPositionId?: string;
  entryTime: string;
  longEntryPrice?: number;
  shortEntryPrice?: number;
  longPnl: number;
  shortPnl: number;
  combinedPnl: number;
  status: 'ACTIVE' | 'CLOSED' | 'ERROR';
}

interface CreateStrategyRequest {
  name: string;
  fixedOrderAmount: number;
  leverage: number;
  trailingStopLoss: number;
  takeProfit: number;
  basketTakeProfit: number;
  enableUniversalBasketTakeProfit?: boolean;
  universalBasketTakeProfit?: number;
  universalBasketMinOpenLegs?: number;
  exchange: string;
  createdBy: number;
  filterProfileId?: number | null;
  enableStopLossPerCoin?: boolean;
  enableTakeProfitPerCoin?: boolean;
  enableBasketTakeProfit?: boolean;
  stopLossType?: 'CLASSIC' | 'TRAILING';
  longTrailingStopLoss?: number;  // Per-direction SL override for LONG positions
  shortTrailingStopLoss?: number; // Per-direction SL override for SHORT positions
  coinSelectionMode?: 'TOP_N' | 'TOP_100' | 'MANUAL' | 'ALL_COINS' | 'STOCK_INDEX';
  includeStockIndexPerps?: boolean;
  coinLimit?: number;
  selectedCoins?: string[];
  marketType?: 'SPOT' | 'FUTURES';
  maxOpenPositions?: number;
  paperTrading?: boolean; // Paper trading mode (local simulator)
  enableVolatilityFilter?: boolean; // Enable/disable volatility filter
  minMarketVolatility?: number; // Minimum market volatility %
  maxMarketVolatility?: number; // Maximum market volatility %
  volatilityCalculationCoins?: number; // Number of coins to use for volatility calculation (default: 10)
  volatilityCalculationMethod?: 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO'; // Volatility calculation method
  marginMode?: 'CROSS' | 'ISOLATED'; // Margin mode for Futures trading (default: CROSS)
  orderType?: 'MARKET' | 'LIMIT'; // Order type for opening positions
  limitOffset?: number; // Price offset % for limit orders (default: 0.1% = 0.001)
  limitTimeoutSeconds?: number; // Timeout in seconds before canceling limit order and using market (default: 30)
  minMarketActivityThreshold?: number; // Minimum average 24h change % for "market alive" signal (default: 4.4)
  // NEW: micro trailing stop config
  enableMicroTsl?: boolean;
  microTslActivationPercent?: number;
  microTslDistancePercent?: number;
  // US9: Trading Account selection (kucoinConfigId)
  kucoinConfigId?: number | null;
  // BTC correlation / market filter (Use Case 7)
  btcCorrelationEnabled?: boolean;
  btcEmaLength?: number;
  btcEmaTimeframe?: '5m' | '15m';
  btcMomentumLongThreshold?: number;
  btcMomentumShortThreshold?: number;
  btcRangeNoTradeThreshold?: number;
  btcDumpProtectionEnabled?: boolean;
  btcDumpThreshold?: number;
  btcCooldownMinutes?: number;
  // BTC Higher-Timeframe Trend Confirmation
  btcTrendTimeframe?: string;
  btcTrendEmaPeriod?: number;
  // Entry Filters
  entryFiltersEnabled?: boolean;
  fundingRateFilterEnabled?: boolean;
  maxFundingRate?: number;
  rsiFilterEnabled?: boolean;
  rsiLongMin?: number;
  rsiShortMax?: number;
  emaFilterEnabled?: boolean;
  emaPeriod?: number;
  // Minimum Profit Exit
  minProfitExitPercent?: number;
  // Meme Participation Filter
  memeParticipationFilterEnabled?: boolean;
  minParticipationRate?: number; // 0.0–1.0 (e.g. 0.25 = 25 %)
  microReversalFilterEnabled?: boolean;
  microReversalMinDropPercent?: number;
  microReversalMaxDropPercent?: number;
  microReversalLookbackCandles?: number;
  // Coin Selection Oracle
  oracleEnabled?: boolean;
  oracleConfidenceThreshold?: number; // 0.0–1.0
  oracleWeightMomentum?: number;
  oracleWeightRsi?: number;
  oracleWeightVolume?: number;
  oracleWeightFunding?: number;
  oracleWeightEma?: number;
  /** Phase 2: optional at create — default off in UI */
  aiAutoTuneEnabled?: boolean;
  aiAutoTuneRequireApproval?: boolean;
}

interface AdminTradingStrategiesProps {
  onShowAnalytics?: (strategyId: number) => void;
}

const DEFAULT_TP_EDIT_FORM = {
  takeProfit: '',
  basketTakeProfit: '',
  longTakeProfit: '',
  shortTakeProfit: '',
  stopLossType: 'TRAILING' as 'CLASSIC' | 'TRAILING',
  trailingStopLoss: '',
  longTrailingStopLoss: '',
  shortTrailingStopLoss: '',
  enableMicroTsl: false,
  microTslActivationPercent: '',
  microTslDistancePercent: '',
  maxOpenPositions: '',
  leverage: '',
  btcCorrelationEnabled: false,
  btcEmaLength: '',
  btcEmaTimeframe: '',
  btcMomentumLongThreshold: '',
  btcMomentumShortThreshold: '',
  btcRangeNoTradeThreshold: '',
  btcDumpThreshold: '',
  btcCooldownMinutes: '',
  btcTrendTimeframe: '',
  btcTrendEmaPeriod: '',
  minProfitExitPercent: '',
  orderType: 'MARKET' as 'MARKET' | 'LIMIT',
  limitOffset: '',
  limitTimeoutSeconds: '',
  marginMode: 'CROSS' as 'CROSS' | 'ISOLATED',
  fixedOrderAmount: '',
  memeParticipationFilterEnabled: false,
  minParticipationRate: '',
  minMarketActivityThreshold: '',
  enableVolatilityFilter: false,
  minMarketVolatility: '',
  maxMarketVolatility: '',
  volatilityCalculationCoins: '10',
  volatilityCalculationMethod: 'AUTO' as 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO',
  microReversalFilterEnabled: false,
  microReversalMinDropPercent: '',
  microReversalMaxDropPercent: '',
  microReversalLookbackCandles: '',
  lowParticipationMode: 'REDUCED_EXPOSURE' as 'REDUCED_EXPOSURE' | 'ENTRY_BLOCK',
  lowParticipationAutoBlockDays: '0',
  aiRegimeLongBlockEnabled: false,
  aiRegimeLongBlockList: 'BEARISH_BTC',
  scalperModeEnabled: false,
  scalperTakeProfit: '2.0',
  scalperTrailingStopLoss: '1.0',
  scalperMaxOpenPositions: '2',
  scalperRegimeList: 'HIGH_VOLATILITY',
  oracleEnabled: false,
  oracleConfidenceThreshold: 0.5,
  oracleWeightMomentum: 0.25 as number | null,
  oracleWeightRsi: 0.30 as number | null,
  oracleWeightVolume: 0.20 as number | null,
  oracleWeightFunding: 0.15 as number | null,
  oracleWeightEma: 0.10 as number | null,
  enableUniversalBasketTakeProfit: false,
  universalBasketTakeProfit: '',
  universalBasketMinOpenLegs: '',
};

function normalizeCoinSymbol(symbol: string | null | undefined): string {
  if (!symbol) return '';
  return symbol.trim().toUpperCase().replace(/USDTM$/i, '').replace(/-USDT$/i, '');
}

/** Ensure saved manual picks always appear in the picker, even if outside the majors universe. */
function mergeCoinUniverse(available: string[], selected: string[]): string[] {
  const merged = new Map<string, string>();
  for (const raw of [...available, ...selected]) {
    const sym = normalizeCoinSymbol(raw);
    if (sym) merged.set(sym, sym);
  }
  return Array.from(merged.values()).sort((a, b) => a.localeCompare(b));
}

async function fetchCryptoUniverseForPicker(
  marketType: 'FUTURES' | 'SPOT',
  options?: { includeStockIndex?: boolean; stockIndexOnly?: boolean }
): Promise<string[]> {
  if (options?.stockIndexOnly) {
    const res = await adminApiService.getStockIndexUniverse(marketType, 0);
    return res.success && Array.isArray(res.data) ? res.data : [];
  }
  const res = await adminApiService.getMajorsUniverse(
    marketType,
    0,
    options?.includeStockIndex !== false
  );
  return res.success && Array.isArray(res.data) ? res.data : [];
}

const AdminTradingStrategies: React.FC<AdminTradingStrategiesProps> = ({ onShowAnalytics }) => {
  const location = useLocation();
  const adminUser = useAppSelector((state) => state.auth.adminUser);
  const adminPermissions = useAppSelector((state) => state.auth.adminUser?.permissions || []);
  const canUseSharedKucoin = adminPermissions.includes('USE_SHARED_KUCOIN_ACCOUNT');
  const canViewSpotCoinsSection =
    adminPermissions.includes('VIEW_ADMIN_TRADING_SPOT_COINS_SECTION') &&
    (adminPermissions.includes('VIEW_ADMIN_TRADING_BOTS') ||
      adminPermissions.includes('MANAGE_TRADING_BOTS'));
  const canRunGlobalReconciliation = adminPermissions.includes('MANAGE_TRADING_RECONCILIATION');
  const [strategies, setStrategies] = useState<TradingStrategy[]>([]);
  const [activePairs, setActivePairs] = useState<ActiveTradingPair[]>([]);
  const [memeUniverse, setMemeUniverse] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tpSaveSuccess, setTpSaveSuccess] = useState<string | null>(null);
  // Track stop progress for strategies so we can show a temporary status message
  const [stoppingStrategyId, setStoppingStrategyId] = useState<number | null>(null);
  const [stopStatusMessage, setStopStatusMessage] = useState<string | null>(null);
  const stopToastTimeoutRef = useRef<number | null>(null);
  const [blacklistedBases, setBlacklistedBases] = useState<Set<string>>(new Set());
  
  // Active pairs pagination
  const [activePairsPage, setActivePairsPage] = useState(1);
  const [activePairsPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  /** MEME = meme-coin-pairs modal; CRYPTO = major crypto pairs (Beta) */
  const [createModalStrategyType, setCreateModalStrategyType] = useState<'MEME' | 'CRYPTO'>('MEME');
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [aiSuggestionMeta, setAiSuggestionMeta] = useState<{
    marketRegime: string;
    regimeLabel: string;
    confidence: number;
    volatilityPreset: string;
    reasoning: string[];
    marketSnapshot?: Record<string, unknown>;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Track if any modal is open to prevent data loss
  const [selectedStrategy, setSelectedStrategy] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const DEBUG_FORCE_STATUS_TEXT = false; // turned off after fixing CSS

  const sagemasterEnabled = useSagemasterEnabled('admin');

  // Analytics modal state
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsStrategyId, setAnalyticsStrategyId] = useState<number | null>(null);
  const [strategyPnlHistory, setStrategyPnlHistory] = useState<{[key: number]: any[]}>({});
  const [strategyTrades, setStrategyTrades] = useState<{[key: number]: any[]}>({});
  const [strategyPositions, setStrategyPositions] = useState<{[key: number]: any[]}>({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Scroll position preservation for analytics modal
  const modalContentRef = useRef<HTMLDivElement>(null);
  const savedScrollPosition = useRef<number>(0);
  const tpFormRestoredRef = useRef(false); // prevent re-restoring on subsequent strategy reloads
  
  // Account overview state for account-level P&L
  const [accountOverview, setAccountOverview] = useState<{
    accountEquity: string;
    unrealisedPNL: string;
    marginBalance: string;
    currency: string;
  } | null>(null);

  // Personal KuCoin accounts for selection (strategy-level Trading Account)
  const [personalAccounts, setPersonalAccounts] = useState<{ id: number; name: string }[]>([]);
  // All available KuCoin accounts (personal + shared system) — used for display + dropdown
  const [allKucoinAccounts, setAllKucoinAccounts] = useState<{ id: number; name: string; shared: boolean; selectable?: boolean; provisioningStatus?: string }[]>([]);
  const [showSubaccountWizard, setShowSubaccountWizard] = useState(false);
  const [subaccountWizardStrategyId, setSubaccountWizardStrategyId] = useState<number | undefined>(undefined);
  const [accountsLoading, setAccountsLoading] = useState<boolean>(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  
  // Track which strategy is currently updating trading account
  const [updatingTradingAccountId, setUpdatingTradingAccountId] = useState<number | null>(null);
  
  // Fee breakdown state
  const [feeBreakdown, setFeeBreakdown] = useState<{
    grossPnl: number;
    totalTradingFees: number;
    totalFundingFees: number;
    netPnl: number;
  } | null>(null);
  
  // Mock data toggle state - per strategy (undefined = auto-detect, true = mock, false = real)
  const [mockDataMode, setMockDataMode] = useState<{[key: number]: boolean | undefined}>({});

  // History modal state (within analytics)
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [allHistoryTrades, setAllHistoryTrades] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDateRange, setHistoryDateRange] = useState<string>('30D');
  const [historyCoinFilter, setHistoryCoinFilter] = useState<string>('ALL');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);

  // User enrollments modal state
  const [showEnrollmentsModal, setShowEnrollmentsModal] = useState(false);
  const [userEnrollments, setUserEnrollments] = useState<any[]>([]);
  const [enrollmentsSummary, setEnrollmentsSummary] = useState<any>(null);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  // Required-plan state
  const [availablePlans, setAvailablePlans] = useState<{ id: number; name: string }[]>([]);
  const [updatingRequiredPlanId, setUpdatingRequiredPlanId] = useState<number | null>(null);

  // Reports modal state
  const [showReportsModal, setShowReportsModal] = useState(false);

  // Logs modal state
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsStrategyName, setLogsStrategyName] = useState<string>('');
  const [showEvaluateModal, setShowEvaluateModal] = useState(false);
  const [evaluateStrategy, setEvaluateStrategy] = useState<any | null>(null);

  // Enhanced strategy modal state
  const [showCreateEnhancedModal, setShowCreateEnhancedModal] = useState(false);

  // Meme blacklist modal state
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [runningGlobalReconciliation, setRunningGlobalReconciliation] = useState(false);
  
  // Take Profit edit modal state
  const [showTPEditModal, setShowTPEditModal] = useState(false);
  const [selectedStrategyForTPEdit, setSelectedStrategyForTPEdit] = useState<TradingStrategy | null>(null);
  const [tpEditForm, setTPEditForm] = useState({ ...DEFAULT_TP_EDIT_FORM });
  const [tpEditErrors, setTPEditErrors] = useState<{[key: string]: string}>({});
  const [tpEditVolatilityPresetMode, setTpEditVolatilityPresetMode] = useState<'preset' | 'custom'>('custom');
  const [tpEditSelectedVolatilityPreset, setTpEditSelectedVolatilityPreset] = useState<'moderate' | 'conservative' | 'aggressive'>('moderate');

  // Entry Filters edit modal state
  const [showFilterEditModal, setShowFilterEditModal] = useState(false);
  const [showAiAutoTuneModal, setShowAiAutoTuneModal] = useState(false);
  const [selectedStrategyForAiAutoTune, setSelectedStrategyForAiAutoTune] = useState<TradingStrategy | null>(null);
  const [aiAutoTuneForm, setAiAutoTuneForm] = useState({
    aiAutoTuneEnabled: false,
    aiAutoTuneRequireApproval: true,
    aiPerformanceTuneEnabled: false,
    aiPerformanceTuneWindow: 12,
    aiPerformanceTuneMinTrades: 8,
    aiPerformanceCoinTuneEnabled: false,
    aiPerformanceSideBlockEnabled: false,
    aiPerformanceTuneLlmEnabled: false,
    aiPerformanceTuneHistoryLearningEnabled: true,
  });
  const [aiRegimeGatesForm, setAiRegimeGatesForm] = useState({
    lowParticipationMode: 'REDUCED_EXPOSURE' as 'REDUCED_EXPOSURE' | 'ENTRY_BLOCK',
    lowParticipationAutoBlockDays: 0,
    aiRegimeLongBlockEnabled: false,
    aiRegimeLongBlockList: 'BEARISH_BTC',
    scalperModeEnabled: false,
    scalperTakeProfit: '2.0',
    scalperTrailingStopLoss: '1.0',
    scalperMaxOpenPositions: '2',
    scalperRegimeList: 'HIGH_VOLATILITY',
  });
  const [aiAutoTunePending, setAiAutoTunePending] = useState<Record<string, unknown> | null>(null);
  const [aiAutoTuneRunning, setAiAutoTuneRunning] = useState(false);
  const [aiAutoTuneModalTab, setAiAutoTuneModalTab] = useState<'settings' | 'performance' | 'history' | 'review'>('settings');
  const [aiAutoTuneHistory, setAiAutoTuneHistory] = useState<Array<{
    id: number;
    appliedAt: string;
    marketRegime?: string;
    trigger?: string;
    outcome?: string;
    runTriggerReason?: string;
    actor?: string;
    changes?: Array<{ field?: string; label?: string; before?: string | null; after?: string | null }>;
    performanceMeta?: Record<string, unknown>;
    performanceAtApply?: Record<string, unknown>;
    performanceAfter?: Record<string, unknown>;
  }>>([]);
  const [aiAutoTuneHistoryPage, setAiAutoTuneHistoryPage] = useState(0);
  const [aiAutoTuneHistoryTotalPages, setAiAutoTuneHistoryTotalPages] = useState(0);
  const [aiAutoTuneHistoryLoading, setAiAutoTuneHistoryLoading] = useState(false);
  const [expandedAiTuneHistoryId, setExpandedAiTuneHistoryId] = useState<number | null>(null);
  const [aiAutoTuneGlobal, setAiAutoTuneGlobal] = useState<{
    schedulerEnabled: boolean;
    schedulerIntervalMs: number;
    minIntervalMs: number;
    performanceSnapshotEnabled?: boolean;
    performanceTuneEnabled?: boolean;
    performanceMinTrades?: number;
    performanceHistoryLearningEnabled?: boolean;
    performanceLlmEnabled?: boolean;
    performanceLlmProvider?: string;
    performanceLlmModel?: string;
    performanceLlmConfigured?: boolean;
  } | null>(null);
  const [selectedStrategyForFilterEdit, setSelectedStrategyForFilterEdit] = useState<TradingStrategy | null>(null);
  const [strategyFilterProfiles, setStrategyFilterProfiles] = useState<Array<{ id: number; name: string }>>([]);
  const [strategyFilterProfileId, setStrategyFilterProfileId] = useState<number | ''>('');

  // --- Clone Strategy modal state ---
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [strategyToClone, setStrategyToClone] = useState<TradingStrategy | null>(null);
  const [cloneForm, setCloneForm] = useState({
    name: '',
    takeProfit: '',
    fixedOrderAmount: '',
    maxOpenPositions: '',
    coinSelectionMode: 'TOP_N' as 'TOP_N' | 'MANUAL',
    coinLimit: 20,
  });
  const [cloningInProgress, setCloningInProgress] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Track actual visible viewport height (avoids iOS Safari 100vh ≠ visible area bug)
  const [viewportH, setViewportH] = useState(window.innerHeight);
  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // --- Edit Coins modal state ---
  const [showEditCoinsModal, setShowEditCoinsModal] = useState(false);
  const [selectedStrategyForCoinEdit, setSelectedStrategyForCoinEdit] = useState<TradingStrategy | null>(null);
  const [editCoinsForm, setEditCoinsForm] = useState<{
    coinSelectionMode: 'TOP_N' | 'MANUAL';
    coinLimit: number;
    selectedCoins: string[];
    coinSearch: string;
  }>({ coinSelectionMode: 'TOP_N', coinLimit: 20, selectedCoins: [], coinSearch: '' });
  const [editCoinsAvailable, setEditCoinsAvailable] = useState<string[]>([]);
  const [loadingEditCoins, setLoadingEditCoins] = useState(false);
  const [savingEditCoins, setSavingEditCoins] = useState(false);
  // --- Coin Prediction state ---
  const [predictedCoins, setPredictedCoins] = useState<CoinPrediction[]>([]);
  const [predictingCoins, setPredictingCoins] = useState(false);
  const [showPredictionPanel, setShowPredictionPanel] = useState(false);
  const [predictionMessage, setPredictionMessage] = useState('');
  const [filterEditForm, setFilterEditForm] = useState({
    entryFiltersEnabled: false,
    // Sub-filter toggles
    fundingRateFilterEnabled: true,
    rsiFilterEnabled: true,
    emaFilterEnabled: true,
    maxFundingRateLong: '0.05',   // display as % (0.05 = 0.05%)
    rsiPeriod: '14',
    rsiCandleLimit: '15',
    rsiLongThreshold: '55',
    rsiShortThreshold: '45',
    emaPeriod: '20',
  });

  // Create strategy form state
  const [createForm, setCreateForm] = useState<CreateStrategyRequest>({
    name: '',
    fixedOrderAmount: 1000,
    leverage: 1,
    trailingStopLoss: 0.8,
    takeProfit: 2.0,
    basketTakeProfit: 2.0,
    enableUniversalBasketTakeProfit: false,
    universalBasketTakeProfit: 2.0,
    universalBasketMinOpenLegs: 2,
    exchange: 'KUCOIN',
    createdBy: 1, // This should come from current user context
    enableStopLossPerCoin: true,
    enableTakeProfitPerCoin: true,
    enableBasketTakeProfit: true,
    stopLossType: 'TRAILING', // Default to trailing stop loss
    longTrailingStopLoss: undefined, // Optional: per-direction SL for longs
    shortTrailingStopLoss: undefined, // Optional: per-direction SL for shorts
    coinSelectionMode: 'TOP_N', // Default to top N by volume
    coinLimit: 10, // Default to top 10 coins
    includeStockIndexPerps: true,
    selectedCoins: [], // Empty for manual selection
    marketType: 'SPOT', // Default to spot market
    maxOpenPositions: 3, // Default to 3 positions (max recommended)
    paperTrading: false, // Default to live trading (not paper trading)
    enableVolatilityFilter: false, // Default to disabled
    minMarketVolatility: undefined, // Optional minimum volatility %
    maxMarketVolatility: undefined, // Optional maximum volatility %
    volatilityCalculationCoins: 10, // Default to top 10 coins
    volatilityCalculationMethod: 'AUTO', // Default to AUTO (uses best available method)
    marginMode: 'CROSS', // Default to CROSS margin mode
    orderType: 'MARKET', // Default to market orders for backward compatibility
    limitOffset: 0.1, // Default to 0.1% offset for limit orders
    limitTimeoutSeconds: 30, // Default to 30 seconds timeout
    enableMicroTsl: false, // keep OFF by default for safety
    microTslActivationPercent: 4.0, // arm at +4% PnL
    microTslDistancePercent: 1.0, // trail 1% behind
    // BTC correlation defaults (disabled by default for safety)
    btcCorrelationEnabled: false,
    btcEmaLength: 50,
    btcEmaTimeframe: '5m',
    btcMomentumLongThreshold: 0.5,
    btcMomentumShortThreshold: 0.5,
    btcRangeNoTradeThreshold: 0.8,
    btcDumpProtectionEnabled: true,
    btcDumpThreshold: 1.5,
    btcCooldownMinutes: 20,
    // BTC Higher-Timeframe Trend Confirmation (disabled by default)
    btcTrendTimeframe: '',
    btcTrendEmaPeriod: 21,
    // Entry Filters (disabled by default — non-breaking)
    entryFiltersEnabled: false,
    fundingRateFilterEnabled: true,
    maxFundingRate: 0.05,        // displayed as percentage; backend receives /100
    rsiFilterEnabled: true,
    rsiLongMin: 55,
    rsiShortMax: 45,
    emaFilterEnabled: true,
    emaPeriod: 20,
    // Minimum Profit Exit (disabled by default)
    minProfitExitPercent: 0,
    // Meme Participation Filter (disabled by default)
    memeParticipationFilterEnabled: false,
    minParticipationRate: 0.25,
    microReversalFilterEnabled: false,
    microReversalMinDropPercent: -0.5,
    microReversalMaxDropPercent: -0.1,
    microReversalLookbackCandles: 12,
    // Coin Selection Oracle (disabled by default)
    oracleEnabled: false,
    oracleConfidenceThreshold: 0.50,
    oracleWeightMomentum: 0.25,
    oracleWeightRsi: 0.30,
    oracleWeightVolume: 0.20,
    oracleWeightFunding: 0.15,
    oracleWeightEma: 0.10,
    aiAutoTuneEnabled: false,
    aiAutoTuneRequireApproval: true,
  });
  const [volatilityPresetMode, setVolatilityPresetMode] = useState<'preset' | 'custom'>('preset');
  const [selectedVolatilityPreset, setSelectedVolatilityPreset] = useState<'moderate' | 'conservative' | 'aggressive'>('moderate');

  // Debug: Monitor volatility filter state changes
  useEffect(() => {
    console.log('🔍 Volatility Filter State Changed:', {
      enableVolatilityFilter: createForm.enableVolatilityFilter,
      type: typeof createForm.enableVolatilityFilter,
      booleanValue: Boolean(createForm.enableVolatilityFilter),
      strictlyTrue: createForm.enableVolatilityFilter === true
    });
  }, [createForm.enableVolatilityFilter]);

  // Load KuCoin accounts for Trading Account selection
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        setAccountsLoading(true);
        setAccountsError(null);

        // Load personal accounts (for backward-compat state used elsewhere)
        const personalRes = await adminApiService.getMyTradingAccounts();
        if (personalRes.success && personalRes.data) {
          setPersonalAccounts((personalRes.data as any[]).map(acc => ({
            id: acc.id,
            name: acc.name as string,
          })));
        }

        // Load combined list (personal + shared system accounts)
        const allRes = await adminApiService.getAllKucoinAccounts();
        if (allRes.success && allRes.data) {
          setAllKucoinAccounts((allRes.data as any[]).map(acc => ({
            id: acc.id,
            name: acc.name as string,
            shared: !!acc.shared,
          })));
        } else {
          setAccountsError(allRes.error || 'Failed to load KuCoin accounts');
        }
      } catch (e: any) {
        console.error('Failed to load KuCoin accounts for strategies', e);
        setAccountsError(e?.message || 'Failed to load KuCoin accounts');
      } finally {
        setAccountsLoading(false);
      }
    };

    loadAccounts();
  }, []);

  const selectablePersonalKucoinAccounts = allKucoinAccounts.filter(
    (acc) => !acc.shared && acc.selectable !== false
  );

  /** Resolve kucoinConfigId for create — dropdown can display an account while state is still empty. */
  const resolveKucoinConfigIdForSubmit = (): number | undefined => {
    if (createForm.kucoinConfigId != null) {
      return createForm.kucoinConfigId;
    }
    if (!canUseSharedKucoin && selectablePersonalKucoinAccounts.length === 1) {
      return selectablePersonalKucoinAccounts[0].id;
    }
    return undefined;
  };

  // Auto-select sole personal KuCoin account when shared system account is not allowed.
  useEffect(() => {
    if (!showCreateModal || canUseSharedKucoin || createForm.kucoinConfigId != null) return;
    if (selectablePersonalKucoinAccounts.length !== 1) return;
    setCreateForm((prev) =>
      prev.kucoinConfigId != null
        ? prev
        : { ...prev, kucoinConfigId: selectablePersonalKucoinAccounts[0].id }
    );
  }, [
    showCreateModal,
    canUseSharedKucoin,
    createForm.kucoinConfigId,
    selectablePersonalKucoinAccounts,
  ]);

  // Volatility filter presets (based on documented recommendations)
  const volatilityPresets = {
    moderate: {
      name: 'Moderate (Recommended for Pairs Trading)',
      description: 'Balanced approach - suitable for most trading scenarios',
      minMarketVolatility: 2.0,
      maxMarketVolatility: 10.0,
      volatilityCalculationCoins: 10,
      volatilityCalculationMethod: 'AUTO' as const,
      icon: '⚖️',
      color: '#3b82f6'
    },
    conservative: {
      name: 'Conservative (Low Risk)',
      description: 'Tighter volatility range - trade only in stable conditions',
      minMarketVolatility: 3.0,
      maxMarketVolatility: 8.0,
      volatilityCalculationCoins: 10,
      volatilityCalculationMethod: 'AUTO' as const,
      icon: '🛡️',
      color: '#10b981'
    },
    aggressive: {
      name: 'Aggressive (High Opportunity)',
      description: 'Wider range - capture more opportunities in volatile markets',
      minMarketVolatility: 1.0,
      maxMarketVolatility: 15.0,
      volatilityCalculationCoins: 15,
      volatilityCalculationMethod: 'AUTO' as const,
      icon: '🚀',
      color: '#f59e0b'
    }
  };

  // Handler to apply volatility preset
  const applyVolatilityPreset = (preset: 'moderate' | 'conservative' | 'aggressive') => {
    const presetConfig = volatilityPresets[preset];
    setSelectedVolatilityPreset(preset);
    // Use functional setState to avoid overwriting concurrent state updates (like enableVolatilityFilter)
    setCreateForm(prevForm => ({
      ...prevForm, // Use the LATEST state, not the captured closure
      minMarketVolatility: presetConfig.minMarketVolatility,
      maxMarketVolatility: presetConfig.maxMarketVolatility,
      volatilityCalculationCoins: presetConfig.volatilityCalculationCoins,
      volatilityCalculationMethod: presetConfig.volatilityCalculationMethod
    }));
  };

  const applyTpEditVolatilityPreset = (preset: 'moderate' | 'conservative' | 'aggressive') => {
    const presetConfig = volatilityPresets[preset];
    setTpEditSelectedVolatilityPreset(preset);
    setTPEditForm(prev => ({
      ...prev,
      minMarketVolatility: String(presetConfig.minMarketVolatility),
      maxMarketVolatility: String(presetConfig.maxMarketVolatility),
      volatilityCalculationCoins: String(presetConfig.volatilityCalculationCoins),
      volatilityCalculationMethod: presetConfig.volatilityCalculationMethod,
    }));
  };

  // Enhanced strategy form state
  const [createEnhancedForm, setCreateEnhancedForm] = useState<CreateStrategyRequest>({
    name: '',
    fixedOrderAmount: 1000,
    leverage: 1,
    trailingStopLoss: 0.8,
    takeProfit: 2.0,
    basketTakeProfit: 2.0,
    enableUniversalBasketTakeProfit: false,
    universalBasketTakeProfit: 2.0,
    universalBasketMinOpenLegs: 2,
    exchange: 'KUCOIN',
    createdBy: 1,
    enableStopLossPerCoin: true,
    enableTakeProfitPerCoin: true,
    enableBasketTakeProfit: true,
    stopLossType: 'TRAILING',
    longTrailingStopLoss: undefined, // Optional: per-direction SL for longs
    shortTrailingStopLoss: undefined, // Optional: per-direction SL for shorts
    coinSelectionMode: 'TOP_N',
    coinLimit: 10,
    selectedCoins: [],
    marketType: 'SPOT',
    maxOpenPositions: 3,
    paperTrading: false,
    enableVolatilityFilter: false,
    minMarketVolatility: undefined,
    maxMarketVolatility: undefined,
    volatilityCalculationCoins: 10,
    volatilityCalculationMethod: 'AUTO',
    marginMode: 'CROSS',
    orderType: 'MARKET',
    limitOffset: 0.1, // Default to 0.1% offset for limit orders
    limitTimeoutSeconds: 30, // Default to 30 seconds timeout
    enableMicroTsl: false, // keep OFF by default for safety
    microTslActivationPercent: 4.0, // arm at +4% PnL
    microTslDistancePercent: 1.0, // trail 1% behind
    // BTC correlation defaults (disabled by default for safety)
    btcCorrelationEnabled: false,
    btcEmaLength: 50,
    btcEmaTimeframe: '5m',
    btcMomentumLongThreshold: 0.5,
    btcMomentumShortThreshold: 0.5,
    btcRangeNoTradeThreshold: 0.8,
    btcDumpProtectionEnabled: true,
    btcDumpThreshold: 1.5,
    btcCooldownMinutes: 20,
    // BTC Higher-Timeframe Trend Confirmation (disabled by default)
    btcTrendTimeframe: '',
    btcTrendEmaPeriod: 21,
    // Minimum Profit Exit (disabled by default)
    minProfitExitPercent: 0,
    // Meme Participation Filter (disabled by default)
    memeParticipationFilterEnabled: false,
    minParticipationRate: 0.25,
    microReversalFilterEnabled: false,
    microReversalMinDropPercent: -0.5,
    microReversalMaxDropPercent: -0.1,
    microReversalLookbackCandles: 12,
    // Coin Selection Oracle (disabled by default)
    oracleEnabled: false,
    oracleConfidenceThreshold: 0.50,
    oracleWeightMomentum: 0.25,
    oracleWeightRsi: 0.30,
    oracleWeightVolume: 0.20,
    oracleWeightFunding: 0.15,
    oracleWeightEma: 0.10,
  });

  // Coin selection state - initialize with fallback to ensure coins are always available
  const [availableCoins, setAvailableCoins] = useState<string[]>(['DOGE', 'SHIB', 'PEPE', 'FLOKI', 'BONK']);
  const [loadingCoins, setLoadingCoins] = useState(false);
  const [allCoins, setAllCoins] = useState<string[]>([]);
  const [loadingAllCoins, setLoadingAllCoins] = useState(false);
  
  // Enhanced coin selection state
  const [availableCoinsEnhanced, setAvailableCoinsEnhanced] = useState<string[]>(['DOGE', 'SHIB', 'PEPE', 'FLOKI', 'BONK']);
  const [loadingCoinsEnhanced, setLoadingCoinsEnhanced] = useState(false);

  // Fetch available meme coins based on selected market type
  const fetchAvailableCoins = async () => {
    try {
      setLoadingCoins(true);
      const marketType = createForm.marketType || 'FUTURES';
      if (createModalStrategyType === 'CRYPTO') {
        const coins = await fetchCryptoUniverseForPicker(marketType as 'FUTURES' | 'SPOT', {
          includeStockIndex: createForm.includeStockIndexPerps !== false,
          stockIndexOnly: createForm.coinSelectionMode === 'STOCK_INDEX',
        });
        if (coins.length > 0) {
          setAvailableCoins(coins);
        } else {
          setAvailableCoins(['BTC', 'ETH', 'SOL', 'ADA', 'XRP', 'BNB']);
        }
        return;
      }
      const response = await apiService.get(`/api/admin/trading-strategies/universe?marketType=${marketType}`);
      console.log(`Fetch available coins (${marketType}) response:`, response);
      
      // apiService.get() already extracts result.data, so response.data should be the array
      let coins: string[] = [];
      if (response && response.success) {
        // apiService wraps the response: { data: actualData, success: true }
        // The backend returns: { status: "success", data: [...] }
        // apiService extracts: response.data = [...]
        if (Array.isArray(response.data)) {
          coins = response.data;
        }
        // Handle nested structure if apiService didn't extract it
        else if (response.data && typeof response.data === 'object' && Array.isArray(response.data.data)) {
          coins = response.data.data;
        }
      }
      
      console.log('Extracted coins from response:', coins);
      
      // Always ensure we have coins - use what we got or fallback
      if (coins.length > 0) {
        setAvailableCoins(coins);
        console.log('Set available coins from API:', coins);
      } else {
        // Keep existing coins or use fallback
        const currentCoins = availableCoins.length > 0 ? availableCoins : ['DOGE', 'SHIB', 'PEPE', 'FLOKI', 'BONK'];
        console.log('Response empty or invalid - Keeping/Using coins:', currentCoins);
        setAvailableCoins(currentCoins);
      }
    } catch (error: any) {
      console.error('Failed to fetch available coins:', error);
      // Fallback to default list
      const fallbackCoins = ['DOGE', 'SHIB', 'PEPE', 'FLOKI', 'BONK'];
      console.log('Error - Using fallback coins:', fallbackCoins);
      setAvailableCoins(fallbackCoins);
    } finally {
      setLoadingCoins(false);
    }
  };

  // Fetch all coins for ALL_COINS selection mode (meme-only or major-only depending on strategy type)
  const fetchAllCoins = async () => {
    try {
      setLoadingAllCoins(true);
      const marketType = createForm.marketType || 'FUTURES';
      if (createModalStrategyType === 'CRYPTO') {
        const coins = await fetchCryptoUniverseForPicker(marketType as 'FUTURES' | 'SPOT', {
          includeStockIndex: createForm.includeStockIndexPerps !== false,
          stockIndexOnly: createForm.coinSelectionMode === 'STOCK_INDEX',
        });
        setAllCoins(coins.length > 0 ? coins : ['BTC', 'ETH', 'SOL', 'ADA', 'XRP', 'BNB']);
        return;
      }
      const response = await apiService.get(`/api/admin/trading-strategies/universe?marketType=${marketType}`);
      console.log(`Fetch all coins (${marketType}) response:`, response);
      
      let coins: string[] = [];
      if (response && response.success) {
        if (Array.isArray(response.data)) {
          coins = response.data;
        } else if (response.data && typeof response.data === 'object' && Array.isArray(response.data.data)) {
          coins = response.data.data;
        }
      }
      
      console.log('Extracted all coins from response:', coins);
      
      if (coins.length > 0) {
        setAllCoins(coins);
        console.log('Set all coins from API:', coins.length, 'coins');
      } else {
        const currentCoins = allCoins.length > 0 ? allCoins : [];
        console.log('Response empty or invalid - Keeping/Using coins:', currentCoins.length);
        setAllCoins(currentCoins);
      }
    } catch (error: any) {
      console.error('Failed to fetch all coins:', error);
      setAllCoins([]);
    } finally {
      setLoadingAllCoins(false);
    }
  };

  // Handle coin selection mode change
  const handleCoinSelectionModeChange = (mode: 'TOP_N' | 'TOP_100' | 'MANUAL' | 'ALL_COINS' | 'STOCK_INDEX') => {
    setCreateForm({
      ...createForm,
      coinSelectionMode: mode,
      selectedCoins: (mode === 'MANUAL' || mode === 'ALL_COINS') ? [] : createForm.selectedCoins
    });
    // Fetch coins when switching to manual mode if not already loaded
    if (mode === 'MANUAL' && availableCoins.length === 0) {
      fetchAvailableCoins();
    }
    // Fetch all coins when switching to ALL_COINS mode
    if (mode === 'ALL_COINS' && allCoins.length === 0) {
      fetchAllCoins();
    }
  };

  // Refresh coins when market type changes (if in manual or all coins mode)
  useEffect(() => {
    if ((createForm.coinSelectionMode === 'MANUAL' || createForm.coinSelectionMode === 'ALL_COINS') && createForm.marketType) {
      // Clear selected coins and fetch new list for the selected market type
      setCreateForm(prev => ({
        ...prev,
        selectedCoins: []
      }));
      if (createForm.coinSelectionMode === 'MANUAL') {
        fetchAvailableCoins();
      } else if (createForm.coinSelectionMode === 'ALL_COINS') {
        fetchAllCoins();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createForm.marketType, createForm.coinSelectionMode]);

  // Refetch coin lists when switching between MEME and CRYPTO create flows
  useEffect(() => {
    if (!showCreateModal) return;
    setAvailableCoins([]);
    setAllCoins([]);
    setCreateForm(prev => ({ ...prev, selectedCoins: [] }));
    if (createForm.coinSelectionMode === 'MANUAL') {
      fetchAvailableCoins();
    } else if (createForm.coinSelectionMode === 'ALL_COINS') {
      fetchAllCoins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createModalStrategyType]);

  // Fetch coins when modal opens and user is in manual or all coins mode
  useEffect(() => {
    if (showCreateModal && createForm.coinSelectionMode === 'MANUAL') {
      fetchAvailableCoins();
    } else if (showCreateModal && createForm.coinSelectionMode === 'ALL_COINS') {
      fetchAllCoins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal]);

  // Handle manual coin selection
  const handleCoinToggle = (coin: string) => {
    const currentSelected = createForm.selectedCoins || [];
    if (currentSelected.includes(coin)) {
      setCreateForm({
        ...createForm,
        selectedCoins: currentSelected.filter(c => c !== coin)
      });
    } else {
      setCreateForm({
        ...createForm,
        selectedCoins: [...currentSelected, coin]
      });
    }
  };

  // Enhanced form helper functions
  const fetchAvailableCoinsEnhanced = async () => {
    try {
      setLoadingCoinsEnhanced(true);
      const marketType = createEnhancedForm.marketType || 'SPOT';
      const response = await apiService.get(`/api/admin/trading-strategies/universe?marketType=${marketType}`);
      
      let coins: string[] = [];
      if (response && response.success) {
        if (Array.isArray(response.data)) {
          coins = response.data;
        } else if (response.data && typeof response.data === 'object' && Array.isArray(response.data.data)) {
          coins = response.data.data;
        }
      }
      
      if (coins.length > 0) {
        setAvailableCoinsEnhanced(coins);
      } else {
        const currentCoins = availableCoinsEnhanced.length > 0 ? availableCoinsEnhanced : ['DOGE', 'SHIB', 'PEPE', 'FLOKI', 'BONK'];
        setAvailableCoinsEnhanced(currentCoins);
      }
    } catch (error: any) {
      console.error('Failed to fetch available coins for enhanced form:', error);
      const fallbackCoins = ['DOGE', 'SHIB', 'PEPE', 'FLOKI', 'BONK'];
      setAvailableCoinsEnhanced(fallbackCoins);
    } finally {
      setLoadingCoinsEnhanced(false);
    }
  };

  const handleCoinSelectionModeChangeEnhanced = (mode: 'TOP_N' | 'MANUAL') => {
    setCreateEnhancedForm({
      ...createEnhancedForm,
      coinSelectionMode: mode,
      selectedCoins: mode === 'MANUAL' ? [] : createEnhancedForm.selectedCoins
    });
    if (mode === 'MANUAL' && availableCoinsEnhanced.length === 0) {
      fetchAvailableCoinsEnhanced();
    }
  };

  useEffect(() => {
    if (createEnhancedForm.coinSelectionMode === 'MANUAL' && createEnhancedForm.marketType) {
      setCreateEnhancedForm(prev => ({
        ...prev,
        selectedCoins: []
      }));
      fetchAvailableCoinsEnhanced();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createEnhancedForm.marketType, createEnhancedForm.coinSelectionMode]);

  useEffect(() => {
    if (showCreateEnhancedModal && createEnhancedForm.coinSelectionMode === 'MANUAL') {
      fetchAvailableCoinsEnhanced();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateEnhancedModal]);

  const handleCoinToggleEnhanced = (coin: string) => {
    const currentSelected = createEnhancedForm.selectedCoins || [];
    if (currentSelected.includes(coin)) {
      setCreateEnhancedForm({
        ...createEnhancedForm,
        selectedCoins: currentSelected.filter(c => c !== coin)
      });
    } else {
      setCreateEnhancedForm({
        ...createEnhancedForm,
        selectedCoins: [...currentSelected, coin]
      });
    }
  };

  // Handle select all/none for manual selection
  const handleSelectAllCoins = () => {
    if (createForm.selectedCoins?.length === availableCoins.length) {
      setCreateForm({ ...createForm, selectedCoins: [] });
    } else {
      setCreateForm({ ...createForm, selectedCoins: [...availableCoins] });
    }
  };

  // --- Edit Coins handlers ---
  const handleCoinEdit = async (strategy: TradingStrategy) => {
    const config = parseStrategyConfig(strategy.config) as any;
    const currentMode: 'TOP_N' | 'MANUAL' = config.coinSelectionMode === 'MANUAL' ? 'MANUAL' : 'TOP_N';
    const currentLimit = config.coinLimit ?? 20;
    const currentCoins: string[] = Array.isArray(config.selectedCoins)
      ? config.selectedCoins.map((c: string) => normalizeCoinSymbol(c)).filter(Boolean)
      : [];

    setSelectedStrategyForCoinEdit(strategy);
    setEditCoinsForm({
      coinSelectionMode: currentMode,
      coinLimit: currentLimit,
      selectedCoins: currentCoins,
      coinSearch: '',
    });

    const cryptoPairsFallback = ['BTC', 'ETH', 'SOL', 'ADA', 'XRP', 'BNB'];
    const memeFallback = ['DOGE', 'SHIB', 'PEPE', 'FLOKI', 'BONK'];

    // Fetch the tradeable universe for the manual coin picker.
    // CRYPTO_PAIRS → major coins (BTC, ETH, SOL…); MEME → meme/futures universe.
    setLoadingEditCoins(true);
    try {
      const marketType = (config.marketType as string) || 'FUTURES';
      const isCryptoPairs = strategy.strategyType === 'CRYPTO_PAIRS';
      let coins: string[] = [];

      if (isCryptoPairs) {
        const cfg = config as StrategyConfig;
        const stockIndexOnly = cfg.coinSelectionMode === 'STOCK_INDEX';
        const coinsRaw = await fetchCryptoUniverseForPicker(marketType as 'FUTURES' | 'SPOT', {
          includeStockIndex: cfg.includeStockIndexPerps !== false,
          stockIndexOnly,
        });
        const coins = coinsRaw.map((c: string) => normalizeCoinSymbol(c)).filter(Boolean);
        setEditCoinsAvailable(
          mergeCoinUniverse(
            coins.length > 0 ? coins : cryptoPairsFallback,
            currentCoins
          )
        );
      } else {
        const response = await apiService.get(
          `/api/admin/trading-strategies/universe?marketType=${marketType}`
        );
        if (response?.success && Array.isArray(response.data)) {
          coins = response.data.map((c: string) => normalizeCoinSymbol(c)).filter(Boolean);
        } else if (response?.success && Array.isArray(response.data?.data)) {
          coins = response.data.data.map((c: string) => normalizeCoinSymbol(c)).filter(Boolean);
        }

        setEditCoinsAvailable(
          mergeCoinUniverse(
            coins.length > 0 ? coins : memeFallback,
            currentCoins
          )
        );
      }
    } catch {
      setEditCoinsAvailable(
        mergeCoinUniverse(
          strategy.strategyType === 'CRYPTO_PAIRS' ? cryptoPairsFallback : memeFallback,
          currentCoins
        )
      );
    } finally {
      setLoadingEditCoins(false);
    }

    // Reset prediction state each time the modal opens
    setPredictedCoins([]);
    setShowPredictionPanel(false);
    setPredictionMessage('');

    setShowEditCoinsModal(true);
  };

  const handleCoinEditToggle = (coin: string) => {
    const sym = normalizeCoinSymbol(coin);
    if (!sym) return;
    setEditCoinsForm(prev => ({
      ...prev,
      selectedCoins: prev.selectedCoins.includes(sym)
        ? prev.selectedCoins.filter(c => c !== sym)
        : [...prev.selectedCoins, sym],
    }));
  };

  const handleCoinEditSelectAll = () => {
    const visible = editCoinsAvailable.filter(c =>
      c.toLowerCase().includes(editCoinsForm.coinSearch.toLowerCase())
    );
    const allSelected = visible.every(c => editCoinsForm.selectedCoins.includes(c));
    setEditCoinsForm(prev => ({
      ...prev,
      selectedCoins: allSelected
        ? prev.selectedCoins.filter(c => !visible.includes(c))
        : Array.from(new Set([...prev.selectedCoins, ...visible])),
    }));
  };

  const handleCoinEditSave = async () => {
    if (!selectedStrategyForCoinEdit) return;

    if (editCoinsForm.coinSelectionMode === 'MANUAL' && editCoinsForm.selectedCoins.length < 2) {
      alert('Please select at least 2 coins for Manual mode.');
      return;
    }
    if (editCoinsForm.coinSelectionMode === 'TOP_N' && editCoinsForm.coinLimit < 1) {
      alert('Please enter a valid coin limit (minimum 1).');
      return;
    }

    setSavingEditCoins(true);
    try {
      const payload: any = { coinSelectionMode: editCoinsForm.coinSelectionMode };
      if (editCoinsForm.coinSelectionMode === 'TOP_N') {
        payload.coinLimit = editCoinsForm.coinLimit;
      } else {
        payload.selectedCoins = editCoinsForm.selectedCoins;
      }

      const response = await adminApiService.updateStrategyCoinSelection(
        selectedStrategyForCoinEdit.id,
        payload
      );

      if (response.success) {
        setShowEditCoinsModal(false);
        setSelectedStrategyForCoinEdit(null);
        loadStrategies();
        setError(null);
        alert(`✅ Coin selection updated! Changes apply on the next bot execution tick (~60s).`);
      } else {
        setError(response.error || 'Failed to update coin selection');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update coin selection');
    } finally {
      setSavingEditCoins(false);
    }
  };

  // ── Smart Predict: score & rank coins for this strategy ──────────────────
  const handlePredictCoins = async () => {
    if (!selectedStrategyForCoinEdit) return;
    setPredictingCoins(true);
    setPredictionMessage('');
    setShowPredictionPanel(true);
    try {
      const response = await adminApiService.predictStrategyCoins(selectedStrategyForCoinEdit.id, 'FUTURES');
      if (response.success && response.data) {
        setPredictedCoins(response.data);
        const hotCount = response.data.filter(c => c.score >= 70).length;
        const goodCount = response.data.filter(c => c.score >= 50 && c.score < 70).length;
        const histCount = response.data.filter(c => c.hasHistory).length;
        setPredictionMessage(
          `${response.data.length} coins scored · ${histCount} with trade history · ${hotCount} 🔥 HOT · ${goodCount} ✅ GOOD`
        );
      } else {
        setPredictionMessage(response.error || 'Prediction failed');
      }
    } catch (err: any) {
      setPredictionMessage(err.message || 'Prediction failed');
    } finally {
      setPredictingCoins(false);
    }
  };

  /** Auto-select the top N predicted coins (only those in editCoinsAvailable) */
  const handleApplyPredictions = (topN: number) => {
    const recommended = predictedCoins
      .map(c => normalizeCoinSymbol(c.coinName))
      .filter(sym => sym && editCoinsAvailable.includes(sym))
      .slice(0, topN);
    setEditCoinsForm(prev => ({ ...prev, selectedCoins: recommended }));
  };

  // ── Clone Strategy ────────────────────────────────────────────────────────
  const handleOpenClone = (strategy: TradingStrategy) => {
    const config = parseStrategyConfig(strategy.config) as any;
    setStrategyToClone(strategy);
    setCloneForm({
      name: `${strategy.name} - Copy`,
      takeProfit: config.takeProfit != null ? String(config.takeProfit) : '',
      fixedOrderAmount: config.fixedOrderAmount != null ? String(config.fixedOrderAmount) : '',
      maxOpenPositions: config.maxOpenPositions != null ? String(config.maxOpenPositions) : '',
      coinSelectionMode: config.coinSelectionMode === 'MANUAL' ? 'MANUAL' : 'TOP_N',
      coinLimit: config.coinLimit ?? 20,
    });
    setCloneError(null);
    setShowCloneModal(true);
  };

  const handleCloneSave = async () => {
    if (!strategyToClone) return;
    if (!cloneForm.name.trim()) {
      setCloneError('Strategy name is required.');
      return;
    }
    setCloningInProgress(true);
    setCloneError(null);
    try {
      const overrides: any = { name: cloneForm.name.trim() };
      if (cloneForm.takeProfit !== '') overrides.takeProfit = parseFloat(cloneForm.takeProfit);
      if (cloneForm.fixedOrderAmount !== '') overrides.fixedOrderAmount = parseFloat(cloneForm.fixedOrderAmount);
      if (cloneForm.maxOpenPositions !== '') overrides.maxOpenPositions = parseInt(cloneForm.maxOpenPositions, 10);
      overrides.coinSelectionMode = cloneForm.coinSelectionMode;
      overrides.coinLimit = cloneForm.coinLimit;

      const response = await adminApiService.cloneStrategy(strategyToClone.id, overrides);
      if (response.success) {
        setShowCloneModal(false);
        setStrategyToClone(null);
        await loadStrategies();
      } else {
        setCloneError(response.error || 'Clone failed. Please try again.');
      }
    } catch (err: any) {
      setCloneError(err.message || 'Clone failed. Please try again.');
    } finally {
      setCloningInProgress(false);
    }
  };

  // Helper function to parse strategy config
  const parseStrategyConfig = (configString: string): StrategyConfig => {
    try {
      return JSON.parse(configString);
    } catch (error) {
      console.error('Failed to parse strategy config:', error);
      return {};
    }
  };

  // Sparkline component for P&L chart
  const Sparkline: React.FC<{ points: number[]; width?: number; height?: number; color?: string }> = ({ 
    points, 
    width = 300, 
    height = 60,
    color = '#007bff'
  }) => {
    if (!points || points.length === 0) {
      return <div style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>No data</div>;
    }

    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min || 1;
    const padding = 4;

    const normalizedPoints = points.map(p => ((p - min) / range) * (height - padding * 2) + padding);
    const stepX = width / (points.length - 1 || 1);

    let path = `M 0 ${height - normalizedPoints[0]}`;
    for (let i = 1; i < normalizedPoints.length; i++) {
      path += ` L ${i * stepX} ${height - normalizedPoints[i]}`;
    }

    return (
      <svg width={width} height={height} style={{ display: 'block' }}>
        <path d={path} fill="none" stroke={color} strokeWidth={2} />
      </svg>
    );
  };

  // Helper function to detect if data is mock
  const isPaperTradingStrategy = (strategyId: number): boolean => {
    const strategy = strategies.find(s => s.id === strategyId);
    if (strategy) {
      const config = parseStrategyConfig(strategy.config);
      return config.paperTrading === true;
    }
    return false;
  };

  // Helper function to check if there are any real money live bots
  const hasRealMoneyLiveBots = (): boolean => {
    return strategies.some(strategy => {
      const config = parseStrategyConfig(strategy.config);
      return config.paperTrading !== true; // Real money if paperTrading is false, undefined, or null
    });
  };
  
  const isMockData = (strategyId: number): boolean => {
    // Paper trading strategies always use real market data (not mock)
    // The trades are simulated but use real prices from Kucoin
    if (isPaperTradingStrategy(strategyId)) {
      return false; // Always show as "Real Data" for paper trading
    }
    
    // If user has explicitly toggled to mock mode, return true
    if (mockDataMode[strategyId] === true) {
      return true;
    }
    
    // If user has explicitly toggled to real mode, return false
    if (mockDataMode[strategyId] === false) {
      return false;
    }
    
    // For real money bots (paperTrading !== true), default to real data
    // This prevents newly created real money bots from showing mock data
    const strategy = strategies.find(s => s.id === strategyId);
    if (strategy) {
      const config = parseStrategyConfig(strategy.config);
      // If it's a real money bot (not paper trading), default to real data
      if (config.paperTrading !== true) {
        // Real money bot - default to real data unless explicitly toggled
        // Only return true (mock) if we have strong evidence it's mock data
    const trades = strategyTrades[strategyId] || [];
    const positions = strategyPositions[strategyId] || [];
    
        // Strong mock data indicators:
        // 1. Trades have mock-specific patterns (e.g., all trades are exactly the same amount)
        // 2. Trades have unrealistic timestamps or patterns
        // 3. Multiple closed trades but none have proper exit reasons
        
        // For real money bots, we need STRONG evidence of mock data
        // Just having BUY/SELL trades without CLOSE trades is NOT enough
        // (newly opened positions won't have CLOSE trades yet)
        
        // Check if we have closed trades with exit reasons (definitive real data)
        const hasClosedTradesWithExitReason = trades.some((trade: any) => 
          trade.closedAt && trade.exitReason && trade.side === 'CLOSE'
        );
        
        // If we have closed trades with exit reasons, it's definitely real data
        if (hasClosedTradesWithExitReason) {
          return false;
        }
        
        // If we have open positions, it's likely real data (real money bot with active positions)
        if (positions.length > 0) {
          return false; // Real money bot with open positions = real data
        }
        
        // If we have trades but no positions and no closed trades, it could be:
        // 1. Real money bot that just opened positions (not closed yet) - default to real
        // 2. Mock data - but we need more evidence
        
        // Default to real data for real money bots (conservative approach)
        // User can explicitly toggle to mock if needed
        return false;
      }
    }
    
    // Auto-detect based on data (for strategies where paperTrading is not set or unclear)
    const trades = strategyTrades[strategyId] || [];
    const positions = strategyPositions[strategyId] || [];
    
    if (trades.length === 0 && positions.length === 0) {
      // No data at all - default to real data (don't assume mock)
      return false;
    }
    
    // Check if any trades have closedAt/exitReason (real data indicators)
    const hasRealTradeData = trades.some((trade: any) => 
      trade.closedAt || (trade.exitReason && trade.side === 'CLOSE')
    );
    
    // Only assume mock data if we have trades but NO real data indicators
    // AND no open positions (which would indicate real money bot)
    if (trades.length > 0 && !hasRealTradeData && positions.length === 0) {
      return true; // Likely mock data
    }
    
    // Default to real data (conservative)
    return false;
  };
  
  // Toggle mock data mode for a strategy
  const toggleMockDataMode = async (strategyId: number) => {
    // Disable toggle for paper trading strategies
    if (isPaperTradingStrategy(strategyId)) {
      return; // Do nothing - toggle is disabled for paper trading
    }
    
    const currentMode = mockDataMode[strategyId];
    
    // Cycle through: undefined (auto) → true (mock) → false (real) → undefined (auto)
    let newMode: boolean | undefined;
    if (currentMode === undefined) {
      newMode = true; // Auto → Mock
    } else if (currentMode === true) {
      newMode = false; // Mock → Real
    } else {
      newMode = undefined; // Real → Auto
    }
    
    // Update toggle state
    setMockDataMode(prev => ({ ...prev, [strategyId]: newMode }));
    
    // Always refetch data when toggling, regardless of modal state
    // Use newMode directly since state update is asynchronous
    // When newMode is undefined, use isMockData to determine (but it will use old state, so we need to calculate)
    try {
      // Determine what forceMock should be based on newMode
      // If newMode is undefined, we need to check what isMockData would return
      // But since state update is async, we'll use newMode directly and handle undefined case
      let shouldUseMock: boolean;
      if (newMode === true) {
        shouldUseMock = true;
      } else if (newMode === false) {
        shouldUseMock = false;
      } else {
        // When undefined (auto-detect), check current data to determine
        // This matches what isMockData() would do
        const trades = strategyTrades[strategyId] || [];
        const positions = strategyPositions[strategyId] || [];
        const hasRealTradeData = trades.some((trade: any) => 
          trade.closedAt || (trade.exitReason && trade.side === 'CLOSE')
        );
        shouldUseMock = trades.length > 0 && !hasRealTradeData;
      }
      
      const forceMockParam = shouldUseMock ? '&forceMock=true' : '&forceMock=false';
      
      if (strategyId === analyticsStrategyId) {
        // If analytics modal is open, use the full fetch function
        // Pass the new mode directly to avoid timing issues
        await fetchStrategyAnalytics(strategyId, shouldUseMock);
      } else {
        // Even if analytics modal isn't open, fetch data so it's available when needed
        // Fetch trades to update the strategy card display
        const tradesResponse = await apiService.get(`/api/admin/trading-strategies/${strategyId}/analytics/trades?limit=10${forceMockParam}`);
        if (tradesResponse.success) {
          const tradesData = Array.isArray(tradesResponse.data) 
            ? tradesResponse.data 
            : (tradesResponse.data?.data || tradesResponse.data || []);
          setStrategyTrades(prev => ({ ...prev, [strategyId]: tradesData }));
        }
        
        // Also fetch positions for completeness - always include forceMock parameter
        const positionsParam = forceMockParam.substring(1); // Remove the '&'
        const positionsResponse = await apiService.get(`/api/admin/trading-strategies/${strategyId}/analytics/positions?${positionsParam}`);
        if (positionsResponse.success) {
          const posData = Array.isArray(positionsResponse.data)
            ? positionsResponse.data
            : (positionsResponse.data?.data || positionsResponse.data || []);
          setStrategyPositions(prev => ({ ...prev, [strategyId]: Array.isArray(posData) ? posData : [] }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch strategy data after toggle:', error);
    }
  };

  // Fetch strategy analytics data
  const fetchStrategyAnalytics = async (strategyId: number, overrideMode?: boolean | undefined, silentRefresh: boolean = false) => {
    try {
      // Save scroll position before refresh (if modal is open and content ref exists)
      if (showAnalyticsModal && modalContentRef.current) {
        savedScrollPosition.current = modalContentRef.current.scrollTop;
      }
      
      // Only show loading indicator on initial load or manual refresh, not on auto-refresh
      if (!silentRefresh) {
      setAnalyticsLoading(true);
      }
      
      // For paper trading strategies, always use real data (explicitly set forceMock=false)
      // Paper trading uses real market data from Kucoin, so we fetch the simulated trades
      let forceMockParam = '';
      if (isPaperTradingStrategy(strategyId)) {
        // Explicitly set forceMock=false for paper trading to prevent mock data fallback
        forceMockParam = '&forceMock=false';
      } else {
        // For non-paper trading strategies, determine if we should use mock data
        // If overrideMode is provided, use it (for immediate toggle updates)
        // Otherwise, use isMockData() to match what the toggle switch displays
        let shouldUseMock: boolean;
        if (overrideMode !== undefined) {
          // Use the override mode directly
          shouldUseMock = overrideMode === true;
        } else {
          // Use isMockData() to match what the toggle switch displays
          shouldUseMock = isMockData(strategyId);
        }
        forceMockParam = shouldUseMock ? '&forceMock=true' : '&forceMock=false';
      }
      
      // Fetch all data in parallel for better performance
      const [pnlResponse, tradesResponse, positionsResponse, analyticsResponse] = await Promise.all([
        apiService.get(`/api/admin/trading-strategies/${strategyId}/analytics/pnl-history?days=30${forceMockParam}`),
        apiService.get(`/api/admin/trading-strategies/${strategyId}/analytics/trades?limit=10${forceMockParam}`),
        apiService.get(`/api/admin/trading-strategies/${strategyId}/analytics/positions?${forceMockParam ? forceMockParam.substring(1) : 'forceMock=false'}`),
        apiService.get(`/api/admin/analytics/strategies/${strategyId}?range=1M`)
      ]);

      // Helper function to quickly compare arrays (faster than JSON.stringify)
      const arraysEqual = (a: any[], b: any[]): boolean => {
        if (!a || !b || a.length !== b.length) return false;
        // Quick check: compare first and last items, and length
        if (a.length === 0) return true;
        if (a[0] !== b[0] || a[a.length - 1] !== b[b.length - 1]) return false;
        // For small arrays, do full comparison; for large arrays, just check key fields
        if (a.length <= 10) {
          return JSON.stringify(a) === JSON.stringify(b);
        }
        // For large arrays, compare key identifying fields
        return a.every((item, i) => {
          const bItem = b[i];
          return item.timestamp === bItem?.timestamp && 
                 item.symbol === bItem?.symbol && 
                 item.pnl === bItem?.pnl;
        });
      };

      // Batch state updates to prevent multiple re-renders
      // Use React's automatic batching (React 18+) or manual batching
      React.startTransition(() => {
        // Update P&L history only if changed
      if (pnlResponse.success) {
          setStrategyPnlHistory(prev => {
            const currentData = prev[strategyId];
            const newData = pnlResponse.data;
            // Quick comparison for performance
            if (currentData && newData && arraysEqual(currentData, newData)) {
              return prev; // No change, return same reference (prevents re-render)
            }
            return { ...prev, [strategyId]: newData };
          });
        }

        // Update trades only if changed
      if (tradesResponse.success) {
        const tradesData = Array.isArray(tradesResponse.data) 
          ? tradesResponse.data 
          : (tradesResponse.data?.data || tradesResponse.data || []);
          setStrategyTrades(prev => {
            const currentData = prev[strategyId];
            // Quick comparison for performance
            if (currentData && tradesData && arraysEqual(currentData, tradesData)) {
              return prev; // No change, return same reference (prevents re-render)
            }
            return { ...prev, [strategyId]: tradesData };
          });
        }

        // Update positions only if changed (normalize shape like trades — always store an array)
      if (positionsResponse.success) {
          const positionsData = Array.isArray(positionsResponse.data)
            ? positionsResponse.data
            : (positionsResponse.data?.data || positionsResponse.data || []);
          setStrategyPositions(prev => {
            const currentData = prev[strategyId];
            const newData = Array.isArray(positionsData) ? positionsData : [];
            if (currentData && arraysEqual(currentData, newData)) {
              return prev;
            }
            return { ...prev, [strategyId]: newData };
          });
        }

        // Update fee breakdown only if changed
      if (analyticsResponse.success && analyticsResponse.data) {
        const analytics = analyticsResponse.data;
          const newFeeBreakdown = {
          grossPnl: analytics.grossPnl || analytics.totalPnl || 0,
          totalTradingFees: analytics.totalTradingFees || 0,
          totalFundingFees: analytics.totalFundingFees || 0,
          netPnl: analytics.netPnl || (analytics.totalPnl - (analytics.totalTradingFees || 0) - (analytics.totalFundingFees || 0))
          };
          setFeeBreakdown(prev => {
            if (prev && 
                prev.grossPnl === newFeeBreakdown.grossPnl &&
                prev.totalTradingFees === newFeeBreakdown.totalTradingFees &&
                prev.totalFundingFees === newFeeBreakdown.totalFundingFees &&
                prev.netPnl === newFeeBreakdown.netPnl) {
              return prev; // No change
            }
            return newFeeBreakdown;
          });
        }
      });

      // Fetch account overview (for account-level P&L) - only if not already fetched recently
      // This doesn't change often, so we can skip it during auto-refresh to reduce repaints
      if (!accountOverview) {
      try {
        const overviewResponse = await adminApiService.getKucoinAccountOverview('USDT');
        if (overviewResponse.success && overviewResponse.data) {
          setAccountOverview(overviewResponse.data);
        }
      } catch (error) {
        console.error('Failed to fetch account overview:', error);
        setAccountOverview(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch strategy analytics:', error);
    } finally {
      // Only hide loading indicator if we showed it
      if (!silentRefresh) {
      setAnalyticsLoading(false);
      }
      
      // Restore scroll position after data update (use requestAnimationFrame for smooth update)
      if (showAnalyticsModal && modalContentRef.current) {
        requestAnimationFrame(() => {
          if (modalContentRef.current) {
            modalContentRef.current.scrollTop = savedScrollPosition.current;
          }
        });
      }
    }
  };
  
  // Restore scroll position after render when data changes
  useLayoutEffect(() => {
    if (showAnalyticsModal && modalContentRef.current && savedScrollPosition.current > 0) {
      // Use requestAnimationFrame to ensure DOM is fully updated before restoring scroll
      requestAnimationFrame(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = savedScrollPosition.current;
        }
      });
    }
  }, [showAnalyticsModal, strategyTrades, strategyPositions, strategyPnlHistory, analyticsLoading]);

  // Get sparkline data from P&L history
  const getSparklineData = (strategyId: number): number[] => {
    const history = strategyPnlHistory[strategyId];
    if (history && history.length > 0) {
      return history.map((entry: any) => parseFloat(entry.cumulativePnl) || 0);
    }
    return [];
  };

  // Open analytics modal
  const openAnalyticsModal = (strategyId: number) => {
    // Reset scroll position when opening modal
    savedScrollPosition.current = 0;
    setAnalyticsStrategyId(strategyId);
    setShowAnalyticsModal(true);
    fetchStrategyAnalytics(strategyId);
  };

  // Scroll to top when Analytics modal opens on mobile
  useEffect(() => {
    if (showAnalyticsModal) {
      // On mobile, scroll to top when modal opens
      if (window.innerWidth <= 768) {
        // Function to perform all scroll operations
        const performScroll = () => {
          // Scroll window and document
          window.scrollTo(0, 0);
          if (document.documentElement) {
            document.documentElement.scrollTop = 0;
          }
          if (document.body) {
            document.body.scrollTop = 0;
          }
          
          // Scroll the admin-content container (this is the scrollable container on mobile)
          const adminContent = document.querySelector('.admin-content') as HTMLElement;
          if (adminContent) {
            adminContent.scrollTop = 0;
            adminContent.scrollTo(0, 0);
          }
          // Also try scrolling the admin-main if it exists
          const adminMain = document.querySelector('.admin-main') as HTMLElement;
          if (adminMain) {
            adminMain.scrollTop = 0;
          }
          // Try scrolling admin-content-body as well
          const adminContentBody = document.querySelector('.admin-content-body') as HTMLElement;
          if (adminContentBody) {
            adminContentBody.scrollTop = 0;
          }
        };
        
        // Perform scroll immediately
        performScroll();
        
        // Also perform scroll after render using double RAF to ensure DOM is ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            performScroll();
          });
        });
        
        // Prevent body scroll while modal is open
        document.body.classList.add('modal-open');
      }
    } else {
      // Restore body scroll when modal closes
      if (window.innerWidth <= 768) {
        document.body.classList.remove('modal-open');
      }
    }
    
    // Cleanup: restore body scroll on unmount
    return () => {
      if (window.innerWidth <= 768) {
        document.body.classList.remove('modal-open');
      }
    };
  }, [showAnalyticsModal]);

  // Fetch all history trades for a strategy
  const fetchAllHistoryTrades = async (strategyId: number) => {
    try {
      setHistoryLoading(true);
      
      // For paper trading strategies, always use real data (explicitly set forceMock=false)
      let forceMockParam = '';
      if (isPaperTradingStrategy(strategyId)) {
        // Explicitly set forceMock=false for paper trading to prevent mock data fallback
        forceMockParam = '&forceMock=false';
      } else {
        // Get current mock data mode for this strategy
        // Use isMockData() to match what the toggle switch displays
        const shouldUseMock = isMockData(strategyId);
        forceMockParam = shouldUseMock ? '&forceMock=true' : '&forceMock=false';
      }
      
      // Fetch a large number of trades (1000) to get comprehensive history
      const response = await apiService.get(`/api/admin/trading-strategies/${strategyId}/analytics/trades?limit=1000${forceMockParam}`);
      if (response.success) {
        // Handle both direct array response and nested response.data structure
        const tradesData = Array.isArray(response.data) ? response.data : (response.data?.data || response.data || []);
        setAllHistoryTrades(tradesData);
        console.log('History trades loaded:', tradesData.length, 'trades');
      } else {
        console.error('Failed to fetch history trades:', response.error);
        setAllHistoryTrades([]);
      }
    } catch (error) {
      console.error('Failed to fetch history trades:', error);
      setAllHistoryTrades([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Open history modal
  const openHistoryModal = (strategyId: number) => {
    setHistoryPage(1);
    setHistoryDateRange('30D');
    setHistoryCoinFilter('ALL');
    setShowHistoryModal(true);
    fetchAllHistoryTrades(strategyId);
  };

  // Filter history trades based on date range and coin
  const filterHistoryTrades = () => {
    let filtered = [...allHistoryTrades];

    // Filter by date range
    if (historyDateRange !== 'ALL') {
      const now = new Date();
      let cutoffDate = new Date();
      
      switch (historyDateRange) {
        case '7D':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case '30D':
          cutoffDate.setDate(now.getDate() - 30);
          break;
        case '90D':
          cutoffDate.setDate(now.getDate() - 90);
          break;
      }
      
      filtered = filtered.filter((trade: any) => {
        const tradeDate = new Date(trade.timestamp || trade.closedAt || trade.createdAt || trade.entryTime || trade.exitTime);
        return tradeDate >= cutoffDate;
      });
    }

    // Filter by coin
    if (historyCoinFilter !== 'ALL') {
      filtered = filtered.filter((trade: any) => 
        trade.symbol && trade.symbol.toUpperCase() === historyCoinFilter.toUpperCase()
      );
    }

    // Sort by date (newest first)
    filtered.sort((a: any, b: any) => {
      const dateA = new Date(a.timestamp || a.closedAt || a.createdAt || a.entryTime || a.exitTime).getTime();
      const dateB = new Date(b.timestamp || b.closedAt || b.createdAt || b.entryTime || b.exitTime).getTime();
      return dateB - dateA;
    });

    return filtered;
  };

  useEffect(() => {
    loadStrategies();
    loadActivePairs();
    loadUniverse();
    loadAiAutoTuneGlobalSettings();
    // Fetch available retail subscription plans for the required-plan dropdown
    apiService.get('/api/retail/subscription/plans').then((res: any) => {
      if (res.success && Array.isArray(res.data)) {
        setAvailablePlans(res.data.map((p: any) => ({ id: p.id, name: p.name })));
      }
    }).catch(() => {/* non-critical */});
    
    // Load saved form data from localStorage if modal was closed unexpectedly
    const savedFormData = localStorage.getItem('memeCoinStrategyForm');
    if (savedFormData && !showCreateModal) {
      try {
        const parsed = JSON.parse(savedFormData);
        setCreateForm(parsed);
      } catch (e) {
        console.error('Failed to load saved form data:', e);
      }
    }
  }, []);
  
  // Save form data to localStorage when modal is open to prevent data loss
  useEffect(() => {
    if (showCreateModal || showCreateEnhancedModal) {
      setIsModalOpen(true);
      // Save form data periodically while modal is open
      const saveInterval = setInterval(() => {
        if (showCreateModal) {
          localStorage.setItem('memeCoinStrategyForm', JSON.stringify(createForm));
        }
        if (showCreateEnhancedModal) {
          localStorage.setItem('memeCoinEnhancedStrategyForm', JSON.stringify(createEnhancedForm));
        }
      }, 1000); // Save every second
      
      return () => {
        clearInterval(saveInterval);
        setIsModalOpen(false);
      };
    } else {
      setIsModalOpen(false);
    }
  }, [showCreateModal, showCreateEnhancedModal, createForm, createEnhancedForm]);

  // ── Persist "Edit Strategy Config" modal form to localStorage on every change
  //    (synchronous — no delay — so even an instant page refresh saves the data).
  useEffect(() => {
    if (showTPEditModal && selectedStrategyForTPEdit) {
      localStorage.setItem('tpEditForm', JSON.stringify(tpEditForm));
      localStorage.setItem('tpEditStrategyId', String(selectedStrategyForTPEdit.id));
    }
  }, [tpEditForm, showTPEditModal, selectedStrategyForTPEdit]);

  // ── Restore "Edit Strategy Config" modal after a page refresh or component
  //    remount. Runs once as soon as strategies are available (either immediately
  //    on first render or after the async loadStrategies() call resolves).
  useEffect(() => {
    if (strategies.length > 0 && !tpFormRestoredRef.current) {
      tpFormRestoredRef.current = true;
      try {
        const savedForm = localStorage.getItem('tpEditForm');
        const savedId   = localStorage.getItem('tpEditStrategyId');
        if (savedForm && savedId) {
          const strategy = strategies.find(s => s.id === parseInt(savedId, 10));
          if (strategy) {
            const parsedForm = JSON.parse(savedForm);
            setSelectedStrategyForTPEdit(strategy);
            // Merge into default shape so any new fields added after the draft
            // was saved are always present (e.g. `leverage` added later).
            setTPEditForm(prev => ({ ...prev, ...parsedForm }));
            setTPEditErrors({});
            setShowTPEditModal(true);
            console.log('[TP Edit] Restored draft for strategy', strategy.name, parsedForm);
          } else {
            // Strategy no longer exists — discard stale draft
            localStorage.removeItem('tpEditForm');
            localStorage.removeItem('tpEditStrategyId');
          }
        }
      } catch (e) {
        console.error('Failed to restore TP edit form from localStorage:', e);
        localStorage.removeItem('tpEditForm');
        localStorage.removeItem('tpEditStrategyId');
      }
    }
  }, [strategies]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Diagnostics to verify data rendering in table
    try {
      // Log sizes and sample rows
      console.log('[AdminTradingStrategies] activePairs count:', activePairs.length, 'sample:', activePairs[0]);
      console.log('[AdminTradingStrategies] memeUniverse:', memeUniverse);
      console.log('[AdminTradingStrategies] strategies count:', strategies.length);
    } catch {}
  }, [activePairs, memeUniverse, strategies]);

  // Handle navigation state to auto-open analytics modal
  useEffect(() => {
    const state = location.state as { openAnalyticsForBot?: number } | null;
    if (state?.openAnalyticsForBot && strategies.length > 0) {
      // Find the strategy by ID
      const strategy = strategies.find(s => s.id === state.openAnalyticsForBot);
      if (strategy) {
        // Open the analytics modal for this bot
        openAnalyticsModal(state.openAnalyticsForBot);
        // Clear the state to prevent re-opening on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, strategies]);

  const normalizeBaseSymbol = (raw: string | null | undefined): string => {
    if (!raw) return '';
    return raw
      .toUpperCase()
      .replace('/USDT', '')
      .replace('-USDT', '')
      .replace('USDTM', '')
      .replace('USDT', '')
      .trim();
  };

  const loadMemeBlacklist = async () => {
    try {
      const res = await adminApiService.getMemeBlacklist();
      if (res.success && Array.isArray(res.data)) {
        setBlacklistedBases(
          new Set(
            res.data
              .map((e: any) => normalizeBaseSymbol(e?.baseSymbol))
              .filter(Boolean)
          )
        );
      }
    } catch {
      // Non-fatal for trading UI.
    }
  };

  useEffect(() => {
    loadMemeBlacklist();
  }, []);

  useEffect(() => {
    if (!showBlacklistModal) {
      loadMemeBlacklist();
    }
  }, [showBlacklistModal]);

  // When a strategy is in "stopping" state, automatically hide the status message
  // once the backend reports the strategy as STOPPED and there are no ACTIVE pairs left.
  useEffect(() => {
    if (!stoppingStrategyId) return;

    try {
      const strategy = strategies.find(s => s.id === stoppingStrategyId);
      const hasActivePairs = activePairs.some(
        p => p.strategyId === stoppingStrategyId && p.status === 'ACTIVE'
      );

      if (strategy && strategy.status === 'STOPPED' && !hasActivePairs) {
        setStoppingStrategyId(null);
        setStopStatusMessage(null);
      }
    } catch {
      // Fail-safe: do not break UI if anything goes wrong
    }
  }, [stoppingStrategyId, strategies, activePairs]);

  useEffect(() => {
    if (!stoppingStrategyId || !stopStatusMessage) return;
    if (stopToastTimeoutRef.current) {
      window.clearTimeout(stopToastTimeoutRef.current);
    }
    // Failsafe so UI never gets stuck if backend/DB state lags.
    stopToastTimeoutRef.current = window.setTimeout(() => {
      setStoppingStrategyId(null);
      setStopStatusMessage(null);
      stopToastTimeoutRef.current = null;
    }, 75000);

    return () => {
      if (stopToastTimeoutRef.current) {
        window.clearTimeout(stopToastTimeoutRef.current);
        stopToastTimeoutRef.current = null;
      }
    };
  }, [stoppingStrategyId, stopStatusMessage]);

  /** Open positions for analytics UI — same source of truth as Full Analytics (raw positions API). */
  const getVisibleOpenPositions = (strategyId: number) => {
    const allPositions = strategyPositions[strategyId] || [];

    // Do not hide rows based on recent-trades heuristics: the trades list is truncated (e.g. limit 10)
    // and can show a CLOSE for a symbol while /analytics/positions still correctly returns open legs
    // (hedge legs, re-entries, or history ordering). Cross-referencing trades caused only 1 of 3
    // positions to show vs AdminAnalyticsTabs which lists strategyPositions as returned.

    return allPositions.filter((position: any) => {
      const base = normalizeBaseSymbol(position?.symbol);
      if (!base) return false;
      if (blacklistedBases.has(base)) return false;
      return true;
    });
  };

  // Auto-refresh analytics data when modal is open
  useEffect(() => {
    if (showAnalyticsModal && analyticsStrategyId) {
      // For real money bots, ensure we fetch real data (not mock) on modal open
      const strategy = strategies.find(s => s.id === analyticsStrategyId);
      if (strategy) {
        const config = parseStrategyConfig(strategy.config);
        // If it's a real money bot, explicitly set forceMock=false to ensure real data
        if (config.paperTrading !== true && mockDataMode[analyticsStrategyId] !== true) {
          // Real money bot and not explicitly toggled to mock - fetch real data
          fetchStrategyAnalytics(analyticsStrategyId, false);
        } else {
          // Use default detection
      fetchStrategyAnalytics(analyticsStrategyId);
        }
      } else {
        // Fallback to default
        fetchStrategyAnalytics(analyticsStrategyId);
      }
      
      // Set up interval to refresh every 10 seconds
      // Only refresh if modal is still open and user is viewing it
      const interval = setInterval(() => {
        if (showAnalyticsModal && analyticsStrategyId) {
          // Silent refresh - don't show loading indicator to prevent repaint
          // Use same logic as initial fetch to ensure real money bots get real data
          const strategy = strategies.find(s => s.id === analyticsStrategyId);
          if (strategy) {
            const config = parseStrategyConfig(strategy.config);
            if (config.paperTrading !== true && mockDataMode[analyticsStrategyId] !== true) {
              fetchStrategyAnalytics(analyticsStrategyId, false, true);
            } else {
              fetchStrategyAnalytics(analyticsStrategyId, undefined, true);
            }
          } else {
            fetchStrategyAnalytics(analyticsStrategyId, undefined, true);
          }
        }
      }, 10000); // 10 seconds
      
      // Cleanup interval when modal closes
      return () => clearInterval(interval);
    }
  }, [showAnalyticsModal, analyticsStrategyId, strategies, mockDataMode]);

  const loadStrategies = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/admin/trading-strategies');
      if (response.success) {
        setStrategies(response.data);
      } else {
        setError(response.error || 'Failed to load strategies');
      }
    } catch (error: any) {
      console.error('Failed to load strategies:', error);
      setError(error.message || 'Failed to load strategies');
    } finally {
      setLoading(false);
    }
  };

  const loadActivePairs = async () => {
    try {
      const adminUserId = adminUser?.id ? Number(adminUser.id) : null;
      const qs = adminUserId ? `?adminUserId=${encodeURIComponent(String(adminUserId))}` : '';
      const response = await apiService.get(`/api/admin/trading-strategies/pairs/active${qs}`);
      if (response.success) {
        console.log('[AdminTradingStrategies] Loaded active pairs:', response.data);
        setActivePairs(response.data || []);
      }
    } catch (error: any) {
      console.error('Failed to load active pairs:', error);
    }
  };

  const loadUniverse = async () => {
    try {
      const response = await apiService.get('/api/admin/trading-strategies/universe');
      if (response.success) {
        console.log('[AdminTradingStrategies] Loaded meme universe:', response.data);
        setMemeUniverse(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error: any) {
      console.error('Failed to load meme universe:', error);
    }
  };

  const fetchUserEnrollments = async () => {
    try {
      setEnrollmentsLoading(true);
      const response = await apiService.get('/api/admin/trading-strategies/user-enrollments');
      if (response.success && response.data) {
        setUserEnrollments(response.data.userEnrollments || []);
        setEnrollmentsSummary(response.data.summary || null);
      } else {
        setError(response.error || 'Failed to load user enrollments');
      }
    } catch (error: any) {
      console.error('Failed to load user enrollments:', error);
      setError(error.message || 'Failed to load user enrollments');
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  /** Map backend AI suggestion payload into create-form shape (numbers for UI). */
  const mapAiSuggestedConfigToForm = (cfg: Record<string, unknown>): CreateStrategyRequest => {
    const num = (key: string, fallback?: number) => {
      const v = cfg[key];
      if (v == null) return fallback as number | undefined;
      return typeof v === 'number' ? v : parseFloat(String(v));
    };
    const bool = (key: string, fallback = false) => {
      const v = cfg[key];
      if (v == null) return fallback;
      return Boolean(v);
    };
    let maxFundingRate = num('maxFundingRate');
    if (maxFundingRate != null && maxFundingRate < 0.01) {
      maxFundingRate = maxFundingRate * 100;
    }
    return {
      name: String(cfg.name ?? ''),
      fixedOrderAmount: num('fixedOrderAmount', 50) ?? 50,
      leverage: num('leverage', 1) ?? 1,
      trailingStopLoss: num('trailingStopLoss', 4.5) ?? 4.5,
      takeProfit: num('takeProfit', 7) ?? 7,
      basketTakeProfit: num('basketTakeProfit', 5) ?? 5,
      enableUniversalBasketTakeProfit: bool('enableUniversalBasketTakeProfit'),
      universalBasketTakeProfit: num('universalBasketTakeProfit', 2),
      universalBasketMinOpenLegs: num('universalBasketMinOpenLegs', 2),
      exchange: String(cfg.exchange ?? 'KUCOIN'),
      createdBy: num('createdBy', 1) ?? 1,
      enableStopLossPerCoin: bool('enableStopLossPerCoin', true),
      enableTakeProfitPerCoin: bool('enableTakeProfitPerCoin', true),
      enableBasketTakeProfit: bool('enableBasketTakeProfit', true),
      stopLossType: (cfg.stopLossType as 'CLASSIC' | 'TRAILING') ?? 'CLASSIC',
      coinSelectionMode: (cfg.coinSelectionMode as 'TOP_N' | 'MANUAL' | 'ALL_COINS') ?? 'TOP_N',
      coinLimit: num('coinLimit', 10) ?? 10,
      selectedCoins: Array.isArray(cfg.selectedCoins) ? (cfg.selectedCoins as string[]) : [],
      marketType: (cfg.marketType as 'SPOT' | 'FUTURES') ?? 'FUTURES',
      maxOpenPositions: num('maxOpenPositions', 3) ?? 3,
      paperTrading: bool('paperTrading'),
      enableVolatilityFilter: bool('enableVolatilityFilter', true),
      minMarketVolatility: num('minMarketVolatility'),
      maxMarketVolatility: num('maxMarketVolatility'),
      volatilityCalculationCoins: num('volatilityCalculationCoins', 10) ?? 10,
      volatilityCalculationMethod: (cfg.volatilityCalculationMethod as 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO') ?? 'AUTO',
      marginMode: (cfg.marginMode as 'CROSS' | 'ISOLATED') ?? 'CROSS',
      orderType: (cfg.orderType as 'MARKET' | 'LIMIT') ?? 'MARKET',
      limitOffset: num('limitOffset', 0.1) ?? 0.1,
      limitTimeoutSeconds: num('limitTimeoutSeconds', 30) ?? 30,
      minMarketActivityThreshold: num('minMarketActivityThreshold', 4.4) ?? 4.4,
      enableMicroTsl: bool('enableMicroTsl'),
      microTslActivationPercent: num('microTslActivationPercent', 4),
      microTslDistancePercent: num('microTslDistancePercent', 1),
      btcCorrelationEnabled: bool('btcCorrelationEnabled', true),
      btcEmaLength: num('btcEmaLength', 50) ?? 50,
      btcEmaTimeframe: (cfg.btcEmaTimeframe as '5m' | '15m') ?? '5m',
      btcMomentumLongThreshold: num('btcMomentumLongThreshold', 0.5) ?? 0.5,
      btcMomentumShortThreshold: num('btcMomentumShortThreshold', 0.5) ?? 0.5,
      btcRangeNoTradeThreshold: num('btcRangeNoTradeThreshold', 0.8) ?? 0.8,
      btcDumpProtectionEnabled: bool('btcDumpProtectionEnabled', true),
      btcDumpThreshold: num('btcDumpThreshold', 1.5) ?? 1.5,
      btcCooldownMinutes: num('btcCooldownMinutes', 20) ?? 20,
      btcTrendTimeframe: String(cfg.btcTrendTimeframe ?? '1h'),
      btcTrendEmaPeriod: num('btcTrendEmaPeriod', 21) ?? 21,
      entryFiltersEnabled: bool('entryFiltersEnabled', true),
      fundingRateFilterEnabled: bool('fundingRateFilterEnabled', true),
      maxFundingRate: maxFundingRate ?? 0.05,
      rsiFilterEnabled: bool('rsiFilterEnabled', true),
      rsiLongMin: num('rsiLongMin', 55) ?? 55,
      rsiShortMax: num('rsiShortMax', 45) ?? 45,
      emaFilterEnabled: bool('emaFilterEnabled', true),
      emaPeriod: num('emaPeriod', 20) ?? 20,
      minProfitExitPercent: num('minProfitExitPercent', 1) ?? 1,
      memeParticipationFilterEnabled: bool('memeParticipationFilterEnabled', true),
      minParticipationRate: num('minParticipationRate', 0.25) ?? 0.25,
      microReversalFilterEnabled: bool('microReversalFilterEnabled', false),
      microReversalMinDropPercent: num('microReversalMinDropPercent', -0.5) ?? -0.5,
      microReversalMaxDropPercent: num('microReversalMaxDropPercent', -0.1) ?? -0.1,
      microReversalLookbackCandles: num('microReversalLookbackCandles', 12) ?? 12,
      oracleEnabled: bool('oracleEnabled', true),
      oracleConfidenceThreshold: num('oracleConfidenceThreshold', 0.5) ?? 0.5,
      oracleWeightMomentum: num('oracleWeightMomentum', 0.25) ?? 0.25,
      oracleWeightRsi: num('oracleWeightRsi', 0.3) ?? 0.3,
      oracleWeightVolume: num('oracleWeightVolume', 0.2) ?? 0.2,
      oracleWeightFunding: num('oracleWeightFunding', 0.15) ?? 0.15,
      oracleWeightEma: num('oracleWeightEma', 0.1) ?? 0.1,
    };
  };

  const createAiStrategy = async () => {
    try {
      setAiSuggestLoading(true);
      setError(null);
      const response = await adminApiService.suggestAiStrategyConfig('FUTURES');
      if (!response.success || !response.data?.suggestedConfig) {
        setError(response.error || 'Failed to generate AI strategy suggestion');
        return;
      }
      const data = response.data;
      const mapped = mapAiSuggestedConfigToForm(data.suggestedConfig);
      setCreateForm(mapped);
      const preset = data.volatilityPreset;
      if (preset === 'moderate' || preset === 'conservative' || preset === 'aggressive') {
        setSelectedVolatilityPreset(preset);
        setVolatilityPresetMode('preset');
      }
      setAiSuggestionMeta({
        marketRegime: data.marketRegime,
        regimeLabel: data.regimeLabel,
        confidence: data.confidence,
        volatilityPreset: data.volatilityPreset,
        reasoning: data.reasoning || [],
        marketSnapshot: data.marketSnapshot,
      });
      setCreateModalStrategyType('MEME');
      setShowCreateModal(true);
      fetchAvailableCoins();
    } catch (err: any) {
      console.error('createAiStrategy failed:', err);
      setError(err.message || 'Failed to generate AI strategy suggestion');
    } finally {
      setAiSuggestLoading(false);
    }
  };

  const createCryptoAiStrategy = async () => {
    try {
      setAiSuggestLoading(true);
      setError(null);
      const response = await adminApiService.suggestCryptoAiStrategyConfig('FUTURES', {
        coinSelectionMode: createForm.coinSelectionMode || 'TOP_100',
        coinLimit: createForm.coinLimit || 100,
        selectedCoins: createForm.selectedCoins,
      });
      if (!response.success || !response.data?.suggestedConfig) {
        setError(response.error || 'Failed to generate crypto AI strategy suggestion');
        return;
      }
      const data = response.data;
      const mapped = mapAiSuggestedConfigToForm(data.suggestedConfig);
      setCreateForm((prev) => ({
        ...mapped,
        coinSelectionMode: 'TOP_100',
        coinLimit: 100,
        marketType: 'FUTURES',
        kucoinConfigId: prev.kucoinConfigId,
      }));
      const preset = data.volatilityPreset;
      if (preset === 'moderate' || preset === 'conservative' || preset === 'aggressive') {
        setSelectedVolatilityPreset(preset);
        setVolatilityPresetMode('preset');
      }
      setAiSuggestionMeta({
        marketRegime: data.marketRegime,
        regimeLabel: data.regimeLabel,
        confidence: data.confidence,
        volatilityPreset: data.volatilityPreset,
        reasoning: data.reasoning || [],
        marketSnapshot: data.marketSnapshot,
      });
      setCreateModalStrategyType('CRYPTO');
      setShowCreateModal(true);
      fetchAvailableCoins();
    } catch (err: any) {
      console.error('createCryptoAiStrategy failed:', err);
      setError(err.message || 'Failed to generate crypto AI strategy suggestion');
    } finally {
      setAiSuggestLoading(false);
    }
  };

  // Function to prefill form with optimal profit-making settings
  const prefillOptimalStrategy = () => {
    setAiSuggestionMeta(null);
    // Optimal settings based on analysis: docs/Analysis/strategy-loss-analysis-and-fixes.md
    const optimalSettings: CreateStrategyRequest = {
      name: '',
      fixedOrderAmount: 50, // Minimum recommended: $50 (instead of $19)
      leverage: 1, // Keep 1x leverage
      trailingStopLoss: 4.5, // 4-5% stop loss (using 4.5% as middle ground)
      takeProfit: 7.0, // Keep 7% take profit
      basketTakeProfit: 5.0, // Keep 5% basket TP
      exchange: 'KUCOIN',
      createdBy: 1,
      enableStopLossPerCoin: true,
      enableTakeProfitPerCoin: true,
      enableBasketTakeProfit: true,
      stopLossType: 'CLASSIC', // Use Classic instead of Trailing (more predictable)
      coinSelectionMode: 'TOP_N', // Default to top N by volume
      coinLimit: 10, // Top 10 coins
      selectedCoins: [], // Empty for top N mode
      marketType: 'FUTURES', // Use Futures (as per user's current setup)
      maxOpenPositions: 3, // Keep 3 max positions
      paperTrading: false, // Default to live trading
      enableVolatilityFilter: false, // Disabled by default
      minMarketVolatility: undefined,
      maxMarketVolatility: undefined,
      volatilityCalculationCoins: 10,
      volatilityCalculationMethod: 'AUTO',
      marginMode: 'CROSS', // Default to CROSS margin mode
      orderType: 'MARKET', // Default to market orders
      limitOffset: 0.1,
      limitTimeoutSeconds: 30
    };
    
    setCreateForm(optimalSettings);
    setShowCreateModal(true);
    fetchAvailableCoins();
  };

  const createMemeCoinStrategy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate coin selection
      if (createForm.coinSelectionMode === 'MANUAL') {
        if (!createForm.selectedCoins || createForm.selectedCoins.length < 2) {
          setError('Please select at least 2 coins for manual selection');
          setLoading(false);
          return;
        }
      }
      
      const kucoinConfigId = resolveKucoinConfigIdForSubmit();

      // Convert maxFundingRate from display percentage (e.g. 0.05%) to decimal (0.0005) for backend
      const submitForm = {
        ...createForm,
        ...(kucoinConfigId != null ? { kucoinConfigId } : {}),
        maxFundingRate: createForm.maxFundingRate != null
          ? parseFloat((createForm.maxFundingRate / 100).toFixed(6))
          : undefined,
      };
      const response = await apiService.post('/api/admin/trading-strategies/meme-coin-pairs', submitForm);
      if (response.success) {
        const newStrategyId = response.data?.id || response.data?.strategyId;
        
        // Immediately set mock data mode for new strategy based on paperTrading setting
        if (newStrategyId) {
          // If paperTrading is false (real money), set mockDataMode to false to show "Real" immediately
          if (!createForm.paperTrading) {
            setMockDataMode(prev => ({ ...prev, [newStrategyId]: false }));
          }
        }
        
        // Clear saved form data
        localStorage.removeItem('memeCoinStrategyForm');
        
        await loadStrategies();
        setAiSuggestionMeta(null);
        setShowCreateModal(false);
        // Reset form
        setCreateForm({
          name: '',
          fixedOrderAmount: 1000,
          leverage: 1,
          trailingStopLoss: 0.8,
          takeProfit: 2.0,
          basketTakeProfit: 2.0,
          exchange: 'KUCOIN',
          createdBy: 1,
          enableStopLossPerCoin: true,
          enableTakeProfitPerCoin: true,
          enableBasketTakeProfit: true,
          enableUniversalBasketTakeProfit: false,
          universalBasketTakeProfit: 2.0,
          universalBasketMinOpenLegs: 2,
          stopLossType: 'TRAILING',
          coinSelectionMode: 'TOP_N',
          coinLimit: 10,
          selectedCoins: [],
          marketType: 'SPOT',
          maxOpenPositions: 3,
          paperTrading: false,
          enableVolatilityFilter: false,
          minMarketVolatility: undefined,
          maxMarketVolatility: undefined,
          volatilityCalculationCoins: 10,
          volatilityCalculationMethod: 'AUTO',
          marginMode: 'CROSS',
          // Entry filters reset
          entryFiltersEnabled: false,
          fundingRateFilterEnabled: true,
          maxFundingRate: 0.05,
          rsiFilterEnabled: true,
          rsiLongMin: 55,
          rsiShortMax: 45,
          emaFilterEnabled: true,
          emaPeriod: 20
        });
        // Don't clear availableCoins - keep the fallback list
        // setAvailableCoins([]);
      } else {
        setError(response.error || 'Failed to create strategy');
      }
    } catch (error: any) {
      console.error('Failed to create strategy:', error);
      setError(error.message || 'Failed to create strategy');
    } finally {
      setLoading(false);
    }
  };

  const createCryptoCoinStrategy = async () => {
    try {
      setLoading(true);
      setError(null);

      if (createForm.coinSelectionMode === 'MANUAL' || createForm.coinSelectionMode === 'ALL_COINS') {
        if (!createForm.selectedCoins || createForm.selectedCoins.length < 2) {
          setError('Please select at least 2 major coins for manual selection');
          setLoading(false);
          return;
        }
      }
      if (createForm.coinSelectionMode === 'STOCK_INDEX' && createForm.marketType !== 'FUTURES') {
        setError('Stock-index perps require FUTURES market type');
        setLoading(false);
        return;
      }

      const kucoinConfigId = resolveKucoinConfigIdForSubmit();

      const submitForm = {
        ...createForm,
        ...(kucoinConfigId != null ? { kucoinConfigId } : {}),
        coinSelectionMode: createForm.coinSelectionMode === 'TOP_N' ? 'TOP_100' : createForm.coinSelectionMode,
        maxFundingRate: createForm.maxFundingRate != null
          ? parseFloat((createForm.maxFundingRate / 100).toFixed(6))
          : undefined,
      };
      const response = await apiService.post('/api/admin/trading-strategies/crypto-coin-pairs', submitForm);
      if (response.success) {
        const newStrategyId = response.data?.id || response.data?.strategyId;
        if (newStrategyId && !createForm.paperTrading) {
          setMockDataMode(prev => ({ ...prev, [newStrategyId]: false }));
        }
        await loadStrategies();
        setAiSuggestionMeta(null);
        setShowCreateModal(false);
        setCreateModalStrategyType('MEME');
        setCreateForm({
          name: '',
          fixedOrderAmount: 1000,
          leverage: 1,
          trailingStopLoss: 0.8,
          takeProfit: 2.0,
          basketTakeProfit: 2.0,
          exchange: 'KUCOIN',
          createdBy: 1,
          enableStopLossPerCoin: true,
          enableTakeProfitPerCoin: true,
          enableBasketTakeProfit: true,
          enableUniversalBasketTakeProfit: false,
          universalBasketTakeProfit: 2.0,
          universalBasketMinOpenLegs: 2,
          stopLossType: 'TRAILING',
          coinSelectionMode: 'TOP_100',
          coinLimit: 100,
          selectedCoins: [],
          marketType: 'FUTURES',
          maxOpenPositions: 3,
          paperTrading: false,
          enableVolatilityFilter: false,
          marginMode: 'CROSS',
          entryFiltersEnabled: false,
          fundingRateFilterEnabled: true,
          maxFundingRate: 0.05,
          rsiFilterEnabled: true,
          rsiLongMin: 55,
          rsiShortMax: 45,
          emaFilterEnabled: true,
          emaPeriod: 20,
        });
      } else {
        setError(response.error || 'Failed to create crypto strategy');
      }
    } catch (error: any) {
      console.error('Failed to create crypto strategy:', error);
      setError(error.message || 'Failed to create crypto strategy');
    } finally {
      setLoading(false);
    }
  };

  const createEnhancedMemeCoinStrategy = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate coin selection
      if (createEnhancedForm.coinSelectionMode === 'MANUAL') {
        if (!createEnhancedForm.selectedCoins || createEnhancedForm.selectedCoins.length < 2) {
          setError('Please select at least 2 coins for manual selection');
          setLoading(false);
          return;
        }
      }
      
      const response = await apiService.post('/api/admin/trading-strategies/meme-coin-pairs-enhanced', createEnhancedForm);
      if (response.success) {
        const newStrategyId = response.data?.id || response.data?.strategyId;
        
        // Immediately set mock data mode for new strategy based on paperTrading setting
        if (newStrategyId) {
          // If paperTrading is false (real money), set mockDataMode to false to show "Real" immediately
          if (!createEnhancedForm.paperTrading) {
            setMockDataMode(prev => ({ ...prev, [newStrategyId]: false }));
          }
        }
        
        // Clear saved form data
        localStorage.removeItem('memeCoinEnhancedStrategyForm');
        
        await loadStrategies();
        setShowCreateEnhancedModal(false);
        // Reset form
        setCreateEnhancedForm({
          name: '',
          fixedOrderAmount: 1000,
          leverage: 1,
          trailingStopLoss: 0.8,
          takeProfit: 2.0,
          basketTakeProfit: 2.0,
          enableUniversalBasketTakeProfit: false,
          universalBasketTakeProfit: 2.0,
          universalBasketMinOpenLegs: 2,
          exchange: 'KUCOIN',
          createdBy: 1,
          enableStopLossPerCoin: true,
          enableTakeProfitPerCoin: true,
          enableBasketTakeProfit: true,
          stopLossType: 'TRAILING',
          coinSelectionMode: 'TOP_N',
          coinLimit: 10,
          selectedCoins: [],
          marketType: 'SPOT',
          maxOpenPositions: 3,
          paperTrading: false,
          enableVolatilityFilter: false,
          minMarketVolatility: undefined,
          maxMarketVolatility: undefined,
          volatilityCalculationCoins: 10,
          volatilityCalculationMethod: 'AUTO',
          marginMode: 'CROSS'
        });
      } else {
        setError(response.error || 'Failed to create enhanced strategy');
      }
    } catch (error: any) {
      console.error('Failed to create enhanced strategy:', error);
      setError(error.message || 'Failed to create enhanced strategy');
    } finally {
      setLoading(false);
    }
  };

  const startStrategy = async (strategyId: number) => {
    try {
      const response = await apiService.post(`/api/admin/trading-strategies/${strategyId}/start`);
      if (response.success) {
        await loadStrategies();
      } else {
        const msg = response.error || response.message || 'Failed to start strategy';
        setError(msg);
        if (typeof msg === 'string' && (msg.includes('provisioning') || msg.includes('Trading account'))) {
          alert(msg);
        }
      }
    } catch (error: any) {
      console.error('Failed to start strategy:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to start strategy';
      setError(msg);
      alert(msg);
    }
  };

  const stopStrategy = async (strategyId: number) => {
    try {
      // When user clicks Stop, immediately show a non-blocking status message
      const strategy = strategies.find(s => s.id === strategyId);
      setStoppingStrategyId(strategyId);
      setStopStatusMessage(
        strategy
          ? `Stopping "${strategy.name}" – closing open positions on KuCoin. This may take up to 60 seconds.`
          : 'Stopping strategy – closing open positions on KuCoin. This may take up to 60 seconds.'
      );

      const response = await apiService.post(`/api/admin/trading-strategies/${strategyId}/stop`);
      if (response.success) {
        await loadStrategies();
        await loadActivePairs();
      } else {
        setError(response.error || 'Failed to stop strategy');
        setStoppingStrategyId(null);
        setStopStatusMessage(null);
      }
    } catch (error: any) {
      console.error('Failed to stop strategy:', error);
      setError(error.message || 'Failed to stop strategy');
      setStoppingStrategyId(null);
      setStopStatusMessage(null);
    }
  };

  const pauseStrategy = async (strategyId: number) => {
    try {
      const response = await apiService.post(`/api/admin/trading-strategies/${strategyId}/pause`);
      if (response.success) {
        await loadStrategies();
      } else {
        setError(response.error || 'Failed to pause strategy');
      }
    } catch (error: any) {
      console.error('Failed to pause strategy:', error);
      setError(error.message || 'Failed to pause strategy');
    }
  };

  // Closes the Edit Strategy Config modal and discards the localStorage draft.
  const closeTPEditModal = () => {
    setShowTPEditModal(false);
    setSelectedStrategyForTPEdit(null);
    setTPEditErrors({});
    localStorage.removeItem('tpEditForm');
    localStorage.removeItem('tpEditStrategyId');
  };

  const handleTPEdit = (strategy: TradingStrategy) => {
    const config = parseStrategyConfig(strategy.config);
    setSelectedStrategyForTPEdit(strategy);
    setTPEditForm({
      // Take Profit
      takeProfit: config.takeProfit?.toString() || '',
      basketTakeProfit: config.basketTakeProfit?.toString() || '',
      longTakeProfit: (config as any).longTakeProfit?.toString() || '',
      shortTakeProfit: (config as any).shortTakeProfit?.toString() || '',
      // Stop Loss
      stopLossType: config.stopLossType || 'TRAILING',
      trailingStopLoss: config.trailingStopLoss?.toString() || '',
      longTrailingStopLoss: config.longTrailingStopLoss?.toString() || '',
      shortTrailingStopLoss: config.shortTrailingStopLoss?.toString() || '',
      // Micro Trailing Stop
      enableMicroTsl: config.enableMicroTsl || false,
      microTslActivationPercent: config.microTslActivationPercent?.toString() || '',
      microTslDistancePercent: config.microTslDistancePercent?.toString() || '',
      // Market & Positions
      maxOpenPositions: config.maxOpenPositions?.toString() || '',
      leverage: config.leverage?.toString() || '',
      // BTC Correlation Filter
      btcCorrelationEnabled: (config as any).btcCorrelationEnabled === true,
      btcEmaLength: config.btcEmaLength?.toString() || '',
      btcEmaTimeframe: config.btcEmaTimeframe || '',
      btcMomentumLongThreshold: config.btcMomentumLongThreshold?.toString() || '',
      btcMomentumShortThreshold: config.btcMomentumShortThreshold?.toString() || '',
      btcRangeNoTradeThreshold: config.btcRangeNoTradeThreshold?.toString() || '',
      // Dump Protection
      btcDumpThreshold: config.btcDumpThreshold?.toString() || '',
      btcCooldownMinutes: config.btcCooldownMinutes?.toString() || '',
      // BTC Higher-Timeframe Trend Confirmation
      btcTrendTimeframe: config.btcTrendTimeframe || '',
      btcTrendEmaPeriod: config.btcTrendEmaPeriod?.toString() || '',
      // Minimum Profit Exit
      minProfitExitPercent: config.minProfitExitPercent?.toString() || '',
      // Order Type
      orderType: (config.orderType as 'MARKET' | 'LIMIT') || 'MARKET',
      limitOffset: config.limitOffset != null ? (config.limitOffset * 100).toFixed(2) : '',
      limitTimeoutSeconds: config.limitTimeoutSeconds?.toString() || '',
      // Margin Mode
      marginMode: (config.marginMode as 'CROSS' | 'ISOLATED') || 'CROSS',
      // Order Amount
      fixedOrderAmount: config.fixedOrderAmount?.toString() || '',
      // Meme Participation Filter
      memeParticipationFilterEnabled: (config as any).memeParticipationFilterEnabled === true,
      minParticipationRate: config.minParticipationRate != null
        ? (config.minParticipationRate * 100).toFixed(0)   // stored as 0–1, display as %
        : '',
      // Market Activity Threshold
      minMarketActivityThreshold: config.minMarketActivityThreshold != null
        ? String(config.minMarketActivityThreshold)
        : '',
      // Volatility Filter
      enableVolatilityFilter: (config as any).enableVolatilityFilter === true,
      minMarketVolatility: config.minMarketVolatility != null ? String(config.minMarketVolatility) : '',
      maxMarketVolatility: config.maxMarketVolatility != null ? String(config.maxMarketVolatility) : '',
      volatilityCalculationCoins: config.volatilityCalculationCoins != null
        ? String(config.volatilityCalculationCoins)
        : '10',
      volatilityCalculationMethod: (config.volatilityCalculationMethod as 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO') || 'AUTO',
      microReversalFilterEnabled: (config as any).microReversalFilterEnabled === true,
      microReversalMinDropPercent: (config as any).microReversalMinDropPercent?.toString() || '',
      microReversalMaxDropPercent: (config as any).microReversalMaxDropPercent?.toString() || '',
      microReversalLookbackCandles: (config as any).microReversalLookbackCandles?.toString() || '',
      lowParticipationMode: ((config as any).lowParticipationMode as 'REDUCED_EXPOSURE' | 'ENTRY_BLOCK') || 'REDUCED_EXPOSURE',
      lowParticipationAutoBlockDays: (config as any).lowParticipationAutoBlockDays?.toString() || '0',
      aiRegimeLongBlockEnabled: (config as any).aiRegimeLongBlockEnabled === true,
      aiRegimeLongBlockList: (config as any).aiRegimeLongBlockList || 'BEARISH_BTC',
      scalperModeEnabled: (config as any).scalperModeEnabled === true,
      scalperTakeProfit: (config as any).scalperTakeProfit?.toString() || '2.0',
      scalperTrailingStopLoss: (config as any).scalperTrailingStopLoss?.toString() || '1.0',
      scalperMaxOpenPositions: (config as any).scalperMaxOpenPositions?.toString() || '2',
      scalperRegimeList: (config as any).scalperRegimeList || 'HIGH_VOLATILITY',
      oracleEnabled: (config as any).oracleEnabled === true,
      oracleConfidenceThreshold: (config as any).oracleConfidenceThreshold ?? 0.5,
      oracleWeightMomentum: (config as any).oracleWeightMomentum ?? null,
      oracleWeightRsi: (config as any).oracleWeightRsi ?? null,
      oracleWeightVolume: (config as any).oracleWeightVolume ?? null,
      oracleWeightFunding: (config as any).oracleWeightFunding ?? null,
      oracleWeightEma: (config as any).oracleWeightEma ?? null,
      enableUniversalBasketTakeProfit: (config as any).enableUniversalBasketTakeProfit === true,
      universalBasketTakeProfit: (config as any).universalBasketTakeProfit != null
        ? String((config as any).universalBasketTakeProfit)
        : '',
      universalBasketMinOpenLegs: (config as any).universalBasketMinOpenLegs != null
        ? String((config as any).universalBasketMinOpenLegs)
        : '',
    });
    setTpEditVolatilityPresetMode('custom');
    setTPEditErrors({});
    setShowTPEditModal(true);
  };

  const validateTPForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!tpEditForm.takeProfit || tpEditForm.takeProfit.trim() === '') {
      errors.takeProfit = 'Take Profit is required';
    } else {
      const tp = parseFloat(tpEditForm.takeProfit);
      if (isNaN(tp) || tp <= 0 || tp >= 100) {
        errors.takeProfit = 'Take Profit must be between 0.01% and 99.99%';
      }
    }
    
    if (tpEditForm.basketTakeProfit && tpEditForm.basketTakeProfit.trim() !== '') {
      const basketTP = parseFloat(tpEditForm.basketTakeProfit);
      if (isNaN(basketTP) || basketTP <= 0 || basketTP >= 100) {
        errors.basketTakeProfit = 'Basket Take Profit must be between 0.01% and 99.99%';
      }
    }

    if (tpEditForm.enableUniversalBasketTakeProfit) {
      if (!tpEditForm.universalBasketTakeProfit || tpEditForm.universalBasketTakeProfit.trim() === '') {
        errors.universalBasketTakeProfit = 'Universal basket target (%) is required when enabled';
      } else {
        const u = parseFloat(tpEditForm.universalBasketTakeProfit);
        if (isNaN(u) || u <= 0 || u >= 100) {
          errors.universalBasketTakeProfit = 'Must be between 0.01% and 99.99%';
        }
      }
      if (tpEditForm.universalBasketMinOpenLegs && tpEditForm.universalBasketMinOpenLegs.trim() !== '') {
        const m = parseInt(tpEditForm.universalBasketMinOpenLegs, 10);
        if (isNaN(m) || m < 1) {
          errors.universalBasketMinOpenLegs = 'Minimum open legs must be at least 1';
        }
      }
    }
    
    if (tpEditForm.longTakeProfit && tpEditForm.longTakeProfit.trim() !== '') {
      const longTP = parseFloat(tpEditForm.longTakeProfit);
      if (isNaN(longTP) || longTP <= 0 || longTP >= 100) {
        errors.longTakeProfit = 'Long Take Profit must be between 0.01% and 99.99%';
      }
    }
    
    if (tpEditForm.shortTakeProfit && tpEditForm.shortTakeProfit.trim() !== '') {
      const shortTP = parseFloat(tpEditForm.shortTakeProfit);
      if (isNaN(shortTP) || shortTP <= 0 || shortTP >= 100) {
        errors.shortTakeProfit = 'Short Take Profit must be between 0.01% and 99.99%';
      }
    }

    // Stop Loss validation
    if (tpEditForm.trailingStopLoss && tpEditForm.trailingStopLoss.trim() !== '') {
      const sl = parseFloat(tpEditForm.trailingStopLoss);
      if (isNaN(sl) || sl <= 0 || sl >= 100) {
        errors.trailingStopLoss = 'Stop Loss must be between 0.01% and 99.99%';
      }
    }
    if (tpEditForm.longTrailingStopLoss && tpEditForm.longTrailingStopLoss.trim() !== '') {
      const sl = parseFloat(tpEditForm.longTrailingStopLoss);
      if (isNaN(sl) || sl <= 0 || sl >= 100) {
        errors.longTrailingStopLoss = 'Long SL must be between 0.01% and 99.99%';
      }
    }
    if (tpEditForm.shortTrailingStopLoss && tpEditForm.shortTrailingStopLoss.trim() !== '') {
      const sl = parseFloat(tpEditForm.shortTrailingStopLoss);
      if (isNaN(sl) || sl <= 0 || sl >= 100) {
        errors.shortTrailingStopLoss = 'Short SL must be between 0.01% and 99.99%';
      }
    }

    // Micro TSL validation
    if (tpEditForm.enableMicroTsl) {
      if (tpEditForm.microTslActivationPercent && tpEditForm.microTslActivationPercent.trim() !== '') {
        const act = parseFloat(tpEditForm.microTslActivationPercent);
        if (isNaN(act) || act <= 0 || act >= 100) {
          errors.microTslActivationPercent = 'Activation must be between 0.01% and 99.99%';
        }
      }
      if (tpEditForm.microTslDistancePercent && tpEditForm.microTslDistancePercent.trim() !== '') {
        const dist = parseFloat(tpEditForm.microTslDistancePercent);
        if (isNaN(dist) || dist <= 0 || dist >= 100) {
          errors.microTslDistancePercent = 'Distance must be between 0.01% and 99.99%';
        }
      }
    }

    // Max Positions validation
    if (tpEditForm.maxOpenPositions && tpEditForm.maxOpenPositions.trim() !== '') {
      const mp = parseInt(tpEditForm.maxOpenPositions);
      if (isNaN(mp) || mp < 1 || mp > 50) {
        errors.maxOpenPositions = 'Max Positions must be between 1 and 50';
      }
    }

    // Leverage validation
    if (tpEditForm.leverage && tpEditForm.leverage.trim() !== '') {
      const lev = parseInt(tpEditForm.leverage);
      if (isNaN(lev) || lev < 1 || lev > 125) {
        errors.leverage = 'Leverage must be between 1x and 125x';
      }
    }

    // BTC Correlation Filter validation
    if (tpEditForm.btcMomentumLongThreshold && tpEditForm.btcMomentumLongThreshold.trim() !== '') {
      const val = parseFloat(tpEditForm.btcMomentumLongThreshold);
      if (isNaN(val) || val <= 0) errors.btcMomentumLongThreshold = 'Must be a positive value';
    }
    if (tpEditForm.btcMomentumShortThreshold && tpEditForm.btcMomentumShortThreshold.trim() !== '') {
      const val = parseFloat(tpEditForm.btcMomentumShortThreshold);
      if (isNaN(val) || val <= 0) errors.btcMomentumShortThreshold = 'Must be a positive value';
    }
    if (tpEditForm.btcRangeNoTradeThreshold && tpEditForm.btcRangeNoTradeThreshold.trim() !== '') {
      const val = parseFloat(tpEditForm.btcRangeNoTradeThreshold);
      if (isNaN(val) || val <= 0) errors.btcRangeNoTradeThreshold = 'Must be a positive value';
    }

    // Dump Protection Cooldown validation
    if (tpEditForm.btcCooldownMinutes && tpEditForm.btcCooldownMinutes.trim() !== '') {
      const cd = parseInt(tpEditForm.btcCooldownMinutes);
      if (isNaN(cd) || cd < 1 || cd > 1440) {
        errors.btcCooldownMinutes = 'Cooldown must be between 1 and 1440 minutes';
      }
    }

    // Market Activity Threshold validation (0 = disabled)
    if (tpEditForm.minMarketActivityThreshold && tpEditForm.minMarketActivityThreshold.trim() !== '') {
      const activity = parseFloat(tpEditForm.minMarketActivityThreshold);
      if (isNaN(activity) || activity < 0 || activity > 100) {
        errors.minMarketActivityThreshold = 'Must be between 0% and 100% (use 0 to disable)';
      }
    }

    // Volatility Filter validation
    if (tpEditForm.enableVolatilityFilter) {
      if (!tpEditForm.maxMarketVolatility || tpEditForm.maxMarketVolatility.trim() === '') {
        errors.maxMarketVolatility = 'Max market volatility is required when filter is enabled';
      } else {
        const maxVol = parseFloat(tpEditForm.maxMarketVolatility);
        if (isNaN(maxVol) || maxVol <= 0 || maxVol > 100) {
          errors.maxMarketVolatility = 'Max volatility must be between 0.01% and 100%';
        }
      }
      if (tpEditForm.minMarketVolatility && tpEditForm.minMarketVolatility.trim() !== '') {
        const minVol = parseFloat(tpEditForm.minMarketVolatility);
        const maxVol = parseFloat(tpEditForm.maxMarketVolatility);
        if (isNaN(minVol) || minVol < 0 || minVol > 100) {
          errors.minMarketVolatility = 'Min volatility must be between 0% and 100%';
        } else if (!isNaN(maxVol) && minVol >= maxVol) {
          errors.minMarketVolatility = 'Min volatility must be less than max volatility';
        }
      }
    }
    
    setTPEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTPSave = async () => {
    if (!validateTPForm()) {
      return;
    }
    
    if (!selectedStrategyForTPEdit) {
      return;
    }
    
    const isActive = selectedStrategyForTPEdit.status === 'ACTIVE';
    const openPositionsCount = activePairs.filter(p => 
      p.strategyId === selectedStrategyForTPEdit.id && p.status === 'ACTIVE'
    ).length;
    
    // Show warning for ACTIVE strategies
    if (isActive) {
      const confirmed = window.confirm(
        `⚠️ WARNING: Updating Take Profit on ACTIVE Strategy\n\n` +
        `Strategy: ${selectedStrategyForTPEdit.name}\n` +
        `Current TP: ${parseStrategyConfig(selectedStrategyForTPEdit.config).takeProfit}%\n` +
        `New TP: ${tpEditForm.takeProfit}%\n` +
        `Open Positions: ${openPositionsCount}\n\n` +
        `⚠️ CRITICAL: Exchange-level TP orders exist and will NOT be updated automatically.\n` +
        `You must manually cancel TP orders on Kucoin UI or close/reopen positions.\n\n` +
        `What will happen:\n` +
        `✅ New positions will use ${tpEditForm.takeProfit}% TP\n` +
        `✅ Monitoring loop will use ${tpEditForm.takeProfit}% TP on next cycle\n` +
        `❌ Exchange orders will still use old TP value\n\n` +
        `Do you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }
    
    try {
      setLoading(true);
      const updateData: any = {
        takeProfit: parseFloat(tpEditForm.takeProfit)
      };
      
      if (tpEditForm.basketTakeProfit && tpEditForm.basketTakeProfit.trim() !== '') {
        updateData.basketTakeProfit = parseFloat(tpEditForm.basketTakeProfit);
      }
      updateData.enableUniversalBasketTakeProfit = tpEditForm.enableUniversalBasketTakeProfit;
      if (tpEditForm.universalBasketTakeProfit && tpEditForm.universalBasketTakeProfit.trim() !== '') {
        updateData.universalBasketTakeProfit = parseFloat(tpEditForm.universalBasketTakeProfit);
      }
      if (tpEditForm.universalBasketMinOpenLegs && tpEditForm.universalBasketMinOpenLegs.trim() !== '') {
        updateData.universalBasketMinOpenLegs = parseInt(tpEditForm.universalBasketMinOpenLegs, 10);
      }
      if (tpEditForm.longTakeProfit && tpEditForm.longTakeProfit.trim() !== '') {
        updateData.longTakeProfit = parseFloat(tpEditForm.longTakeProfit);
      }
      if (tpEditForm.shortTakeProfit && tpEditForm.shortTakeProfit.trim() !== '') {
        updateData.shortTakeProfit = parseFloat(tpEditForm.shortTakeProfit);
      }
      // Stop Loss
      updateData.stopLossType = tpEditForm.stopLossType;
      if (tpEditForm.trailingStopLoss && tpEditForm.trailingStopLoss.trim() !== '') {
        updateData.trailingStopLoss = parseFloat(tpEditForm.trailingStopLoss);
      }
      // Micro Trailing Stop
      updateData.enableMicroTsl = tpEditForm.enableMicroTsl;
      if (tpEditForm.microTslActivationPercent && tpEditForm.microTslActivationPercent.trim() !== '') {
        updateData.microTslActivationPercent = parseFloat(tpEditForm.microTslActivationPercent);
      }
      if (tpEditForm.microTslDistancePercent && tpEditForm.microTslDistancePercent.trim() !== '') {
        updateData.microTslDistancePercent = parseFloat(tpEditForm.microTslDistancePercent);
      }
      // Market & Positions
      if (tpEditForm.maxOpenPositions && tpEditForm.maxOpenPositions.trim() !== '') {
        updateData.maxOpenPositions = parseInt(tpEditForm.maxOpenPositions);
      }
      if (tpEditForm.leverage && tpEditForm.leverage.trim() !== '') {
        updateData.leverage = parseInt(tpEditForm.leverage);
      }
      // Per-direction Stop Loss overrides
      if (tpEditForm.longTrailingStopLoss && tpEditForm.longTrailingStopLoss.trim() !== '') {
        updateData.longTrailingStopLoss = parseFloat(tpEditForm.longTrailingStopLoss);
      }
      if (tpEditForm.shortTrailingStopLoss && tpEditForm.shortTrailingStopLoss.trim() !== '') {
        updateData.shortTrailingStopLoss = parseFloat(tpEditForm.shortTrailingStopLoss);
      }
      // BTC Correlation Filter — always send the enabled flag
      updateData.btcCorrelationEnabled = tpEditForm.btcCorrelationEnabled;
      if (tpEditForm.btcMomentumLongThreshold && tpEditForm.btcMomentumLongThreshold.trim() !== '') {
        updateData.btcMomentumLongThreshold = parseFloat(tpEditForm.btcMomentumLongThreshold);
      }
      if (tpEditForm.btcMomentumShortThreshold && tpEditForm.btcMomentumShortThreshold.trim() !== '') {
        updateData.btcMomentumShortThreshold = parseFloat(tpEditForm.btcMomentumShortThreshold);
      }
      if (tpEditForm.btcRangeNoTradeThreshold && tpEditForm.btcRangeNoTradeThreshold.trim() !== '') {
        updateData.btcRangeNoTradeThreshold = parseFloat(tpEditForm.btcRangeNoTradeThreshold);
      }
      // Dump Protection Threshold & Cooldown
      if (tpEditForm.btcDumpThreshold && tpEditForm.btcDumpThreshold.trim() !== '') {
        updateData.btcDumpThreshold = parseFloat(tpEditForm.btcDumpThreshold);
      }
      if (tpEditForm.btcCooldownMinutes && tpEditForm.btcCooldownMinutes.trim() !== '') {
        updateData.btcCooldownMinutes = parseInt(tpEditForm.btcCooldownMinutes);
      }

      // BTC EMA Length & Timeframe
      if (tpEditForm.btcEmaLength && tpEditForm.btcEmaLength.trim() !== '') {
        updateData.btcEmaLength = parseInt(tpEditForm.btcEmaLength);
      }
      if (tpEditForm.btcEmaTimeframe && tpEditForm.btcEmaTimeframe.trim() !== '') {
        updateData.btcEmaTimeframe = tpEditForm.btcEmaTimeframe;
      }

      // BTC Higher-Timeframe Trend Confirmation
      // Send even when empty — empty string means "disabled" on the backend
      if (tpEditForm.btcTrendTimeframe !== undefined) {
        updateData.btcTrendTimeframe = tpEditForm.btcTrendTimeframe;
      }
      if (tpEditForm.btcTrendEmaPeriod && tpEditForm.btcTrendEmaPeriod.trim() !== '') {
        updateData.btcTrendEmaPeriod = parseInt(tpEditForm.btcTrendEmaPeriod);
      }

      // Minimum Profit Exit — send 0 to disable, positive value to enable
      if (tpEditForm.minProfitExitPercent !== undefined && tpEditForm.minProfitExitPercent !== '') {
        updateData.minProfitExitPercent = parseFloat(tpEditForm.minProfitExitPercent);
      }

      // Order Type
      updateData.orderType = tpEditForm.orderType;
      if (tpEditForm.orderType === 'LIMIT') {
        if (tpEditForm.limitOffset && tpEditForm.limitOffset.trim() !== '') {
          // User enters % (e.g. 0.1), backend expects decimal (0.001)
          updateData.limitOffset = parseFloat(tpEditForm.limitOffset) / 100;
        }
        if (tpEditForm.limitTimeoutSeconds && tpEditForm.limitTimeoutSeconds.trim() !== '') {
          updateData.limitTimeoutSeconds = parseInt(tpEditForm.limitTimeoutSeconds);
        }
      }

      // Margin Mode
      updateData.marginMode = tpEditForm.marginMode;

      // Order Amount
      if (tpEditForm.fixedOrderAmount && tpEditForm.fixedOrderAmount.trim() !== '') {
        const orderAmt = parseFloat(tpEditForm.fixedOrderAmount);
        if (!isNaN(orderAmt) && orderAmt > 0) {
          updateData.fixedOrderAmount = orderAmt;
        }
      }

      // Meme Participation Filter — always send the enabled flag
      updateData.memeParticipationFilterEnabled = tpEditForm.memeParticipationFilterEnabled;
      if (tpEditForm.minParticipationRate && tpEditForm.minParticipationRate.trim() !== '') {
        const pct = parseFloat(tpEditForm.minParticipationRate);
        if (!isNaN(pct) && pct >= 0 && pct <= 100) {
          updateData.minParticipationRate = pct / 100; // convert % → 0–1 for backend
        }
      }

      // Market Activity Threshold — 0 disables the "market is alive" gate
      if (tpEditForm.minMarketActivityThreshold.trim() === '') {
        updateData.minMarketActivityThreshold = 0;
      } else {
        updateData.minMarketActivityThreshold = parseFloat(tpEditForm.minMarketActivityThreshold);
      }

      // Volatility Filter — always send the enabled flag
      updateData.enableVolatilityFilter = tpEditForm.enableVolatilityFilter;
      if (tpEditForm.enableVolatilityFilter) {
        if (tpEditForm.minMarketVolatility && tpEditForm.minMarketVolatility.trim() !== '') {
          updateData.minMarketVolatility = parseFloat(tpEditForm.minMarketVolatility);
        }
        if (tpEditForm.maxMarketVolatility && tpEditForm.maxMarketVolatility.trim() !== '') {
          updateData.maxMarketVolatility = parseFloat(tpEditForm.maxMarketVolatility);
        }
        if (tpEditForm.volatilityCalculationCoins && tpEditForm.volatilityCalculationCoins.trim() !== '') {
          updateData.volatilityCalculationCoins = parseInt(tpEditForm.volatilityCalculationCoins, 10);
        }
        updateData.volatilityCalculationMethod = tpEditForm.volatilityCalculationMethod || 'AUTO';
      }

      updateData.microReversalFilterEnabled = tpEditForm.microReversalFilterEnabled;
      if (tpEditForm.microReversalMinDropPercent && tpEditForm.microReversalMinDropPercent.trim() !== '') {
        updateData.microReversalMinDropPercent = parseFloat(tpEditForm.microReversalMinDropPercent);
      }
      if (tpEditForm.microReversalMaxDropPercent && tpEditForm.microReversalMaxDropPercent.trim() !== '') {
        updateData.microReversalMaxDropPercent = parseFloat(tpEditForm.microReversalMaxDropPercent);
      }
      if (tpEditForm.microReversalLookbackCandles && tpEditForm.microReversalLookbackCandles.trim() !== '') {
        updateData.microReversalLookbackCandles = parseInt(tpEditForm.microReversalLookbackCandles, 10);
      }

      // US-REB-01: LOW_PARTICIPATION behaviour mode
      updateData.lowParticipationMode = (tpEditForm as any).lowParticipationMode || 'REDUCED_EXPOSURE';
      // US-REB-03: auto-block days
      updateData.lowParticipationAutoBlockDays = parseInt((tpEditForm as any).lowParticipationAutoBlockDays || '0', 10) || 0;
      // US-RLB-01: long block
      updateData.aiRegimeLongBlockEnabled = (tpEditForm as any).aiRegimeLongBlockEnabled === true;
      updateData.aiRegimeLongBlockList = (tpEditForm as any).aiRegimeLongBlockList || 'BEARISH_BTC';
      // US-SCL-01: scalper mode
      updateData.scalperModeEnabled = (tpEditForm as any).scalperModeEnabled === true;
      updateData.scalperTakeProfit = parseFloat((tpEditForm as any).scalperTakeProfit || '2.0');
      updateData.scalperTrailingStopLoss = parseFloat((tpEditForm as any).scalperTrailingStopLoss || '1.0');
      updateData.scalperMaxOpenPositions = parseInt((tpEditForm as any).scalperMaxOpenPositions || '2', 10) || 2;
      updateData.scalperRegimeList = (tpEditForm as any).scalperRegimeList || 'HIGH_VOLATILITY';

      // Coin Selection Oracle
      updateData.oracleEnabled = tpEditForm.oracleEnabled;
      if (tpEditForm.oracleConfidenceThreshold != null) {
        updateData.oracleConfidenceThreshold = tpEditForm.oracleConfidenceThreshold;
      }
      if ((tpEditForm as any).oracleWeightMomentum != null) {
        updateData.oracleWeightMomentum = (tpEditForm as any).oracleWeightMomentum;
      }
      if ((tpEditForm as any).oracleWeightRsi != null) {
        updateData.oracleWeightRsi = (tpEditForm as any).oracleWeightRsi;
      }
      if ((tpEditForm as any).oracleWeightVolume != null) {
        updateData.oracleWeightVolume = (tpEditForm as any).oracleWeightVolume;
      }
      if ((tpEditForm as any).oracleWeightFunding != null) {
        updateData.oracleWeightFunding = (tpEditForm as any).oracleWeightFunding;
      }
      if ((tpEditForm as any).oracleWeightEma != null) {
        updateData.oracleWeightEma = (tpEditForm as any).oracleWeightEma;
      }

      const response = await adminApiService.updateStrategyTakeProfit(
        selectedStrategyForTPEdit.id,
        updateData
      );
      
      if (response.success) {
        // 1. Immediately update strategy in state from the save response (no wait)
        const updatedStrategy: TradingStrategy | null = response.data ?? null;
        if (updatedStrategy && updatedStrategy.id) {
          setStrategies(prev => prev.map(s => s.id === updatedStrategy.id ? updatedStrategy : s));
        }

        // 2. Close modal and clear form BEFORE the reload so the user sees a clean UI
        setShowTPEditModal(false);
        setSelectedStrategyForTPEdit(null);
        setTPEditErrors({});
        localStorage.removeItem('tpEditForm');
        localStorage.removeItem('tpEditStrategyId');
        setTPEditForm({ ...DEFAULT_TP_EDIT_FORM });

        // 3. Await a full strategies reload so the table is definitely fresh
        await loadStrategies();
        setError(null);

        // 4. Show inline success banner (no blocking alert)
        setTpSaveSuccess('✅ Strategy config saved successfully. Table has been refreshed with the latest values.');
        setTimeout(() => setTpSaveSuccess(null), 6000);
      } else {
        setError(response.error || 'Failed to update Strategy Config');
      }
    } catch (err: any) {
      console.error('Error updating Take Profit:', err);
      setError(err.message || 'Failed to update Take Profit');
    } finally {
      setLoading(false);
    }
  };

  const hasAiAutoTunePending = (config: StrategyConfig) =>
    !!(config.aiAutoTunePendingJson && String(config.aiAutoTunePendingJson).trim().length > 0);

  const isAiAutoTuneApprovalMode = (config: StrategyConfig) =>
    config.aiAutoTuneEnabled === true && config.aiAutoTuneRequireApproval !== false;

  const getAiAutoTuneModeBadge = (config: StrategyConfig): string | null => {
    if (config.aiAutoTuneEnabled !== true) return null;
    return isAiAutoTuneApprovalMode(config) ? '🛡️ AI APPROVAL' : '⚡ AI AUTO';
  };

  const formatAiAutoTuneInterval = (ms?: number) => {
    if (!ms || ms < 60000) return `${Math.round((ms || 0) / 1000)}s`;
    return `${Math.round(ms / 60000)} min`;
  };

  const loadAiAutoTuneGlobalSettings = async () => {
    try {
      const res = await adminApiService.getAiAutoTuneGlobalSettings();
      if (res.success && res.data) {
        setAiAutoTuneGlobal(res.data);
      }
    } catch {
      // non-fatal
    }
  };

  const formatAiAutoTuneLastApplied = (ts?: number) => {
    if (!ts) return 'Never';
    try {
      return new Date(ts).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  const getAiAutoTuneButtonLabel = (config: StrategyConfig) => {
    if (hasAiAutoTunePending(config)) return '⏳ AI Pending';
    if (config.aiAutoTuneEnabled === true) return '✅ AI On';
    return '🤖 AI Tune';
  };

  const getAiAutoTuneButtonStyle = (config: StrategyConfig): React.CSSProperties => {
    const base: React.CSSProperties = { border: 'none', fontSize: 13, padding: '8px 12px' };
    if (hasAiAutoTunePending(config)) {
      return {
        ...base,
        background: '#f59e0b',
        border: '2px solid #d97706',
        color: '#fff',
        fontWeight: 700,
        boxShadow: '0 0 0 2px #fef3c7',
      };
    }
    if (config.aiAutoTuneEnabled === true) {
      return {
        ...base,
        background: '#16a34a',
        border: '2px solid #15803d',
        color: '#fff',
        fontWeight: 700,
        boxShadow: '0 0 0 2px #bbf7d0',
      };
    }
    return { ...base, background: '#6d28d9', color: 'white', fontWeight: 600 };
  };

  // --- AI auto-tune (Phase 2) ---
  const loadAiAutoTuneHistory = async (strategyId: number, pageIndex: number = 0) => {
    setAiAutoTuneHistoryLoading(true);
    try {
      const res = await adminApiService.getAiAutoTuneHistory(strategyId, pageIndex, 10);
      if (res.success && res.data) {
        setAiAutoTuneHistory(Array.isArray(res.data.content) ? res.data.content : []);
        setAiAutoTuneHistoryPage(Number(res.data.page) || 0);
        setAiAutoTuneHistoryTotalPages(Number(res.data.totalPages) || 0);
      }
    } catch {
      // non-fatal
    } finally {
      setAiAutoTuneHistoryLoading(false);
    }
  };

  const handleAiAutoTuneEdit = async (strategy: TradingStrategy) => {
    const config = parseStrategyConfig(strategy.config);
    setSelectedStrategyForAiAutoTune(strategy);
    setAiAutoTuneForm({
      aiAutoTuneEnabled: config.aiAutoTuneEnabled === true,
      aiAutoTuneRequireApproval: config.aiAutoTuneRequireApproval !== false,
      aiPerformanceTuneEnabled: config.aiPerformanceTuneEnabled === true,
      aiPerformanceTuneWindow: config.aiPerformanceTuneWindow ?? 12,
      aiPerformanceTuneMinTrades: config.aiPerformanceTuneMinTrades ?? 8,
      aiPerformanceCoinTuneEnabled: config.aiPerformanceCoinTuneEnabled === true,
      aiPerformanceSideBlockEnabled: config.aiPerformanceSideBlockEnabled === true,
      aiPerformanceTuneLlmEnabled: config.aiPerformanceTuneLlmEnabled === true,
      aiPerformanceTuneHistoryLearningEnabled: config.aiPerformanceTuneHistoryLearningEnabled !== false,
    });
    setAiRegimeGatesForm({
      lowParticipationMode: ((config as any).lowParticipationMode as 'REDUCED_EXPOSURE' | 'ENTRY_BLOCK') || 'REDUCED_EXPOSURE',
      lowParticipationAutoBlockDays: (config as any).lowParticipationAutoBlockDays ?? 0,
      aiRegimeLongBlockEnabled: (config as any).aiRegimeLongBlockEnabled === true,
      aiRegimeLongBlockList: (config as any).aiRegimeLongBlockList || 'BEARISH_BTC',
      scalperModeEnabled: (config as any).scalperModeEnabled === true,
      scalperTakeProfit: (config as any).scalperTakeProfit?.toString() || '2.0',
      scalperTrailingStopLoss: (config as any).scalperTrailingStopLoss?.toString() || '1.0',
      scalperMaxOpenPositions: (config as any).scalperMaxOpenPositions?.toString() || '2',
      scalperRegimeList: (config as any).scalperRegimeList || 'HIGH_VOLATILITY',
    });
    setAiAutoTunePending(null);
    setAiAutoTuneModalTab('settings');
    setExpandedAiTuneHistoryId(null);
    setShowAiAutoTuneModal(true);
    loadAiAutoTuneGlobalSettings();
    loadAiAutoTuneHistory(strategy.id, 0);
    try {
      const pendingRes = await adminApiService.getAiAutoTunePending(strategy.id);
      if (pendingRes.success) {
        setAiAutoTunePending(pendingRes.data ?? null);
      }
    } catch {
      // non-fatal
    }
  };

  const handleAiAutoTuneSave = async () => {
    if (!selectedStrategyForAiAutoTune) return;
    try {
      setLoading(true);
      // Save AI tune settings
      const response = await adminApiService.updateAiAutoTuneSettings(
        selectedStrategyForAiAutoTune.id,
        aiAutoTuneForm
      );
      if (!response.success) {
        setError(response.error || 'Failed to update AI auto-tune settings');
        return;
      }
      // Save regime gates into strategy config via the same config patch endpoint
      await adminApiService.updateStrategyTakeProfit(selectedStrategyForAiAutoTune.id, {
        lowParticipationMode: aiRegimeGatesForm.lowParticipationMode,
        lowParticipationAutoBlockDays: Number(aiRegimeGatesForm.lowParticipationAutoBlockDays) || 0,
        aiRegimeLongBlockEnabled: aiRegimeGatesForm.aiRegimeLongBlockEnabled,
        aiRegimeLongBlockList: aiRegimeGatesForm.aiRegimeLongBlockList || 'BEARISH_BTC',
        scalperModeEnabled: aiRegimeGatesForm.scalperModeEnabled,
        scalperTakeProfit: parseFloat(aiRegimeGatesForm.scalperTakeProfit) || 2.0,
        scalperTrailingStopLoss: parseFloat(aiRegimeGatesForm.scalperTrailingStopLoss) || 1.0,
        scalperMaxOpenPositions: parseInt(aiRegimeGatesForm.scalperMaxOpenPositions, 10) || 2,
        scalperRegimeList: aiRegimeGatesForm.scalperRegimeList || 'HIGH_VOLATILITY',
      } as any);
      await loadStrategies();
      if (selectedStrategyForAiAutoTune && response.data?.config) {
        setSelectedStrategyForAiAutoTune({ ...selectedStrategyForAiAutoTune, config: response.data.config });
      }
      setError(null);
      alert(
        aiAutoTuneForm.aiAutoTuneEnabled
          ? `AI auto-tune enabled (${aiAutoTuneForm.aiAutoTuneRequireApproval ? 'Manual approve — AI Pending + Apply' : 'Auto-apply — changes apply on Run Now/scheduler'}). Save done — button turns green (✅ AI On).`
          : 'AI auto-tune disabled for this strategy.'
      );
      await loadAiAutoTuneHistory(selectedStrategyForAiAutoTune.id, 0);
    } catch (err: any) {
      setError(err.message || 'Failed to update AI auto-tune settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAiAutoTuneRunNow = async () => {
    if (!selectedStrategyForAiAutoTune) return;
    if (!aiAutoTuneForm.aiAutoTuneEnabled) {
      alert('Enable AI auto-tune first, then save, before running.');
      return;
    }
    try {
      setAiAutoTuneRunning(true);
      const response = await adminApiService.runAiAutoTuneNow(selectedStrategyForAiAutoTune.id);
      if (response.success) {
        const outcome = response.data?.outcome ?? 'unknown';
        const detailMessage = response.data?.message;
        const skipReason = response.data?.skipReason;
        setAiAutoTunePending(response.data?.pending ?? null);
        const listRes = await apiService.get('/api/admin/trading-strategies');
        if (listRes.success && Array.isArray(listRes.data)) {
          setStrategies(listRes.data);
          const updated = listRes.data.find((s: TradingStrategy) => s.id === selectedStrategyForAiAutoTune.id);
          if (updated) {
            setSelectedStrategyForAiAutoTune(updated);
          }
        } else {
          await loadStrategies();
        }
        const regimeLabel = response.data?.regimeLabel;
        const changeCount = response.data?.changeCount ?? 0;
        let alertText = detailMessage
          || (outcome === 'skipped' && skipReason === 'ALREADY_OPTIMAL'
            ? `No changes needed${regimeLabel ? ` — strategy already matches ${regimeLabel}` : ''}.`
            : `AI auto-tune finished: ${outcome}`);
        if (outcome === 'skipped' && response.data?.fieldEvaluation?.length) {
          const diffs = response.data.fieldEvaluation.filter(r => r.wouldChange);
          if (diffs.length === 0) {
            alertText += '\n\nAll tunable fields already match the current market recommendation.';
          }
        } else if (changeCount > 0) {
          alertText += `\n\n${changeCount} field(s) ${outcome === 'pending' ? 'queued for approval' : 'updated'}.`;
        }
        alert(alertText);
        await loadAiAutoTuneHistory(selectedStrategyForAiAutoTune.id, 0);
      } else {
        setError(response.error || 'AI auto-tune run failed');
      }
    } catch (err: any) {
      setError(err.message || 'AI auto-tune run failed');
    } finally {
      setAiAutoTuneRunning(false);
    }
  };

  const handleAiAutoTuneApplyPending = async () => {
    if (!selectedStrategyForAiAutoTune) return;
    try {
      setLoading(true);
      const response = await adminApiService.applyAiAutoTunePending(selectedStrategyForAiAutoTune.id);
      if (response.success) {
        setAiAutoTunePending(null);
        await loadStrategies();
        await loadAiAutoTuneHistory(selectedStrategyForAiAutoTune.id, 0);
        alert('Pending AI changes applied to this strategy.');
      } else {
        setError(response.error || 'Failed to apply pending changes');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to apply pending changes');
    } finally {
      setLoading(false);
    }
  };

  const handleAiAutoTuneDismissPending = async () => {
    if (!selectedStrategyForAiAutoTune) return;
    try {
      setLoading(true);
      const response = await adminApiService.dismissAiAutoTunePending(selectedStrategyForAiAutoTune.id);
      if (response.success) {
        setAiAutoTunePending(null);
        await loadStrategies();
        await loadAiAutoTuneHistory(selectedStrategyForAiAutoTune.id, 0);
      } else {
        setError(response.error || 'Failed to dismiss pending suggestion');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to dismiss pending suggestion');
    } finally {
      setLoading(false);
    }
  };

  // --- Entry Filters edit handlers ---
  const handleFilterEdit = (strategy: TradingStrategy) => {
    const config = parseStrategyConfig(strategy.config);
    setSelectedStrategyForFilterEdit(strategy);
    // maxFundingRate is stored as decimal (0.0005) — display as % (0.05)
    // NOTE: config stores field as "maxFundingRate", the backend request DTO calls it "maxFundingRateLong"
    const storedRate = (config as any).maxFundingRate;
    const displayRate = storedRate != null
      ? (Number(storedRate) * 100).toFixed(4).replace(/\.?0+$/, '')
      : '0.05';
    setFilterEditForm({
      entryFiltersEnabled: (config as any).entryFiltersEnabled === true,
      // Sub-filter toggles — default true (enabled) if not explicitly set to false
      fundingRateFilterEnabled: (config as any).fundingRateFilterEnabled !== false,
      rsiFilterEnabled: (config as any).rsiFilterEnabled !== false,
      emaFilterEnabled: (config as any).emaFilterEnabled !== false,
      maxFundingRateLong: displayRate,
      rsiPeriod: (config as any).rsiPeriod?.toString() ?? '14',
      rsiCandleLimit: (config as any).rsiCandleLimit?.toString() ?? '15',
      // NOTE: config stores as "rsiLongMin"/"rsiShortMax", request DTO uses "rsiLongThreshold"/"rsiShortThreshold"
      rsiLongThreshold: (config as any).rsiLongMin?.toString() ?? '55',
      rsiShortThreshold: (config as any).rsiShortMax?.toString() ?? '45',
      emaPeriod: (config as any).emaPeriod?.toString() ?? '20',
    });
    setStrategyFilterProfileId(strategy.filterProfileId ?? '');
    void apiService.get('/api/admin/trading-strategies/filter-profiles')
      .then((response: any) => {
        const rows = response?.data?.data ?? response?.data ?? [];
        setStrategyFilterProfiles(Array.isArray(rows) ? rows.map((p: any) => ({ id: p.id, name: p.name })) : []);
      })
      .catch(() => setStrategyFilterProfiles([]));
    setShowFilterEditModal(true);
  };

  const handleStrategyFilterProfileSave = async () => {
    if (!selectedStrategyForFilterEdit) return;
    try {
      setLoading(true);
      const response = await adminApiService.updateStrategyFilterProfile(
        selectedStrategyForFilterEdit.id,
        strategyFilterProfileId === '' ? null : Number(strategyFilterProfileId),
      );
      if (response.success) {
        const updated = response.data;
        if (updated?.id) {
          setStrategies(prev => prev.map(s => (s.id === updated.id ? { ...s, ...updated } : s)));
          setSelectedStrategyForFilterEdit(prev => (prev ? { ...prev, filterProfileId: updated.filterProfileId ?? null } : prev));
        } else {
          await loadStrategies();
        }
        alert(strategyFilterProfileId === '' ? 'Filter profile cleared.' : 'Filter profile linked.');
      } else {
        alert(response.error || 'Failed to update filter profile.');
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to update filter profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSave = async () => {
    if (!selectedStrategyForFilterEdit) return;

    try {
      setLoading(true);
      const payload: any = {
        entryFiltersEnabled: filterEditForm.entryFiltersEnabled,
        // Sub-filter toggles
        fundingRateFilterEnabled: filterEditForm.fundingRateFilterEnabled,
        rsiFilterEnabled: filterEditForm.rsiFilterEnabled,
        emaFilterEnabled: filterEditForm.emaFilterEnabled,
        // Convert display % back to decimal for the backend
        maxFundingRateLong: parseFloat(filterEditForm.maxFundingRateLong) / 100,
        rsiPeriod: parseInt(filterEditForm.rsiPeriod, 10),
        rsiCandleLimit: parseInt(filterEditForm.rsiCandleLimit, 10),
        rsiLongThreshold: parseFloat(filterEditForm.rsiLongThreshold),
        rsiShortThreshold: parseFloat(filterEditForm.rsiShortThreshold),
        emaPeriod: parseInt(filterEditForm.emaPeriod, 10),
      };

      const response = await adminApiService.updateStrategyEntryFilters(
        selectedStrategyForFilterEdit.id,
        payload
      );

      if (response.success) {
        setShowFilterEditModal(false);
        setSelectedStrategyForFilterEdit(null);
        loadStrategies();
        setError(null);
        alert(`Entry filters ${filterEditForm.entryFiltersEnabled ? 'enabled' : 'disabled'} successfully.`);
      } else {
        setError(response.error || 'Failed to update Entry Filters');
      }
    } catch (err: any) {
      console.error('Error updating Entry Filters:', err);
      setError(err.message || 'Failed to update Entry Filters');
    } finally {
      setLoading(false);
    }
  };

  // Handle Trading Account update
  const handleUpdateTradingAccount = async (strategyId: number, kucoinConfigId: number | null) => {
    setUpdatingTradingAccountId(strategyId);
    setError(null);
    
    try {
      const response = await adminApiService.updateStrategyTradingAccount(strategyId, kucoinConfigId);
      
      if (response.success) {
        // Refresh strategies list to show updated account
        await loadStrategies();
        setError(null);
        
        // Show success message
        const matched = kucoinConfigId !== null ? allKucoinAccounts.find(a => a.id === kucoinConfigId) : null;
        const accountName = (kucoinConfigId === null || matched?.shared)
          ? 'Shared KuCoin (System)' 
          : (matched?.name || `Account #${kucoinConfigId}`);
        alert(`Trading Account updated successfully to: ${accountName}`);
      } else {
        setError(response.error || 'Failed to update Trading Account');
        // Refresh to reset the select to original value
        await loadStrategies();
      }
    } catch (err: any) {
      console.error('Error updating Trading Account:', err);
      setError(err.message || 'Failed to update Trading Account');
      // Refresh to reset the select to original value
      await loadStrategies();
    } finally {
      setUpdatingTradingAccountId(null);
    }
  };

  const deleteStrategy = async (strategyId: number) => {
    if (!window.confirm('Are you sure you want to delete this strategy?')) {
      return;
    }

    try {
      const response = await apiService.delete(`/api/admin/trading-strategies/${strategyId}`);
      if (response.success) {
        await loadStrategies();
      } else {
        setError(response.error || 'Failed to delete strategy');
      }
    } catch (error: any) {
      console.error('Failed to delete strategy:', error);
      setError(error.message || 'Failed to delete strategy');
    }
  };

  // ── Set Required Subscription Plan ──────────────────────────────────────────
  const handleSetRequiredPlan = async (strategy: TradingStrategy, planName: string | null) => {
    setUpdatingRequiredPlanId(strategy.id);
    try {
      const response = await adminApiService.setStrategyRequiredPlan(strategy.id, planName);
      if (response.success) {
        setStrategies(prev =>
          prev.map(s => s.id === strategy.id ? { ...s, requiredPlan: planName } : s)
        );
      } else {
        setError(response.error || 'Failed to update required plan');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update required plan');
    } finally {
      setUpdatingRequiredPlanId(null);
    }
  };

  // ── Publish / Unpublish a strategy for retail users ─────────────────────────
  const handlePublishToggle = async (strategy: TradingStrategy) => {
    const action = strategy.published ? 'unpublish' : 'publish';
    const confirmMsg = strategy.published
      ? `Unpublish "${strategy.name}"? Retail users will no longer see it (existing allocations are not affected).`
      : `Publish "${strategy.name}" to retail users? They will be able to enroll and allocate funds to it.`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const fn = strategy.published
        ? adminApiService.unpublishStrategy.bind(adminApiService)
        : adminApiService.publishStrategy.bind(adminApiService);
      const response = await fn(strategy.id);
      if (response.success) {
        // Optimistic update — flip the flag in local state immediately
        setStrategies(prev =>
          prev.map(s => s.id === strategy.id ? { ...s, published: !s.published } : s)
        );
      } else {
        setError(response.error || `Failed to ${action} strategy`);
      }
    } catch (error: any) {
      console.error(`Failed to ${action} strategy:`, error);
      setError(error.message || `Failed to ${action} strategy`);
    }
  };

  const handleRunGlobalReconciliation = async () => {
    if (!canRunGlobalReconciliation) return;
    if (!window.confirm('Run global reconciliation now? This will sync trading state with KuCoin for all strategies.')) {
      return;
    }
    setRunningGlobalReconciliation(true);
    setError(null);
    try {
      const response = await apiService.post('/api/admin/trading-strategies/reconciliation/fix-hedge-positions/all');
      if (response.success) {
        const data = response.data || {};
        await Promise.all([loadStrategies(), loadActivePairs()]);
        alert(
          `Reconciliation complete.\nChecked: ${data.checkedCount ?? 0}\nFixed: ${data.fixedCount ?? 0}\nErrors: ${data.errorCount ?? 0}`
        );
      } else {
        setError(response.error || 'Failed to run global reconciliation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to run global reconciliation');
    } finally {
      setRunningGlobalReconciliation(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'status-badge active';
      case 'PAUSED':
        return 'status-badge paused';
      case 'STOPPED':
        return 'status-badge stopped';
      default:
        return 'status-badge stopped';
    }
  };

  const getPairStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'status-badge active';
      case 'CLOSED':
        return 'status-badge stopped';
      case 'ERROR':
        return 'status-badge error';
      default:
        return 'status-badge stopped';
    }
  };

  const getPairStatusStyle = (status: string | undefined) => {
    const s = status || 'ACTIVE';
    switch (s) {
      case 'ACTIVE':
        return { background: '#dcfce7', color: '#166534', borderRadius: 12, padding: '4px 8px', fontSize: 12, fontWeight: 600 } as React.CSSProperties;
      case 'CLOSED':
        return { background: '#f3f4f6', color: '#374151', borderRadius: 12, padding: '4px 8px', fontSize: 12, fontWeight: 600 } as React.CSSProperties;
      case 'ERROR':
        return { background: '#fee2e2', color: '#991b1b', borderRadius: 12, padding: '4px 8px', fontSize: 12, fontWeight: 600 } as React.CSSProperties;
      default:
        return { background: '#f3f4f6', color: '#374151', borderRadius: 12, padding: '4px 8px', fontSize: 12, fontWeight: 600 } as React.CSSProperties;
    }
  };

  // Check if strategy has active trading pairs
  const hasActiveTradingPairs = (strategyId: number): boolean => {
    return activePairs.some(pair => pair.strategyId === strategyId && pair.status === 'ACTIVE');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatEntryPrice = (price: number | undefined | null) => {
    const n = Number(price);
    if (!Number.isFinite(n) || n <= 0) {
      return '—';
    }
    return n.toFixed(6);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const hasOpenLongLeg = (pair: ActiveTradingPair): boolean =>
    Boolean(pair.longPositionId && String(pair.longPositionId).trim());

  const hasOpenShortLeg = (pair: ActiveTradingPair): boolean =>
    Boolean(pair.shortPositionId && String(pair.shortPositionId).trim());

  const formatActivePairLegs = (pair: ActiveTradingPair): string => {
    const longOpen = hasOpenLongLeg(pair);
    const shortOpen = hasOpenShortLeg(pair);
    if (longOpen && shortOpen) {
      return `LONG ${pair.longCoin} / SHORT ${pair.shortCoin}`;
    }
    if (longOpen) {
      return `LONG ${pair.longCoin} (short leg closed)`;
    }
    if (shortOpen) {
      return `SHORT ${pair.shortCoin} (long leg closed)`;
    }
    return `${pair.longCoin || '—'} / ${pair.shortCoin || '—'}`;
  };

  const formatLegPnl = (pair: ActiveTradingPair, side: 'LONG' | 'SHORT'): string => {
    const open = side === 'LONG' ? hasOpenLongLeg(pair) : hasOpenShortLeg(pair);
    if (!open) return '—';
    return formatCurrency(side === 'LONG' ? pair.longPnl : pair.shortPnl);
  };

  const legPnlClass = (pair: ActiveTradingPair, side: 'LONG' | 'SHORT'): string => {
    const open = side === 'LONG' ? hasOpenLongLeg(pair) : hasOpenShortLeg(pair);
    if (!open) return 'pnl-value';
    const value = side === 'LONG' ? pair.longPnl : pair.shortPnl;
    return `pnl-value ${value >= 0 ? 'positive' : 'negative'}`;
  };

  // Calculate pagination for Active Trading Pairs
  const totalPairs = activePairs.length;
  const totalPages = Math.ceil(totalPairs / activePairsPageSize);
  const startIndex = (activePairsPage - 1) * activePairsPageSize;
  const endIndex = startIndex + activePairsPageSize;
  const paginatedPairs = activePairs.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="admin-component-loading">
        <div className="loading-spinner"></div>
        <p>Loading trading strategies...</p>
      </div>
    );
  }

  return (
    <div className="admin-component">
      {/* Temporary status message when stopping a bot */}
      {stopStatusMessage && stoppingStrategyId && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? 70 : 80,
            right: 20,
            zIndex: 2000,
            backgroundColor: '#111827',
            color: '#f9fafb',
            padding: '10px 16px',
            borderRadius: 9999,
            boxShadow: '0 10px 25px rgba(15,23,42,0.45)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            maxWidth: isMobile ? '90%' : 380,
            fontSize: 12
          }}
        >
          <span style={{ fontSize: 16 }}>⏳</span>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>Stopping bot...</span>
            <span style={{ opacity: 0.9 }}>{stopStatusMessage}</span>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="section-header" style={{ 
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? '12px' : '0'
      }}>
        <h2 className="section-title" style={{ marginBottom: isMobile ? '0' : '0' }}>Trading Strategies</h2>
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          alignItems: 'center',
          flexWrap: 'wrap',
          width: isMobile ? '100%' : 'auto',
          maxWidth: '100%',
          overflow: 'visible'
        }}>
        <button
            onClick={async () => {
              setShowEnrollmentsModal(true);
              await fetchUserEnrollments();
            }}
            className="primary-btn"
            style={{ 
              backgroundColor: '#10b981', 
              borderColor: '#10b981',
              flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
              minWidth: isMobile ? '0' : 'auto',
              fontSize: isMobile ? '13px' : '14px',
              padding: isMobile ? '8px 12px' : undefined
            }}
          >
            {isMobile ? 'Enrollments' : 'User Enrollments'}
          </button>
        <button
            onClick={() => setShowBlacklistModal(true)}
            className="primary-btn"
            style={{ 
              backgroundColor: '#6366f1', 
              borderColor: '#6366f1',
              flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
              minWidth: isMobile ? '0' : 'auto',
              fontSize: isMobile ? '13px' : '14px',
              padding: isMobile ? '8px 12px' : undefined
            }}
          >
            {isMobile ? 'Blacklist' : 'Meme Blacklist'}
          </button>
        {canRunGlobalReconciliation && (
          <button
            onClick={handleRunGlobalReconciliation}
            disabled={runningGlobalReconciliation}
            className="primary-btn"
            style={{
              backgroundColor: '#0ea5e9',
              borderColor: '#0ea5e9',
              opacity: runningGlobalReconciliation ? 0.75 : 1,
              cursor: runningGlobalReconciliation ? 'not-allowed' : 'pointer',
              flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
              minWidth: isMobile ? '0' : 'auto',
              fontSize: isMobile ? '13px' : '14px',
              padding: isMobile ? '8px 12px' : undefined
            }}
            title="Sync all trading strategies with KuCoin and close stale DB open positions"
          >
            {runningGlobalReconciliation ? 'Reconciling...' : (isMobile ? 'Reconcile' : 'Reconcile All')}
          </button>
        )}
        <button
          onClick={() => {
            setAiSuggestionMeta(null);
            setCreateModalStrategyType('MEME');
            const savedFormData = localStorage.getItem('memeCoinStrategyForm');
            if (savedFormData) {
              try {
                const parsed = JSON.parse(savedFormData);
                setCreateForm(parsed);
              } catch (e) {
                console.error('Failed to restore saved form data:', e);
              }
            }
            setShowCreateModal(true);
            fetchAvailableCoins();
          }}
          className="primary-btn"
          style={{
            flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
            minWidth: isMobile ? '0' : 'auto',
            fontSize: isMobile ? '13px' : '14px',
            padding: isMobile ? '8px 12px' : undefined
          }}
        >
          Create Strategy
        </button>
        <button
          onClick={createAiStrategy}
          disabled={aiSuggestLoading}
          className="primary-btn"
          style={{
            backgroundColor: '#7c3aed',
            borderColor: '#7c3aed',
            flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
            minWidth: isMobile ? '0' : 'auto',
            fontSize: isMobile ? '13px' : '14px',
            padding: isMobile ? '8px 12px' : undefined,
            marginLeft: isMobile ? '12px' : '10px',
            fontWeight: 600,
            opacity: aiSuggestLoading ? 0.7 : 1,
          }}
          title="Analyze live meme market (volatility, participation, BTC) and prefill create form — does not affect running bots"
        >
          {aiSuggestLoading ? 'Analyzing...' : '🤖 Create AI Strategy'}
        </button>
        {featureFlags.admin.enableCryptoPairs && (
          <>
            <button
              onClick={() => {
                setAiSuggestionMeta(null);
                setCreateModalStrategyType('CRYPTO');
                setCreateForm(prev => ({
                  ...prev,
                  name: '',
                  coinSelectionMode: 'TOP_100',
                  coinLimit: 100,
                  marketType: 'FUTURES',
                  paperTrading: true,
                }));
                setShowCreateModal(true);
                fetchAvailableCoins();
              }}
              className="primary-btn"
              style={{
                backgroundColor: '#0369a1',
                borderColor: '#0369a1',
                flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
                minWidth: isMobile ? '0' : 'auto',
                fontSize: isMobile ? '13px' : '14px',
                padding: isMobile ? '8px 12px' : undefined,
                marginLeft: isMobile ? '12px' : '10px',
                fontWeight: 600,
              }}
              title="Create Major Crypto Pairs strategy (Beta) — paper trading recommended"
            >
              ₿ Major Crypto (Beta)
            </button>
            <button
              onClick={createCryptoAiStrategy}
              disabled={aiSuggestLoading}
              className="primary-btn"
              style={{
                backgroundColor: '#0e7490',
                borderColor: '#0e7490',
                flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
                minWidth: isMobile ? '0' : 'auto',
                fontSize: isMobile ? '13px' : '14px',
                padding: isMobile ? '8px 12px' : undefined,
                marginLeft: isMobile ? '12px' : '10px',
                fontWeight: 600,
                opacity: aiSuggestLoading ? 0.7 : 1,
              }}
              title="Analyze major-coin market and prefill crypto pairs create form"
            >
              {aiSuggestLoading ? 'Analyzing...' : '🤖 Create AI Crypto Strategy'}
            </button>
          </>
        )}
        <button
          onClick={prefillOptimalStrategy}
          className="primary-btn"
          style={{
            backgroundColor: '#f59e0b',
            borderColor: '#f59e0b',
            flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
            minWidth: isMobile ? '0' : 'auto',
            fontSize: isMobile ? '13px' : '14px',
            padding: isMobile ? '8px 12px' : undefined,
            marginLeft: isMobile ? '12px' : '10px',
            fontWeight: 600
          }}
          title="Prefill form with optimal profit-making settings: $50 position size, Classic 4.5% stop loss, 7% take profit"
        >
          ⭐ Optimal Strategy
        </button>
        <button
          onClick={() => {
            // Restore saved form data if available
            const savedFormData = localStorage.getItem('memeCoinEnhancedStrategyForm');
            if (savedFormData) {
              try {
                const parsed = JSON.parse(savedFormData);
                setCreateEnhancedForm(parsed);
              } catch (e) {
                console.error('Failed to restore saved form data:', e);
              }
            }
            setShowCreateEnhancedModal(true);
          }}
          className="primary-btn"
          style={{
            backgroundColor: '#10b981',
            borderColor: '#10b981',
            flex: isMobile ? '1 1 calc(50% - 6px)' : 'none',
            minWidth: isMobile ? '0' : 'auto',
            fontSize: isMobile ? '13px' : '14px',
            padding: isMobile ? '8px 12px' : undefined,
            marginLeft: isMobile ? '12px' : '10px'
          }}
        >
          ➕ Create Enhanced Strategy
        </button>
        <button
          onClick={() => setShowReportsModal(true)}
          className="primary-btn"
          style={{
            backgroundColor: '#6366f1',
            borderColor: '#6366f1',
            flex: isMobile ? '1 1 calc(50% - 6px)' : '0 0 auto',
            minWidth: isMobile ? '0' : 'auto',
            fontSize: isMobile ? '13px' : '14px',
            padding: isMobile ? '8px 12px' : undefined,
            whiteSpace: 'nowrap'
          }}
        >
          Reports
        </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {/* Strategy Config Save Success Banner */}
      {tpSaveSuccess && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px',
          background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8,
          marginBottom: 12, fontSize: 14, color: '#065f46', fontWeight: 500,
        }}>
          <span style={{ fontSize: 18 }}>✅</span>
          <span style={{ flex: 1 }}>{tpSaveSuccess}</span>
          <button
            onClick={() => setTpSaveSuccess(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#065f46', padding: '0 4px' }}
          >×</button>
        </div>
      )}

      {/* KuCoin spot coins section */}
      {canViewSpotCoinsSection && (
        <div style={{ marginBottom: 24 }}>
          <KucoinBalances />
        </div>
      )}

      {/* US-RDX-01: Regime Status Dashboard Panel */}
      <RegimeDashboardPanel />

      {/* Strategies Table */}
      <div className="data-table-container">
        <h3 className="table-title">Trading Strategies</h3>
        
        {isMobile ? (
          /* Mobile Card Layout */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {strategies.map(strategy => {
              const config = parseStrategyConfig(strategy.config);
              return (
                <div 
                  key={strategy.id} 
                  style={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: 8, 
                    padding: 16,
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  {/* Strategy Header */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: 8,
                      flexWrap: 'wrap',
                      gap: 8
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                          <h4 style={{ margin: 0, fontSize: 16, color: '#3b82f6', whiteSpace: 'nowrap' }}>
                            {strategy.name}
                          </h4>
                          {strategy.status === 'ACTIVE' && (
                            <span 
                              style={{
                                backgroundColor: '#d1fae5',
                                color: '#065f46',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                border: '1px solid #10b981',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                zIndex: 1,
                                position: 'relative'
                              }}
                              title={hasActiveTradingPairs(strategy.id) 
                                ? "Strategy is actively trading with open positions"
                                : "Strategy is active and monitoring the market"}
                            >
                              ✅ TRADING
                            </span>
                          )}
                          {config.aiAutoTuneEnabled && (() => {
                            const modeBadge = getAiAutoTuneModeBadge(config);
                            const approval = isAiAutoTuneApprovalMode(config);
                            return (
                              <span
                                style={{
                                  backgroundColor: approval ? '#ede9fe' : '#ecfdf5',
                                  color: approval ? '#5b21b6' : '#047857',
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  border: approval ? '1px solid #a78bfa' : '1px solid #6ee7b7',
                                  whiteSpace: 'nowrap',
                                }}
                                title={
                                  approval
                                    ? 'AI tune on — changes need Apply Pending (or scheduler queues pending)'
                                    : 'AI tune on — changes auto-apply when scheduler or Run Now runs'
                                }
                              >
                                {modeBadge}
                              </span>
                            );
                          })()}
                          {hasAiAutoTunePending(config) && (
                            <span
                              style={{
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                border: '1px solid #fbbf24',
                                whiteSpace: 'nowrap',
                              }}
                              title="AI changes awaiting approval"
                            >
                              ⏳ AI PENDING
                            </span>
                          )}
                          {config.paperTrading && (
                            <span 
                              style={{
                                backgroundColor: '#ffc107',
                                color: '#000',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title="Paper Trading (Local Simulator): Uses real market data but simulates trade execution. No real money at risk."
                            >
                              📝 PAPER TRADING
                            </span>
                          )}
                          {strategy.lastError && strategy.lastError.includes('INSUFFICIENT') && (
                            <span 
                              style={{
                                backgroundColor: '#fee',
                                color: '#dc3545',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                border: '1px solid #dc3545'
                              }}
                              title={strategy.lastError || "Insufficient balance to open positions. Please add funds or reduce order size."}
                            >
                              ⚠️ INSUFFICIENT BALANCE
                            </span>
                          )}
                          {strategy.lastError && strategy.lastError.includes('VOLATILITY') && (
                            <span 
                              style={{
                                backgroundColor: '#fff3cd',
                                color: '#856404',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                border: '1px solid #856404'
                              }}
                              title={strategy.lastError || "Market volatility outside acceptable range. Strategy is monitoring but not opening new positions."}
                            >
                              🛡️ VOLATILITY BLOCKED
                            </span>
                          )}
                        </div>
                        {/* Mock/Real Data Toggle Switch */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>
                            {isMockData(strategy.id) ? 'Mock' : 'Real'}
                          </span>
                          <button
                            onClick={() => toggleMockDataMode(strategy.id)}
                            disabled={isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()}
                            style={{
                              position: 'relative',
                              width: 44,
                              height: 24,
                              borderRadius: 12,
                              border: 'none',
                              backgroundColor: isMockData(strategy.id) ? '#ffc107' : '#28a745',
                              cursor: (isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()) ? 'not-allowed' : 'pointer',
                              opacity: (isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()) ? 0.6 : 1,
                              transition: 'background-color 0.2s',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              outline: 'none'
                            }}
                            title={isPaperTradingStrategy(strategy.id)
                              ? "Paper Trading: Always uses real market data (toggle disabled)"
                              : hasRealMoneyLiveBots()
                              ? "Toggle disabled: You have real money live bots. Cannot mix real and mock data."
                              : (isMockData(strategy.id) 
                                ? "Currently showing mock data. Click to switch to real data." 
                                : "Currently showing real data. Click to switch to mock data.")}
                          >
                            <span style={{
                              position: 'absolute',
                              left: isMockData(strategy.id) ? 2 : 22,
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: 'white',
                              transition: 'left 0.2s',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                          </button>
                          <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>
                            Data
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span 
                        className={getStatusColor(strategy.status)}
                        style={{ 
                          padding: '4px 8px', 
                          borderRadius: 4, 
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        {strategy.status}
                      </span>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 600,
                          background: strategy.published ? '#dcfce7' : '#f3f4f6',
                          color: strategy.published ? '#16a34a' : '#6b7280',
                          border: `1px solid ${strategy.published ? '#86efac' : '#d1d5db'}`
                        }}
                      >
                        {strategy.published ? '🟢 Published' : '⚫ Unpublished'}
                      </span>
                      </div>
                    </div>
                    
                    {/* Strategy Details */}
                    <div style={{ display: 'grid', gap: 6, fontSize: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Type:</span>
                        <span style={{ fontWeight: 600 }}>{strategy.strategyType.replace('_', ' ')}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Exchange:</span>
                        <span className="exchange-badge">{config.exchange || 'N/A'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Trading Account:</span>
                        <span style={{ fontWeight: 600 }}>
                          {(() => {
                            const id = (config as any).kucoinConfigId as number | undefined;
                            if (id == null) return 'Shared KuCoin (System)';
                            const match = allKucoinAccounts.find(a => a.id === id);
                            if (match?.shared) return 'Shared KuCoin (System)';
                            if (match) return match.name;
                            return '🔒 Another Admin\'s Account';
                          })()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Leverage:</span>
                        <span style={{ fontWeight: 600 }}>{config.leverage ? `${config.leverage}x` : 'N/A'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Order Amount:</span>
                        <span style={{ fontWeight: 600 }}>{config.fixedOrderAmount ? `$${config.fixedOrderAmount.toLocaleString()}` : 'N/A'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Take Profit:</span>
                        <span style={{ fontWeight: 600 }}>{config.takeProfit ? `${config.takeProfit}%` : 'N/A'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Stop Loss:</span>
                        <span style={{ fontWeight: 600 }}>{config.trailingStopLoss ? `${config.trailingStopLoss}%` : 'N/A'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Created:</span>
                        <span style={{ fontWeight: 600 }}>{formatDate(strategy.createdAt)}</span>
                      </div>
                      {(() => {
                        const mobileStrategyCoins = (config as any).selectedCoins || [];
                        const mobileCoinMode = (config as any).coinSelectionMode;
                        const mobileCoinLimit = (config as any).coinLimit;

                        let mobileDisplayCoins: string[] = [];
                        let mobileDisplayText = '';

                        if ((mobileCoinMode === 'MANUAL' || mobileCoinMode === 'ALL_COINS') && mobileStrategyCoins.length > 0) {
                          mobileDisplayCoins = mobileStrategyCoins;
                        } else if (mobileCoinMode === 'TOP_N' && mobileCoinLimit) {
                          mobileDisplayText = mobileCoinLimit === 999 ? 'Top All Available' : `Top ${mobileCoinLimit} by Volume`;
                        } else if (memeUniverse && memeUniverse.length > 0) {
                          mobileDisplayCoins = memeUniverse;
                        }

                        if (mobileDisplayCoins.length === 0 && !mobileDisplayText) return null;

                        return (
                          <div style={{ marginTop: 8 }}>
                            <span style={{ color: '#666', fontSize: 12, fontWeight: 600 }}>
                              Universe{mobileDisplayCoins.length > 0 ? ` (${mobileDisplayCoins.length})` : ''}:
                            </span>
                            {mobileDisplayCoins.length > 0 ? (
                              <div style={{
                                marginTop: 6,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                              }}>
                                {mobileDisplayCoins.map((coin: string, idx: number) => (
                                  <span
                                    key={idx}
                                    style={{
                                      backgroundColor: '#eef2ff',
                                      color: '#3730a3',
                                      borderRadius: 6,
                                      padding: '2px 6px',
                                      fontSize: '11px',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {coin}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div style={{
                                marginTop: 4,
                                fontSize: 11,
                                color: '#3730a3',
                                backgroundColor: '#eef2ff',
                                padding: '4px 8px',
                                borderRadius: 4,
                                display: 'inline-block',
                              }}>
                                {mobileDisplayText}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: 8,
                    paddingTop: 12,
                    borderTop: '1px solid #e5e7eb'
                  }}>
                    {(() => {
                      const usesSharedAccount = config.kucoinConfigId == null;
                      const blockedBySharedKucoin = usesSharedAccount && !canUseSharedKucoin;
                      const startTitle = blockedBySharedKucoin
                        ? 'Cannot start: this strategy uses the shared KuCoin (System) account. Assign a personal trading account first, or ask a Super Admin to grant the "Use Shared KuCoin Account" permission.'
                        : undefined;
                      return strategy.status === 'STOPPED' ? (
                        <button
                          onClick={() => !blockedBySharedKucoin && startStrategy(strategy.id)}
                          className="action-btn start-btn"
                          style={{ fontSize: 13, padding: '8px 12px', opacity: blockedBySharedKucoin ? 0.45 : 1, cursor: blockedBySharedKucoin ? 'not-allowed' : 'pointer' }}
                          title={startTitle}
                          disabled={blockedBySharedKucoin}
                        >
                          Start
                        </button>
                      ) : strategy.status === 'ACTIVE' ? (
                        <>
                          <button
                            onClick={() => pauseStrategy(strategy.id)}
                            className="action-btn pause-btn"
                            style={{ fontSize: 13, padding: '8px 12px' }}
                          >
                            Pause
                          </button>
                          <button
                            onClick={() => stopStrategy(strategy.id)}
                            className="action-btn stop-btn"
                            style={{ fontSize: 13, padding: '8px 12px' }}
                          >
                            Stop
                          </button>
                          <button
                            onClick={() => {
                              setEvaluateStrategy({ strategy, config });
                              setShowEvaluateModal(true);
                            }}
                            className="action-btn"
                            style={{ 
                              fontSize: 13, 
                              padding: '8px 12px',
                              border: '1px solid #3b82f6',
                              backgroundColor: '#eff6ff',
                              color: '#1d4ed8',
                              fontWeight: 600
                            }}
                          >
                            Evaluate
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => !blockedBySharedKucoin && startStrategy(strategy.id)}
                          className="action-btn start-btn"
                          style={{ fontSize: 13, padding: '8px 12px', opacity: blockedBySharedKucoin ? 0.45 : 1, cursor: blockedBySharedKucoin ? 'not-allowed' : 'pointer' }}
                          title={startTitle}
                          disabled={blockedBySharedKucoin}
                        >
                          Resume
                        </button>
                      );
                    })()}
                    {adminAuthService.hasPermission('VIEW_TRADING_BOT_ANALYTICS') && (
                      <button
                        onClick={() => openAnalyticsModal(strategy.id)}
                        className="action-btn analytics-btn"
                        style={{ 
                          background: '#667eea', 
                          color: 'white', 
                          border: 'none',
                          fontSize: 13, 
                          padding: '8px 12px',
                          gridColumn: strategy.status === 'ACTIVE' ? 'span 2' : 'auto'
                        }}
                      >
                        📊 Analytics
                      </button>
                    )}
                    {adminAuthService.hasPermission('VIEW_TRADING_STRATEGY_LOGS') && (
                      <button
                        onClick={() => {
                          setLogsStrategyName(strategy.name);
                          setShowLogsModal(true);
                        }}
                        className="action-btn logs-btn"
                        style={{ 
                          background: '#10b981', 
                          color: 'white', 
                          border: 'none',
                          fontSize: 13, 
                          padding: '8px 12px',
                          gridColumn: strategy.status === 'ACTIVE' ? 'span 2' : 'auto'
                        }}
                      >
                        📋 Logs
                      </button>
                    )}
                    <button
                      onClick={() => handleTPEdit(strategy)}
                      className="action-btn"
                      style={{ 
                        background: '#086afb', 
                        color: 'white', 
                        border: 'none',
                        fontSize: 13, 
                        padding: '8px 12px',
                        gridColumn: 'span 2'
                      }}
                      title="Edit Strategy"
                    >
                      ✏️ Edit Strategy
                    </button>
                    {(strategy.strategyType === 'MEME_COIN_PAIRS' || strategy.strategyType === 'CRYPTO_PAIRS') && (
                      <button
                        onClick={() => handleCoinEdit(strategy)}
                        className="action-btn"
                        style={{
                          background: '#0d9488',
                          color: 'white',
                          border: 'none',
                          fontSize: 13,
                          padding: '8px 12px',
                          gridColumn: 'span 2'
                        }}
                        title="Edit Coin Selection"
                      >
                        🪙 Coins
                      </button>
                    )}
                    {(strategy.strategyType === 'MEME_COIN_PAIRS' || strategy.strategyType === 'CRYPTO_PAIRS') && (
                      <button
                        onClick={() => handleFilterEdit(strategy)}
                        className="action-btn"
                        style={{
                          background: '#7c3aed',
                          color: 'white',
                          border: 'none',
                          fontSize: 13,
                          padding: '8px 12px',
                          gridColumn: 'span 2'
                        }}
                        title="Edit Entry Filters"
                      >
                        🔍 Edit Filters
                      </button>
                    )}
                    {(strategy.strategyType === 'MEME_COIN_PAIRS' || strategy.strategyType === 'CRYPTO_PAIRS') && (
                      <button
                        onClick={() => handleAiAutoTuneEdit(strategy)}
                        className="action-btn"
                        style={{
                          ...getAiAutoTuneButtonStyle(parseStrategyConfig(strategy.config)),
                          gridColumn: 'span 2',
                        }}
                        title={
                          parseStrategyConfig(strategy.config).aiAutoTuneEnabled
                            ? 'AI auto-tune is ON for this bot — click to manage'
                            : 'Enable per-bot AI auto-tune (does not affect other bots)'
                        }
                      >
                        {getAiAutoTuneButtonLabel(parseStrategyConfig(strategy.config))}
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenClone(strategy)}
                      className="action-btn"
                      style={{
                        background: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        fontSize: 13,
                        padding: '8px 12px',
                        gridColumn: 'span 2'
                      }}
                      title="Clone this strategy"
                    >
                      📋 Clone
                    </button>
                    <button
                      onClick={() => handlePublishToggle(strategy)}
                      className="action-btn"
                      style={{
                        background: strategy.published ? '#dc2626' : '#16a34a',
                        color: 'white',
                        border: 'none',
                        fontSize: 13,
                        padding: '8px 12px',
                        gridColumn: 'span 2'
                      }}
                      title={strategy.published ? 'Unpublish from retail users' : 'Publish to retail users'}
                    >
                      {strategy.published ? '🔴 Unpublish' : '🟢 Publish'}
                    </button>
                    {/* Required plan selector */}
                    <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>🔒 Required Plan</label>
                      <select
                        value={strategy.requiredPlan || ''}
                        disabled={updatingRequiredPlanId === strategy.id}
                        onChange={e => handleSetRequiredPlan(strategy, e.target.value || null)}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid #d1d5db',
                          fontSize: 12,
                          background: strategy.requiredPlan ? '#fef9c3' : '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">No restriction (any plan)</option>
                        {availablePlans.map(p => (
                          <option key={p.id} value={p.name}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      onClick={() => deleteStrategy(strategy.id)}
                      className="action-btn delete-btn"
                      style={{ 
                        fontSize: 13, 
                        padding: '8px 12px',
                        gridColumn: 'span 2'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop Table Layout */
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Exchange</th>
                  <th>Trading Account</th>
                  <th>Leverage</th>
                  <th>Order Amount</th>
                  <th>Take Profit</th>
                  <th>Stop Loss</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Universe</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {strategies.map(strategy => {
                  const config = parseStrategyConfig(strategy.config);
                  return (
                    <tr key={strategy.id} className="data-row">
                      <td className="strategy-name">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span>{strategy.name}</span>
                          {strategy.status === 'ACTIVE' && (
                            <span 
                              style={{
                                backgroundColor: '#d1fae5',
                                color: '#065f46',
                                padding: '3px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                border: '1px solid #10b981'
                              }}
                              title={hasActiveTradingPairs(strategy.id) 
                                ? "Strategy is actively trading with open positions"
                                : "Strategy is active and monitoring the market"}
                            >
                              ✅ TRADING
                            </span>
                          )}
                          {config.aiAutoTuneEnabled && (() => {
                            const modeBadge = getAiAutoTuneModeBadge(config);
                            const approval = isAiAutoTuneApprovalMode(config);
                            return (
                              <span
                                style={{
                                  backgroundColor: approval ? '#ede9fe' : '#ecfdf5',
                                  color: approval ? '#5b21b6' : '#047857',
                                  padding: '3px 8px',
                                  borderRadius: '10px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  border: approval ? '1px solid #a78bfa' : '1px solid #6ee7b7',
                                  whiteSpace: 'nowrap',
                                }}
                                title={
                                  approval
                                    ? 'AI tune on — changes need Apply Pending (or scheduler queues pending)'
                                    : 'AI tune on — changes auto-apply when scheduler or Run Now runs'
                                }
                              >
                                {modeBadge}
                              </span>
                            );
                          })()}
                          {hasAiAutoTunePending(config) && (
                            <span
                              style={{
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                padding: '3px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                border: '1px solid #fbbf24',
                                whiteSpace: 'nowrap',
                              }}
                              title="AI changes awaiting approval"
                            >
                              ⏳ AI PENDING
                            </span>
                          )}
                          {config.paperTrading && (
                            <span 
                              style={{
                                backgroundColor: '#ffc107',
                                color: '#000',
                                padding: '3px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px'
                              }}
                              title="Paper Trading (Local Simulator): Uses real market data but simulates trade execution. No real money at risk."
                            >
                              📝 PAPER
                            </span>
                          )}
                          {strategy.lastError && strategy.lastError.includes('INSUFFICIENT') && (
                            <span 
                              style={{
                                backgroundColor: '#fee',
                                color: '#dc3545',
                                padding: '3px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                border: '1px solid #dc3545'
                              }}
                              title={strategy.lastError || "Insufficient balance to open positions. Please add funds or reduce order size."}
                            >
                              ⚠️ INSUFFICIENT BALANCE
                            </span>
                          )}
                          {strategy.lastError && strategy.lastError.includes('VOLATILITY') && (
                            <span 
                              style={{
                                backgroundColor: '#fff3cd',
                                color: '#856404',
                                padding: '3px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                border: '1px solid #856404'
                              }}
                              title={strategy.lastError || "Market volatility outside acceptable range. Strategy is monitoring but not opening new positions."}
                            >
                              🛡️ VOLATILITY BLOCKED
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="strategy-type">{strategy.strategyType.replace('_', ' ')}</td>
                      <td className="exchange">
                        <span className="exchange-badge">
                          {config.exchange || 'N/A'}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const id = (config as any).kucoinConfigId as number | undefined;
                          const isActive = strategy.status === 'ACTIVE';
                          const isUpdating = updatingTradingAccountId === strategy.id;
                          // Look up in the full list (personal + shared system accounts)
                          const assignedAccount = id != null ? allKucoinAccounts.find(a => a.id === id) : null;
                          // True "foreign" = has a specific account ID but it belongs to another admin
                          // (not in allKucoinAccounts means it's genuinely locked to another admin's private account)
                          const isForeignAccount = id != null && !assignedAccount;

                          // Strategy uses another admin's private account — show a safe fixed label, no dropdown
                          if (isForeignAccount) {
                            return (
                              <span style={{
                                fontSize: 12,
                                padding: '4px 8px',
                                color: '#6b7280',
                                fontStyle: 'italic'
                              }}>
                                🔒 Another Admin's Account
                              </span>
                            );
                          }

                          // Shared system accounts map to the "" option (same as null/unassigned)
                          const selectValue = assignedAccount?.shared ? '' : (id?.toString() ?? '');
                          const isOnSharedAccount = selectValue === '';

                          const resolveName = (val: number | null) => {
                            if (val === null) return 'Shared KuCoin (System)';
                            const acc = allKucoinAccounts.find(a => a.id === val);
                            if (acc?.shared) return 'Shared KuCoin (System)';
                            return acc?.name || `Account #${val}`;
                          };

                          // If the strategy is currently on the shared account but this admin
                          // lacks USE_SHARED_KUCOIN_ACCOUNT, show a read-only locked label
                          // instead of an interactive (but broken/empty) select.
                          if (isOnSharedAccount && !canUseSharedKucoin) {
                            return (
                              <span style={{
                                fontSize: 12,
                                padding: '4px 8px',
                                color: '#92400e',
                                background: '#fef3c7',
                                border: '1px solid #fbbf24',
                                borderRadius: '4px',
                                display: 'inline-block',
                              }}
                                title="This strategy is on the shared system account. You need the 'Use Shared KuCoin Account' permission to change it."
                              >
                                🔒 Shared (no permission)
                              </span>
                            );
                          }

                          return (
                            <select
                              value={selectValue}
                              onChange={(e) => {
                                const newValue = e.target.value === '' ? null : Number(e.target.value);
                                const oldValue = assignedAccount?.shared ? null : (id ?? null);
                                if (window.confirm(
                                  `Change Trading Account for strategy "${strategy.name}"?\n\n` +
                                  `From: ${resolveName(oldValue)}\n` +
                                  `To: ${resolveName(newValue)}\n\n` +
                                  `This will affect all new trades opened by this strategy.`
                                )) {
                                  handleUpdateTradingAccount(strategy.id, newValue);
                                } else {
                                  e.target.value = selectValue;
                                }
                              }}
                              disabled={isActive || isUpdating || accountsLoading}
                              style={{
                                fontSize: 12,
                                padding: '4px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                backgroundColor: isActive || isUpdating ? '#f3f4f6' : 'white',
                                color: isActive ? '#6b7280' : '#111827',
                                fontWeight: 500,
                                cursor: isActive || isUpdating ? 'not-allowed' : 'pointer',
                                minWidth: '150px',
                                width: '100%'
                              }}
                              title={isActive 
                                ? 'Cannot change trading account while strategy is ACTIVE. Please pause or stop the strategy first.' 
                                : isUpdating 
                                  ? 'Updating...' 
                                  : 'Click to change trading account'}
                            >
                              {/* Shared system account only visible to admins with USE_SHARED_KUCOIN_ACCOUNT */}
                              {canUseSharedKucoin && (
                                <option value="">Shared KuCoin (System)</option>
                              )}
                              {/* Only show personal (non-shared) accounts as distinct options */}
                              {allKucoinAccounts.filter(acc => !acc.shared && acc.selectable !== false).map(acc => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.name}
                                  {acc.provisioningStatus && acc.provisioningStatus !== 'READY'
                                    ? ` (${acc.provisioningStatus})`
                                    : ''}
                                </option>
                              ))}
                            </select>
                          );
                        })()}
                      </td>
                      <td className="leverage">
                        {config.leverage ? `${config.leverage}x` : 'N/A'}
                      </td>
                      <td className="order-amount">
                        {config.fixedOrderAmount ? `$${config.fixedOrderAmount.toLocaleString()}` : 'N/A'}
                      </td>
                      <td className="take-profit">
                        {config.takeProfit ? `${config.takeProfit}%` : 'N/A'}
                      </td>
                      <td className="stop-loss">
                        {config.trailingStopLoss ? `${config.trailingStopLoss}%` : 'N/A'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span
                          className={getStatusColor(strategy.status)}
                          title={strategy.status}
                          style={{ display: 'inline-block' }}
                        >
                          {strategy.status}
                        </span>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 6px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 600,
                              background: strategy.published ? '#dcfce7' : '#f3f4f6',
                              color: strategy.published ? '#16a34a' : '#6b7280',
                              border: `1px solid ${strategy.published ? '#86efac' : '#d1d5db'}`
                            }}
                          >
                            {strategy.published ? '🟢 Published' : '⚫ Unpublished'}
                        </span>
                        </div>
                      </td>

                      <td className="created-date">{formatDate(strategy.createdAt)}</td>
                      <td>
                        {(() => {
                          // Get coins from strategy config
                          const strategyCoins = (config as any).selectedCoins || [];
                          const coinSelectionMode = (config as any).coinSelectionMode;
                          const coinLimit = (config as any).coinLimit;
                          
                          // Determine display text
                          let displayCoins: string[] = [];
                          let displayText = '';
                          
                          if ((coinSelectionMode === 'MANUAL' || coinSelectionMode === 'ALL_COINS') && strategyCoins.length > 0) {
                            // MANUAL and ALL_COINS both use the stored selectedCoins list
                            displayCoins = strategyCoins;
                            displayText = strategyCoins.join(', ');
                          } else if (coinSelectionMode === 'TOP_N' && coinLimit) {
                            if (coinLimit === 999) {
                              displayText = `Top All Available`;
                            } else {
                              displayText = `Top ${coinLimit} by Volume`;
                            }
                          } else if (memeUniverse && memeUniverse.length > 0) {
                            // Fallback to global universe if config doesn't have specific coins
                            displayCoins = memeUniverse;
                            displayText = memeUniverse.join(', ');
                          } else {
                            displayText = 'No coins configured';
                          }
                          
                          const allCoinsText = displayCoins.length > 0 ? displayCoins.join(', ') : displayText;
                          
                          return (
                            <div 
                              title={allCoinsText}
                              style={{ 
                                maxWidth: '300px',
                                wordWrap: 'break-word',
                                lineHeight: '1.4'
                              }}
                            >
                              {displayCoins.length > 0 ? (
                                <div style={{ 
                                  display: 'flex', 
                                  flexWrap: 'wrap', 
                                  gap: '4px',
                                  alignItems: 'center'
                                }}>
                                  {displayCoins.map((coin, idx) => (
                                    <span 
                                      key={idx}
                                      className="exchange-badge" 
                                      style={{ 
                                        backgroundColor: '#eef2ff', 
                                        color: '#3730a3', 
                                        borderRadius: 6,
                                        padding: '2px 6px',
                                        fontSize: '11px',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {coin}
                            </span>
                                  ))}
                                  <span style={{ marginLeft: 4, fontSize: 11, color: '#6b7280' }}>
                                    ({displayCoins.length})
                                  </span>
                          </div>
                        ) : (
                                <span 
                                  className="exchange-badge" 
                                  style={{ 
                                    backgroundColor: '#eef2ff', 
                                    color: '#3730a3', 
                                    borderRadius: 6,
                                    padding: '2px 6px',
                                    fontSize: '11px'
                                  }}
                                >
                                  {displayText}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          {(() => {
                            const usesSharedAccount = config.kucoinConfigId == null;
                            const blockedBySharedKucoin = usesSharedAccount && !canUseSharedKucoin;
                            const startTitle = blockedBySharedKucoin
                              ? 'Cannot start: this strategy uses the shared KuCoin (System) account. Assign a personal trading account first, or ask a Super Admin to grant the "Use Shared KuCoin Account" permission.'
                              : undefined;
                            return strategy.status === 'STOPPED' ? (
                              <button
                                onClick={() => !blockedBySharedKucoin && startStrategy(strategy.id)}
                                className="action-btn start-btn"
                                style={{ opacity: blockedBySharedKucoin ? 0.45 : 1, cursor: blockedBySharedKucoin ? 'not-allowed' : 'pointer' }}
                                title={startTitle}
                                disabled={blockedBySharedKucoin}
                              >
                                Start
                              </button>
                            ) : strategy.status === 'ACTIVE' ? (
                              <>
                                <button
                                  onClick={() => pauseStrategy(strategy.id)}
                                  className="action-btn pause-btn"
                                >
                                  Pause
                                </button>
                                <button
                                  onClick={() => stopStrategy(strategy.id)}
                                  className="action-btn stop-btn"
                                >
                                  Stop
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => !blockedBySharedKucoin && startStrategy(strategy.id)}
                                className="action-btn start-btn"
                                style={{ opacity: blockedBySharedKucoin ? 0.45 : 1, cursor: blockedBySharedKucoin ? 'not-allowed' : 'pointer' }}
                                title={startTitle}
                                disabled={blockedBySharedKucoin}
                              >
                                Resume
                              </button>
                            );
                          })()}
                          {adminAuthService.hasPermission('VIEW_TRADING_BOT_ANALYTICS') && (
                          <button
                            onClick={() => openAnalyticsModal(strategy.id)}
                            className="action-btn analytics-btn"
                            style={{ background: '#667eea', color: 'white', border: 'none' }}
                          >
                            Analytics
                          </button>
                          )}
                          {adminAuthService.hasPermission('VIEW_TRADING_STRATEGY_LOGS') && (
                          <button
                            onClick={() => {
                              setLogsStrategyName(strategy.name);
                              setShowLogsModal(true);
                            }}
                            className="action-btn logs-btn"
                            style={{ background: '#10b981', color: 'white', border: 'none' }}
                          >
                            Logs
                          </button>
                          )}
                          <button
                            onClick={() => handleTPEdit(strategy)}
                            className="action-btn"
                            style={{ background: '#086afb', color: 'white', border: 'none' }}
                            title="Edit Strategy"
                          >
                            Edit Strategy
                          </button>
                          {(strategy.strategyType === 'MEME_COIN_PAIRS' || strategy.strategyType === 'CRYPTO_PAIRS') && (
                            <button
                              onClick={() => handleCoinEdit(strategy)}
                              className="action-btn"
                              style={{ background: '#0d9488', color: 'white', border: 'none' }}
                              title="Edit Coin Selection"
                            >
                              🪙 Coins
                            </button>
                          )}
                          {(strategy.strategyType === 'MEME_COIN_PAIRS' || strategy.strategyType === 'CRYPTO_PAIRS') && (
                            <button
                              onClick={() => handleFilterEdit(strategy)}
                              className="action-btn"
                              style={{ background: '#7c3aed', color: 'white', border: 'none' }}
                              title="Edit Entry Filters"
                            >
                              🔍 Filters
                            </button>
                          )}
                          {(strategy.strategyType === 'MEME_COIN_PAIRS' || strategy.strategyType === 'CRYPTO_PAIRS') && (() => {
                            const cfg = parseStrategyConfig(strategy.config);
                            return (
                              <button
                                onClick={() => handleAiAutoTuneEdit(strategy)}
                                className="action-btn"
                                style={getAiAutoTuneButtonStyle(cfg)}
                                title={
                                  cfg.aiAutoTuneEnabled
                                    ? 'AI auto-tune is ON — click to manage or run now'
                                    : 'Enable live AI auto-tune for this bot only'
                                }
                              >
                                {getAiAutoTuneButtonLabel(cfg)}
                              </button>
                            );
                          })()}
                          <button
                            onClick={() => {
                              const config = parseStrategyConfig(strategy.config);
                              setEvaluateStrategy({ strategy, config });
                              setShowEvaluateModal(true);
                            }}
                            className="action-btn"
                            style={{ 
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              border: '1px solid #3b82f6',
                              fontWeight: 600 
                            }}
                            title="Open evaluation view for this strategy"
                          >
                            Evaluate
                          </button>
                          <button
                            onClick={() => handleOpenClone(strategy)}
                            className="action-btn"
                            style={{ background: '#f59e0b', color: 'white', border: 'none' }}
                            title="Clone this strategy"
                          >
                            📋 Clone
                          </button>
                          <button
                            onClick={() => handlePublishToggle(strategy)}
                            className="action-btn"
                            style={{
                              background: strategy.published ? '#dc2626' : '#16a34a',
                              color: 'white',
                              border: 'none'
                            }}
                            title={strategy.published ? 'Unpublish from retail users' : 'Publish to retail users'}
                          >
                            {strategy.published ? '🔴 Unpublish' : '🟢 Publish'}
                          </button>
                          {/* Required plan selector */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>🔒 Required Plan</span>
                            <select
                              value={strategy.requiredPlan || ''}
                              disabled={updatingRequiredPlanId === strategy.id}
                              onChange={e => handleSetRequiredPlan(strategy, e.target.value || null)}
                              style={{
                                padding: '4px 6px',
                                borderRadius: 5,
                                border: '1px solid #d1d5db',
                                fontSize: 11,
                                background: strategy.requiredPlan ? '#fef9c3' : '#fff',
                                cursor: 'pointer',
                                maxWidth: 120
                              }}
                            >
                              <option value="">Any plan</option>
                              {availablePlans.map(p => (
                                <option key={p.id} value={p.name}>{p.name}</option>
                              ))}
                            </select>
                          </div>
                          <button
                            onClick={() => deleteStrategy(strategy.id)}
                            className="action-btn delete-btn"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Active Pairs Table */}
      <div className="data-table-container">
        <h3 className="table-title">Active Trading Pairs</h3>
        
        {isMobile ? (
          /* Mobile Card Layout */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {paginatedPairs.map(pair => (
              <div 
                key={pair.id} 
                style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 8, 
                  padding: 16,
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
              >
                {/* Pair Header */}
                <div style={{ marginBottom: 12 }}>
                  <div className="pair-display" style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, color: '#111827', marginBottom: 6 }}>
                      {formatActivePairLegs(pair)}
                    </div>
                    {hasOpenLongLeg(pair) && (
                      <div>
                        <span className="long-coin" style={{ color: '#059669', fontWeight: 600 }}>LONG {pair.longCoin}</span>
                        <span style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>
                          @ {formatEntryPrice(pair.longEntryPrice)}
                        </span>
                      </div>
                    )}
                    {hasOpenShortLeg(pair) && (
                      <div style={{ marginTop: 4 }}>
                        <span className="short-coin" style={{ color: '#dc2626', fontWeight: 600 }}>SHORT {pair.shortCoin}</span>
                        <span style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>
                          @ {formatEntryPrice(pair.shortEntryPrice)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Opened: {formatDate(pair.entryTime)}
                  </div>
                </div>

                {/* PnL Details */}
                <div style={{ 
                  display: 'grid', 
                  gap: 8, 
                  paddingTop: 12,
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, color: '#666' }}>Long PnL:</span>
                    <span
                      className={legPnlClass(pair, 'LONG')}
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: !hasOpenLongLeg(pair) ? '#9ca3af' : pair.longPnl >= 0 ? '#059669' : '#dc2626',
                      }}
                    >
                      {formatLegPnl(pair, 'LONG')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, color: '#666' }}>Short PnL:</span>
                    <span
                      className={legPnlClass(pair, 'SHORT')}
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: !hasOpenShortLeg(pair) ? '#9ca3af' : pair.shortPnl >= 0 ? '#059669' : '#dc2626',
                      }}
                    >
                      {formatLegPnl(pair, 'SHORT')}
                    </span>
                  </div>
                  <div 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      paddingTop: 8,
                      borderTop: '1px solid #e5e7eb',
                      marginTop: 4
                    }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>Combined PnL:</span>
                    <span 
                      className={`pnl-value combined ${pair.combinedPnl >= 0 ? 'positive' : 'negative'}`}
                      style={{ 
                        fontSize: 17, 
                        fontWeight: 700,
                        color: pair.combinedPnl >= 0 ? '#059669' : '#dc2626'
                      }}
                    >
                      {formatCurrency(pair.combinedPnl)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    <span style={{ fontSize: 14, color: '#666' }}>Status:</span>
                    <span 
                      style={{ 
                        fontSize: 13, 
                        fontWeight: 600,
                        padding: '4px 8px',
                        borderRadius: 4,
                        backgroundColor: pair.status === 'ACTIVE' ? '#d1fae5' : '#fee2e2',
                        color: pair.status === 'ACTIVE' ? '#065f46' : '#991b1b'
                      }}
                    >
                      {pair.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Desktop Table Layout */
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Pair</th>
                  <th>Entry prices</th>
                  <th>Entry Time</th>
                  <th>Long PnL</th>
                  <th>Short PnL</th>
                  <th>Combined PnL</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                  {paginatedPairs.map(pair => (
                  <tr key={pair.id} className="data-row">
                    <td className="pair-info">
                      <div className="pair-display">
                        {hasOpenLongLeg(pair) && (
                          <span className="long-coin">LONG {pair.longCoin}</span>
                        )}
                        {hasOpenLongLeg(pair) && hasOpenShortLeg(pair) && (
                          <span className="pair-separator"> / </span>
                        )}
                        {hasOpenShortLeg(pair) && (
                          <span className="short-coin">SHORT {pair.shortCoin}</span>
                        )}
                        {!hasOpenLongLeg(pair) && !hasOpenShortLeg(pair) && (
                          <span>{formatActivePairLegs(pair)}</span>
                        )}
                        {(hasOpenLongLeg(pair) !== hasOpenShortLeg(pair)) && (
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                            Single leg open
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="entry-prices" style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                      {hasOpenLongLeg(pair) && (
                        <div>
                          <span style={{ color: '#059669', fontWeight: 600 }}>L</span>{' '}
                          {formatEntryPrice(pair.longEntryPrice)}
                        </div>
                      )}
                      {hasOpenShortLeg(pair) && (
                        <div>
                          <span style={{ color: '#dc2626', fontWeight: 600 }}>S</span>{' '}
                          {formatEntryPrice(pair.shortEntryPrice)}
                        </div>
                      )}
                      {!hasOpenLongLeg(pair) && !hasOpenShortLeg(pair) && (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td className="entry-time">{formatDate(pair.entryTime)}</td>
                    <td className={legPnlClass(pair, 'LONG')}>
                      {formatLegPnl(pair, 'LONG')}
                    </td>
                    <td className={legPnlClass(pair, 'SHORT')}>
                      {formatLegPnl(pair, 'SHORT')}
                    </td>
                    <td className={`pnl-value combined ${pair.combinedPnl >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(pair.combinedPnl)}
                    </td>
                    <td className={`pnl-value`}>
                      {/* {(pair.status === 'ACTIVE' ? 'ACTIVE' : 'NACTIVE')} */}
                      {(pair.status)}
                    </td>
                 
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginTop: 20,
                  padding: '12px 16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: 8,
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: 14, color: '#666' }}>
                    Showing {startIndex + 1} to {Math.min(endIndex, totalPairs)} of {totalPairs} pairs
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      onClick={() => setActivePairsPage(prev => Math.max(1, prev - 1))}
                      disabled={activePairsPage === 1}
                      style={{
                        padding: '6px 12px',
                        fontSize: 14,
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        backgroundColor: activePairsPage === 1 ? '#f3f4f6' : 'white',
                        color: activePairsPage === 1 ? '#9ca3af' : '#374151',
                        cursor: activePairsPage === 1 ? 'not-allowed' : 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Previous
                    </button>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        // Show first page, last page, current page, and pages around current
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= activePairsPage - 1 && page <= activePairsPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setActivePairsPage(page)}
                              style={{
                                padding: '6px 12px',
                                fontSize: 14,
                                border: '1px solid #d1d5db',
                                borderRadius: 6,
                                backgroundColor: activePairsPage === page ? '#007bff' : 'white',
                                color: activePairsPage === page ? 'white' : '#374151',
                                cursor: 'pointer',
                                fontWeight: activePairsPage === page ? 600 : 500,
                                minWidth: 36
                              }}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === activePairsPage - 2 ||
                          page === activePairsPage + 2
                        ) {
                          return (
                            <span key={page} style={{ padding: '6px 4px', color: '#9ca3af' }}>
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setActivePairsPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={activePairsPage === totalPages}
                      style={{
                        padding: '6px 12px',
                        fontSize: 14,
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        backgroundColor: activePairsPage === totalPages ? '#f3f4f6' : 'white',
                        color: activePairsPage === totalPages ? '#9ca3af' : '#374151',
                        cursor: activePairsPage === totalPages ? 'not-allowed' : 'pointer',
                        fontWeight: 500
                      }}
                    >
                      Next
                    </button>
                  </div>
          </div>
        )}
      </div>

      {/* Create Strategy Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {createModalStrategyType === 'CRYPTO'
                  ? 'Create Major Crypto Pairs Strategy (Beta)'
                  : 'Create Meme Coin Pairs Strategy'}
              </h3>
              <button
                onClick={() => {
                  if (createModalStrategyType === 'MEME') {
                    localStorage.setItem('memeCoinStrategyForm', JSON.stringify(createForm));
                  }
                  setAiSuggestionMeta(null);
                  setCreateModalStrategyType('MEME');
                  setShowCreateModal(false);
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              {/* AI Strategy Suggestion Indicator */}
              {aiSuggestionMeta && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#ede9fe',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #a78bfa',
                  fontSize: '13px',
                  color: '#5b21b6'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '16px' }}>🤖</span>
                    <strong style={{ fontSize: '14px' }}>
                      AI Market Analysis — {aiSuggestionMeta.regimeLabel}
                    </strong>
                    <span style={{
                      fontSize: '11px',
                      backgroundColor: '#ddd6fe',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      marginLeft: 'auto'
                    }}>
                      {Math.round(aiSuggestionMeta.confidence * 100)}% confidence
                    </span>
                  </div>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px', fontSize: '12px', lineHeight: '1.6' }}>
                    {aiSuggestionMeta.reasoning.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                  {aiSuggestionMeta.marketSnapshot && (
                    <div style={{ fontSize: '11px', marginTop: '8px', opacity: 0.9 }}>
                      Volatility: {String(aiSuggestionMeta.marketSnapshot.avgVolatilityPercent ?? '—')}% ·
                      Participation: {String(aiSuggestionMeta.marketSnapshot.participationRate ?? '—')}% ·
                      BTC: {String(aiSuggestionMeta.marketSnapshot.btcRegime ?? '—')}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', marginTop: '8px', fontStyle: 'italic' }}>
                    Review and edit all values before creating. Running bots are not affected until you submit.
                  </div>
                </div>
              )}
              {/* Optimal Settings Indicator */}
              {!aiSuggestionMeta && createForm.fixedOrderAmount === 50 && 
               createForm.stopLossType === 'CLASSIC' && 
               createForm.trailingStopLoss === 4.5 && 
               createForm.takeProfit === 7.0 && 
               createForm.marketType === 'FUTURES' && (
                <div style={{ 
                  padding: '12px 16px', 
                  backgroundColor: '#fef3c7', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  border: '1px solid #fbbf24',
                  fontSize: '13px',
                  color: '#92400e'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '16px' }}>⭐</span>
                    <strong style={{ fontSize: '14px' }}>Optimal Profit-Making Settings Applied</strong>
                  </div>
                  <div style={{ fontSize: '12px', marginTop: '6px', lineHeight: '1.5' }}>
                    These settings are optimized for profitability: $50 position size (reduces fee impact), 
                    Classic 4.5% stop loss (accounts for fees), 7% take profit, Futures market. 
                    Based on analysis: <code style={{ fontSize: '11px', backgroundColor: '#fde68a', padding: '2px 4px', borderRadius: '3px' }}>docs/Analysis/strategy-loss-analysis-and-fixes.md</code>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Strategy Name</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                  className="form-input"
                  placeholder="Enter strategy name"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Fixed Order Amount (USDT)</label>
                <input
                  type="number"
                  value={createForm.fixedOrderAmount || ''}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    // If empty, set to empty string (will be converted to 0 when needed)
                    if (rawValue === '' || rawValue === null || rawValue === undefined) {
                      setCreateForm({...createForm, fixedOrderAmount: 0});
                      return;
                    }
                    // Parse the number, which automatically removes leading zeros
                    const numValue = parseFloat(rawValue);
                    // Only update if it's a valid number
                    if (!isNaN(numValue) && numValue >= 0) {
                      setCreateForm({...createForm, fixedOrderAmount: numValue});
                    }
                  }}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Leverage</label>
                <input
                  type="number"
                  value={createForm.leverage}
                  onChange={(e) => setCreateForm({...createForm, leverage: Number(e.target.value)})}
                  className="form-input"
                  min="1"
                  max="10"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Maximum Open Positions</label>
                <select
                  value={createForm.maxOpenPositions || 3}
                  onChange={(e) => setCreateForm({...createForm, maxOpenPositions: Number(e.target.value)})}
                  className="form-input"
                  style={{ width: '100%', padding: '8px 12px' }}
                >
                  <option value={2}>2 positions (1 pair)</option>
                  <option value={3}>3 positions (max recommended)</option>
                  <option value={4}>4 positions (2 pairs)</option>
                  <option value={6}>6 positions (3 pairs)</option>
                  <option value={8}>8 positions (4 pairs)</option>
                  <option value={10}>10 positions (5 pairs)</option>
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  Maximum number of open positions at any time. When one position closes, the bot will open new positions to maintain this limit (up to the maximum). Recommended: 3 positions for efficient bankroll management.
                </small>
              </div>
              
              <div className="form-group">
                <label className="form-label">Market Type</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="marketType"
                      value="SPOT"
                      checked={createForm.marketType === 'SPOT'}
                      onChange={(e) => setCreateForm({...createForm, marketType: 'SPOT'})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Spot Market</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Trade spot pairs (e.g., DOGE-USDT, PEPE-USDT). No leverage, direct coin trading.
                      </div>
                    </div>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="marketType"
                      value="FUTURES"
                      checked={createForm.marketType === 'FUTURES'}
                      onChange={(e) => setCreateForm({...createForm, marketType: 'FUTURES'})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Futures Market</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Trade perpetual futures contracts (e.g., PEPEUSDTM, DOGEUSDTM). Supports leverage and funding rates.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Trading Account selection (strategy-level) */}
              <div className="form-group">
                <label className="form-label">Trading Account</label>
                {!canUseSharedKucoin && selectablePersonalKucoinAccounts.length === 0 && (
                  <div style={{ padding: '10px 12px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '6px', marginBottom: '8px' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                      <strong>No trading accounts available.</strong> You do not have permission to use the shared
                      system account. Please provision a personal KuCoin sub-account or ask a Super Admin to grant
                      the <em>Use Shared KuCoin Account</em> permission.
                    </p>
                  </div>
                )}
                <select
                  className="form-input"
                  value={createForm.kucoinConfigId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateForm({
                      ...createForm,
                      kucoinConfigId: value === '' ? undefined : Number(value),
                    });
                  }}
                  disabled={accountsLoading}
                >
                  {canUseSharedKucoin && (
                    <option value="">Shared KuCoin (System)</option>
                  )}
                  {!canUseSharedKucoin && selectablePersonalKucoinAccounts.length > 1 && createForm.kucoinConfigId == null && (
                    <option value="" disabled>Select a trading account</option>
                  )}
                  {selectablePersonalKucoinAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  {canUseSharedKucoin
                    ? 'Select which KuCoin API account this strategy will use for new trades. If left empty, the shared system account will be used.'
                    : 'Select which personal KuCoin sub-account this strategy will use for new trades.'}
                </small>
                {!canUseSharedKucoin && createForm.kucoinConfigId == null && selectablePersonalKucoinAccounts.length === 1 && (
                  <small style={{ fontSize: '12px', color: '#0369a1', display: 'block', marginTop: '4px' }}>
                    Using your personal account: {selectablePersonalKucoinAccounts[0].name}
                  </small>
                )}
                {accountsError && (
                  <small style={{ fontSize: '12px', color: '#b91c1c', display: 'block', marginTop: '4px' }}>
                    {accountsError}
                  </small>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ marginTop: 8, fontSize: 13 }}
                  onClick={() => {
                    setSubaccountWizardStrategyId(undefined);
                    setShowSubaccountWizard(true);
                  }}
                >
                  + Create funded sub-account (master → sub)
                </button>
              </div>

              {/* BTC Correlation Filter (Optional) - Create modal */}
              <div className="form-group" style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
                  Bitcoin Correlation Filter (Optional)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={createForm.btcCorrelationEnabled === true}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        btcCorrelationEnabled: e.target.checked,
                      })
                    }
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 14, color: '#374151' }}>
                    Enable BTC correlation filter for this strategy
                  </span>
                </div>

                {createForm.btcCorrelationEnabled && (
                  <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
                    <div className="form-group">
                      <label className="form-label">BTC EMA Length</label>
                      <input
                        type="number"
                        min={10}
                        max={200}
                        value={createForm.btcEmaLength ?? 50}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setCreateForm({ ...createForm, btcEmaLength: undefined });
                            return;
                          }
                          const n = Number(raw);
                          if (!Number.isFinite(n)) return;
                          setCreateForm({ ...createForm, btcEmaLength: n });
                        }}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">BTC EMA Timeframe</label>
                      <select
                        className="form-input"
                        value={createForm.btcEmaTimeframe ?? '5m'}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            btcEmaTimeframe: e.target.value as '5m' | '15m',
                          })
                        }
                      >
                        <option value="5m">5 minutes</option>
                        <option value="15m">15 minutes</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Min BTC 5m Move Up (%) to Allow Longs</label>
                      <input
                        type="number"
                        min={0.1}
                        max={5}
                        step={0.1}
                        value={createForm.btcMomentumLongThreshold ?? 0.5}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setCreateForm({ ...createForm, btcMomentumLongThreshold: undefined });
                            return;
                          }
                          const n = Number(raw);
                          if (!Number.isFinite(n)) return;
                          setCreateForm({ ...createForm, btcMomentumLongThreshold: n });
                        }}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Min BTC 5m Move Down (%) to Allow Shorts</label>
                      <input
                        type="number"
                        min={0.1}
                        max={5}
                        step={0.1}
                        value={createForm.btcMomentumShortThreshold ?? 0.5}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setCreateForm({ ...createForm, btcMomentumShortThreshold: undefined });
                            return;
                          }
                          const n = Number(raw);
                          if (!Number.isFinite(n)) return;
                          setCreateForm({ ...createForm, btcMomentumShortThreshold: n });
                        }}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Block Trades if BTC 15m Range &lt; (%)</label>
                      <input
                        type="number"
                        min={0.1}
                        max={5}
                        step={0.1}
                        value={createForm.btcRangeNoTradeThreshold ?? 0.8}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setCreateForm({ ...createForm, btcRangeNoTradeThreshold: undefined });
                            return;
                          }
                          const n = Number(raw);
                          if (!Number.isFinite(n)) return;
                          setCreateForm({ ...createForm, btcRangeNoTradeThreshold: n });
                        }}
                        className="form-input"
                      />
                    </div>

                    <div
                      className="form-group"
                      style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <input
                          type="checkbox"
                          checked={createForm.btcDumpProtectionEnabled !== false}
                          onChange={(e) =>
                            setCreateForm({
                              ...createForm,
                              btcDumpProtectionEnabled: e.target.checked,
                            })
                          }
                          style={{ width: 18, height: 18, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: 14, color: '#374151' }}>Enable BTC dump protection</span>
                      </div>

                      {createForm.btcDumpProtectionEnabled !== false && (
                        <div style={{ display: 'grid', gap: 12 }}>
                          <div className="form-group">
                            <label className="form-label">Dump Threshold (%) over 5–10 minutes</label>
                            <input
                              type="number"
                              min={0.5}
                              max={10}
                              step={0.1}
                              value={createForm.btcDumpThreshold ?? 1.5}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                  setCreateForm({ ...createForm, btcDumpThreshold: undefined });
                                  return;
                                }
                                const n = Number(raw);
                                if (!Number.isFinite(n)) return;
                                setCreateForm({ ...createForm, btcDumpThreshold: n });
                              }}
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Cooldown After Dump (minutes)</label>
                            <input
                              type="number"
                              min={1}
                              max={120}
                              step={1}
                              value={createForm.btcCooldownMinutes ?? 20}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                  setCreateForm({ ...createForm, btcCooldownMinutes: undefined });
                                  return;
                                }
                                const n = Number(raw);
                                if (!Number.isFinite(n)) return;
                                setCreateForm({ ...createForm, btcCooldownMinutes: Math.trunc(n) });
                              }}
                              className="form-input"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* BTC Higher-Timeframe Trend Confirmation — Create modal */}
              <div className="form-group" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ fontSize: 15, fontWeight: 600, color: '#7c3aed', marginBottom: 6 }}>
                  📈 Higher-Timeframe Trend Confirmation <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(optional)</span>
                </label>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                  Only open longs when BTC is <strong>above</strong> its 1H/4H EMA, and shorts when <strong>below</strong>.
                  Blocks trades in BTC "dead zones" where meme coins decouple from BTC momentum.
                  Leave timeframe set to <em>Disabled</em> to skip this filter entirely.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Trend Timeframe</label>
                    <select
                      className="form-input"
                      value={createForm.btcTrendTimeframe ?? ''}
                      onChange={(e) => setCreateForm({ ...createForm, btcTrendTimeframe: e.target.value })}
                    >
                      <option value="">Disabled</option>
                      <option value="1h">1 Hour</option>
                      <option value="4h">4 Hours</option>
                    </select>
                  </div>
                  {createForm.btcTrendTimeframe && (
                    <div className="form-group">
                      <label className="form-label">EMA Period</label>
                      <input
                        type="number"
                        min={5}
                        max={200}
                        step={1}
                        value={createForm.btcTrendEmaPeriod ?? 21}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n) && n >= 5) setCreateForm({ ...createForm, btcTrendEmaPeriod: Math.trunc(n) });
                        }}
                        className="form-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* ── Participation Filter — Create modal ── */}
              <div className="form-group" style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                  {participationFilterTitle(undefined, createModalStrategyType === 'CRYPTO')} (Optional)
                </label>
                <p style={{ margin: '0 0 10px 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                  {participationFilterDescription(undefined, createModalStrategyType === 'CRYPTO')}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={createForm.memeParticipationFilterEnabled === true}
                    onChange={e => setCreateForm({ ...createForm, memeParticipationFilterEnabled: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 14, color: '#374151' }}>
                    {participationFilterEnableLabel(undefined, createModalStrategyType === 'CRYPTO')}
                  </span>
                </div>
                {createForm.memeParticipationFilterEnabled && (
                  <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                    <div className="form-group">
                      <label className="form-label">Min Participation Rate (%)</label>
                      <input
                        type="number"
                        min={5}
                        max={100}
                        step={5}
                        value={
                          createForm.minParticipationRate != null
                            ? Math.round(createForm.minParticipationRate * 100)
                            : 25
                        }
                        onChange={e => {
                          const n = Number(e.target.value);
                          if (Number.isFinite(n) && n >= 0 && n <= 100)
                            setCreateForm({ ...createForm, minParticipationRate: n / 100 });
                        }}
                        className="form-input"
                        placeholder="e.g. 25"
                      />
                      <small style={{ fontSize: 12, color: '#6b7280' }}>
                        Minimum % of selected coins that must be active before entries are allowed.
                        Default: <strong>25%</strong>. Requires <strong>Manual</strong> coin selection mode.
                      </small>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Micro-Reversal Filter — Create modal ── */}
              <div className="form-group" style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                  ⚡ Micro-Reversal Filter (Optional)
                </label>
                <p style={{ margin: '0 0 10px 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                  Blocks new entries when BTC drops between <strong>−0.50%</strong> and <strong>−0.10%</strong> from a recent 5m local high (chop / fake-window trap). Separate from BTC correlation. Default off.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={createForm.microReversalFilterEnabled === true}
                    onChange={e => setCreateForm({ ...createForm, microReversalFilterEnabled: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 14, color: '#374151' }}>
                    Enable Micro-Reversal Filter for this strategy
                  </span>
                </div>
                {createForm.microReversalFilterEnabled && (
                  <div style={{ display: 'grid', gap: 10, marginTop: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                    <div className="form-group">
                      <label className="form-label">Min drop % (block floor)</label>
                      <input type="number" step="0.01" value={createForm.microReversalMinDropPercent ?? -0.5}
                        onChange={e => setCreateForm({ ...createForm, microReversalMinDropPercent: parseFloat(e.target.value) })}
                        className="form-input" />
                      <small style={{ fontSize: 12, color: '#6b7280' }}>Default: −0.50%</small>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Max drop % (block ceiling)</label>
                      <input type="number" step="0.01" value={createForm.microReversalMaxDropPercent ?? -0.1}
                        onChange={e => setCreateForm({ ...createForm, microReversalMaxDropPercent: parseFloat(e.target.value) })}
                        className="form-input" />
                      <small style={{ fontSize: 12, color: '#6b7280' }}>Default: −0.10%</small>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Lookback candles (5m)</label>
                      <input type="number" min={3} max={60} step={1} value={createForm.microReversalLookbackCandles ?? 12}
                        onChange={e => setCreateForm({ ...createForm, microReversalLookbackCandles: parseInt(e.target.value, 10) })}
                        className="form-input" />
                      <small style={{ fontSize: 12, color: '#6b7280' }}>Default: 12 (1 hour)</small>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Coin Selection Oracle — Create modal ───────────────── */}
              <div style={{ marginTop: 20, padding: '14px', border: `1px solid ${createForm.oracleEnabled ? '#2563eb' : '#e5e7eb'}`, borderRadius: '8px', backgroundColor: createForm.oracleEnabled ? '#eff6ff' : 'transparent' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>🔮 Coin Selection Oracle</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6b7280' }}>
                      Score every coin with RSI, momentum, volume, funding rate &amp; EMA — pick the best LONG+SHORT pair.
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: createForm.oracleEnabled ? '#1d4ed8' : '#6b7280', flexShrink: 0, marginLeft: 12 }}>
                    <input
                      type="checkbox"
                      checked={!!createForm.oracleEnabled}
                      onChange={e => setCreateForm({ ...createForm, oracleEnabled: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: '#1d4ed8', cursor: 'pointer' }}
                    />
                    {createForm.oracleEnabled ? 'Enabled' : 'Disabled'}
                  </label>
                </div>
                {createForm.oracleEnabled && (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600 }}>
                      Min Confidence Threshold (0.0 – 1.0)
                    </label>
                    <input
                      type="number" step="0.05" min="0.1" max="1.0"
                      value={createForm.oracleConfidenceThreshold ?? 0.5}
                      onChange={e => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0 && v <= 1)
                          setCreateForm({ ...createForm, oracleConfidenceThreshold: v });
                      }}
                      className="form-input"
                      placeholder="0.50"
                    />
                    <small style={{ fontSize: 11, color: '#6b7280' }}>
                      Below this score the oracle falls back to random shuffle. Default: <strong>0.50</strong>.
                    </small>

                    {/* Signal Weights — Create modal */}
                    <div style={{ marginTop: '14px', borderTop: '1px dashed #bfdbfe', paddingTop: '12px' }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#1e3a8a' }}>
                        Signal Weights <span style={{ fontWeight: 400, color: '#6b7280' }}>(leave blank to use global defaults)</span>
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {[
                          { key: 'oracleWeightMomentum', label: 'Momentum',    defaultVal: 0.25 },
                          { key: 'oracleWeightRsi',      label: 'RSI',         defaultVal: 0.30 },
                          { key: 'oracleWeightVolume',   label: 'Volume',      defaultVal: 0.20 },
                          { key: 'oracleWeightFunding',  label: 'Funding Rate',defaultVal: 0.15 },
                          { key: 'oracleWeightEma',      label: 'EMA Trend',   defaultVal: 0.10 },
                        ].map(({ key, label, defaultVal }) => (
                          <div key={key}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: 3 }}>
                              {label}
                            </label>
                            <input
                              type="number" step="0.05" min="0" max="1.0"
                              value={(createForm as any)[key] ?? ''}
                              placeholder={`Global (${defaultVal})`}
                              onChange={e => {
                                const raw = e.target.value;
                                const v = raw === '' ? null : parseFloat(raw);
                                setCreateForm({ ...createForm, [key]: (v !== null && !isNaN(v) && v >= 0 && v <= 1) ? v : undefined } as any);
                              }}
                              className="form-input"
                            />
                          </div>
                        ))}
                      </div>
                      <small style={{ fontSize: 11, color: '#6b7280' }}>
                        Weights should ideally sum to 1.0. Blank = use <code>application.properties</code> global value.
                      </small>
                    </div>
                  </div>
                )}
              </div>

              {/* Minimum Profit Exit — Create modal */}
              <div className="form-group" style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
                  🛡️ Minimum Profit Before TSL Exit (%)
                </label>
                <p style={{ margin: '0 0 10px 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                  TSL will NOT close the position until this profit level has been reached.
                  Set to <strong>0</strong> to disable (default). Recommended: <strong>1.0</strong>
                </p>
                <input
                  type="number"
                  min={0}
                  max={5}
                  step={0.1}
                  value={createForm.minProfitExitPercent ?? 0}
                  onChange={(e) => {
                    const n = parseFloat(e.target.value);
                    if (Number.isFinite(n) && n >= 0) setCreateForm({ ...createForm, minProfitExitPercent: n });
                  }}
                  className="form-input"
                  placeholder="e.g. 1.0"
                />
              </div>

              {/* Entry Filters (Optional) — Create modal */}
              <div className="form-group" style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
                  Entry Filters (Optional)
                </label>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
                  Pre-trade validation checks. All enabled filters must pass before any order is placed.
                  Filters run on <strong>5-minute closed candles</strong>. Disabled by default — safe to enable once live.
                </div>

                {/* Master toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={createForm.entryFiltersEnabled === true}
                    onChange={(e) => setCreateForm({ ...createForm, entryFiltersEnabled: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>
                    Enable Entry Filters for this strategy
                  </span>
                </div>

                {createForm.entryFiltersEnabled && (
                  <div style={{ display: 'grid', gap: 16, marginTop: 8 }}>

                    {/* --- Funding Rate Guard --- */}
                    <div style={{ padding: 14, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <input
                          type="checkbox"
                          checked={createForm.fundingRateFilterEnabled !== false}
                          onChange={(e) => setCreateForm({ ...createForm, fundingRateFilterEnabled: e.target.checked })}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#0369a1' }}>
                          🏦 Funding Rate Guard <span style={{ fontWeight: 400, color: '#6b7280' }}>(longs only)</span>
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                        Blocks a long entry when the current KuCoin Futures funding rate exceeds the threshold.
                        Shorts are never affected.
                      </div>
                      {createForm.fundingRateFilterEnabled !== false && (
                        <div className="form-group">
                          <label className="form-label">Max Funding Rate (%)</label>
                          <input
                            type="number"
                            min={0.01}
                            max={1}
                            step={0.01}
                            value={createForm.maxFundingRate ?? 0.05}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') { setCreateForm({ ...createForm, maxFundingRate: undefined }); return; }
                              const n = Number(raw);
                              if (!Number.isFinite(n)) return;
                              setCreateForm({ ...createForm, maxFundingRate: n });
                            }}
                            className="form-input"
                          />
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                            Default 0.05% — sent to backend as decimal (÷100). Rejection logged as FUNDING_RATE_EXCEEDED.
                          </div>
                        </div>
                      )}
                    </div>

                    {/* --- RSI Filter --- */}
                    <div style={{ padding: 14, background: '#fdf4ff', borderRadius: 8, border: '1px solid #e9d5ff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <input
                          type="checkbox"
                          checked={createForm.rsiFilterEnabled !== false}
                          onChange={(e) => setCreateForm({ ...createForm, rsiFilterEnabled: e.target.checked })}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#7e22ce' }}>
                          📊 RSI Entry Filter <span style={{ fontWeight: 400, color: '#6b7280' }}>(both sides)</span>
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                        Uses RSI(14) with Wilder's smoothing on 5-minute closed candles.
                        Rejection logged as RSI_FILTER_FAILED.
                      </div>
                      {createForm.rsiFilterEnabled !== false && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div className="form-group">
                            <label className="form-label">RSI Min — Long entries</label>
                            <input
                              type="number"
                              min={1}
                              max={99}
                              step={1}
                              value={createForm.rsiLongMin ?? 55}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') { setCreateForm({ ...createForm, rsiLongMin: undefined }); return; }
                                const n = Number(raw);
                                if (!Number.isFinite(n)) return;
                                setCreateForm({ ...createForm, rsiLongMin: Math.trunc(n) });
                              }}
                              className="form-input"
                            />
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>RSI must be &gt; this value</div>
                          </div>
                          <div className="form-group">
                            <label className="form-label">RSI Max — Short entries</label>
                            <input
                              type="number"
                              min={1}
                              max={99}
                              step={1}
                              value={createForm.rsiShortMax ?? 45}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') { setCreateForm({ ...createForm, rsiShortMax: undefined }); return; }
                                const n = Number(raw);
                                if (!Number.isFinite(n)) return;
                                setCreateForm({ ...createForm, rsiShortMax: Math.trunc(n) });
                              }}
                              className="form-input"
                            />
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>RSI must be &lt; this value</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* --- EMA Trend Filter --- */}
                    <div style={{ padding: 14, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <input
                          type="checkbox"
                          checked={createForm.emaFilterEnabled !== false}
                          onChange={(e) => setCreateForm({ ...createForm, emaFilterEnabled: e.target.checked })}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#15803d' }}>
                          📈 EMA Trend Filter <span style={{ fontWeight: 400, color: '#6b7280' }}>(longs only)</span>
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                        Price must be above the EMA to open a long. Reuses candle data from RSI fetch — no extra API call.
                        Rejection logged as EMA_TREND_FILTER_FAILED.
                      </div>
                      {createForm.emaFilterEnabled !== false && (
                        <div className="form-group">
                          <label className="form-label">EMA Period</label>
                          <input
                            type="number"
                            min={5}
                            max={200}
                            step={1}
                            value={createForm.emaPeriod ?? 20}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') { setCreateForm({ ...createForm, emaPeriod: undefined }); return; }
                              const n = Number(raw);
                              if (!Number.isFinite(n)) return;
                              setCreateForm({ ...createForm, emaPeriod: Math.trunc(n) });
                            }}
                            className="form-input"
                          />
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Default 20 periods on 5-minute candles</div>
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </div>

              {/* Order Type - Show for both SPOT and FUTURES */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ fontSize: '15px', fontWeight: 600, marginBottom: '12px' }}>Order Type</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="orderType"
                      value="MARKET"
                      checked={(createForm.orderType || 'MARKET') === 'MARKET'}
                      onChange={() => setCreateForm({ ...createForm, orderType: 'MARKET' })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Market Order</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Execute immediately at the current market price. Higher exchange fees (taker fees), best for fast entry/exit.
                      </div>
                    </div>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="orderType"
                      value="LIMIT"
                      checked={createForm.orderType === 'LIMIT'}
                      onChange={() => setCreateForm({ ...createForm, orderType: 'LIMIT' })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Limit Order (Lower Fees)</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Bot places orders at a calculated price (around the current price) to try to act as a maker. Lower exchange fees, but fills may take longer or occasionally miss.
                      </div>
                    </div>
                  </label>
                </div>
                
                {/* Advanced Limit Order Options - Only show when LIMIT is selected */}
                {createForm.orderType === 'LIMIT' && (
                  <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                      Advanced Limit Order Settings
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label className="form-label" style={{ fontSize: '13px', marginBottom: '6px' }}>
                        Price Offset (%)
                      </label>
                      <input
                        type="number"
                        min="0.01"
                        max="5"
                        step="0.01"
                        value={createForm.limitOffset || 0.1}
                        onChange={(e) => setCreateForm({ ...createForm, limitOffset: parseFloat(e.target.value) || 0.1 })}
                        className="form-input"
                        style={{ width: '100%', padding: '8px 12px' }}
                      />
                      <small style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                        How far from current price to place limit order. Default: 0.1% (0.001). 
                        Lower = closer to market (faster fill, less maker benefit). Higher = further from market (slower fill, better maker fees).
                      </small>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: '13px', marginBottom: '6px' }}>
                        Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="300"
                        step="5"
                        value={createForm.limitTimeoutSeconds || 30}
                        onChange={(e) => setCreateForm({ ...createForm, limitTimeoutSeconds: parseInt(e.target.value) || 30 })}
                        className="form-input"
                        style={{ width: '100%', padding: '8px 12px' }}
                      />
                      <small style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                        If limit order doesn't fill within this time, bot will cancel it and place a market order instead. 
                        Default: 30 seconds. Range: 5-300 seconds.
                      </small>
                    </div>
                  </div>
                )}
              </div>

              {/* Margin Mode - Only show for Futures */}
              {createForm.marketType === 'FUTURES' && (
                <div className="form-group">
                  <label className="form-label">Margin Mode (Futures Only)</label>
                  <select
                    value={createForm.marginMode || 'CROSS'}
                    onChange={(e) => setCreateForm({...createForm, marginMode: e.target.value as 'CROSS' | 'ISOLATED'})}
                    className="form-input"
                    style={{ width: '100%', padding: '8px 12px' }}
                  >
                    <option value="CROSS">Cross Margin (Default)</option>
                    <option value="ISOLATED">Isolated Margin</option>
                  </select>
                  <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                    Cross: Shared margin across positions. Isolated: Each position has its own margin (safer for pairs trading).
                  </small>
                </div>
              )}

              {/* Stop Loss Type Section */}
              <div className="form-group">
                <label className="form-label">Stop Loss Type</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="stopLossType"
                      value="CLASSIC"
                      checked={createForm.stopLossType === 'CLASSIC'}
                      onChange={(e) => setCreateForm({...createForm, stopLossType: 'CLASSIC'})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Classic Stop Loss</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Fixed price level set at entry. Closes position if price hits this level.
                      </div>
                    </div>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="stopLossType"
                      value="TRAILING"
                      checked={createForm.stopLossType === 'TRAILING'}
                      onChange={(e) => setCreateForm({...createForm, stopLossType: 'TRAILING'})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Trailing Stop Loss</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Dynamic stop loss that moves with price in your favor, stays fixed if price moves against you.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {createForm.stopLossType === 'CLASSIC' ? 'Stop Loss (%)' : 'Trailing Stop Loss (%)'}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={createForm.trailingStopLoss || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty string or convert to number
                    setCreateForm({...createForm, trailingStopLoss: value === '' ? 0 : Number(value)});
                  }}
                  className="form-input"
                  placeholder={createForm.stopLossType === 'CLASSIC' ? 'Fixed stop loss percentage' : 'Trailing stop loss percentage'}
                />
                <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  {createForm.stopLossType === 'CLASSIC' 
                    ? 'Fixed percentage below (long) or above (short) entry price. Position closes if price reaches this level.'
                    : 'Percentage that trails the price. Moves up (long) or down (short) as price moves favorably, locks in when price reverses.'}
                </small>
              </div>

              {/* Asymmetric Stop Loss */}
              <div className="form-group">
                <div style={{ padding: '12px 14px', background: '#f0f9ff', borderRadius: '8px', border: '1px dashed #93c5fd' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#1d4ed8', fontWeight: 600 }}>⚡ Asymmetric Stop Loss (Optional)</p>
                  <p style={{ margin: '0 0 10px 0', fontSize: '11px', color: '#6b7280' }}>Override the shared SL per direction. Leave blank to use shared value above for both sides.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>🔵 Long SL (%)</label>
                      <input
                        type="number" step="0.1" min="0.01" max="99.99"
                        placeholder="e.g. 3.5"
                        value={createForm.longTrailingStopLoss ?? ''}
                        onChange={e => setCreateForm({...createForm, longTrailingStopLoss: e.target.value === '' ? undefined : Number(e.target.value)})}
                        className="form-input"
                        style={{ fontSize: '13px' }}
                      />
                      <small style={{ fontSize: '11px', color: '#6b7280' }}>Wider — longs need more room</small>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>🔴 Short SL (%)</label>
                      <input
                        type="number" step="0.1" min="0.01" max="99.99"
                        placeholder="e.g. 3"
                        value={createForm.shortTrailingStopLoss ?? ''}
                        onChange={e => setCreateForm({...createForm, shortTrailingStopLoss: e.target.value === '' ? undefined : Number(e.target.value)})}
                        className="form-input"
                        style={{ fontSize: '13px' }}
                      />
                      <small style={{ fontSize: '11px', color: '#6b7280' }}>Tighter — shorts move faster</small>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Take Profit (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={createForm.takeProfit || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateForm({...createForm, takeProfit: value === '' ? 0 : Number(value)});
                  }}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Basket Take Profit (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={createForm.basketTakeProfit || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateForm({...createForm, basketTakeProfit: value === '' ? 0 : Number(value)});
                  }}
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginTop: 8, paddingTop: 12, borderTop: '1px dashed #e5e7eb' }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!createForm.enableUniversalBasketTakeProfit}
                    onChange={(e) => setCreateForm({ ...createForm, enableUniversalBasketTakeProfit: e.target.checked })}
                    style={{ width: 16, height: 16 }}
                  />
                  <span>Enable Universal Basket Take Profit (strategy-wide)</span>
                </label>
                <small style={{ fontSize: 12, color: '#6b7280', display: 'block', marginTop: 4 }}>
                  When aggregate unrealized P&amp;L across all open pair legs and hedge legs reaches the target % of total reference notional, close the entire book. Default off.
                </small>
                {createForm.enableUniversalBasketTakeProfit && (
                  <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 140px' }}>
                      <label className="form-label">Universal target (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={createForm.universalBasketTakeProfit ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCreateForm({ ...createForm, universalBasketTakeProfit: v === '' ? undefined : Number(v) });
                        }}
                        className="form-input"
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <label className="form-label">Min open legs</label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={createForm.universalBasketMinOpenLegs ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCreateForm({ ...createForm, universalBasketMinOpenLegs: v === '' ? undefined : parseInt(v, 10) });
                        }}
                        className="form-input"
                        placeholder="2"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Profit-Lock Micro Trailing Stop */}
              <div
                className="form-group"
                style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}
              >
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={!!createForm.enableMicroTsl}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        enableMicroTsl: e.target.checked,
                      })
                    }
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span>Enable Profit-Lock Micro Trailing Stop</span>
                </label>
                <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  When unrealized PnL reaches the activation %, arm a micro trailing stop that stays
                  a fixed % behind the highest price after activation. Trails in your favor only
                  and does not replace the main Take Profit.
                </small>

                {createForm.enableMicroTsl && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">Activation PnL (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={createForm.microTslActivationPercent ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCreateForm({
                            ...createForm,
                            microTslActivationPercent: value === '' ? undefined : Number(value),
                          });
                        }}
                        className="form-input"
                        placeholder="e.g. 4 (arm at +4%)"
                      />
                    </div>

                    <div style={{ flex: 1 }}>
                      <label className="form-label">Micro-TSL Distance (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={createForm.microTslDistancePercent ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCreateForm({
                            ...createForm,
                            microTslDistancePercent: value === '' ? undefined : Number(value),
                          });
                        }}
                        className="form-input"
                        placeholder="e.g. 1 (1% behind)"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Coin Selection Section */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ marginBottom: '12px', fontWeight: 600 }}>
                  {createModalStrategyType === 'CRYPTO' ? 'Major Coin Selection' : 'Meme Coin Selection'}
                </label>
                
                {/* Selection Mode Radio Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                  {createModalStrategyType === 'CRYPTO' ? (
                    <>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="coinSelectionMode"
                          value="TOP_100"
                          checked={createForm.coinSelectionMode === 'TOP_100' || createForm.coinSelectionMode === 'TOP_N'}
                          onChange={() => handleCoinSelectionModeChange('TOP_100')}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Top 100 Majors by Volume</span>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Liquid non-meme USDT pairs (BTC, ETH, SOL, …). Meme bases excluded by default.
                          </div>
                        </div>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="coinSelectionMode"
                          value="STOCK_INDEX"
                          checked={createForm.coinSelectionMode === 'STOCK_INDEX'}
                          onChange={() => handleCoinSelectionModeChange('STOCK_INDEX')}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Stock-Index Perps Only</span>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            KuCoin equity-linked perpetuals only (MU, TSLA, MSTR, …). FUTURES required.
                          </div>
                        </div>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="coinSelectionMode"
                          value="MANUAL"
                          checked={createForm.coinSelectionMode === 'MANUAL'}
                          onChange={() => handleCoinSelectionModeChange('MANUAL')}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Manually Select Majors</span>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Pick BTC, ETH, SOL, MU, TSLA, etc. from the combined universe
                          </div>
                        </div>
                      </label>
                      {(createForm.coinSelectionMode === 'TOP_100' || createForm.coinSelectionMode === 'TOP_N') && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 26, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={createForm.includeStockIndexPerps !== false}
                            onChange={e => setCreateForm({ ...createForm, includeStockIndexPerps: e.target.checked })}
                            style={{ width: 16, height: 16, accentColor: '#086afb' }}
                          />
                          <span style={{ fontSize: '12px', color: '#374151' }}>
                            Include stock-index perps (MU, TSLA, …) in Top 100 universe
                          </span>
                        </label>
                      )}
                    </>
                  ) : (
                  <>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="coinSelectionMode"
                      value="TOP_N"
                      checked={createForm.coinSelectionMode === 'TOP_N'}
                      onChange={(e) => handleCoinSelectionModeChange('TOP_N')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Use Top N by Volume</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Automatically select the top N meme coins by 24h trading volume
                      </div>
                    </div>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="coinSelectionMode"
                      value="MANUAL"
                      checked={createForm.coinSelectionMode === 'MANUAL'}
                      onChange={(e) => handleCoinSelectionModeChange('MANUAL')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Manually Select Coins</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Choose specific meme coins from the available list
                      </div>
                    </div>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="coinSelectionMode"
                      value="ALL_COINS"
                      checked={createForm.coinSelectionMode === 'ALL_COINS'}
                      onChange={(e) => handleCoinSelectionModeChange('ALL_COINS')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Select from All Coins</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Choose from all available coins (not just meme coins)
                      </div>
                    </div>
                  </label>
                  </>
                  )}
                </div>

                {/* Top N / Top 100 / Stock Index Selection */}
                {(createForm.coinSelectionMode === 'TOP_N' || createForm.coinSelectionMode === 'TOP_100' || createForm.coinSelectionMode === 'STOCK_INDEX') && createModalStrategyType === 'CRYPTO' && (
                  <div style={{ marginTop: '12px' }}>
                    <label className="form-label" style={{ marginBottom: '8px' }}>
                      {createForm.coinSelectionMode === 'STOCK_INDEX' ? 'Number of Stock-Index Perps' : 'Number of Coins (max 100 for majors)'}
                    </label>
                    <select
                      value={createForm.coinLimit || (createForm.coinSelectionMode === 'STOCK_INDEX' ? 50 : 100)}
                      onChange={(e) => setCreateForm({...createForm, coinLimit: Number(e.target.value)})}
                      className="form-input"
                      style={{ width: '100%', padding: '8px 12px' }}
                    >
                      <option value={10}>10 coins</option>
                      <option value={20}>20 coins</option>
                      <option value={50}>50 coins</option>
                      <option value={100}>100 coins</option>
                    </select>
                  </div>
                )}

                {/* Top N Selection — meme only */}
                {(createForm.coinSelectionMode === 'TOP_N' || createForm.coinSelectionMode === 'TOP_100') && createModalStrategyType !== 'CRYPTO' && (
                  <div style={{ marginTop: '12px' }}>
                    <label className="form-label" style={{ marginBottom: '8px' }}>Number of Coins</label>
                    <select
                      value={createForm.coinLimit || 10}
                      onChange={(e) => setCreateForm({...createForm, coinLimit: Number(e.target.value)})}
                      className="form-input"
                      style={{ width: '100%', padding: '8px 12px' }}
                    >
                      <option value={5}>5 coins</option>
                      <option value={10}>10 coins</option>
                      <option value={15}>15 coins</option>
                      <option value={20}>20 coins</option>
                      <option value={25}>25 coins</option>
                      <option value={30}>30 coins</option>
                      <option value={50}>50 coins</option>
                      <option value={75}>75 coins</option>
                      <option value={100}>100 coins</option>
                      <option value={999}>All Available</option>
                    </select>
                    <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                      The strategy will use the top {createForm.coinLimit === 999 ? 'available' : createForm.coinLimit} coins by 24h trading volume
                    </small>
                  </div>
                )}

                {/* All Coins Selection */}
                {createForm.coinSelectionMode === 'ALL_COINS' && (
                  <div style={{ marginTop: '12px' }}>
                    {loadingAllCoins ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                        Loading all available coins...
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <label className="form-label" style={{ marginBottom: 0 }}>Select Coins</label>
                          <button
                            type="button"
                            onClick={() => {
                              if (createForm.selectedCoins?.length === allCoins.length) {
                                setCreateForm({ ...createForm, selectedCoins: [] });
                              } else {
                                setCreateForm({ ...createForm, selectedCoins: [...allCoins] });
                              }
                            }}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              backgroundColor: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              color: '#374151'
                            }}
                          >
                            {createForm.selectedCoins?.length === allCoins.length ? 'Clear All' : 'Select All'}
                          </button>
                        </div>
                        <div style={{
                          maxHeight: '300px',
                          overflowY: 'auto',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          minHeight: '100px'
                        }}>
                          {allCoins.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '2px 4px', rowGap: '2px' }}>
                              {allCoins.map((coin) => (
                                <label
                                  key={coin}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    cursor: 'pointer',
                                    padding: '3px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: (createForm.selectedCoins || []).includes(coin) ? '#e0f2fe' : 'transparent',
                                    border: (createForm.selectedCoins || []).includes(coin) ? '1px solid #086afb' : '1px solid transparent',
                                    transition: 'all 0.2s',
                                    lineHeight: '1.2'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={(createForm.selectedCoins || []).includes(coin)}
                                    onChange={() => handleCoinToggle(coin)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0, margin: 0 }}
                                  />
                                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151', lineHeight: '1.2' }}>{coin}</span>
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '8px' }}>
                          Selected: {(createForm.selectedCoins || []).length} coin{(createForm.selectedCoins || []).length !== 1 ? 's' : ''}
                          {(createForm.selectedCoins || []).length < 2 && (
                            <span style={{ color: '#dc2626', marginLeft: '4px' }}> (Minimum 2 coins required)</span>
                          )}
                        </small>
                      </>
                    )}
                  </div>
                )}

                {/* Manual Selection */}
                {createForm.coinSelectionMode === 'MANUAL' && (
                  <div style={{ marginTop: '12px' }}>
                    {loadingCoins ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                        Loading available coins...
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <label className="form-label" style={{ marginBottom: 0 }}>Select Coins</label>
                          <button
                            type="button"
                            onClick={handleSelectAllCoins}
                            style={{
                              padding: '4px 12px',
                              fontSize: '12px',
                              backgroundColor: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              color: '#374151'
                            }}
                          >
                            {createForm.selectedCoins?.length === availableCoins.length ? 'Clear All' : 'Select All'}
                          </button>
                        </div>
                        <div style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          minHeight: '100px'
                        }}>
                          {availableCoins.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '2px 4px', rowGap: '2px' }}>
                              {availableCoins.map((coin) => (
                                <label
                                  key={coin}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    cursor: 'pointer',
                                    padding: '3px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: (createForm.selectedCoins || []).includes(coin) ? '#e0f2fe' : 'transparent',
                                    border: (createForm.selectedCoins || []).includes(coin) ? '1px solid #086afb' : '1px solid transparent',
                                    transition: 'all 0.2s',
                                    lineHeight: '1.2'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={(createForm.selectedCoins || []).includes(coin)}
                                    onChange={() => handleCoinToggle(coin)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0, margin: 0 }}
                                  />
                                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151', lineHeight: '1.2' }}>{coin}</span>
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '8px' }}>
                          Selected: {(createForm.selectedCoins || []).length} coin{(createForm.selectedCoins || []).length !== 1 ? 's' : ''}
                          {(createForm.selectedCoins || []).length < 2 && (
                            <span style={{ color: '#dc2626', marginLeft: '4px' }}> (Minimum 2 coins required)</span>
                          )}
                        </small>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Strategy Configuration Checkboxes */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ marginBottom: '12px', fontWeight: 600 }}>Strategy Configuration</label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createForm.enableStopLossPerCoin ?? true}
                      onChange={(e) => setCreateForm({...createForm, enableStopLossPerCoin: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      Enable Trading Stop Loss for each coin separately
                    </span>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createForm.enableTakeProfitPerCoin ?? true}
                      onChange={(e) => setCreateForm({...createForm, enableTakeProfitPerCoin: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      Enable Take Profit for each coin separately
                    </span>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createForm.enableBasketTakeProfit ?? true}
                      onChange={(e) => setCreateForm({...createForm, enableBasketTakeProfit: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      Enable Basket Target Profit for combined
                    </span>
                  </label>
                </div>
              </div>

              {/* Market Activity Threshold Section - "Market is Alive" Signal */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontSize: '15px', fontWeight: 600, color: '#374151' }}>
                  🚦 Market Activity Threshold (Optional)
                </label>
                <p style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', marginBottom: '12px' }}>
                  Minimum average absolute 24h change of top 10 meme coins. Acts as a "market is alive" signal. 
                  If average 24h change is below this threshold, new pairs won't be created. Leave empty to disable this check.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={createForm.minMarketActivityThreshold ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCreateForm({
                        ...createForm,
                        minMarketActivityThreshold: value ? parseFloat(value) : undefined
                      });
                    }}
                    placeholder="4.4"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#374151',
                      backgroundColor: '#ffffff'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'nowrap' }}>%</span>
                </div>
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', fontStyle: 'italic' }}>
                  Default: 4.4% (leave empty to disable)
                </p>
              </div>

              {/* Volatility Filter Section - Optional */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', marginBottom: createForm.enableVolatilityFilter ? '16px' : '0' }}>
                  <input
                    type="checkbox"
                    checked={createForm.enableVolatilityFilter ?? false}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      console.log('✅ Volatility Filter checkbox clicked:', enabled);
                      // Use functional setState to ensure we have the latest state
                      setCreateForm(prevForm => ({
                        ...prevForm,
                        enableVolatilityFilter: enabled
                      }));
                      console.log('✅ Form updated with enableVolatilityFilter:', enabled);
                      // Apply default preset when enabling
                      if (enabled && volatilityPresetMode === 'preset') {
                        setTimeout(() => {
                          console.log('✅ Applying volatility preset:', selectedVolatilityPreset);
                          applyVolatilityPreset(selectedVolatilityPreset);
                        }, 0);
                      }
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '15px', color: '#374151', fontWeight: 600 }}>
                        📊 Enable Volatility Filter
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                        (Optional)
                      </span>
                      {createForm.enableVolatilityFilter && (
                        <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
                          ✓ Enabled
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', marginTop: '4px' }}>
                      Gate trading based on market volatility. Skip creating new pairs when volatility is outside the specified range.
                    </div>
                  </div>
                </label>
                
                {/* Volatility filter configuration section */}
                {createForm.enableVolatilityFilter === true && (
                  <div style={{ 
                    marginTop: '16px', 
                    paddingLeft: '30px',
                    paddingRight: '30px',
                    animation: 'slideDown 0.3s ease-out',
                    borderLeft: '3px solid #3b82f6',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    backgroundColor: '#f0f9ff', /* Light blue background for visibility */
                    border: '2px solid #3b82f6', /* Visible border */
                    borderRadius: '8px'
                  }}>
                    {/* Preset vs Custom Selection */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                        Configuration Mode:
                      </label>
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setVolatilityPresetMode('preset');
                            applyVolatilityPreset(selectedVolatilityPreset);
                          }}
                          style={{
                            flex: 1,
                            padding: '10px 16px',
                            border: volatilityPresetMode === 'preset' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                            borderRadius: '8px',
                            backgroundColor: volatilityPresetMode === 'preset' ? '#eff6ff' : '#ffffff',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: volatilityPresetMode === 'preset' ? '#3b82f6' : '#6b7280',
                            transition: 'all 0.2s'
                          }}
                        >
                          🎯 Use Optimal Preset
                        </button>
                        <button
                          type="button"
                          onClick={() => setVolatilityPresetMode('custom')}
                          style={{
                            flex: 1,
                            padding: '10px 16px',
                            border: volatilityPresetMode === 'custom' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                            borderRadius: '8px',
                            backgroundColor: volatilityPresetMode === 'custom' ? '#eff6ff' : '#ffffff',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: volatilityPresetMode === 'custom' ? '#3b82f6' : '#6b7280',
                            transition: 'all 0.2s'
                          }}
                        >
                          ⚙️ Custom Values
                        </button>
                      </div>

                      {/* Preset Selection */}
                      {volatilityPresetMode === 'preset' && (
                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                            Select Volatility Strategy:
                          </label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {Object.entries(volatilityPresets).map(([key, preset]) => (
                              <div
                                key={key}
                                onClick={() => applyVolatilityPreset(key as 'moderate' | 'conservative' | 'aggressive')}
                                style={{
                                  padding: '14px',
                                  border: selectedVolatilityPreset === key ? `2px solid ${preset.color}` : '1px solid #d1d5db',
                                  borderRadius: '8px',
                                  backgroundColor: selectedVolatilityPreset === key ? `${preset.color}10` : '#ffffff',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  position: 'relative'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                                  <span style={{ fontSize: '24px', marginTop: '2px' }}>{preset.icon}</span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                                      {preset.name}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                                      {preset.description}
                                    </div>
                                    <div style={{ 
                                      display: 'flex', 
                                      gap: '16px', 
                                      fontSize: '12px', 
                                      color: '#374151',
                                      backgroundColor: '#f9fafb',
                                      padding: '8px 12px',
                                      borderRadius: '6px',
                                      fontFamily: 'monospace'
                                    }}>
                                      <span><strong>Min:</strong> {preset.minMarketVolatility}%</span>
                                      <span><strong>Max:</strong> {preset.maxMarketVolatility}%</span>
                                      <span><strong>Coins:</strong> {preset.volatilityCalculationCoins}</span>
                                      <span><strong>Method:</strong> {preset.volatilityCalculationMethod}</span>
                                    </div>
                                  </div>
                                  {selectedVolatilityPreset === key && (
                                    <div style={{ 
                                      position: 'absolute',
                                      top: '12px',
                                      right: '12px',
                                      backgroundColor: preset.color,
                                      color: 'white',
                                      borderRadius: '50%',
                                      width: '24px',
                                      height: '24px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '12px',
                                      fontWeight: 'bold'
                                    }}>
                                      ✓
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Custom Values (only shown in custom mode) */}
                    {volatilityPresetMode === 'custom' && (
                      <>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        Min Market Volatility (%):
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={createForm.minMarketVolatility ?? ''}
                        onChange={(e) => setCreateForm({
                          ...createForm, 
                          minMarketVolatility: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="2.0"
                        style={{ width: '200px', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                        Don't trade when average market volatility is below this percentage
                      </small>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        Max Market Volatility (%):
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={createForm.maxMarketVolatility ?? ''}
                        onChange={(e) => setCreateForm({
                          ...createForm, 
                          maxMarketVolatility: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="10.0"
                        style={{ width: '200px', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                        Don't trade when average market volatility exceeds this percentage
                      </small>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        Number of Coins for Calculation:
                      </label>
                      <select
                        value={createForm.volatilityCalculationCoins ?? 10}
                        onChange={(e) => setCreateForm({
                          ...createForm, 
                          volatilityCalculationCoins: parseInt(e.target.value)
                        })}
                        style={{ width: '200px', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      >
                        <option value={5}>Top 5 coins</option>
                        <option value={10}>Top 10 coins (Recommended)</option>
                        <option value={15}>Top 15 coins</option>
                        <option value={20}>Top 20 coins</option>
                      </select>
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                        Number of top coins by volume to use for volatility calculation
                      </small>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        Calculation Method:
                      </label>
                      <select
                        value={createForm.volatilityCalculationMethod ?? 'AUTO'}
                        onChange={(e) => setCreateForm({
                          ...createForm, 
                          volatilityCalculationMethod: e.target.value as 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO'
                        })}
                        style={{ width: '200px', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      >
                        <option value="AUTO">Auto (Recommended)</option>
                        <option value="HIGH_LOW_RANGE">High/Low Range</option>
                        <option value="CHANGE_24H">24h Change Rate</option>
                      </select>
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                        <strong>Auto:</strong> Uses High/Low range when available, falls back to 24h change<br/>
                        <strong>High/Low Range:</strong> (High24h - Low24h) / Price × 100% (most accurate)<br/>
                        <strong>24h Change Rate:</strong> Absolute value of 24h price change % (fallback method)
                      </small>
                    </div>
                    </>
                    )}
                    
                    {/* Current Configuration Summary (shown in both modes) */}
                    <div style={{ 
                      padding: '14px', 
                      backgroundColor: '#f0fdf4', 
                      borderRadius: '8px', 
                      marginTop: '16px',
                      marginBottom: '12px',
                      fontSize: '13px',
                      color: '#15803d',
                      border: '1px solid #86efac'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
                        ✅ Current Configuration {volatilityPresetMode === 'preset' && `(${volatilityPresets[selectedVolatilityPreset].name})`}
                      </strong>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '8px',
                        backgroundColor: '#ffffff',
                        padding: '10px',
                        borderRadius: '6px'
                      }}>
                        <div><strong>Min Volatility:</strong> {createForm.minMarketVolatility ?? 'None'}%</div>
                        <div><strong>Max Volatility:</strong> {createForm.maxMarketVolatility ?? 'None'}%</div>
                        <div><strong>Coins:</strong> Top {createForm.volatilityCalculationCoins ?? 10}</div>
                        <div><strong>Method:</strong> {createForm.volatilityCalculationMethod ?? 'AUTO'}</div>
                      </div>
                    </div>

                    {/* Information box (shown in both modes) */}
                    <div style={{ 
                      padding: '12px', 
                      backgroundColor: '#eff6ff', 
                      borderRadius: '6px', 
                      marginTop: '12px',
                      fontSize: '13px',
                      color: '#1e40af',
                      border: '1px solid #bfdbfe'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '6px' }}>💡 How it works:</strong>
                      <ul style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.6' }}>
                        <li>Calculates average volatility from top {createForm.volatilityCalculationCoins ?? 10} meme coins by volume</li>
                        <li>Uses {createForm.volatilityCalculationMethod === 'AUTO' ? 'Auto method (High/Low range preferred)' : createForm.volatilityCalculationMethod === 'HIGH_LOW_RANGE' ? 'High/Low range method' : '24h change rate method'} for calculation</li>
                        <li>If volatility is outside the range, <strong>skips creating new pairs</strong> (but continues monitoring existing ones)</li>
                        <li><strong>⚠️ These values are actively enforced by the bot</strong> - new pairs won't be created when market volatility is outside this range</li>
                        <li>Leave min/max blank to set no limit (e.g., only min or only max)</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* AI Auto-Tune at create (optional) */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', backgroundColor: '#faf5ff', padding: '16px', borderRadius: '8px', border: '1px solid #ddd6fe' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#5b21b6', marginBottom: 8 }}>🤖 AI Auto-Tune (optional at create)</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 10 }}>
                  <input
                    type="checkbox"
                    checked={createForm.aiAutoTuneEnabled === true}
                    onChange={e => setCreateForm({
                      ...createForm,
                      aiAutoTuneEnabled: e.target.checked,
                    })}
                    style={{ width: 18, height: 18, accentColor: '#7c3aed' }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Enable live AI auto-tune when created</span>
                </label>
                {createForm.aiAutoTuneEnabled && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginLeft: 28 }}>
                    <label style={{ fontSize: 12, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="createAiTuneMode"
                        checked={createForm.aiAutoTuneRequireApproval !== false}
                        onChange={() => setCreateForm({ ...createForm, aiAutoTuneRequireApproval: true })}
                      />{' '}
                      🛡️ Manual approve
                    </label>
                    <label style={{ fontSize: 12, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="createAiTuneMode"
                        checked={createForm.aiAutoTuneRequireApproval === false}
                        onChange={() => setCreateForm({ ...createForm, aiAutoTuneRequireApproval: false })}
                      />{' '}
                      ⚡ Auto-apply
                    </label>
                  </div>
                )}
                <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 0', lineHeight: 1.4 }}>
                  Separate from <strong>Create AI Strategy</strong> (one-time config). Server scheduler must be enabled for hands-off runs.
                </p>
              </div>

              {/* Paper Trading Mode */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', backgroundColor: '#fffbeb', padding: '16px', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={createForm.paperTrading ?? false}
                    onChange={(e) => setCreateForm({...createForm, paperTrading: e.target.checked})}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '15px', color: '#374151', fontWeight: 600 }}>
                        📝 Enable Paper Trading Mode (Local Simulator)
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', marginTop: '4px' }}>
                      Paper trading uses <strong>real market data</strong> from Kucoin but <strong>simulates trade execution locally</strong>. 
                      No real money is at risk. Perfect for testing strategies before going live.
                    </div>
                  </div>
                </label>
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => {
                  // Save form data before closing
                  localStorage.setItem('memeCoinStrategyForm', JSON.stringify(createForm));
                  setShowCreateModal(false);
                }}
                className="secondary-btn"
              >
                Cancel
              </button>
              <button
                onClick={createModalStrategyType === 'CRYPTO' ? createCryptoCoinStrategy : createMemeCoinStrategy}
                disabled={loading || !createForm.name}
                className="primary-btn"
              >
                {loading ? 'Creating...' : createModalStrategyType === 'CRYPTO' ? 'Create Crypto Strategy' : 'Create Strategy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Enhanced Strategy Modal (V2 - Enhanced Version) */}
      {showCreateEnhancedModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Create Enhanced Meme Coin Pairs Strategy (V2)</h3>
              <button
                onClick={() => {
                  // Save form data before closing
                  localStorage.setItem('memeCoinEnhancedStrategyForm', JSON.stringify(createEnhancedForm));
                  setShowCreateEnhancedModal(false);
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#eff6ff', 
                borderRadius: '6px', 
                marginBottom: '16px',
                fontSize: '13px',
                color: '#1e40af',
                border: '1px solid #bfdbfe'
              }}>
                <strong>✨ Enhanced Version:</strong> This is a copy of the original strategy creation that can be modified independently without affecting the original.
              </div>

              <div className="form-group">
                <label className="form-label">Strategy Name</label>
                <input
                  type="text"
                  value={createEnhancedForm.name}
                  onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, name: e.target.value})}
                  className="form-input"
                  placeholder="Enter strategy name"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Fixed Order Amount (USDT)</label>
                <input
                  type="number"
                  value={createEnhancedForm.fixedOrderAmount || ''}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    if (rawValue === '' || rawValue === null || rawValue === undefined) {
                      setCreateEnhancedForm({...createEnhancedForm, fixedOrderAmount: 0});
                      return;
                    }
                    const numValue = parseFloat(rawValue);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setCreateEnhancedForm({...createEnhancedForm, fixedOrderAmount: numValue});
                    }
                  }}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Leverage</label>
                <input
                  type="number"
                  value={createEnhancedForm.leverage}
                  onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, leverage: Number(e.target.value)})}
                  className="form-input"
                  min="1"
                  max="10"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Maximum Open Positions</label>
                <select
                  value={createEnhancedForm.maxOpenPositions || 3}
                  onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, maxOpenPositions: Number(e.target.value)})}
                  className="form-input"
                  style={{ width: '100%', padding: '8px 12px' }}
                >
                  <option value={2}>2 positions (1 pair)</option>
                  <option value={3}>3 positions (max recommended)</option>
                  <option value={4}>4 positions (2 pairs)</option>
                  <option value={6}>6 positions (3 pairs)</option>
                  <option value={8}>8 positions (4 pairs)</option>
                  <option value={10}>10 positions (5 pairs)</option>
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  Maximum number of open positions at any time. When one position closes, the bot will open new positions to maintain this limit (up to the maximum). Recommended: 3 positions for efficient bankroll management.
                </small>
              </div>
              
              <div className="form-group">
                <label className="form-label">Market Type</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="marketTypeEnhanced"
                      value="SPOT"
                      checked={createEnhancedForm.marketType === 'SPOT'}
                      onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, marketType: 'SPOT'})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Spot Market</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Trade spot pairs (e.g., DOGE-USDT, PEPE-USDT). No leverage, direct coin trading.
                      </div>
                    </div>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="marketTypeEnhanced"
                      value="FUTURES"
                      checked={createEnhancedForm.marketType === 'FUTURES'}
                      onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, marketType: 'FUTURES'})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Futures Market</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Trade perpetual futures contracts (e.g., PEPEUSDTM, DOGEUSDTM). Supports leverage and funding rates.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Trading Account selection (strategy-level) */}
              <div className="form-group">
                <label className="form-label">Trading Account</label>
                {!canUseSharedKucoin && selectablePersonalKucoinAccounts.length === 0 && (
                  <div style={{ padding: '10px 12px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '6px', marginBottom: '8px' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                      <strong>No trading accounts available.</strong> You do not have permission to use the shared
                      system account. Please provision a personal KuCoin sub-account or ask a Super Admin to grant
                      the <em>Use Shared KuCoin Account</em> permission.
                    </p>
                  </div>
                )}
                <select
                  className="form-input"
                  value={createEnhancedForm.kucoinConfigId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateEnhancedForm({
                      ...createEnhancedForm,
                      kucoinConfigId: value === '' ? undefined : Number(value),
                    });
                  }}
                  disabled={accountsLoading}
                >
                  {canUseSharedKucoin && (
                    <option value="">Shared KuCoin (System)</option>
                  )}
                  {!canUseSharedKucoin && selectablePersonalKucoinAccounts.length > 1 && createForm.kucoinConfigId == null && (
                    <option value="" disabled>Select a trading account</option>
                  )}
                  {selectablePersonalKucoinAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  {canUseSharedKucoin
                    ? 'Select which KuCoin API account this strategy will use for new trades. If left empty, the shared system account will be used.'
                    : 'Select which personal KuCoin sub-account this strategy will use for new trades.'}
                </small>
                {!canUseSharedKucoin && createForm.kucoinConfigId == null && selectablePersonalKucoinAccounts.length === 1 && (
                  <small style={{ fontSize: '12px', color: '#0369a1', display: 'block', marginTop: '4px' }}>
                    Using your personal account: {selectablePersonalKucoinAccounts[0].name}
                  </small>
                )}
                {accountsError && (
                  <small style={{ fontSize: '12px', color: '#b91c1c', display: 'block', marginTop: '4px' }}>
                    {accountsError}
                  </small>
                )}
              </div>

              {createEnhancedForm.marketType === 'FUTURES' && (
                <div className="form-group">
                  <label className="form-label">Margin Mode (Futures Only)</label>
                  <select
                    value={createEnhancedForm.marginMode || 'CROSS'}
                    onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, marginMode: e.target.value as 'CROSS' | 'ISOLATED'})}
                    className="form-input"
                    style={{ width: '100%', padding: '8px 12px' }}
                  >
                    <option value="CROSS">Cross Margin (Default)</option>
                    <option value="ISOLATED">Isolated Margin</option>
                  </select>
                  <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                    Cross: Shared margin across positions. Isolated: Each position has its own margin (safer for pairs trading).
                  </small>
                </div>
              )}

              {/* Order Type */}
              <div className="form-group">
                <label className="form-label">Order Type</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="orderTypeEnhanced"
                      value="MARKET"
                      checked={(createEnhancedForm.orderType || 'MARKET') === 'MARKET'}
                      onChange={() => setCreateEnhancedForm({ ...createEnhancedForm, orderType: 'MARKET' })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Market Order</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Execute immediately at the current market price. Higher exchange fees (taker fees), best for fast entry/exit.
                      </div>
                    </div>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="orderTypeEnhanced"
                      value="LIMIT"
                      checked={createEnhancedForm.orderType === 'LIMIT'}
                      onChange={() => setCreateEnhancedForm({ ...createEnhancedForm, orderType: 'LIMIT' })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Limit Order (Lower Fees)</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Bot places orders at a calculated price (around the current price) to try to act as a maker. Lower exchange fees, but fills may take longer or occasionally miss.
                      </div>
                    </div>
                  </label>
                </div>
                
                {/* Advanced Limit Order Options - Only show when LIMIT is selected */}
                {createEnhancedForm.orderType === 'LIMIT' && (
                  <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                      Advanced Limit Order Settings
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label className="form-label" style={{ fontSize: '13px', marginBottom: '6px' }}>
                        Price Offset (%)
                      </label>
                      <input
                        type="number"
                        min="0.01"
                        max="5"
                        step="0.01"
                        value={createEnhancedForm.limitOffset || 0.1}
                        onChange={(e) => setCreateEnhancedForm({ ...createEnhancedForm, limitOffset: parseFloat(e.target.value) || 0.1 })}
                        className="form-input"
                        style={{ width: '100%', padding: '8px 12px' }}
                      />
                      <small style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                        How far from current price to place limit order. Default: 0.1% (0.001). 
                        Lower = closer to market (faster fill, less maker benefit). Higher = further from market (slower fill, better maker fees).
                      </small>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" style={{ fontSize: '13px', marginBottom: '6px' }}>
                        Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="300"
                        step="5"
                        value={createEnhancedForm.limitTimeoutSeconds || 30}
                        onChange={(e) => setCreateEnhancedForm({ ...createEnhancedForm, limitTimeoutSeconds: parseInt(e.target.value) || 30 })}
                        className="form-input"
                        style={{ width: '100%', padding: '8px 12px' }}
                      />
                      <small style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                        If limit order doesn't fill within this time, bot will cancel it and place a market order instead. 
                        Default: 30 seconds. Range: 5-300 seconds.
                      </small>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Stop Loss Type</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="stopLossTypeEnhanced"
                      value="CLASSIC"
                      checked={createEnhancedForm.stopLossType === 'CLASSIC'}
                      onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, stopLossType: 'CLASSIC'})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Classic Stop Loss</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Fixed price level set at entry. Closes position if price hits this level.
                      </div>
                    </div>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="stopLossTypeEnhanced"
                      value="TRAILING"
                      checked={createEnhancedForm.stopLossType === 'TRAILING'}
                      onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, stopLossType: 'TRAILING'})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Trailing Stop Loss</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Dynamic stop loss that moves with price in your favor, stays fixed if price moves against you.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {createEnhancedForm.stopLossType === 'CLASSIC' ? 'Stop Loss (%)' : 'Trailing Stop Loss (%)'}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={createEnhancedForm.trailingStopLoss || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateEnhancedForm({...createEnhancedForm, trailingStopLoss: value === '' ? 0 : Number(value)});
                  }}
                  className="form-input"
                  placeholder={createEnhancedForm.stopLossType === 'CLASSIC' ? 'Fixed stop loss percentage' : 'Trailing stop loss percentage'}
                />
                <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  {createEnhancedForm.stopLossType === 'CLASSIC' 
                    ? 'Fixed percentage below (long) or above (short) entry price. Position closes if price reaches this level.'
                    : 'Percentage that trails the price. Moves up (long) or down (short) as price moves favorably, locks in when price reverses.'}
                </small>
              </div>

              {/* Asymmetric Stop Loss */}
              <div className="form-group">
                <div style={{ padding: '12px 14px', background: '#f0f9ff', borderRadius: '8px', border: '1px dashed #93c5fd' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#1d4ed8', fontWeight: 600 }}>⚡ Asymmetric Stop Loss (Optional)</p>
                  <p style={{ margin: '0 0 10px 0', fontSize: '11px', color: '#6b7280' }}>Override the shared SL per direction. Leave blank to use shared value above for both sides.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>🔵 Long SL (%)</label>
                      <input
                        type="number" step="0.1" min="0.01" max="99.99"
                        placeholder="e.g. 3.5"
                        value={createEnhancedForm.longTrailingStopLoss ?? ''}
                        onChange={e => setCreateEnhancedForm({...createEnhancedForm, longTrailingStopLoss: e.target.value === '' ? undefined : Number(e.target.value)})}
                        className="form-input"
                        style={{ fontSize: '13px' }}
                      />
                      <small style={{ fontSize: '11px', color: '#6b7280' }}>Wider — longs need more room</small>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>🔴 Short SL (%)</label>
                      <input
                        type="number" step="0.1" min="0.01" max="99.99"
                        placeholder="e.g. 3"
                        value={createEnhancedForm.shortTrailingStopLoss ?? ''}
                        onChange={e => setCreateEnhancedForm({...createEnhancedForm, shortTrailingStopLoss: e.target.value === '' ? undefined : Number(e.target.value)})}
                        className="form-input"
                        style={{ fontSize: '13px' }}
                      />
                      <small style={{ fontSize: '11px', color: '#6b7280' }}>Tighter — shorts move faster</small>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Take Profit (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={createEnhancedForm.takeProfit || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateEnhancedForm({...createEnhancedForm, takeProfit: value === '' ? 0 : Number(value)});
                  }}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Basket Take Profit (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={createEnhancedForm.basketTakeProfit || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateEnhancedForm({...createEnhancedForm, basketTakeProfit: value === '' ? 0 : Number(value)});
                  }}
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ marginTop: 8, paddingTop: 12, borderTop: '1px dashed #e5e7eb' }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!createEnhancedForm.enableUniversalBasketTakeProfit}
                    onChange={(e) =>
                      setCreateEnhancedForm({ ...createEnhancedForm, enableUniversalBasketTakeProfit: e.target.checked })
                    }
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <span>Enable Universal Basket Take Profit (strategy-wide)</span>
                </label>
                <small style={{ fontSize: 12, color: '#6b7280', display: 'block', marginTop: 4 }}>
                  When aggregate unrealized P&amp;L across all open pair legs and hedge legs reaches the target % of total reference notional, close the entire book. Default off.
                </small>
                {createEnhancedForm.enableUniversalBasketTakeProfit && (
                  <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 140px' }}>
                      <label className="form-label">Universal target (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={createEnhancedForm.universalBasketTakeProfit ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCreateEnhancedForm({
                            ...createEnhancedForm,
                            universalBasketTakeProfit: v === '' ? undefined : Number(v),
                          });
                        }}
                        className="form-input"
                      />
                    </div>
                    <div style={{ flex: '1 1 120px' }}>
                      <label className="form-label">Min open legs</label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={createEnhancedForm.universalBasketMinOpenLegs ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCreateEnhancedForm({
                            ...createEnhancedForm,
                            universalBasketMinOpenLegs: v === '' ? undefined : parseInt(v, 10),
                          });
                        }}
                        className="form-input"
                        placeholder="2"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Profit-Lock Micro Trailing Stop */}
              <div
                className="form-group"
                style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}
              >
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={!!createEnhancedForm.enableMicroTsl}
                    onChange={(e) =>
                      setCreateEnhancedForm({
                        ...createEnhancedForm,
                        enableMicroTsl: e.target.checked,
                      })
                    }
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span>Enable Profit-Lock Micro Trailing Stop</span>
                </label>
                <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  When unrealized PnL reaches the activation %, arm a micro trailing stop that stays
                  a fixed % behind the highest price after activation. Trails in your favor only
                  and does not replace the main Take Profit.
                </small>

                {createEnhancedForm.enableMicroTsl && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">Activation PnL (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={createEnhancedForm.microTslActivationPercent ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCreateEnhancedForm({
                            ...createEnhancedForm,
                            microTslActivationPercent: value === '' ? undefined : Number(value),
                          });
                        }}
                        className="form-input"
                        placeholder="e.g. 4 (arm at +4%)"
                      />
                    </div>

                    <div style={{ flex: 1 }}>
                      <label className="form-label">Micro-TSL Distance (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={createEnhancedForm.microTslDistancePercent ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCreateEnhancedForm({
                            ...createEnhancedForm,
                            microTslDistancePercent: value === '' ? undefined : Number(value),
                          });
                        }}
                        className="form-input"
                        placeholder="e.g. 1 (1% behind)"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Meme Coin Selection Section */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ marginBottom: '12px', fontWeight: 600 }}>Meme Coin Selection</label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="coinSelectionModeEnhanced"
                      value="TOP_N"
                      checked={createEnhancedForm.coinSelectionMode === 'TOP_N'}
                      onChange={(e) => handleCoinSelectionModeChangeEnhanced('TOP_N')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Use Top N by Volume</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Automatically select the top N meme coins by 24h trading volume
                      </div>
                    </div>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="coinSelectionModeEnhanced"
                      value="MANUAL"
                      checked={createEnhancedForm.coinSelectionMode === 'MANUAL'}
                      onChange={(e) => handleCoinSelectionModeChangeEnhanced('MANUAL')}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Manually Select Coins</span>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        Choose specific meme coins from the available list
                      </div>
                    </div>
                  </label>
                </div>

                {createEnhancedForm.coinSelectionMode === 'TOP_N' && (
                  <div style={{ marginTop: '12px' }}>
                    <label className="form-label" style={{ marginBottom: '8px' }}>Number of Coins</label>
                    <select
                      value={createEnhancedForm.coinLimit || 10}
                      onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, coinLimit: Number(e.target.value)})}
                      className="form-input"
                      style={{ width: '100%', padding: '8px 12px' }}
                    >
                      <option value={5}>Top 5 coins</option>
                      <option value={10}>Top 10 coins (Recommended)</option>
                      <option value={15}>Top 15 coins</option>
                      <option value={20}>Top 20 coins</option>
                      <option value={25}>Top 25 coins</option>
                      <option value={30}>Top 30 coins</option>
                      <option value={50}>Top 50 coins</option>
                      <option value={75}>Top 75 coins</option>
                      <option value={100}>Top 100 coins</option>
                    </select>
                  </div>
                )}

                {createEnhancedForm.coinSelectionMode === 'MANUAL' && (
                  <div style={{ marginTop: '12px' }}>
                    {loadingCoinsEnhanced ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                        Loading coins...
                      </div>
                    ) : (
                      <>
                        <div style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          minHeight: '100px'
                        }}>
                          {availableCoinsEnhanced.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '2px 4px', rowGap: '2px' }}>
                              {availableCoinsEnhanced.map((coin) => (
                                <label
                                  key={coin}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    cursor: 'pointer',
                                    padding: '3px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: (createEnhancedForm.selectedCoins || []).includes(coin) ? '#e0f2fe' : 'transparent',
                                    border: (createEnhancedForm.selectedCoins || []).includes(coin) ? '1px solid #086afb' : '1px solid transparent',
                                    transition: 'all 0.2s',
                                    lineHeight: '1.2'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={(createEnhancedForm.selectedCoins || []).includes(coin)}
                                    onChange={() => handleCoinToggleEnhanced(coin)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0, margin: 0 }}
                                  />
                                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151', lineHeight: '1.2' }}>{coin}</span>
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '8px' }}>
                          Selected: {(createEnhancedForm.selectedCoins || []).length} coin{(createEnhancedForm.selectedCoins || []).length !== 1 ? 's' : ''}
                          {(createEnhancedForm.selectedCoins || []).length < 2 && (
                            <span style={{ color: '#dc2626', marginLeft: '4px' }}> (Minimum 2 coins required)</span>
                          )}
                        </small>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Strategy Configuration Checkboxes */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <label className="form-label" style={{ marginBottom: '12px', fontWeight: 600 }}>Strategy Configuration</label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createEnhancedForm.enableStopLossPerCoin ?? true}
                      onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, enableStopLossPerCoin: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      Enable Trading Stop Loss for each coin separately
                    </span>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createEnhancedForm.enableTakeProfitPerCoin ?? true}
                      onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, enableTakeProfitPerCoin: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      Enable Take Profit for each coin separately
                    </span>
                  </label>
                  
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createEnhancedForm.enableBasketTakeProfit ?? true}
                      onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, enableBasketTakeProfit: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      Enable Basket Target Profit for combined positions
                    </span>
                  </label>
                </div>
              </div>

              {/* Market Activity Threshold (Enhanced) */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontSize: '15px', fontWeight: 600, color: '#374151' }}>
                  🚦 Market Activity Threshold (Optional)
                </label>
                <p style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', marginBottom: '12px' }}>
                  Minimum average absolute 24h change of top 10 meme coins. Acts as a "market is alive" signal. 
                  If average 24h change is below this threshold, new pairs won't be created. Leave empty to disable this check.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={createEnhancedForm.minMarketActivityThreshold ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCreateEnhancedForm({
                        ...createEnhancedForm,
                        minMarketActivityThreshold: value ? parseFloat(value) : undefined
                      });
                    }}
                    placeholder="4.4"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#374151',
                      backgroundColor: '#ffffff'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'nowrap' }}>%</span>
                </div>
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', fontStyle: 'italic' }}>
                  Default: 4.4% (leave empty to disable)
                </p>
              </div>

              {/* Volatility Filter (Enhanced) */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={createEnhancedForm.enableVolatilityFilter ?? false}
                    onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, enableVolatilityFilter: e.target.checked})}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '15px', color: '#374151', fontWeight: 600 }}>
                        📊 Enable Volatility Filter
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                        (Optional)
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', marginTop: '4px' }}>
                      Gate trading based on market volatility. Skip creating new pairs when volatility is outside the specified range.
                    </div>
                  </div>
                </label>
                
                {createEnhancedForm.enableVolatilityFilter && (
                  <div style={{ marginTop: '16px', paddingLeft: '30px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        Min Market Volatility (%):
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={createEnhancedForm.minMarketVolatility ?? ''}
                        onChange={(e) => setCreateEnhancedForm({
                          ...createEnhancedForm, 
                          minMarketVolatility: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="2.0"
                        style={{ width: '200px', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                        Don't trade when average market volatility is below this percentage
                      </small>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        Max Market Volatility (%):
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={createEnhancedForm.maxMarketVolatility ?? ''}
                        onChange={(e) => setCreateEnhancedForm({
                          ...createEnhancedForm, 
                          maxMarketVolatility: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="10.0"
                        style={{ width: '200px', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                        Don't trade when average market volatility exceeds this percentage
                      </small>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        Number of Coins for Calculation:
                      </label>
                      <select
                        value={createEnhancedForm.volatilityCalculationCoins ?? 10}
                        onChange={(e) => setCreateEnhancedForm({
                          ...createEnhancedForm, 
                          volatilityCalculationCoins: parseInt(e.target.value)
                        })}
                        style={{ width: '200px', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      >
                        <option value={5}>Top 5 coins</option>
                        <option value={10}>Top 10 coins (Recommended)</option>
                        <option value={15}>Top 15 coins</option>
                        <option value={20}>Top 20 coins</option>
                      </select>
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                        Number of top coins by volume to use for volatility calculation
                      </small>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                        Calculation Method:
                      </label>
                      <select
                        value={createEnhancedForm.volatilityCalculationMethod ?? 'AUTO'}
                        onChange={(e) => setCreateEnhancedForm({
                          ...createEnhancedForm, 
                          volatilityCalculationMethod: e.target.value as 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO'
                        })}
                        style={{ width: '200px', padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      >
                        <option value="AUTO">Auto (Recommended)</option>
                        <option value="HIGH_LOW_RANGE">High/Low Range</option>
                        <option value="CHANGE_24H">24h Change Rate</option>
                      </select>
                      <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                        <strong>Auto:</strong> Uses High/Low range when available, falls back to 24h change<br/>
                        <strong>High/Low Range:</strong> (High24h - Low24h) / Price × 100% (most accurate)<br/>
                        <strong>24h Change Rate:</strong> Absolute value of 24h price change % (fallback method)
                      </small>
                    </div>
                  </div>
                )}
              </div>

              {/* Paper Trading Mode */}
              <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', backgroundColor: '#fffbeb', padding: '16px', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={createEnhancedForm.paperTrading ?? false}
                    onChange={(e) => setCreateEnhancedForm({...createEnhancedForm, paperTrading: e.target.checked})}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '15px', color: '#374151', fontWeight: 600 }}>
                        📝 Enable Paper Trading Mode (Local Simulator)
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5', marginTop: '4px' }}>
                      Paper trading uses <strong>real market data</strong> from Kucoin but <strong>simulates trade execution locally</strong>. 
                      No real money is at risk. Perfect for testing strategies before going live.
                    </div>
                  </div>
                </label>
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => {
                  // Save form data before closing
                  localStorage.setItem('memeCoinEnhancedStrategyForm', JSON.stringify(createEnhancedForm));
                  setShowCreateEnhancedModal(false);
                }}
                className="secondary-btn"
              >
                Cancel
              </button>
              <button
                onClick={createEnhancedMemeCoinStrategy}
                disabled={loading || !createEnhancedForm.name}
                className="primary-btn"
                style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                {loading ? 'Creating...' : 'Create Enhanced Strategy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Analytics Modal */}
      {showAnalyticsModal && analyticsStrategyId && (() => {
        const strategy = strategies.find(s => s.id === analyticsStrategyId);
        if (!strategy) return null;
        
        const config = parseStrategyConfig(strategy.config || '{}');
        
        const formatExitReason = (reason: string) => {
          if (!reason) return '';
          const reasonMap: {[key: string]: string} = {
            'TAKE_PROFIT': 'Take Profit',
            'STOP_LOSS': 'Stop Loss',
            'TRAILING_STOP': 'Trailing Stop Loss',
            'BASKET_PROFIT': 'Basket TP',
            'BASKET_TP': 'Basket TP',
            'UNIVERSAL_BASKET_PROFIT': 'Universal Basket TP',
            'REBALANCE': 'Rebalance',
            'MANUAL': 'Manual Close',
            'LONG_STOP_LOSS': 'Stop Loss',
            'SHORT_STOP_LOSS': 'Stop Loss',
            'LONG_TAKE_PROFIT': 'Take Profit',
            'SHORT_TAKE_PROFIT': 'Take Profit'
          };
          return reasonMap[reason] || reason;
        };
        
        const formatDateTime = (dateString: string) => {
          if (!dateString) return '';
          try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            });
          } catch {
            return '';
          }
        };
        
        const getPositionColor = (side: string) => {
          if (!side) return '#666';
          const upperSide = side.toUpperCase();
          if (upperSide === 'LONG' || upperSide === 'BUY') return '#198754';
          if (upperSide === 'SHORT' || upperSide === 'SELL') return '#dc3545';
          if (upperSide === 'CLOSE') return '#086afb';
          return '#666';
        };
        
        const getPositionBg = (side: string) => {
          if (!side) return 'transparent';
          const upperSide = side.toUpperCase();
          if (upperSide === 'LONG' || upperSide === 'BUY') return '#d1f4e0';
          if (upperSide === 'SHORT' || upperSide === 'SELL') return '#ffe0e0';
          if (upperSide === 'CLOSE') return '#e0f2fe';
          return 'transparent';
        };

        // Calculate Total P&L (calculated directly - simple enough to not need memoization)
        const realizedPnl = strategyPnlHistory[analyticsStrategyId] && strategyPnlHistory[analyticsStrategyId].length > 0
          ? Number(strategyPnlHistory[analyticsStrategyId][strategyPnlHistory[analyticsStrategyId].length - 1]?.cumulativePnl) || 0
          : 0;
        
        const visibleOpenPositions = getVisibleOpenPositions(analyticsStrategyId);
        const unrealizedPnl = visibleOpenPositions.reduce((sum: number, position: any) => {
          return sum + (Number(position.unrealizedPnl) || 0);
        }, 0);
        
        const totalPnl = realizedPnl + unrealizedPnl;
        const isMobileModal = window.innerWidth <= 768;

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: isMobileModal ? 'flex-start' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobileModal ? '0' : '20px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: isMobileModal ? '0' : '8px',
              width: '100%',
              maxWidth: '900px',
              maxHeight: isMobileModal ? '100vh' : '90vh',
              height: isMobileModal ? '100vh' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              margin: isMobileModal ? '0' : 'auto'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>{strategy.name}</h2>
                  {(() => {
                    const strategy = strategies.find(s => s.id === analyticsStrategyId);
                    if (strategy) {
                      const config = parseStrategyConfig(strategy.config);
                      if (config.paperTrading) {
                        return (
                          <span 
                            style={{
                              backgroundColor: '#ffc107',
                              color: '#000',
                              padding: '6px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Paper Trading (Local Simulator): Uses real market data but simulates trade execution. No real money at risk."
                          >
                            📝 PAPER TRADING
                          </span>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
                <button
                  onClick={() => {
                    // Reset scroll position when closing modal
                    savedScrollPosition.current = 0;
                    setShowAnalyticsModal(false);
                    setAnalyticsStrategyId(null);
                    setAccountOverview(null);
                    setFeeBreakdown(null);
                  }}
                  style={{
                    fontSize: isMobileModal ? '32px' : '28px',
                    padding: isMobileModal ? '8px 12px' : '8px 12px',
                    minWidth: isMobileModal ? '48px' : 'auto',
                    minHeight: isMobileModal ? '48px' : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#333',
                    fontWeight: 'bold',
                    lineHeight: '1',
                    flexShrink: 0
                  }}
                >
                  ×
                </button>
              </div>

              {/* Modal Content */}
              <div 
                ref={modalContentRef}
                style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px'
                }}
              >
                {/* Strategy Details */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>Type:</span>
                    <span style={{ fontSize: 14, color: '#666' }}>{strategy.strategyType.replace('_', ' ')}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 16 }}>Status:</span>
                    <span style={{
                      fontSize: 14,
                      color: strategy.status === 'ACTIVE' ? '#28a745' : strategy.status === 'PAUSED' ? '#ffc107' : '#dc3545',
                      fontWeight: 600
                    }}>
                      {strategy.status}
                    </span>
                  </div>

                  <StrategyExchangeInfo sagemasterEnabled={sagemasterEnabled} variant="badges" />

                  {/* Parameters */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                    {config.leverage && <span style={{ fontSize: 12, color: '#444', background: '#f8f9fa', padding: '6px 8px', borderRadius: 6 }}>Leverage: <strong>{config.leverage}x</strong></span>}
                    {config.fixedOrderAmount && <span style={{ fontSize: 12, color: '#444', background: '#f8f9fa', padding: '6px 8px', borderRadius: 6 }}>Order: <strong>${Number(config.fixedOrderAmount).toLocaleString()}</strong></span>}
                    {config.takeProfit && <span style={{ fontSize: 12, color: '#444', background: '#f8f9fa', padding: '6px 8px', borderRadius: 6 }}>TP: <strong>{config.takeProfit}%</strong></span>}
                    {config.trailingStopLoss && <span style={{ fontSize: 12, color: '#444', background: '#f8f9fa', padding: '6px 8px', borderRadius: 6 }}>TSL: <strong>{config.trailingStopLoss}%</strong></span>}
                  </div>

                  {/* Paper Trading Notice */}
                  {config.paperTrading && (
                    <div style={{
                      backgroundColor: '#fffbeb',
                      border: '1px solid #fcd34d',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                      <span style={{ fontSize: '18px', flexShrink: 0 }}>📝</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', marginBottom: '4px' }}>
                          Paper Trading Mode (Local Simulator)
                        </div>
                        <div style={{ fontSize: '12px', color: '#78350f', lineHeight: '1.5' }}>
                          This strategy uses <strong>real market data</strong> from Kucoin but <strong>simulates trade execution locally</strong>. 
                          No real money is at risk. All positions, P&L, and trades shown here are simulated.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Strategy Description */}
                  {strategy.strategyType === 'MEME_COIN_PAIRS' && (
                    <div style={{ fontSize: 14, color: '#666', lineHeight: 1.5, marginBottom: 16 }}>
                      <strong>Meme Coin Pairs Strategy:</strong> This strategy trades pairs of meme coins simultaneously - 
                      going LONG on one coin and SHORT on another. It profits from relative price movements 
                      between the coins, making it market-neutral. Perfect for volatile meme coin markets.
                    </div>
                  )}

                  {strategy.strategyType === 'CRYPTO_PAIRS' && (
                    <div style={{ fontSize: 14, color: '#666', lineHeight: 1.5, marginBottom: 16 }}>
                      <strong>Major Crypto Pairs Strategy (Beta):</strong> Same pair/hedge engine as meme bots,
                      applied to liquid majors (BTC, ETH, SOL, …). Uses separate AI regimes and participation
                      signals tuned for major-coin markets.
                    </div>
                  )}

                  {/* Complete Strategy Configuration */}
                  {(strategy.strategyType === 'MEME_COIN_PAIRS' || strategy.strategyType === 'CRYPTO_PAIRS') && (
                    <div style={{
                      marginTop: 20,
                      padding: 16,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 8,
                      border: '1px solid #e9ecef'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600, color: '#1a202c' }}>Strategy Configuration</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
                        {/* Stop Loss Configuration */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>Stop Loss</div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Type: <strong>{config.stopLossType === 'CLASSIC' ? 'Classic' : config.stopLossType === 'TRAILING' ? 'Trailing' : 'Trailing (default)'}</strong>
                          </div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Value: <strong>{config.trailingStopLoss || config.stopLoss || 'N/A'}%</strong>
                          </div>
                          {config.longTrailingStopLoss && (
                            <div style={{ fontSize: 12, color: '#444' }}>
                              🔵 Long SL: <strong>{config.longTrailingStopLoss}%</strong>
                            </div>
                          )}
                          {config.shortTrailingStopLoss && (
                            <div style={{ fontSize: 12, color: '#444' }}>
                              🔴 Short SL: <strong>{config.shortTrailingStopLoss}%</strong>
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Per Coin: <strong>{config.enableStopLossPerCoin !== false ? 'Enabled' : 'Disabled'}</strong>
                          </div>
                        </div>

                        {/* Take Profit Configuration */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>Take Profit</div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Individual: <strong>{config.takeProfit || 'N/A'}%</strong>
                          </div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Per Coin: <strong>{config.enableTakeProfitPerCoin !== false ? 'Enabled' : 'Disabled'}</strong>
                          </div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Basket TP: <strong>{config.basketTakeProfit || 'N/A'}%</strong>
                          </div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Basket Enabled: <strong>{config.enableBasketTakeProfit !== false ? 'Yes' : 'No'}</strong>
                          </div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Universal Basket TP:{' '}
                            <strong>
                              {(config as any).enableUniversalBasketTakeProfit === true
                                ? `${(config as any).universalBasketTakeProfit ?? '—'}% (min legs ${(config as any).universalBasketMinOpenLegs ?? '—'})`
                                : 'Off'}
                            </strong>
                          </div>
                        </div>

                        {/* Profit-Lock Micro Trailing Stop Configuration (conditional) */}
                        {config.enableMicroTsl && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>
                              🔒 Profit-Lock Micro Trailing Stop
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Enabled: <strong>Yes</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Activation: <strong>{config.microTslActivationPercent || 'N/A'}%</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Distance: <strong>{config.microTslDistancePercent || 'N/A'}%</strong>
                            </div>
                            <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic', marginTop: 2 }}>
                              Activates at {config.microTslActivationPercent || 'N/A'}% profit, trails {config.microTslDistancePercent || 'N/A'}% behind
                            </div>
                          </div>
                        )}

                        {/* Market & Position Configuration */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>Market & Positions</div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Market Type: <strong>{config.marketType === 'FUTURES' ? 'Futures' : config.marketType === 'SPOT' ? 'Spot' : 'Spot (default)'}</strong>
                          </div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Max Positions: <strong>{config.maxOpenPositions || 3}</strong>
                          </div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Exchange: <strong>{config.exchange || 'KUCOIN'}</strong>
                          </div>
                          {config.marketType === 'FUTURES' && (
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Position Type: <strong>{config.marginMode === 'ISOLATED' ? 'Isolated' : 'Cross'}</strong>
                            </div>
                          )}
                        </div>

                        {/* Coin Selection Configuration */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>Coin Selection</div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Mode: <strong>{config.coinSelectionMode === 'MANUAL' ? 'Manual' : config.coinSelectionMode === 'TOP_N' ? 'Top N by Volume' : 'Top N (default)'}</strong>
                          </div>
                          {config.coinSelectionMode === 'TOP_N' && (
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Coin Limit: <strong>{config.coinLimit === 999 ? 'All Available' : config.coinLimit || 10}</strong>
                            </div>
                          )}
                          {config.coinSelectionMode === 'MANUAL' && config.selectedCoins && config.selectedCoins.length > 0 && (
                            <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>
                              Selected Coins: <strong>{config.selectedCoins.length} coin{config.selectedCoins.length !== 1 ? 's' : ''}</strong>
                              <div style={{ fontSize: 11, color: '#666', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {config.selectedCoins.slice(0, 10).map((coin: string, idx: number) => (
                                  <span key={idx} style={{ 
                                    padding: '2px 6px', 
                                    backgroundColor: '#e3f2fd', 
                                    borderRadius: 4,
                                    fontSize: 10
                                  }}>{coin}</span>
                                ))}
                                {config.selectedCoins.length > 10 && (
                                  <span style={{ fontSize: 10, color: '#999' }}>+{config.selectedCoins.length - 10} more</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Market Activity Threshold (conditional) */}
                        {config.minMarketActivityThreshold !== undefined && config.minMarketActivityThreshold !== null && config.minMarketActivityThreshold > 0 && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>
                              🚦 Market Activity Threshold
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Threshold: <strong>{config.minMarketActivityThreshold}%</strong>
                            </div>
                            <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic', marginTop: 2 }}>
                              Average 24h change of top 10 coins must be ≥ {config.minMarketActivityThreshold}% to create new pairs
                            </div>
                          </div>
                        )}

                        {/* Volatility Filter Configuration (conditional) */}
                        {config.enableVolatilityFilter && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>
                              📊 Volatility Filter
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Min: <strong>{config.minMarketVolatility !== undefined && config.minMarketVolatility !== null ? `${config.minMarketVolatility}%` : 'None'}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Max: <strong>{config.maxMarketVolatility !== undefined && config.maxMarketVolatility !== null ? `${config.maxMarketVolatility}%` : 'None'}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Coins: <strong>Top {config.volatilityCalculationCoins || 10}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Method: <strong>{config.volatilityCalculationMethod === 'AUTO' ? 'Auto' : config.volatilityCalculationMethod === 'HIGH_LOW_RANGE' ? 'High/Low Range' : config.volatilityCalculationMethod === 'CHANGE_24H' ? '24h Change' : 'Auto'}</strong>
                            </div>
                          </div>
                        )}

                        {/* BTC Correlation Filter Configuration — always shown */}
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>
                              ₿ Bitcoin Correlation Filter
                            </div>
                          {!config.btcCorrelationEnabled ? (
                            <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                              ⭕ Disabled — trades open regardless of BTC conditions
                            </div>
                          ) : (
                            <>
                              <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✅ Enabled</div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              BTC EMA: <strong>{config.btcEmaLength || 50} @ {config.btcEmaTimeframe || '5m'}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Min BTC 5m Move Up (Longs): <strong>{config.btcMomentumLongThreshold ?? 0.5}%</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Min BTC 5m Move Down (Shorts): <strong>{config.btcMomentumShortThreshold ?? 0.5}%</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Block if BTC 15m Range &lt; <strong>{config.btcRangeNoTradeThreshold ?? 0.8}%</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>
                              Dump Protection: <strong>{config.btcDumpProtectionEnabled === false ? 'Disabled' : 'Enabled'}</strong>
                            </div>
                            {config.btcDumpProtectionEnabled !== false && (
                              <>
                                <div style={{ fontSize: 12, color: '#444' }}>
                                  Dump Threshold: <strong>{config.btcDumpThreshold ?? 1.5}% over 5–10m</strong>
                                </div>
                                <div style={{ fontSize: 12, color: '#444' }}>
                                  Cooldown: <strong>{config.btcCooldownMinutes ?? 20} minutes</strong>
                                </div>
                                </>
                              )}
                              </>
                            )}
                        </div>

                        {/* BTC Higher-Timeframe Trend Confirmation — only shown when BTC filter is active */}
                        {config.btcTrendTimeframe && config.btcCorrelationEnabled && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#7c3aed', marginBottom: 4 }}>
                              📈 Trend Confirmation ({config.btcTrendTimeframe.toUpperCase()} EMA{config.btcTrendEmaPeriod ?? 21})
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Longs: BTC &gt; {config.btcTrendTimeframe.toUpperCase()} EMA | Shorts: BTC &lt; {config.btcTrendTimeframe.toUpperCase()} EMA
                            </div>
                          </div>
                        )}

                        {/* Minimum Profit Exit */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#065f46', marginBottom: 4 }}>
                            🛡️ Min Profit Exit
                          </div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            {config.minProfitExitPercent != null && config.minProfitExitPercent > 0
                              ? <>TSL blocked below <strong>{config.minProfitExitPercent}%</strong> profit</>
                              : <span style={{ color: '#9ca3af' }}>Disabled (TSL exits at any profit)</span>
                            }
                          </div>
                        </div>

                        {/* Entry Filters Configuration (conditional) */}
                        {(config as any).entryFiltersEnabled && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>
                              🔍 Entry Filters
                            </div>
                            <div style={{ fontSize: 12, color: '#444' }}>
                              Enabled: <strong>Yes</strong>
                            </div>
                            {/* Funding Rate Guard */}
                                <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>
                              🏦 Funding Rate Guard:{' '}
                              {(config as any).fundingRateFilterEnabled === false ? (
                                <strong style={{ color: '#dc2626' }}>Disabled</strong>
                              ) : (
                                <>
                                  <strong>Enabled</strong>
                                  {' — Max Rate (Longs): '}
                                  <strong>
                                    {(config as any).maxFundingRate != null
                                      ? `${(Number((config as any).maxFundingRate) * 100).toFixed(4).replace(/\.?0+$/, '')}%`
                                      : '0.05%'}
                                  </strong>
                              </>
                            )}
                            </div>
                            {/* RSI Filter */}
                                <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>
                              📊 RSI Filter:{' '}
                              {(config as any).rsiFilterEnabled === false ? (
                                <strong style={{ color: '#dc2626' }}>Disabled</strong>
                              ) : (
                                <>
                                  <strong>Enabled</strong>
                                  {' — Min (Longs): '}<strong>{(config as any).rsiLongMin ?? 55}</strong>
                                  {' | Max (Shorts): '}<strong>{(config as any).rsiShortMax ?? 45}</strong>
                              </>
                            )}
                            </div>
                            {/* EMA Trend Filter */}
                                <div style={{ fontSize: 12, color: '#444', marginTop: 4 }}>
                              📈 EMA Trend Filter:{' '}
                              {(config as any).emaFilterEnabled === false ? (
                                <strong style={{ color: '#dc2626' }}>Disabled</strong>
                              ) : (
                                <>
                                  <strong>Enabled</strong>
                                  {' — Period: '}<strong>{(config as any).emaPeriod ?? 20}</strong>
                              </>
                            )}
                            </div>
                          </div>
                        )}

                        {/* Participation Filter — always shown */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>
                            {participationFilterTitle(strategy.strategyType)}
                          </div>
                          {!config.memeParticipationFilterEnabled ? (
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              {participationFilterDisabledHint(strategy.strategyType)}
                            </div>
                          ) : (
                            <>
                              <div style={{ fontSize: 12, color: '#0e7490', fontWeight: 600 }}>✅ Enabled</div>
                              <div style={{ fontSize: 12, color: '#444' }}>
                                Min Active Coins: <strong>{config.minParticipationRate != null ? `${Math.round(config.minParticipationRate * 100)}%` : '25% (default)'}</strong>
                              </div>
                              <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic', marginTop: 2 }}>
                                Blocks new pairs if &lt;{config.minParticipationRate != null ? Math.round(config.minParticipationRate * 100) : 25}% of selected coins are active (positive 24h + above-avg volume)
                              </div>
                            </>
                          )}
                        </div>

                        {/* Micro-Reversal Filter — always shown */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>
                            ⚡ Micro-Reversal Filter
                          </div>
                          {!(config as any).microReversalFilterEnabled ? (
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              ⭕ Disabled — no 5m chop trap gate
                            </div>
                          ) : (
                            <>
                              <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>✅ Enabled</div>
                              <div style={{ fontSize: 12, color: '#444' }}>
                                Block zone: <strong>{(config as any).microReversalMinDropPercent ?? -0.5}%</strong> to <strong>{(config as any).microReversalMaxDropPercent ?? -0.1}%</strong>
                                {' · '}Lookback: <strong>{(config as any).microReversalLookbackCandles ?? 12}</strong> × 5m
                              </div>
                            </>
                          )}
                        </div>

                        {/* Coin Selection Oracle status */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1d4ed8', marginBottom: 4 }}>
                            🔮 Coin Selection Oracle
                          </div>
                          {!(config as any).oracleEnabled ? (
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              ⭕ Disabled — using random shuffle
                            </div>
                          ) : (
                            <div style={{ fontSize: 12, color: '#1d4ed8' }}>
                              ✅ Enabled — min confidence: <strong>{(config as any).oracleConfidenceThreshold ?? 0.5}</strong>
                            </div>
                          )}
                        </div>

                        {/* AI Auto-Tune (Phase 2) — always shown */}
                        <div style={{ gridColumn: '1 / -1', padding: 12, backgroundColor: config.aiAutoTuneEnabled ? '#f0fdf4' : '#faf5ff', borderRadius: 8, border: `1px solid ${config.aiAutoTuneEnabled ? '#86efac' : '#ddd6fe'}` }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#5b21b6', marginBottom: 6 }}>
                            🤖 AI Auto-Tune (live market adaptation)
                          </div>
                          {!config.aiAutoTuneEnabled ? (
                            <>
                              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>⭕ Disabled</div>
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, lineHeight: 1.5 }}>
                                This strategy was likely created with <strong>Create AI Strategy</strong> (one-time market snapshot at create).
                                That does <em>not</em> turn on ongoing auto-tune. Click <strong>🤖 AI Tune</strong> → enable → <strong>Save Settings</strong>.
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: 12, color: '#15803d', fontWeight: 700 }}>✅ Enabled — active for this bot only</div>
                              <div style={{ fontSize: 12, color: '#444', marginTop: 6 }}>
                                Mode: <strong>{isAiAutoTuneApprovalMode(config) ? '🛡️ Manual approve (Apply Pending)' : '⚡ Auto-apply (no pending)'}</strong>
                              </div>
                              <div style={{ fontSize: 12, color: '#444' }}>
                                Automatic re-runs (server scheduler): <strong>{aiAutoTuneGlobal?.schedulerEnabled ? `ON (~${formatAiAutoTuneInterval(aiAutoTuneGlobal.schedulerIntervalMs)})` : 'OFF — AI Tune is still ON; use Run Now or enable trading.ai.auto-tune.scheduler.enabled'}</strong>
                              </div>
                              <div style={{ fontSize: 12, color: '#444' }}>
                                Last regime: <strong>{config.aiAutoTuneLastRegime || '— (run AI Tune → Run Now)'}</strong>
                                {isLowParticipationStyleRegime(config.aiAutoTuneLastRegime, strategy.strategyType) && (
                                  <span style={{
                                    marginLeft: 8,
                                    display: 'inline-block',
                                    padding: '1px 8px',
                                    borderRadius: 999,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    background: config.lowParticipationMode === 'ENTRY_BLOCK' ? '#fee2e2' : '#fef3c7',
                                    color: config.lowParticipationMode === 'ENTRY_BLOCK' ? '#b91c1c' : '#b45309',
                                    border: `1px solid ${config.lowParticipationMode === 'ENTRY_BLOCK' ? '#fca5a5' : '#fde68a'}`,
                                  }}
                                    title={config.lowParticipationMode === 'ENTRY_BLOCK'
                                      ? `No new pairs while ${strategy.strategyType === 'CRYPTO_PAIRS' ? 'LOW_LIQUIDITY' : 'LOW_PARTICIPATION'} is active`
                                      : 'Trading with up to 2 positions and tighter filters'}>
                                    {config.lowParticipationMode === 'ENTRY_BLOCK' ? '⛔ Entry blocked' : `⚠️ ${strategy.strategyType === 'CRYPTO_PAIRS' ? 'Low liquidity' : 'Low participation'} · 2 positions`}
                                  </span>
                                )}
                              </div>
                              {/* US-RDX-02: 7-day regime history bar */}
                              <div style={{ marginTop: 6 }}>
                                <RegimeHistoryBar strategyId={strategy.id} />
                              </div>
                              <div style={{ fontSize: 12, color: '#444' }}>
                                Last applied: <strong>{formatAiAutoTuneLastApplied(config.aiAutoTuneLastAppliedAt)}</strong>
                              </div>
                              {hasAiAutoTunePending(config) ? (
                                <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600, marginTop: 8 }}>
                                  ⏳ Pending changes awaiting approval — open AI Tune → Apply Pending
                                </div>
                              ) : (
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>
                                  Bot reloads config every ~60s.
                                  {aiAutoTuneGlobal?.schedulerEnabled
                                    ? ' Server scheduler will re-evaluate this bot on the interval above.'
                                    : ' Use Run Now for an immediate evaluation, or enable the server scheduler.'}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Order Type Configuration */}
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 }}>Order Type</div>
                          <div style={{ fontSize: 12, color: '#444' }}>
                            Type: <strong>{config.orderType === 'LIMIT' ? 'Limit' : config.orderType === 'MARKET' ? 'Market' : 'Market (default)'}</strong>
                          </div>
                          {config.orderType === 'LIMIT' && (
                            <>
                              <div style={{ fontSize: 12, color: '#444' }}>
                                Limit Offset: <strong>{config.limitOffset !== undefined ? `${config.limitOffset}%` : '0.1%'}</strong>
                              </div>
                              <div style={{ fontSize: 12, color: '#444' }}>
                                Timeout: <strong>{config.limitTimeoutSeconds || 30}s</strong>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Strategy Analytics Section */}
                <div style={{
                  padding: 16,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 8,
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Strategy Analytics</h4>
                      {onShowAnalytics && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onShowAnalytics(strategy.id); }}
                          title="Open full analytics panel with KuCoin Live / DB data source toggle"
                          style={{
                            padding: '3px 10px', borderRadius: 5, border: '1px solid #0d6efd',
                            background: 'transparent', color: '#0d6efd', cursor: 'pointer',
                            fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4
                          }}
                        >
                          📊 Full Analytics
                        </button>
                      )}
                      {analyticsStrategyId && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, color: '#666', fontWeight: 500 }}>
                            {isMockData(analyticsStrategyId) ? 'Mock' : 'Real'}
                          </span>
                          <button
                            onClick={() => toggleMockDataMode(analyticsStrategyId)}
                            disabled={isPaperTradingStrategy(analyticsStrategyId) || hasRealMoneyLiveBots()}
                            style={{
                              position: 'relative',
                              width: 40,
                              height: 20,
                              borderRadius: 10,
                              border: 'none',
                              backgroundColor: isMockData(analyticsStrategyId) ? '#ffc107' : '#28a745',
                              cursor: (isPaperTradingStrategy(analyticsStrategyId) || hasRealMoneyLiveBots()) ? 'not-allowed' : 'pointer',
                              opacity: (isPaperTradingStrategy(analyticsStrategyId) || hasRealMoneyLiveBots()) ? 0.6 : 1,
                              transition: 'background-color 0.2s',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              outline: 'none'
                            }}
                            title={isPaperTradingStrategy(analyticsStrategyId)
                              ? "Paper Trading: Always uses real market data (toggle disabled)"
                              : hasRealMoneyLiveBots()
                              ? "Toggle disabled: You have real money live bots. Cannot mix real and mock data."
                              : (isMockData(analyticsStrategyId) 
                                ? "Currently showing mock data. Click to switch to real data." 
                                : "Currently showing real data. Click to switch to mock data.")}
                          >
                            <span style={{
                              position: 'absolute',
                              left: isMockData(analyticsStrategyId) ? 2 : 18,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: 'white',
                              transition: 'left 0.2s',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                          </button>
                          <span style={{ fontSize: 10, color: '#666', fontWeight: 500 }}>
                            Data
                          </span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => openHistoryModal(analyticsStrategyId)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                        backgroundColor: '#086afb',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z"/>
                      </svg>
                      History
                    </button>
                  </div>

                  {/* Recent Trades */}
                  {analyticsLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Loading analytics...</div>
                  ) : (
                    <>
                      {strategyTrades[analyticsStrategyId] && strategyTrades[analyticsStrategyId].length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Recent Trades</h5>
                          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                            {strategyTrades[analyticsStrategyId].slice(0, 5).map((trade: any, index: number) => (
                              <div key={`trade-${trade.timestamp || trade.closedAt || trade.createdAt || index}-${trade.symbol || ''}-${trade.side || ''}`} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '8px',
                                marginBottom: '6px',
                                background: '#ffffff',
                                border: '1px solid #e9ecef',
                                borderRadius: 6
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a202c' }}>
                                      {trade.symbol || 'N/A'}
                                    </span>
                                    {trade.side && (
                                      <span style={{
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: getPositionColor(trade.side),
                                        background: getPositionBg(trade.side),
                                        padding: '2px 6px',
                                        borderRadius: 4
                                      }}>
                                        {trade.side.toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  {(trade.timestamp || trade.closedAt || trade.createdAt || trade.entryTime || trade.exitTime) && (
                                    <div style={{ fontSize: 10, color: '#888', marginLeft: 'auto' }}>
                                      {formatDateTime(trade.timestamp || trade.closedAt || trade.createdAt || trade.exitTime || trade.entryTime)}
                                    </div>
                                  )}
                                </div>
                                {trade.exitReason && trade.side === 'CLOSE' && (
                                  <div style={{ marginBottom: 4 }}>
                                    <div style={{
                                      fontSize: 10,
                                      color: '#086afb',
                                      fontWeight: 600,
                                      background: '#e0f2fe',
                                      padding: '3px 6px',
                                      borderRadius: 4,
                                      display: 'inline-block'
                                    }}>
                                      ✓ Closed: {formatExitReason(trade.exitReason)}
                                    </div>
                                  </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <div style={{ fontSize: 10, color: '#666' }}>
                                    {trade.side === 'CLOSE' && trade.entryPrice ? (() => {
                                      const amount = Number(trade.amount) || 0;
                                      const entryPrice = Number(trade.entryPrice) || 0;
                                      const exitPrice = Number(trade.price) || 0;
                                      // Calculate notional value at exit: Position Size × (Exit Price / Entry Price)
                                      const notionalValueAtExit = entryPrice > 0 ? amount * (exitPrice / entryPrice) : 0;
                                      return (
                                        <span>
                                          ${amount.toFixed(0)} @ Entry: ${entryPrice.toFixed(6)} → Exit: ${exitPrice.toFixed(6)} (Value: ${notionalValueAtExit.toFixed(2)})
                                        </span>
                                      );
                                    })() : (
                                      <span>
                                        ${(Number(trade.amount) || 0).toFixed(0)} @ ${(Number(trade.price) || 0).toFixed(6)}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: (Number(trade.pnl) || 0) >= 0 ? '#198754' : '#dc3545'
                                  }}>
                                    {(() => {
                                      const pnl = Number(trade.pnl) || 0;
                                      // Show more decimal places for small values (< $0.01)
                                      if (Math.abs(pnl) < 0.01 && pnl !== 0) {
                                        return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(6);
                                      }
                                      return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(2);
                                    })()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Open Positions — always show section; empty state when none (matches Full Analytics panel) */}
                      <div style={{ marginBottom: 16 }}>
                        <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Open Positions</h5>
                        <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                          {visibleOpenPositions.length > 0 ? (
                            visibleOpenPositions.map((position: any, index: number) => (
                              <div key={`position-${position.symbol || ''}-${position.side || ''}-${position.entryTime || index}`} style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px 0',
                                borderBottom: index < visibleOpenPositions.length - 1 ? '1px solid #e9ecef' : 'none'
                              }}>
                                <div style={{ fontSize: 11, marginRight: '12px' }}>
                                  <span style={{ fontWeight: 600 }}>{position.symbol || 'N/A'}</span> {position.side || 'N/A'}
                                </div>
                                {position.createdAt && (
                                  <div style={{ fontSize: 10, color: '#888', marginRight: '12px' }}>
                                    {formatDateTime(position.createdAt)}
                                  </div>
                                )}
                                <div style={{ fontSize: 11, color: '#666', marginRight: '12px' }}>
                                  ${(Number(position.amount) || 0).toFixed(0)} @ ${(Number(position.entryPrice) || 0).toFixed(6)}
                                </div>
                                <div style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: (Number(position.unrealizedPnl) || 0) >= 0 ? '#198754' : '#dc3545',
                                  marginLeft: 'auto'
                                }}>
                                  {(() => {
                                    const pnl = Number(position.unrealizedPnl) || 0;
                                    if (Math.abs(pnl) < 0.01 && pnl !== 0) {
                                      return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(4);
                                    }
                                    return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(2);
                                  })()}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div style={{
                              padding: '12px 10px',
                              background: '#fff',
                              border: '1px dashed #dee2e6',
                              borderRadius: 6,
                              fontSize: 11,
                              color: '#6c757d',
                              lineHeight: 1.45
                            }}>
                              No open positions right now — the bot has no active long/short legs in the strategy tracker.
                            </div>
                          )}
                        </div>
                      </div>

                      {/* P&L History Chart */}
                      {strategyPnlHistory[analyticsStrategyId] && strategyPnlHistory[analyticsStrategyId].length > 0 && (
                        <div>
                          <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>30-Day P&L History</h5>
                          <div style={{ height: 80, display: 'flex', alignItems: 'center' }}>
                            <Sparkline
                              points={strategyPnlHistory[analyticsStrategyId].map((entry: any) => parseFloat(entry.cumulativePnl) || 0)}
                              width={300}
                              height={60}
                              color="#007bff"
                            />
                          </div>
                          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            {/* Main P&L Display */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>Strategy P&L: <strong style={{ color: totalPnl >= 0 ? '#198754' : '#dc3545' }}>${totalPnl.toFixed(2)}</strong></span>
                              </div>
                              {accountOverview && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: '#666' }}>|</span>
                                  <span>Account P&L: <strong style={{ 
                                    color: parseFloat(accountOverview.unrealisedPNL || '0') >= 0 ? '#198754' : '#dc3545' 
                                  }}>${parseFloat(accountOverview.unrealisedPNL || '0').toFixed(2)}</strong></span>
                                  <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>(from Kucoin)</span>
                                </div>
                              )}
                            </div>

                            {/* Fee Breakdown Section */}
                            {feeBreakdown && (
                              <div style={{ 
                                marginTop: 8, 
                                padding: 10, 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: 6, 
                                border: '1px solid #e9ecef',
                                fontSize: 11
                              }}>
                                <div style={{ fontWeight: 600, marginBottom: 8, color: '#333', fontSize: 12 }}>
                                  Fee Breakdown & Net P&L:
                                </div>
                                
                                <div style={{ marginBottom: 6 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span>Gross P&L (before fees):</span>
                                    <strong style={{ color: feeBreakdown.grossPnl >= 0 ? '#198754' : '#dc3545' }}>
                                      ${feeBreakdown.grossPnl.toFixed(2)}
                                    </strong>
                                  </div>
                                  
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, marginLeft: 12, color: '#666' }}>
                                    <span>− Trading Fees:</span>
                                    <span style={{ color: '#dc3545' }}>${feeBreakdown.totalTradingFees.toFixed(2)}</span>
                                  </div>
                                  
                                  {feeBreakdown.totalFundingFees !== 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, marginLeft: 12, color: '#666' }}>
                                      <span>− Funding Fees:</span>
                                      <span style={{ color: feeBreakdown.totalFundingFees > 0 ? '#dc3545' : '#198754' }}>
                                        ${feeBreakdown.totalFundingFees.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                  
                                  <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    marginTop: 8, 
                                    paddingTop: 8, 
                                    borderTop: '1px solid #dee2e6',
                                    fontWeight: 600
                                  }}>
                                    <span>Net P&L (Real Profit/Loss):</span>
                                    <strong style={{ 
                                      color: feeBreakdown.netPnl >= 0 ? '#198754' : '#dc3545',
                                      fontSize: 13
                                    }}>
                                      ${feeBreakdown.netPnl.toFixed(2)}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Info Tooltip */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                              <span 
                                style={{ 
                                  cursor: 'pointer', 
                                  color: '#086afb', 
                                  fontSize: 11,
                                  textDecoration: 'underline',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Find the parent div, then get its next sibling (the info tooltip div)
                                  const parentDiv = (e.currentTarget as HTMLElement).parentElement;
                                  if (parentDiv) {
                                    const infoDiv = parentDiv.nextElementSibling as HTMLElement;
                                    if (infoDiv) {
                                      infoDiv.style.display = infoDiv.style.display === 'none' ? 'block' : 'none';
                                    }
                                  }
                                }}
                              >
                                ℹ️ Info
                              </span>
                            </div>
                            
                            <div style={{ 
                              display: 'none', 
                              marginTop: 8, 
                              padding: 10, 
                              backgroundColor: '#f8f9fa', 
                              borderRadius: 6, 
                              fontSize: 11, 
                              lineHeight: 1.5,
                              border: '1px solid #e9ecef'
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: 6, color: '#333' }}>P&L Breakdown:</div>
                              
                              <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #dee2e6' }}>
                                <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>Gross P&L (Strategy-Level):</div>
                              <div style={{ marginBottom: 4 }}>
                                  <strong>Gross P&L = Realized P&L + Unrealized P&L</strong>
                              </div>
                              <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                • <strong>Realized P&L:</strong> ${realizedPnl.toFixed(2)} (from closed trades)
                              </div>
                                <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                  • <strong>Unrealized P&L:</strong> ${unrealizedPnl.toFixed(2)} (from tracked open positions)
                              </div>
                                <div style={{ fontSize: 10, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                                  Note: Calculated from strategy trades and positions only. Price movement only, before fees.
                                </div>
                              </div>
                              
                              {feeBreakdown && (
                                <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #dee2e6' }}>
                                  <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>Trading Fees:</div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • <strong>Opening fees:</strong> Maker/taker fees when opening positions
                                  </div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • <strong>Closing fees:</strong> Maker/taker fees when closing positions
                                  </div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • <strong>Total Trading Fees:</strong> ${feeBreakdown.totalTradingFees.toFixed(2)}
                                  </div>
                                  <div style={{ fontSize: 10, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                                    Spot: ~0.2% per trade (0.4% round trip) | Futures: ~0.06% per trade (0.12% round trip)
                                  </div>
                                </div>
                              )}
                              
                              {feeBreakdown && feeBreakdown.totalFundingFees !== 0 && (
                                <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #dee2e6' }}>
                                  <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>Funding Fees (Futures Only):</div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • Charged every 8 hours for perpetual futures
                                  </div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • <strong>Total Funding Fees:</strong> ${feeBreakdown.totalFundingFees.toFixed(2)}
                                  </div>
                                  <div style={{ fontSize: 10, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                                    Rate varies: 0.01% - 0.1% per 8 hours. Can be positive (you receive) or negative (you pay).
                                  </div>
                                </div>
                              )}
                              
                              {feeBreakdown && (
                                <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #dee2e6' }}>
                                  <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>Net P&L:</div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • <strong>Net P&L = Gross P&L - Trading Fees - Funding Fees</strong>
                                  </div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • <strong>Net P&L:</strong> ${feeBreakdown.netPnl.toFixed(2)}
                                  </div>
                                  <div style={{ fontSize: 10, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                                    This is the actual profit/loss after all costs.
                                  </div>
                                </div>
                              )}
                              
                              {accountOverview && (
                                <div style={{ marginTop: 8 }}>
                                  <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>Account P&L (Account-Level from Kucoin):</div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • <strong>Unrealised P&L:</strong> ${parseFloat(accountOverview.unrealisedPNL || '0').toFixed(2)} (all positions in account)
                                  </div>
                                  <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                    • <strong>Account Equity:</strong> ${parseFloat(accountOverview.accountEquity || '0').toFixed(2)} {accountOverview.currency}
                                  </div>
                                  <div style={{ fontSize: 10, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                                    Note: This is the actual unrealized P&L from Kucoin for ALL positions in the account, including fees, funding costs, and positions from other strategies.
                                  </div>
                                </div>
                              )}
                              
                              <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4, color: '#333', paddingTop: 6, borderTop: '1px solid #dee2e6' }}>
                                Current Strategy Value:
                              </div>
                              <div style={{ fontSize: 12, color: '#666' }}>
                                Current Value = Total Investment + Total P&L
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Show Total P&L even if no history but there are positions or trades */}
                      {(!strategyPnlHistory[analyticsStrategyId] || strategyPnlHistory[analyticsStrategyId].length === 0) &&
                       ((strategyPositions[analyticsStrategyId] && strategyPositions[analyticsStrategyId].length > 0) ||
                        (strategyTrades[analyticsStrategyId] && strategyTrades[analyticsStrategyId].length > 0)) && (
                        <div>
                          <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>30-Day P&L History</h5>
                          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            {/* Main P&L Display */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>Strategy P&L: <strong style={{ color: totalPnl >= 0 ? '#198754' : '#dc3545' }}>${totalPnl.toFixed(2)}</strong></span>
                              </div>
                              {accountOverview && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: '#666' }}>|</span>
                                  <span>Account P&L: <strong style={{ 
                                    color: parseFloat(accountOverview.unrealisedPNL || '0') >= 0 ? '#198754' : '#dc3545' 
                                  }}>${parseFloat(accountOverview.unrealisedPNL || '0').toFixed(2)}</strong></span>
                                  <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>(from Kucoin)</span>
                                </div>
                              )}
                            </div>

                            {/* Fee Breakdown Section */}
                            {feeBreakdown && (
                              <div style={{ 
                                marginTop: 8, 
                                padding: 10, 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: 6, 
                                border: '1px solid #e9ecef',
                                fontSize: 11
                              }}>
                                <div style={{ fontWeight: 600, marginBottom: 8, color: '#333', fontSize: 12 }}>
                                  Fee Breakdown & Net P&L:
                                </div>
                                
                                <div style={{ marginBottom: 6 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span>Gross P&L (before fees):</span>
                                    <strong style={{ color: feeBreakdown.grossPnl >= 0 ? '#198754' : '#dc3545' }}>
                                      ${feeBreakdown.grossPnl.toFixed(2)}
                                    </strong>
                                  </div>
                                  
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, marginLeft: 12, color: '#666' }}>
                                    <span>− Trading Fees:</span>
                                    <span style={{ color: '#dc3545' }}>${feeBreakdown.totalTradingFees.toFixed(2)}</span>
                                  </div>
                                  
                                  {feeBreakdown.totalFundingFees !== 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, marginLeft: 12, color: '#666' }}>
                                      <span>− Funding Fees:</span>
                                      <span style={{ color: feeBreakdown.totalFundingFees > 0 ? '#dc3545' : '#198754' }}>
                                        ${feeBreakdown.totalFundingFees.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                  
                                  <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    marginTop: 8, 
                                    paddingTop: 8, 
                                    borderTop: '1px solid #dee2e6',
                                    fontWeight: 600
                                  }}>
                                    <span>Net P&L (Real Profit/Loss):</span>
                                    <strong style={{ 
                                      color: feeBreakdown.netPnl >= 0 ? '#198754' : '#dc3545',
                                      fontSize: 13
                                    }}>
                                      ${feeBreakdown.netPnl.toFixed(2)}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Info Tooltip */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                              <span 
                                style={{ 
                                  cursor: 'pointer', 
                                  color: '#086afb', 
                                  fontSize: 11,
                                  textDecoration: 'underline',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Find the parent div, then get its next sibling (the info tooltip div)
                                  const parentDiv = (e.currentTarget as HTMLElement).parentElement;
                                  if (parentDiv) {
                                    const infoDiv = parentDiv.nextElementSibling as HTMLElement;
                                    if (infoDiv) {
                                      infoDiv.style.display = infoDiv.style.display === 'none' ? 'block' : 'none';
                                    }
                                  }
                                }}
                              >
                                ℹ️ Info
                              </span>
                            </div>
                            <div style={{ 
                              display: 'none', 
                              marginTop: 8, 
                              padding: 10, 
                              backgroundColor: '#f8f9fa', 
                              borderRadius: 6, 
                              fontSize: 11, 
                              lineHeight: 1.5,
                              border: '1px solid #e9ecef'
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: 6, color: '#333' }}>Total P&L Breakdown:</div>
                              <div style={{ marginBottom: 4 }}>
                                <strong>Total P&L = Realized P&L + Unrealized P&L</strong>
                              </div>
                              <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                • <strong>Realized P&L:</strong> ${realizedPnl.toFixed(2)} (from closed trades)
                              </div>
                              <div style={{ marginLeft: 8, marginBottom: 6 }}>
                                • <strong>Unrealized P&L:</strong> ${unrealizedPnl.toFixed(2)} (from open positions)
                              </div>
                              <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4, color: '#333', paddingTop: 6, borderTop: '1px solid #dee2e6' }}>
                                Current Strategy Value:
                              </div>
                              <div style={{ fontSize: 12, color: '#666' }}>
                                Current Value = Total Investment + Total P&L
                              </div>
                              <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                                Note: "Order: $X" is the trade size, not total investment. Total investment is the sum of all user allocations.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* History Modal (within Analytics) */}
      {showHistoryModal && analyticsStrategyId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                  Trading History - {strategies.find(s => s.id === analyticsStrategyId)?.name || 'Strategy'}
                </h3>
                {analyticsStrategyId && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>
                      {isMockData(analyticsStrategyId) ? 'Mock' : 'Real'}
                    </span>
                    <button
                      onClick={() => {
                        toggleMockDataMode(analyticsStrategyId);
                        fetchAllHistoryTrades(analyticsStrategyId);
                      }}
                      disabled={isPaperTradingStrategy(analyticsStrategyId) || hasRealMoneyLiveBots()}
                      style={{
                        position: 'relative',
                        width: 44,
                        height: 24,
                        borderRadius: 12,
                        border: 'none',
                        backgroundColor: isMockData(analyticsStrategyId) ? '#ffc107' : '#28a745',
                        cursor: (isPaperTradingStrategy(analyticsStrategyId) || hasRealMoneyLiveBots()) ? 'not-allowed' : 'pointer',
                        opacity: (isPaperTradingStrategy(analyticsStrategyId) || hasRealMoneyLiveBots()) ? 0.6 : 1,
                        transition: 'background-color 0.2s',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        outline: 'none'
                      }}
                      title={isPaperTradingStrategy(analyticsStrategyId)
                        ? "Paper Trading: Always uses real market data (toggle disabled)"
                        : hasRealMoneyLiveBots()
                        ? "Toggle disabled: You have real money live bots. Cannot mix real and mock data."
                        : (isMockData(analyticsStrategyId) 
                          ? "Currently showing mock data. Click to switch to real data." 
                          : "Currently showing real data. Click to switch to mock data.")}
                    >
                      <span style={{
                        position: 'absolute',
                        left: isMockData(analyticsStrategyId) ? 2 : 22,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        transition: 'left 0.2s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </button>
                    <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>
                      Data
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setAllHistoryTrades([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: 0,
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Filters */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              backgroundColor: '#f8f9fa'
            }}>
              {/* Date Range Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Period:</label>
                <select
                  value={historyDateRange}
                  onChange={(e) => {
                    setHistoryDateRange(e.target.value);
                    setHistoryPage(1);
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="7D">Last 7 Days</option>
                  <option value="30D">Last 30 Days</option>
                  <option value="90D">Last 90 Days</option>
                  <option value="ALL">All Time</option>
                </select>
              </div>

              {/* Coin Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Coin:</label>
                <select
                  value={historyCoinFilter}
                  onChange={(e) => {
                    setHistoryCoinFilter(e.target.value);
                    setHistoryPage(1);
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="ALL">All Coins</option>
                  {Array.from(new Set(allHistoryTrades.map((t: any) => t.symbol).filter(Boolean))).sort().map((coin: string) => (
                    <option key={coin} value={coin}>{coin}</option>
                  ))}
                </select>
              </div>

              {/* Results Count */}
              <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center' }}>
                {(() => {
                  const filtered = filterHistoryTrades();
                  return `${filtered.length} trade${filtered.length !== 1 ? 's' : ''}`;
                })()}
              </div>
            </div>

            {/* Trades List */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px'
            }}>
              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  Loading history...
                </div>
              ) : (() => {
                const filtered = filterHistoryTrades();
                const paginated = filtered.slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize);
                
                if (paginated.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      No trades found for the selected filters.
                    </div>
                  );
                }

                return (
                  <>
                    {paginated.map((trade: any, index: number) => {
                      const formatExitReason = (reason: string) => {
                        if (!reason) return '';
                        const reasonMap: {[key: string]: string} = {
                          'TAKE_PROFIT': 'Take Profit',
                          'STOP_LOSS': 'Stop Loss',
                          'TRAILING_STOP': 'Trailing Stop Loss',
                          'TRAILING_STOP_LOSS': 'Trailing Stop Loss',
                          'BASKET_PROFIT': 'Basket TP',
                          'BASKET_TP': 'Basket TP',
                          'UNIVERSAL_BASKET_PROFIT': 'Universal Basket TP',
                          'REBALANCE': 'Rebalance',
                          'MANUAL': 'Manual Close',
                          'LONG_STOP_LOSS': 'Stop Loss',
                          'SHORT_STOP_LOSS': 'Stop Loss',
                          'LONG_TAKE_PROFIT': 'Take Profit',
                          'SHORT_TAKE_PROFIT': 'Take Profit',
                          'LONG_TRAILING_STOP_LOSS': 'Trailing Stop Loss',
                          'SHORT_TRAILING_STOP_LOSS': 'Trailing Stop Loss'
                        };
                        return reasonMap[reason] || reason;
                      };
                      
                      const formatDateTime = (dateString: string) => {
                        if (!dateString) return '';
                        try {
                          const date = new Date(dateString);
                          return date.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          });
                        } catch {
                          return '';
                        }
                      };
                      
                      const getPositionColor = (side: string) => {
                        if (!side) return '#666';
                        const upperSide = side.toUpperCase();
                        if (upperSide === 'LONG' || upperSide === 'BUY') return '#198754';
                        if (upperSide === 'SHORT' || upperSide === 'SELL') return '#dc3545';
                        if (upperSide === 'CLOSE') return '#086afb';
                        return '#666';
                      };
                      
                      const getPositionBg = (side: string) => {
                        if (!side) return 'transparent';
                        const upperSide = side.toUpperCase();
                        if (upperSide === 'LONG' || upperSide === 'BUY') return '#d1f4e0';
                        if (upperSide === 'SHORT' || upperSide === 'SELL') return '#ffe0e0';
                        if (upperSide === 'CLOSE') return '#e0f2fe';
                        return 'transparent';
                      };

                      return (
                        <div key={index} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '12px',
                          marginBottom: '8px',
                          background: '#ffffff',
                          border: '1px solid #e9ecef',
                          borderRadius: '6px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '14px', fontWeight: 700, color: '#1a202c' }}>
                                {trade.symbol || 'N/A'}
                              </span>
                              {trade.side && (
                                <span style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  color: getPositionColor(trade.side),
                                  background: getPositionBg(trade.side),
                                  padding: '3px 8px',
                                  borderRadius: '4px'
                                }}>
                                  {trade.side.toUpperCase()}
                                </span>
                              )}
                              {trade.exitReason && trade.side === 'CLOSE' && (
                                <span style={{
                                  fontSize: '10px',
                                  color: '#086afb',
                                  fontWeight: 600,
                                  background: '#e0f2fe',
                                  padding: '3px 8px',
                                  borderRadius: '4px'
                                }}>
                                  ✓ {formatExitReason(trade.exitReason)}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#888' }}>
                              {formatDateTime(trade.timestamp || trade.closedAt || trade.createdAt || trade.entryTime || trade.exitTime)}
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {trade.side === 'CLOSE' && trade.entryPrice ? (() => {
                                const amount = Number(trade.amount) || 0;
                                const entryPrice = Number(trade.entryPrice) || 0;
                                const exitPrice = Number(trade.price) || 0;
                                // Calculate notional value at exit: Position Size × (Exit Price / Entry Price)
                                const notionalValueAtExit = entryPrice > 0 ? amount * (exitPrice / entryPrice) : 0;
                                return (
                                  <span>
                                    ${amount.toFixed(0)} @ Entry: ${entryPrice.toFixed(6)} → Exit: ${exitPrice.toFixed(6)} (Value: ${notionalValueAtExit.toFixed(2)})
                                  </span>
                                );
                              })() : (
                                <span>
                                  ${(Number(trade.amount) || 0).toFixed(0)} @ ${(Number(trade.price || trade.entryPrice) || 0).toFixed(6)}
                                </span>
                              )}
                            </div>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: 700,
                              color: (Number(trade.pnl) || 0) >= 0 ? '#198754' : '#dc3545'
                            }}>
                              {(() => {
                                const pnl = Number(trade.pnl) || 0;
                                // Show more decimal places for small values (< $0.01)
                                if (Math.abs(pnl) < 0.01 && pnl !== 0) {
                                  return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(6);
                                }
                                return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(2);
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>

            {/* Pagination */}
            {(() => {
              const filtered = filterHistoryTrades();
              const totalPages = Math.ceil(filtered.length / historyPageSize);
              
              if (totalPages <= 1) return null;

              return (
                <div style={{
                  padding: '16px 20px',
                  borderTop: '1px solid #e9ecef',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#f8f9fa'
                }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Page {historyPage} of {totalPages}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                      disabled={historyPage === 1}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: historyPage === 1 ? '#f5f5f5' : 'white',
                        cursor: historyPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        color: historyPage === 1 ? '#999' : '#333'
                      }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setHistoryPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={historyPage === totalPages}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: historyPage === totalPages ? '#f5f5f5' : 'white',
                        cursor: historyPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        color: historyPage === totalPages ? '#999' : '#333'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* User Enrollments Modal */}
      {showEnrollmentsModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEnrollmentsModal(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '1200px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 1
              }}
            >
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#111827' }}>
                User Enrollments & Summary
              </h2>
              <button
                onClick={() => setShowEnrollmentsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '24px' }}>
              {enrollmentsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner"></div>
                  <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading user enrollments...</p>
                </div>
              ) : (
                <>
                  {/* Summary Section */}
                  {enrollmentsSummary && (
                    <div
                      style={{
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '24px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                        Accumulative Summary
                      </h3>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '16px'
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Users</div>
                          <div style={{ fontSize: '20px', fontWeight: 600, color: '#111827' }}>
                            {enrollmentsSummary.totalUsers || 0}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Investments</div>
                          <div style={{ fontSize: '20px', fontWeight: 600, color: '#111827' }}>
                            {formatCurrency(Number(enrollmentsSummary.totalInvestments || 0))}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total P&L</div>
                          <div
                            style={{
                              fontSize: '20px',
                              fontWeight: 600,
                              color: Number(enrollmentsSummary.totalPnl || 0) >= 0 ? '#10b981' : '#ef4444'
                            }}
                          >
                            {formatCurrency(Number(enrollmentsSummary.totalPnl || 0))}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Commissions</div>
                          <div style={{ fontSize: '20px', fontWeight: 600, color: '#086afb' }}>
                            {formatCurrency(Number(enrollmentsSummary.totalCommissions || 0))}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Net Profit</div>
                          <div
                            style={{
                              fontSize: '20px',
                              fontWeight: 600,
                              color: Number(enrollmentsSummary.netProfit || 0) >= 0 ? '#10b981' : '#ef4444'
                            }}
                          >
                            {formatCurrency(Number(enrollmentsSummary.netProfit || 0))}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Revenue</div>
                          <div style={{ fontSize: '20px', fontWeight: 600, color: '#10b981' }}>
                            {formatCurrency(Number(enrollmentsSummary.totalRevenue || 0))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* User Enrollments Table */}
                  <div>
                    <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                      User Enrollments
                    </h3>
                    {userEnrollments.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        No user enrollments found
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                              <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                                User
                              </th>
                              <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                                Investment
                              </th>
                              <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                                P&L
                              </th>
                              <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                                Commissions
                              </th>
                              <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                                Net Profit
                              </th>
                              <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                                Strategies
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {userEnrollments.map((user: any, index: number) => (
                              <tr
                                key={user.userId}
                                style={{
                                  borderBottom: '1px solid #e5e7eb',
                                  backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                                }}
                              >
                                <td style={{ padding: '12px' }}>
                                  <div>
                                    <div style={{ fontWeight: 600, color: '#111827' }}>{user.userName}</div>
                                    {user.userEmail && (
                                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{user.userEmail}</div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 500, color: '#111827' }}>
                                  {formatCurrency(Number(user.totalInvestment || 0))}
                                </td>
                                <td
                                  style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    fontWeight: 500,
                                    color: Number(user.totalPnl || 0) >= 0 ? '#10b981' : '#ef4444'
                                  }}
                                >
                                  {formatCurrency(Number(user.totalPnl || 0))}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 500, color: '#086afb' }}>
                                  {formatCurrency(Number(user.totalCommissions || 0))}
                                </td>
                                <td
                                  style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    fontWeight: 600,
                                    color: Number(user.netProfit || 0) >= 0 ? '#10b981' : '#ef4444'
                                  }}
                                >
                                  {formatCurrency(Number(user.netProfit || 0))}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {user.strategies && user.strategies.length > 0 ? (
                                      user.strategies.map((strategy: any, sIndex: number) => {
                                        const isDeEnrolled = strategy.status === 'CANCELLED' && 
                                                             strategy.pnl && 
                                                             strategy.investment && 
                                                             Number(strategy.pnl) <= -Number(strategy.investment);
                                        const isCapped = strategy.pnl && 
                                                        strategy.investment && 
                                                        Number(strategy.pnl) < -Number(strategy.investment);
                                        
                                        return (
                                          <div
                                            key={sIndex}
                                            style={{
                                              fontSize: '12px',
                                              padding: '4px 8px',
                                              backgroundColor: isDeEnrolled ? '#fee2e2' : '#eef2ff',
                                              border: isDeEnrolled ? '1px solid #ef4444' : 'none',
                                              borderRadius: '4px',
                                              display: 'inline-block',
                                              width: 'fit-content',
                                              marginBottom: '4px'
                                            }}
                                          >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                              <span style={{ fontWeight: 600 }}>{strategy.strategyName}:</span>
                                              <span>{formatCurrency(Number(strategy.investment || 0))}</span>
                                              <span>|</span>
                                              <span style={{ color: Number(strategy.pnl || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                                                {formatCurrency(Number(strategy.pnl || 0))}
                                              </span>
                                              {(isCapped || isDeEnrolled) && (
                                                <span style={{ 
                                                  fontSize: '10px', 
                                                  color: '#ef4444', 
                                                  fontWeight: 600,
                                                  marginLeft: '2px'
                                                }}>
                                                  (Capped)
                                                </span>
                                              )}
                                            </div>
                                            {isDeEnrolled && (
                                              <div style={{ 
                                                fontSize: '10px', 
                                                color: '#dc2626', 
                                                fontStyle: 'italic',
                                                marginTop: '2px'
                                              }}>
                                                ⚠ De-enrolled: Losses reached investment
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })
                                    ) : (
                                      <span style={{ fontSize: '12px', color: '#6b7280' }}>No strategies</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trading Strategy Reports Modal */}
      <TradingStrategyReportsModal
        isOpen={showReportsModal}
        onClose={() => setShowReportsModal(false)}
      />

      {/* Strategy Evaluation Modal */}
      {showEvaluateModal && evaluateStrategy && (() => {
        const { strategy, config } = evaluateStrategy;
        const tp = config.takeProfit || 0;
        const sl = config.trailingStopLoss || 0;
        const rr = sl > 0 ? (tp / sl).toFixed(2) : 'N/A';
        const ev = config.expectedValue || null;
        const derivedDailyEv = ev != null
          ? Number(ev)
          : tp && sl
          ? Number(((tp * 0.5 - sl * 0.5)).toFixed(2))
          : 0;
        const samplePnL = [0.8, 0.5, 1.1, 0.2, 1].map(mult =>
          Number((derivedDailyEv * mult).toFixed(2))
        );
        const monthlyMultiplier = derivedDailyEv
          ? Math.pow(1 + derivedDailyEv / 100, 30)
          : 1;
        const yearlyMultiplier = derivedDailyEv
          ? Math.pow(1 + derivedDailyEv / 100, 365)
          : 1;

        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15,23,42,0.65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: isMobile ? 12 : 24
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEvaluateModal(false);
                setEvaluateStrategy(null);
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#0b1120',
                color: '#e5e7eb',
                borderRadius: 16,
                width: '100%',
                maxWidth: 1100,
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid rgba(55,65,81,0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  background:
                    'radial-gradient(circle at top left,#020617 0,#020617 40%,#020617 100%)',
                }}
              >
                <div>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: '#9ca3af', fontWeight: 600 }}>
                    Trading Strategy Evaluation
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 18, fontWeight: 700 }}>
                      {strategy.name}
                    </span>
                    <span
                      style={{
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        border: '1px solid rgba(75,85,99,0.9)',
                        backgroundColor: '#111827',
                        color: '#9ca3af',
                      }}
                    >
                      {strategy.strategyType.replace('_', ' ')}
                    </span>
                    <span
                      style={{
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        backgroundColor: '#d1fae5',
                        color: '#047857',
                        fontWeight: 600,
                      }}
                    >
                      {config.leverage ? `${config.leverage}x` : 'N/A'} · TP {tp || 'N/A'}% · SL {sl || 'N/A'}%
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowEvaluateModal(false);
                    setEvaluateStrategy(null);
                  }}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 999,
                    border: '1px solid rgba(75,85,99,0.9)',
                    background: 'transparent',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                  }}
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div
                style={{
                  padding: 20,
                  overflowY: 'auto',
                  background:
                    'radial-gradient(circle at top,#020617 0,#020617 55%,#020617 100%)',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,2fr) minmax(0,1fr)',
                    gap: 16,
                  }}
                >
                  {/* Left: summary + comparative metrics + config */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Summary */}
                    <div
                      style={{
                        borderRadius: 12,
                        border: '1px solid rgba(31,41,55,0.9)',
                        padding: 14,
                        background:
                          'radial-gradient(circle at top left,#020617 0,#020617 40%,#020617 100%)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 12,
                          fontSize: 13,
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600 }}>
                            Take Profit
                          </div>
                          <div style={{ fontWeight: 600 }}>{tp || 'N/A'}%</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600 }}>
                            Stop Loss
                          </div>
                          <div style={{ fontWeight: 600 }}>{sl || 'N/A'}%</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600 }}>
                            R:R
                          </div>
                          <div style={{ fontWeight: 600 }}>≈ {rr} : 1</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600 }}>
                            EV / Trade
                          </div>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 8px',
                              borderRadius: 999,
                              border: '1px dashed rgba(56,189,248,0.6)',
                              backgroundColor: 'rgba(8,47,73,0.6)',
                              fontSize: 11,
                              color: '#7dd3fc',
                              fontWeight: 600,
                            }}
                          >
                            EV ≈ {ev != null ? `${ev}%` : '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Comparative Metrics (single-strategy view) */}
                    <div
                      style={{
                        borderRadius: 12,
                        border: '1px solid rgba(31,41,55,0.9)',
                        padding: 14,
                        backgroundColor: '#020617',
                        fontSize: 12,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: 1.5,
                          color: '#9ca3af',
                          marginBottom: 6,
                          fontWeight: 600,
                        }}
                      >
                        Reward / Risk &amp; Expected Value
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid rgba(55,65,81,0.9)', color: '#9ca3af', fontWeight: 600 }}>Strategy</th>
                              <th style={{ padding: '6px 8px', borderBottom: '1px solid rgba(55,65,81,0.9)', color: '#9ca3af', fontWeight: 600 }}>TP %</th>
                              <th style={{ padding: '6px 8px', borderBottom: '1px solid rgba(55,65,81,0.9)', color: '#9ca3af', fontWeight: 600 }}>SL %</th>
                              <th style={{ padding: '6px 8px', borderBottom: '1px solid rgba(55,65,81,0.9)', color: '#9ca3af', fontWeight: 600 }}>Approx Win&nbsp;%*</th>
                              <th style={{ padding: '6px 8px', borderBottom: '1px solid rgba(55,65,81,0.9)', color: '#9ca3af', fontWeight: 600 }}>R:R</th>
                              <th style={{ padding: '6px 8px', borderBottom: '1px solid rgba(55,65,81,0.9)', color: '#9ca3af', fontWeight: 600 }}>EV / Trade&nbsp;%*</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid rgba(31,41,55,0.9)', textAlign: 'left' }}>
                                {strategy.name}
                              </td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid rgba(31,41,55,0.9)', textAlign: 'right' }}>
                                {tp || 'N/A'}
                              </td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid rgba(31,41,55,0.9)', textAlign: 'right' }}>
                                {sl || 'N/A'}
                              </td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid rgba(31,41,55,0.9)', textAlign: 'right' }}>
                                {config.approxWinRate != null ? config.approxWinRate : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid rgba(31,41,55,0.9)', textAlign: 'right' }}>
                                ≈ {rr} : 1
                              </td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid rgba(31,41,55,0.9)', textAlign: 'right' }}>
                                {ev != null ? `≈ ${ev}%` : '—'}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div style={{ marginTop: 4, fontSize: 10, color: '#9ca3af' }}>
                          *Approx Win% and EV% are optional fields: when not provided in the strategy
                          config they are shown as placeholders and should be interpreted as
                          illustrative only.
                        </div>
                      </div>
                    </div>

                    {/* Strategy configuration details (mirrors HTML spec layout) */}
                    <div
                      style={{
                        borderRadius: 12,
                        border: '1px solid rgba(31,41,55,0.9)',
                        padding: 14,
                        backgroundColor: '#020617',
                        fontSize: 12,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: 1.5,
                          color: '#9ca3af',
                          marginBottom: 6,
                          fontWeight: 600,
                        }}
                      >
                        Strategy configuration
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: 10,
                        }}
                      >
                        {/* Left column */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {/* Stop Loss */}
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#e5e7eb', marginBottom: 4 }}>
                              Stop Loss
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Type:{' '}
                              <strong>
                                {config.stopLossType === 'CLASSIC'
                                  ? 'Classic'
                                  : config.stopLossType === 'TRAILING'
                                  ? 'Trailing'
                                  : 'Trailing (default)'}
                              </strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Value:{' '}
                              <strong>
                                {config.trailingStopLoss || config.stopLoss || 'N/A'}%
                              </strong>
                            </div>
                            {config.longTrailingStopLoss && (
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                🔵 Long SL: <strong>{config.longTrailingStopLoss}%</strong>
                              </div>
                            )}
                            {config.shortTrailingStopLoss && (
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                🔴 Short SL: <strong>{config.shortTrailingStopLoss}%</strong>
                              </div>
                            )}
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Per Coin:{' '}
                              <strong>
                                {config.enableStopLossPerCoin !== false ? 'Enabled' : 'Disabled'}
                              </strong>
                            </div>
                          </div>

                          {/* Take Profit */}
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#e5e7eb', marginBottom: 4 }}>
                              Take Profit
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Individual:{' '}
                              <strong>{config.takeProfit || 'N/A'}%</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Per Coin:{' '}
                              <strong>
                                {config.enableTakeProfitPerCoin !== false ? 'Enabled' : 'Disabled'}
                              </strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Basket TP:{' '}
                              <strong>{config.basketTakeProfit || 'N/A'}%</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Basket Enabled:{' '}
                              <strong>
                                {config.enableBasketTakeProfit !== false ? 'Yes' : 'No'}
                              </strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Universal Basket TP:{' '}
                              <strong>
                                {(config as any).enableUniversalBasketTakeProfit === true
                                  ? `${(config as any).universalBasketTakeProfit ?? '—'}% (min ${(config as any).universalBasketMinOpenLegs ?? '—'} legs)`
                                  : 'Off'}
                              </strong>
                            </div>
                          </div>

                          {/* Profit-Lock Micro TSL */}
                          {config.enableMicroTsl && (
                            <div>
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: '#e5e7eb',
                                  marginBottom: 4,
                                }}
                              >
                                🔒 Profit-Lock Micro Trailing Stop
                              </div>
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                Enabled: <strong>Yes</strong>
                              </div>
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                Activation:{' '}
                                <strong>
                                  {config.microTslActivationPercent || 'N/A'}%
                                </strong>
                              </div>
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                Distance:{' '}
                                <strong>
                                  {config.microTslDistancePercent || 'N/A'}%
                                </strong>
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: '#6b7280',
                                  fontStyle: 'italic',
                                  marginTop: 2,
                                }}
                              >
                                Activates at {config.microTslActivationPercent || 'N/A'}%
                                profit, trails {config.microTslDistancePercent || 'N/A'}%
                                behind.
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right column */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {/* Market & Positions */}
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#e5e7eb', marginBottom: 4 }}>
                              Market &amp; Positions
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Market Type:{' '}
                              <strong>
                                {config.marketType === 'FUTURES'
                                  ? 'Futures'
                                  : config.marketType === 'SPOT'
                                  ? 'Spot'
                                  : 'Spot (default)'}
                              </strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Max Positions:{' '}
                              <strong>{config.maxOpenPositions || config.maxPositions || 'N/A'}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Exchange:{' '}
                              <strong>{config.exchange || 'KUCOIN'}</strong>
                            </div>
                            {config.marketType === 'FUTURES' && (
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                Position Type:{' '}
                                <strong>
                                  {config.marginMode === 'ISOLATED' ? 'Isolated' : 'Cross'}
                                </strong>
                              </div>
                            )}
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Leverage:{' '}
                              <strong>
                                {config.leverage ? `${config.leverage}x` : 'N/A'}
                              </strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Order Amount:{' '}
                              <strong>
                                {config.fixedOrderAmount
                                  ? `$${config.fixedOrderAmount}`
                                  : 'N/A'}
                              </strong>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Created:{' '}
                              <strong>{formatDate(strategy.createdAt)}</strong>
                            </div>
                          </div>

                          {/* Coin Selection */}
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#e5e7eb', marginBottom: 4 }}>
                              Coin Selection
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                              Mode:{' '}
                              <strong>
                                {config.coinSelectionMode === 'MANUAL'
                                  ? 'Manual'
                                  : config.coinSelectionMode === 'TOP_N'
                                  ? 'Top N by Volume'
                                  : 'Top N (default)'}
                              </strong>
                            </div>
                            {config.coinSelectionMode === 'TOP_N' && (
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                Coin Limit:{' '}
                                <strong>
                                  {config.coinLimit === 999
                                    ? 'All Available'
                                    : config.coinLimit || 10}
                                </strong>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sample 5‑Day P&L (Account: $1,000) */}
                    <div
                      style={{
                        borderRadius: 12,
                        border: '1px solid rgba(31,41,55,0.9)',
                        padding: 14,
                        backgroundColor: '#020617',
                        fontSize: 12,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: 1.5,
                          color: '#9ca3af',
                          marginBottom: 6,
                          fontWeight: 600,
                        }}
                      >
                        Sample 5‑Day P&amp;L (Account: $1,000)
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid rgba(55,65,81,0.9)', color: '#9ca3af', fontWeight: 600 }}>
                                Day
                              </th>
                              <th style={{ padding: '6px 8px', borderBottom: '1px solid rgba(55,65,81,0.9)', color: '#9ca3af', fontWeight: 600 }}>
                                P&amp;L %
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {samplePnL.map((val, idx) => (
                              <tr key={idx}>
                                <td
                                  style={{
                                    padding: '6px 8px',
                                    borderBottom: '1px solid rgba(31,41,55,0.9)',
                                    textAlign: 'left',
                                  }}
                                >
                                  {idx + 1}
                                </td>
                                <td
                                  style={{
                                    padding: '6px 8px',
                                    borderBottom: '1px solid rgba(31,41,55,0.9)',
                                    textAlign: 'right',
                                    color: val >= 0 ? '#22c55e' : '#f97373',
                                    fontWeight: 500,
                                  }}
                                >
                                  {val >= 0 ? '+' : ''}
                                  {val.toFixed(2)}%
                                </td>
                              </tr>
                            ))}
                            <tr>
                              <td
                                style={{
                                  padding: '6px 8px',
                                  textAlign: 'left',
                                  fontWeight: 600,
                                }}
                              >
                                Total (5d)
                              </td>
                              <td
                                style={{
                                  padding: '6px 8px',
                                  textAlign: 'right',
                                  fontWeight: 600,
                                }}
                              >
                                {(() => {
                                  const total = samplePnL.reduce((s, v) => s + v, 0);
                                  return `${total >= 0 ? '+' : ''}${total.toFixed(2)}%`;
                                })()}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div style={{ marginTop: 4, fontSize: 10, color: '#9ca3af' }}>
                          Example equity after 5 days on $1,000 would follow directly from the
                          percentage changes above (ignoring fees and funding). These sequences are
                          hypothetical and for comparison purposes only.
                        </div>
                      </div>
                    </div>

                    {/* Expected Profit Projection (Illustrative) */}
                    <div
                      style={{
                        borderRadius: 12,
                        border: '1px solid rgba(31,41,55,0.9)',
                        padding: 14,
                        backgroundColor: '#020617',
                        fontSize: 12,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: 1.5,
                          color: '#9ca3af',
                          marginBottom: 6,
                          fontWeight: 600,
                        }}
                      >
                        Expected Profit Projection (Illustrative)
                      </div>
                      {/* Simple bar chart */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 10,
                          height: 110,
                          padding: '6px 2px 0',
                          marginBottom: 10,
                        }}
                      >
                        {['Daily EV', '30d', '365d'].map((label, index) => {
                          const heights = [
                            Math.min(100, Math.max(4, Math.abs(derivedDailyEv) * 3)),
                            Math.min(100, Math.max(4, (monthlyMultiplier - 1) * 8)),
                            Math.min(100, Math.max(4, (yearlyMultiplier - 1) * 2)),
                          ];
                          const value =
                            index === 0
                              ? derivedDailyEv
                              : index === 1
                              ? (monthlyMultiplier - 1) * 100
                              : (yearlyMultiplier - 1) * 100;
                          return (
                            <div
                              key={label}
                              style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                fontSize: 10,
                                color: '#e5e7eb',
                              }}
                            >
                              <div
                                style={{
                                  width: 18,
                                  height: 64,
                                  borderRadius: 6,
                                  background: '#22c55e',
                                  boxShadow: '0 0 10px rgba(34,197,94,0.9)',
                                  border: '1px solid rgba(250,250,250,0.9)',
                                }}
                              />
                              <div style={{ marginTop: 4, textAlign: 'center', fontWeight: 600 }}>
                                {label}
                              </div>
                              <div style={{ fontSize: 11, color: '#7dd3fc', fontWeight: 600 }}>
                                {value > 0 ? '+' : ''}
                                {isFinite(value) ? value.toFixed(1) + '%' : '—'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Projection table */}
                      <div style={{ overflowX: 'auto' }}>
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: 12,
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  textAlign: 'left',
                                  padding: '6px 8px',
                                  borderBottom: '1px solid rgba(55,65,81,0.9)',
                                  color: '#9ca3af',
                                  fontWeight: 600,
                                }}
                              >
                                Strategy
                              </th>
                              <th
                                style={{
                                  padding: '6px 8px',
                                  borderBottom: '1px solid rgba(55,65,81,0.9)',
                                  color: '#9ca3af',
                                  fontWeight: 600,
                                }}
                              >
                                Assumed Daily EV %*
                              </th>
                              <th
                                style={{
                                  padding: '6px 8px',
                                  borderBottom: '1px solid rgba(55,65,81,0.9)',
                                  color: '#9ca3af',
                                  fontWeight: 600,
                                }}
                              >
                                Approx 30‑Day**
                              </th>
                              <th
                                style={{
                                  padding: '6px 8px',
                                  borderBottom: '1px solid rgba(55,65,81,0.9)',
                                  color: '#9ca3af',
                                  fontWeight: 600,
                                }}
                              >
                                Approx 365‑Day**
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td
                                style={{
                                  padding: '6px 8px',
                                  borderBottom: '1px solid rgba(31,41,55,0.9)',
                                  textAlign: 'left',
                                }}
                              >
                                {strategy.name}
                              </td>
                              <td
                                style={{
                                  padding: '6px 8px',
                                  borderBottom: '1px solid rgba(31,41,55,0.9)',
                                  textAlign: 'right',
                                }}
                              >
                                {derivedDailyEv
                                  ? `${derivedDailyEv >= 0 ? '+' : ''}${derivedDailyEv.toFixed(
                                      2
                                    )}%`
                                  : '—'}
                              </td>
                              <td
                                style={{
                                  padding: '6px 8px',
                                  borderBottom: '1px solid rgba(31,41,55,0.9)',
                                  textAlign: 'right',
                                }}
                              >
                                {derivedDailyEv
                                  ? `≈ ${monthlyMultiplier.toFixed(1)}x balance`
                                  : '—'}
                              </td>
                              <td
                                style={{
                                  padding: '6px 8px',
                                  borderBottom: '1px solid rgba(31,41,55,0.9)',
                                  textAlign: 'right',
                                }}
                              >
                                {derivedDailyEv
                                  ? `≈ ${yearlyMultiplier.toFixed(1)}x balance`
                                  : '—'}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                        <div style={{ marginTop: 4, fontSize: 10, color: '#9ca3af' }}>
                          *Daily EV values reuse the illustrative EV/trade values when available,
                          interpreted as if achieved once per day. **30‑day and 365‑day multipliers
                          assume ideal compounding with no withdrawals, constant EV, and no slippage,
                          fees, or liquidity constraints; they are not realistic forecasts, but
                          highlight how sensitive long‑term growth is to small changes in daily edge.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: glossary / notes */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 12 }}>
                    <div
                      style={{
                        borderRadius: 12,
                        border: '1px solid rgba(31,41,55,0.9)',
                        padding: 12,
                        backgroundColor: '#020617',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: 1.5,
                          color: '#9ca3af',
                          marginBottom: 6,
                          fontWeight: 600,
                        }}
                      >
                        Key &amp; Glossary
                      </div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <li>
                          <strong>TP</strong> – Take Profit (% where the strategy closes in profit).
                        </li>
                        <li>
                          <strong>SL</strong> – Stop Loss (% where the strategy exits to cap loss).
                        </li>
                        <li>
                          <strong>R:R</strong> – Reward to Risk ratio ≈ TP ÷ SL.
                        </li>
                        <li>
                          <strong>EV / Trade</strong> – Illustrative expected value per trade based on assumed hit‑rate.
                        </li>
                      </ul>
                    </div>
                    <div
                      style={{
                        borderRadius: 12,
                        border: '1px solid rgba(248,250,252,0.12)',
                        padding: 10,
                        backgroundColor: 'rgba(15,23,42,0.8)',
                        color: '#e5e7eb',
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
                        ⚠ Important
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>
                        All metrics here are illustrative and based on configuration only.
                        They do not guarantee future performance. Always combine this
                        view with live P&amp;L and risk controls.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Strategy Logs Modal */}
      <StrategyLogsModal
        isOpen={showLogsModal}
        onClose={() => setShowLogsModal(false)}
        strategyName={logsStrategyName}
      />

      {/* Edit Take Profit Modal */}
      {showTPEditModal && selectedStrategyForTPEdit && (() => {
        const config = parseStrategyConfig(selectedStrategyForTPEdit.config);
        const isActive = selectedStrategyForTPEdit.status === 'ACTIVE';
        const openPositionsCount = activePairs.filter(p => 
          p.strategyId === selectedStrategyForTPEdit.id && p.status === 'ACTIVE'
        ).length;
        const currentTP = config.takeProfit || 0;
        const newTP = parseFloat(tpEditForm.takeProfit) || currentTP;
        
        return (
          <div className="modal-overlay" onClick={e => e.stopPropagation()}>
            <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <h3 className="modal-title">⚙️ Edit Strategy Config — {selectedStrategyForTPEdit.name}</h3>
                  <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 500 }}>
                    💾 Draft auto-saved — safe to refresh
                  </span>
                </div>
                <button
                  onClick={closeTPEditModal}
                  className="modal-close"
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                {/* Strategy Info */}
                <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>
                    <strong>Strategy:</strong> {selectedStrategyForTPEdit.name}
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>
                    <strong>Status:</strong> {selectedStrategyForTPEdit.status} {isActive && '⚠️'}
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '14px' }}>
                    <strong>Open Positions:</strong> {openPositionsCount}
                  </p>
                </div>
                
                {/* Warning Box for ACTIVE strategies */}
                {isActive && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #fbbf24',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#92400e' }}>
                      ⚠️ WARNING: Strategy is ACTIVE
                    </h4>
                    <p style={{ margin: '4px 0', fontSize: '13px', color: '#92400e' }}>
                      Updating Take Profit will:
                    </p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '13px', color: '#92400e' }}>
                      <li>✅ Apply to new positions immediately</li>
                      <li>✅ Apply to monitoring loop on next cycle</li>
                      <li>❌ NOT update exchange-level TP orders (if enabled)</li>
                    </ul>
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', fontWeight: 600, color: '#92400e' }}>
                      Recommended: Pause strategy first, then update.
                    </p>
                  </div>
                )}
                
                {/* Current Configuration */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>Current Configuration</h4>
                  <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Take Profit: <strong>{config.takeProfit ?? '—'}%</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Basket TP: <strong>{config.basketTakeProfit ?? '—'}%</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>
                      Universal Basket TP:{' '}
                      <strong>
                        {(config as any).enableUniversalBasketTakeProfit === true
                          ? `${(config as any).universalBasketTakeProfit ?? '—'}% · min legs ${(config as any).universalBasketMinOpenLegs ?? '—'}`
                          : 'Off'}
                      </strong>
                    </p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Stop Loss Type: <strong>{config.stopLossType ?? '—'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Stop Loss Value: <strong>{config.trailingStopLoss ?? '—'}%</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>🔵 Long SL: <strong>{config.longTrailingStopLoss ? `${config.longTrailingStopLoss}%` : '— (uses shared)'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>🔴 Short SL: <strong>{config.shortTrailingStopLoss ? `${config.shortTrailingStopLoss}%` : '— (uses shared)'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Micro TSL: <strong>{config.enableMicroTsl ? 'ON' : 'OFF'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Micro Activation: <strong>{config.microTslActivationPercent ?? '—'}%</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Micro Distance: <strong>{config.microTslDistancePercent ?? '—'}%</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Max Positions: <strong>{config.maxOpenPositions ?? '—'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Leverage: <strong>{config.leverage ? `${config.leverage}x` : '—'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>BTC Long ≥: <strong>{config.btcMomentumLongThreshold ?? '—'}%</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>BTC Short ≥: <strong>{config.btcMomentumShortThreshold ?? '—'}%</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>BTC 15m Range &lt;: <strong>{config.btcRangeNoTradeThreshold ?? '—'}%</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>Dump Cooldown: <strong>{config.btcCooldownMinutes ?? '—'} min</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>BTC Trend TF: <strong>{config.btcTrendTimeframe || 'Disabled'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>BTC Trend EMA: <strong>{config.btcTrendEmaPeriod ?? '21 (default)'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>🛡️ Min Profit Exit: <strong>{config.minProfitExitPercent != null ? `${config.minProfitExitPercent}%` : 'Disabled'}</strong></p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>
                      🚦 Market Activity:{' '}
                      <strong>
                        {config.minMarketActivityThreshold != null && config.minMarketActivityThreshold > 0
                          ? `≥ ${config.minMarketActivityThreshold}% avg 24h change`
                          : 'Disabled'}
                      </strong>
                    </p>
                    <p style={{ margin: '3px 0', fontSize: '13px' }}>
                      📊 Volatility Filter:{' '}
                      <strong>
                        {(config as any).enableVolatilityFilter === true
                          ? `ON · ${config.minMarketVolatility ?? '—'}% – ${config.maxMarketVolatility ?? '—'}%`
                          : 'Off'}
                      </strong>
                    </p>
                  </div>
                </div>
                
                {/* ── Form Fields ── */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>New Configuration</h4>
                  
                  {/* ── SECTION: Stop Loss ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 700, color: '#374151' }}>🛑 Stop Loss</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {/* Type */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Type</label>
                        <select
                          value={tpEditForm.stopLossType}
                          onChange={e => setTPEditForm({...tpEditForm, stopLossType: e.target.value as 'CLASSIC' | 'TRAILING'})}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: 'white' }}
                        >
                          <option value="TRAILING">Trailing</option>
                          <option value="CLASSIC">Classic (Fixed)</option>
                        </select>
                      </div>
                      {/* Value */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Shared Value (%)</label>
                    <input
                          type="number" step="0.01" min="0.01" max="99.99"
                          placeholder={`e.g. ${config.trailingStopLoss ?? 3.5}`}
                          value={tpEditForm.trailingStopLoss}
                          onChange={e => setTPEditForm({...tpEditForm, trailingStopLoss: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.trailingStopLoss ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        {tpEditErrors.trailingStopLoss && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.trailingStopLoss}</p>}
                      </div>
                    </div>
                    {/* Asymmetric SL overrides */}
                    <div style={{ marginTop: '12px', padding: '10px 12px', background: '#f0f9ff', borderRadius: '6px', border: '1px dashed #93c5fd' }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#1d4ed8', fontWeight: 600 }}>⚡ Asymmetric Stop Loss (Optional — overrides Shared Value per direction)</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>🔵 Long SL (%)</label>
                          <input
                            type="number" step="0.01" min="0.01" max="99.99"
                            placeholder={`e.g. ${config.longTrailingStopLoss ?? 3.5}`}
                            value={tpEditForm.longTrailingStopLoss}
                            onChange={e => setTPEditForm({...tpEditForm, longTrailingStopLoss: e.target.value})}
                            style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.longTrailingStopLoss ? '2px solid #ef4444' : '1px solid #93c5fd', borderRadius: '6px', fontSize: '14px' }}
                          />
                          <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>Wider SL for longs (e.g. 3.5%)</p>
                          {tpEditErrors.longTrailingStopLoss && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.longTrailingStopLoss}</p>}
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>🔴 Short SL (%)</label>
                          <input
                            type="number" step="0.01" min="0.01" max="99.99"
                            placeholder={`e.g. ${config.shortTrailingStopLoss ?? 3}`}
                            value={tpEditForm.shortTrailingStopLoss}
                            onChange={e => setTPEditForm({...tpEditForm, shortTrailingStopLoss: e.target.value})}
                            style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.shortTrailingStopLoss ? '2px solid #ef4444' : '1px solid #93c5fd', borderRadius: '6px', fontSize: '14px' }}
                          />
                          <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>Tighter SL for shorts (e.g. 3%)</p>
                          {tpEditErrors.shortTrailingStopLoss && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.shortTrailingStopLoss}</p>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── SECTION: Take Profit ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 700, color: '#374151' }}>🎯 Take Profit</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      {/* Individual TP */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Individual TP (%) *</label>
                        <input
                          type="number" step="0.01" min="0.01" max="99.99"
                      value={tpEditForm.takeProfit}
                      onChange={e => setTPEditForm({...tpEditForm, takeProfit: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.takeProfit ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        {tpEditErrors.takeProfit && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.takeProfit}</p>}
                  </div>
                      {/* Basket TP */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Basket TP (%)</label>
                      <input
                          type="number" step="0.01" min="0.01" max="99.99"
                          placeholder={`e.g. ${config.basketTakeProfit ?? 4}`}
                        value={tpEditForm.basketTakeProfit}
                        onChange={e => setTPEditForm({...tpEditForm, basketTakeProfit: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.basketTakeProfit ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        {tpEditErrors.basketTakeProfit && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.basketTakeProfit}</p>}
                    </div>
                    </div>
                    {/* Long/Short TP (hedge only) */}
                    {(config as any).hedgeSameSymbol === true && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Long TP (%) <span style={{ fontWeight: 400, color: '#9ca3af' }}>hedge only</span></label>
                        <input
                            type="number" step="0.01" min="0.01" max="99.99"
                          value={tpEditForm.longTakeProfit}
                          onChange={e => setTPEditForm({...tpEditForm, longTakeProfit: e.target.value})}
                            style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.longTakeProfit ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                          {tpEditErrors.longTakeProfit && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.longTakeProfit}</p>}
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Short TP (%) <span style={{ fontWeight: 400, color: '#9ca3af' }}>hedge only</span></label>
                          <input
                            type="number" step="0.01" min="0.01" max="99.99"
                            value={tpEditForm.shortTakeProfit}
                            onChange={e => setTPEditForm({...tpEditForm, shortTakeProfit: e.target.value})}
                            style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.shortTakeProfit ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                          {tpEditErrors.shortTakeProfit && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.shortTakeProfit}</p>}
                        </div>
                    </div>
                  )}
                    <div
                      style={{
                        marginTop: 14,
                        paddingTop: 12,
                        borderTop: '1px dashed #e5e7eb',
                      }}
                    >
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#374151',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={tpEditForm.enableUniversalBasketTakeProfit}
                          onChange={(e) =>
                            setTPEditForm({ ...tpEditForm, enableUniversalBasketTakeProfit: e.target.checked })
                          }
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                        />
                        Enable Universal Basket Take Profit (strategy-wide)
                      </label>
                      <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                        Closes all open pair and hedge legs when combined unrealized P&amp;L reaches the target % of
                        reference notional.
                      </p>
                      {tpEditForm.enableUniversalBasketTakeProfit && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: 6, fontSize: '13px', fontWeight: 600 }}>
                              Universal target (%) *
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              max="99.99"
                              placeholder={`e.g. ${(config as any).universalBasketTakeProfit ?? 2}`}
                              value={tpEditForm.universalBasketTakeProfit}
                              onChange={(e) =>
                                setTPEditForm({ ...tpEditForm, universalBasketTakeProfit: e.target.value })
                              }
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: tpEditErrors.universalBasketTakeProfit
                                  ? '2px solid #ef4444'
                                  : '1px solid #d1d5db',
                                borderRadius: 6,
                                fontSize: 14,
                              }}
                            />
                            {tpEditErrors.universalBasketTakeProfit && (
                              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#ef4444' }}>
                                {tpEditErrors.universalBasketTakeProfit}
                              </p>
                            )}
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: 6, fontSize: '13px', fontWeight: 600 }}>
                              Min open legs
                            </label>
                            <input
                              type="number"
                              min={1}
                              step={1}
                              placeholder={`e.g. ${(config as any).universalBasketMinOpenLegs ?? 2}`}
                              value={tpEditForm.universalBasketMinOpenLegs}
                              onChange={(e) =>
                                setTPEditForm({ ...tpEditForm, universalBasketMinOpenLegs: e.target.value })
                              }
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: tpEditErrors.universalBasketMinOpenLegs
                                  ? '2px solid #ef4444'
                                  : '1px solid #d1d5db',
                                borderRadius: 6,
                                fontSize: 14,
                              }}
                            />
                            {tpEditErrors.universalBasketMinOpenLegs && (
                              <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#ef4444' }}>
                                {tpEditErrors.universalBasketMinOpenLegs}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                      </div>
                      
                  {/* ── SECTION: Profit-Lock Micro Trailing Stop ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#374151' }}>🔒 Profit-Lock Micro Trailing Stop</p>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
                        <input
                          type="checkbox"
                          checked={tpEditForm.enableMicroTsl}
                          onChange={e => setTPEditForm({...tpEditForm, enableMicroTsl: e.target.checked})}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        {tpEditForm.enableMicroTsl ? <span style={{ color: '#10b981', fontWeight: 600 }}>Enabled</span> : <span style={{ color: '#9ca3af' }}>Disabled</span>}
                        </label>
                    </div>
                    {tpEditForm.enableMicroTsl && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Activation (%)</label>
                        <input
                            type="number" step="0.01" min="0.01" max="99.99"
                            placeholder={`e.g. ${config.microTslActivationPercent ?? 3.5}`}
                            value={tpEditForm.microTslActivationPercent}
                            onChange={e => setTPEditForm({...tpEditForm, microTslActivationPercent: e.target.value})}
                            style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.microTslActivationPercent ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                          <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>Activates micro-trail at this profit %</p>
                          {tpEditErrors.microTslActivationPercent && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.microTslActivationPercent}</p>}
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Distance (%)</label>
                          <input
                            type="number" step="0.01" min="0.01" max="99.99"
                            placeholder={`e.g. ${config.microTslDistancePercent ?? 1}`}
                            value={tpEditForm.microTslDistancePercent}
                            onChange={e => setTPEditForm({...tpEditForm, microTslDistancePercent: e.target.value})}
                            style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.microTslDistancePercent ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                          <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>Trails this % behind peak price</p>
                          {tpEditErrors.microTslDistancePercent && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.microTslDistancePercent}</p>}
                        </div>
                    </div>
                  )}
                      </div>

                  {/* ── SECTION: Market & Positions ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 700, color: '#374151' }}>📊 Market & Positions</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Max Positions</label>
                        <input
                          type="number" step="1" min="1" max="50"
                          placeholder={`e.g. ${config.maxOpenPositions ?? 8}`}
                          value={tpEditForm.maxOpenPositions}
                          onChange={e => setTPEditForm({...tpEditForm, maxOpenPositions: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.maxOpenPositions ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        {tpEditErrors.maxOpenPositions && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.maxOpenPositions}</p>}
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Leverage (x)</label>
                        <input
                          type="number" step="1" min="1" max="125"
                          placeholder={`e.g. ${config.leverage ?? 10}`}
                          value={tpEditForm.leverage}
                          onChange={e => setTPEditForm({...tpEditForm, leverage: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.leverage ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>e.g. 10 for 10x • Max 125x (KuCoin limit)</p>
                        {tpEditErrors.leverage && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.leverage}</p>}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Order Amount (USDT)</label>
                        <input
                          type="number" step="0.01" min="1"
                          placeholder={`e.g. ${config.fixedOrderAmount ?? 30}`}
                          value={tpEditForm.fixedOrderAmount}
                          onChange={e => setTPEditForm({...tpEditForm, fixedOrderAmount: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.fixedOrderAmount ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>Current: <strong>${config.fixedOrderAmount ?? 'N/A'}</strong> • Amount per position in USDT</p>
                        {tpEditErrors.fixedOrderAmount && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.fixedOrderAmount}</p>}
                      </div>
                    </div>
                  </div>

                  {/* ── SECTION: Market Activity Threshold ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 700, color: '#374151' }}>🚦 Market Activity Threshold</p>
                    <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: '#6b7280', lineHeight: 1.5 }}>
                      Minimum average absolute 24h change of top 10 coins. Acts as a &quot;market is alive&quot; signal — new pairs are skipped when live activity is below this threshold.
                      Set to <strong>0</strong> or leave empty to disable.
                    </p>
                    {selectedStrategyForTPEdit.lastError && selectedStrategyForTPEdit.lastError.includes('MARKET_NOT_ALIVE') && (
                      <div style={{ padding: '8px 12px', backgroundColor: '#fff3cd', border: '1px solid #856404', borderRadius: 6, marginBottom: 12 }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#856404', fontWeight: 600 }}>
                          🛡️ Currently blocked: {selectedStrategyForTPEdit.lastError}
                        </p>
                        <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#92400e' }}>
                          Lower this threshold or set to 0 to allow pair creation in quiet markets.
                        </p>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        placeholder={`Current: ${config.minMarketActivityThreshold ?? 'disabled'}`}
                        value={tpEditForm.minMarketActivityThreshold}
                        onChange={e => setTPEditForm({ ...tpEditForm, minMarketActivityThreshold: e.target.value })}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: tpEditErrors.minMarketActivityThreshold ? '2px solid #ef4444' : '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '14px',
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#6b7280', whiteSpace: 'nowrap' }}>%</span>
                    </div>
                    <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
                      Current: <strong>{config.minMarketActivityThreshold != null && config.minMarketActivityThreshold > 0 ? `${config.minMarketActivityThreshold}%` : 'Disabled'}</strong>
                      {' · '}Default on create: 4.4%
                    </p>
                    {tpEditErrors.minMarketActivityThreshold && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.minMarketActivityThreshold}</p>
                    )}
                  </div>

                  {/* ── SECTION: Volatility Filter ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: `1px solid ${tpEditForm.enableVolatilityFilter ? '#93c5fd' : '#e5e7eb'}`, borderRadius: '8px', backgroundColor: tpEditForm.enableVolatilityFilter ? '#f0f9ff' : 'transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#374151' }}>📊 Volatility Filter</p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                          Blocks new pair creation when average market volatility is outside the configured range.
                        </p>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: tpEditForm.enableVolatilityFilter ? '#16a34a' : '#dc2626' }}>
                        <input
                          type="checkbox"
                          checked={tpEditForm.enableVolatilityFilter}
                          onChange={e => {
                            const enabled = e.target.checked;
                            setTPEditForm({ ...tpEditForm, enableVolatilityFilter: enabled });
                            if (enabled && tpEditVolatilityPresetMode === 'preset') {
                              applyTpEditVolatilityPreset(tpEditSelectedVolatilityPreset);
                            }
                          }}
                          style={{ width: 16, height: 16, accentColor: '#16a34a', cursor: 'pointer' }}
                        />
                        {tpEditForm.enableVolatilityFilter ? 'Enabled' : 'Disabled'}
                      </label>
                    </div>
                    {selectedStrategyForTPEdit.lastError && selectedStrategyForTPEdit.lastError.includes('VOLATILITY') && (
                      <div style={{ padding: '8px 12px', backgroundColor: '#fff3cd', border: '1px solid #856404', borderRadius: 6, marginBottom: 12 }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#856404', fontWeight: 600 }}>
                          🛡️ Currently blocked: {selectedStrategyForTPEdit.lastError}
                        </p>
                      </div>
                    )}
                    <div style={{ opacity: tpEditForm.enableVolatilityFilter ? 1 : 0.4, pointerEvents: tpEditForm.enableVolatilityFilter ? 'auto' : 'none' }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setTpEditVolatilityPresetMode('preset');
                            applyTpEditVolatilityPreset(tpEditSelectedVolatilityPreset);
                          }}
                          style={{
                            padding: '6px 12px',
                            border: tpEditVolatilityPresetMode === 'preset' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: tpEditVolatilityPresetMode === 'preset' ? '#eff6ff' : '#fff',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          🎯 Preset
                        </button>
                        <button
                          type="button"
                          onClick={() => setTpEditVolatilityPresetMode('custom')}
                          style={{
                            padding: '6px 12px',
                            border: tpEditVolatilityPresetMode === 'custom' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: tpEditVolatilityPresetMode === 'custom' ? '#eff6ff' : '#fff',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          ✏️ Custom
                        </button>
                        {tpEditVolatilityPresetMode === 'preset' && (
                          <>
                            {(['conservative', 'moderate', 'aggressive'] as const).map(preset => (
                              <button
                                key={preset}
                                type="button"
                                onClick={() => applyTpEditVolatilityPreset(preset)}
                                style={{
                                  padding: '6px 10px',
                                  border: tpEditSelectedVolatilityPreset === preset ? `2px solid ${volatilityPresets[preset].color}` : '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  backgroundColor: tpEditSelectedVolatilityPreset === preset ? '#f9fafb' : '#fff',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                {volatilityPresets[preset].icon} {preset.charAt(0).toUpperCase() + preset.slice(1)}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Min Market Volatility (%)</label>
                          <input
                            type="number" step="0.1" min="0" max="100"
                            placeholder={`Current: ${config.minMarketVolatility ?? 'none'}`}
                            value={tpEditForm.minMarketVolatility}
                            onChange={e => setTPEditForm({ ...tpEditForm, minMarketVolatility: e.target.value })}
                            style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.minMarketVolatility ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                          {tpEditErrors.minMarketVolatility && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.minMarketVolatility}</p>}
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Max Market Volatility (%)</label>
                          <input
                            type="number" step="0.1" min="0.1" max="100"
                            placeholder={`Current: ${config.maxMarketVolatility ?? 'none'}`}
                            value={tpEditForm.maxMarketVolatility}
                            onChange={e => setTPEditForm({ ...tpEditForm, maxMarketVolatility: e.target.value })}
                            style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.maxMarketVolatility ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                          <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>Raise this (e.g. 20%) if you see VOLATILITY BLOCKED in high-vol markets</p>
                          {tpEditErrors.maxMarketVolatility && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.maxMarketVolatility}</p>}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Coins for Calculation</label>
                          <select
                            value={tpEditForm.volatilityCalculationCoins}
                            onChange={e => setTPEditForm({ ...tpEditForm, volatilityCalculationCoins: e.target.value })}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: 'white' }}
                          >
                            <option value="5">Top 5</option>
                            <option value="10">Top 10 (recommended)</option>
                            <option value="15">Top 15</option>
                            <option value="20">Top 20</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Calculation Method</label>
                          <select
                            value={tpEditForm.volatilityCalculationMethod}
                            onChange={e => setTPEditForm({ ...tpEditForm, volatilityCalculationMethod: e.target.value as 'HIGH_LOW_RANGE' | 'CHANGE_24H' | 'AUTO' })}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: 'white' }}
                          >
                            <option value="AUTO">AUTO (recommended)</option>
                            <option value="HIGH_LOW_RANGE">High/Low Range</option>
                            <option value="CHANGE_24H">24h Change</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── SECTION: BTC Correlation Filter ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: `1px solid ${tpEditForm.btcCorrelationEnabled ? '#e5e7eb' : '#fca5a5'}`, borderRadius: '8px', backgroundColor: tpEditForm.btcCorrelationEnabled ? 'transparent' : '#fff5f5' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#374151' }}>₿ BTC Correlation Filter</p>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: tpEditForm.btcCorrelationEnabled ? '#16a34a' : '#dc2626' }}>
                        <input
                          type="checkbox"
                          checked={tpEditForm.btcCorrelationEnabled}
                          onChange={e => setTPEditForm({ ...tpEditForm, btcCorrelationEnabled: e.target.checked })}
                          style={{ width: 16, height: 16, accentColor: '#16a34a', cursor: 'pointer' }}
                        />
                        {tpEditForm.btcCorrelationEnabled ? 'Enabled' : 'Disabled'}
                        </label>
                    </div>
                    {!tpEditForm.btcCorrelationEnabled && (
                      <div style={{ padding: '8px 12px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, marginBottom: 12 }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>
                          ⚠️ BTC filter is OFF — trades open regardless of BTC trend, momentum, or dump conditions.
                        </p>
                      </div>
                    )}
                    <div style={{ opacity: tpEditForm.btcCorrelationEnabled ? 1 : 0.4, pointerEvents: tpEditForm.btcCorrelationEnabled ? 'auto' : 'none', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>BTC EMA Length</label>
                        <input
                          type="number" step="1" min="1" max="200"
                          placeholder={`e.g. ${config.btcEmaLength ?? 50}`}
                          value={tpEditForm.btcEmaLength}
                          onChange={e => setTPEditForm({...tpEditForm, btcEmaLength: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                          Current: <strong>{config.btcEmaLength ?? 50}</strong>
                        </p>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>BTC EMA Timeframe</label>
                        <select
                          value={tpEditForm.btcEmaTimeframe || config.btcEmaTimeframe || '5m'}
                          onChange={e => setTPEditForm({...tpEditForm, btcEmaTimeframe: e.target.value})}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: 'white' }}
                        >
                          <option value="5m">5 minutes</option>
                          <option value="15m">15 minutes</option>
                        </select>
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                          Current: <strong>{config.btcEmaTimeframe ?? '5m'}</strong>
                        </p>
                      </div>
                    </div>
                    <div style={{ opacity: tpEditForm.btcCorrelationEnabled ? 1 : 0.4, pointerEvents: tpEditForm.btcCorrelationEnabled ? 'auto' : 'none', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Min BTC 5m Move Up — Longs (%)</label>
                        <input
                          type="number" step="0.01" min="0"
                          placeholder={`e.g. ${config.btcMomentumLongThreshold ?? 0.15}`}
                          value={tpEditForm.btcMomentumLongThreshold}
                          onChange={e => setTPEditForm({...tpEditForm, btcMomentumLongThreshold: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.btcMomentumLongThreshold ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        {tpEditErrors.btcMomentumLongThreshold && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.btcMomentumLongThreshold}</p>}
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Min BTC 5m Move Down — Shorts (%)</label>
                        <input
                          type="number" step="0.01" min="0"
                          placeholder={`e.g. ${config.btcMomentumShortThreshold ?? 0.15}`}
                          value={tpEditForm.btcMomentumShortThreshold}
                          onChange={e => setTPEditForm({...tpEditForm, btcMomentumShortThreshold: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.btcMomentumShortThreshold ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        {tpEditErrors.btcMomentumShortThreshold && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.btcMomentumShortThreshold}</p>}
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Block if BTC 15m Range &lt; (%)</label>
                        <input
                          type="number" step="0.01" min="0"
                          placeholder={`e.g. ${config.btcRangeNoTradeThreshold ?? 0.18}`}
                          value={tpEditForm.btcRangeNoTradeThreshold}
                          onChange={e => setTPEditForm({...tpEditForm, btcRangeNoTradeThreshold: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.btcRangeNoTradeThreshold ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>No trades if BTC 15m candle range is below this threshold (low volatility block)</p>
                        {tpEditErrors.btcRangeNoTradeThreshold && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.btcRangeNoTradeThreshold}</p>}
                      </div>
                    </div>
                      </div>
                      
                  {/* ── SECTION: Dump Protection ── */}
                  <div style={{ marginBottom: '4px', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 700, color: '#374151' }}>🛡️ Dump Protection</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Dump Threshold (%)</label>
                        <input
                          type="number" step="0.1" min="0.5" max="10"
                          placeholder={`e.g. ${config.btcDumpThreshold ?? 1.5}`}
                          value={tpEditForm.btcDumpThreshold}
                          onChange={e => setTPEditForm({...tpEditForm, btcDumpThreshold: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>BTC 10m drop that triggers cooldown (e.g. 1.5 = 1.5% drop in 10 min)</p>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Cooldown (minutes)</label>
                        <input
                          type="number" step="1" min="1" max="1440"
                          placeholder={`e.g. ${config.btcCooldownMinutes ?? 20}`}
                          value={tpEditForm.btcCooldownMinutes}
                          onChange={e => setTPEditForm({...tpEditForm, btcCooldownMinutes: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: tpEditErrors.btcCooldownMinutes ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>No new entries for this many minutes after a dump is detected</p>
                        {tpEditErrors.btcCooldownMinutes && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{tpEditErrors.btcCooldownMinutes}</p>}
                      </div>
                    </div>
                  </div>

                  {/* ── SECTION: BTC Higher-Timeframe Trend Confirmation ── */}
                  <div style={{ marginBottom: '4px', padding: '14px', border: '1px solid #c4b5fd', borderRadius: '8px', background: '#faf5ff' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: 700, color: '#7c3aed' }}>📈 Higher-Timeframe Trend Confirmation <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '12px' }}>(optional)</span></p>
                    <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: '#6b7280' }}>
                      Only open longs when BTC is above its 1H/4H EMA and shorts when below.
                      Blocks trades in BTC "dead zones" where meme coins decouple.
                      Leave timeframe blank to disable.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Trend Timeframe</label>
                        <select
                          value={tpEditForm.btcTrendTimeframe}
                          onChange={e => setTPEditForm({...tpEditForm, btcTrendTimeframe: e.target.value})}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #c4b5fd', borderRadius: '6px', fontSize: '14px', background: 'white' }}
                        >
                          <option value="">Disabled</option>
                          <option value="1h">1 Hour</option>
                          <option value="4h">4 Hours</option>
                        </select>
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                          Current: <strong>{config.btcTrendTimeframe || 'Disabled'}</strong>
                        </p>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>EMA Period</label>
                        <input
                          type="number" step="1" min="5" max="200"
                          placeholder={`e.g. ${config.btcTrendEmaPeriod ?? 21}`}
                          value={tpEditForm.btcTrendEmaPeriod}
                          onChange={e => setTPEditForm({...tpEditForm, btcTrendEmaPeriod: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #c4b5fd', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                          Current: <strong>{config.btcTrendEmaPeriod ?? 21}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                      </div>

                  {/* ── SECTION: Participation Filter ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: `1px solid ${tpEditForm.memeParticipationFilterEnabled ? '#a5f3fc' : '#e5e7eb'}`, borderRadius: '8px', backgroundColor: tpEditForm.memeParticipationFilterEnabled ? '#f0fdff' : 'transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#0e7490' }}>
                          {participationFilterTitle(selectedStrategyForTPEdit.strategyType)}
                        </p>
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                          {participationFilterEditHint(selectedStrategyForTPEdit.strategyType)}{' '}
                          Coin active = positive 24 h change + above-average volume. Cached 2 min.
                        </p>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: tpEditForm.memeParticipationFilterEnabled ? '#0e7490' : '#6b7280', flexShrink: 0, marginLeft: 12 }}>
                        <input
                          type="checkbox"
                          checked={tpEditForm.memeParticipationFilterEnabled}
                          onChange={e => setTPEditForm({ ...tpEditForm, memeParticipationFilterEnabled: e.target.checked })}
                          style={{ width: 16, height: 16, accentColor: '#0e7490', cursor: 'pointer' }}
                        />
                        {tpEditForm.memeParticipationFilterEnabled ? 'Enabled' : 'Disabled'}
                      </label>
                    </div>
                    <div style={{ opacity: tpEditForm.memeParticipationFilterEnabled ? 1 : 0.4, pointerEvents: tpEditForm.memeParticipationFilterEnabled ? 'auto' : 'none' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Min Participation Rate (%)</label>
                      <input
                        type="number" step="5" min="5" max="100"
                        placeholder={`e.g. ${config.minParticipationRate != null ? Math.round(config.minParticipationRate * 100) : 25}`}
                        value={tpEditForm.minParticipationRate}
                        onChange={e => setTPEditForm({ ...tpEditForm, minParticipationRate: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #a5f3fc', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                        Current: <strong>{config.minParticipationRate != null ? `${Math.round(config.minParticipationRate * 100)}%` : '25% (default)'}</strong>
                        {' · '}Minimum % of selected coins that must be active. Requires Manual coin selection.
                      </p>
                    </div>
                  </div>

                  {/* ── SECTION: Micro-Reversal Filter ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: `1px solid ${tpEditForm.microReversalFilterEnabled ? '#fde68a' : '#e5e7eb'}`, borderRadius: '8px', backgroundColor: tpEditForm.microReversalFilterEnabled ? '#fffbeb' : 'transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#b45309' }}>⚡ Micro-Reversal Filter</p>
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                          Blocks new pair creation when BTC is in the −0.10% to −0.50% drop zone from a recent 5m high. Entry gate only.
                        </p>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: tpEditForm.microReversalFilterEnabled ? '#b45309' : '#6b7280', flexShrink: 0, marginLeft: 12 }}>
                        <input
                          type="checkbox"
                          checked={tpEditForm.microReversalFilterEnabled}
                          onChange={e => setTPEditForm({ ...tpEditForm, microReversalFilterEnabled: e.target.checked })}
                          style={{ width: 16, height: 16, accentColor: '#b45309', cursor: 'pointer' }}
                        />
                        {tpEditForm.microReversalFilterEnabled ? 'Enabled' : 'Disabled'}
                      </label>
                    </div>
                    <div style={{ opacity: tpEditForm.microReversalFilterEnabled ? 1 : 0.4, pointerEvents: tpEditForm.microReversalFilterEnabled ? 'auto' : 'none', display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Min drop %</label>
                        <input type="number" step="0.01" placeholder="-0.5"
                          value={tpEditForm.microReversalMinDropPercent}
                          onChange={e => setTPEditForm({ ...tpEditForm, microReversalMinDropPercent: e.target.value })}
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #fde68a', borderRadius: 6, fontSize: 14 }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Max drop %</label>
                        <input type="number" step="0.01" placeholder="-0.1"
                          value={tpEditForm.microReversalMaxDropPercent}
                          onChange={e => setTPEditForm({ ...tpEditForm, microReversalMaxDropPercent: e.target.value })}
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #fde68a', borderRadius: 6, fontSize: 14 }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Lookback (5m candles)</label>
                        <input type="number" min={3} max={60} step={1} placeholder="12"
                          value={tpEditForm.microReversalLookbackCandles}
                          onChange={e => setTPEditForm({ ...tpEditForm, microReversalLookbackCandles: e.target.value })}
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #fde68a', borderRadius: 6, fontSize: 14 }} />
                      </div>
                    </div>
                  </div>

                  {/* ── SECTION: LOW_PARTICIPATION Behaviour Mode (US-REB-01) ── */}
                  <div style={{ marginBottom: '18px', padding: '14px', border: `1px solid ${ (tpEditForm as any).lowParticipationMode === 'ENTRY_BLOCK' ? '#fca5a5' : '#e5e7eb' }`, borderRadius: '8px', backgroundColor: (tpEditForm as any).lowParticipationMode === 'ENTRY_BLOCK' ? '#fff1f2' : 'transparent' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: 700, color: '#b91c1c' }}>🚦 LOW_PARTICIPATION Behaviour</p>
                    <p style={{ margin: '0 0 12px 0', fontSize: '11px', color: '#6b7280' }}>
                      Controls new-pair creation when AI Tune detects a LOW_PARTICIPATION regime.
                      Neither mode force-closes existing open legs.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {(['REDUCED_EXPOSURE', 'ENTRY_BLOCK'] as const).map((mode) => {
                        const selected = ((tpEditForm as any).lowParticipationMode || 'REDUCED_EXPOSURE') === mode;
                        const label = mode === 'REDUCED_EXPOSURE'
                          ? 'Reduced exposure (2 positions, tighter filters) — default'
                          : 'Full entry block (no new pairs, monitor only)';
                        const desc = mode === 'REDUCED_EXPOSURE'
                          ? 'Keep opening pairs with strict RSI / funding / participation gates and maxOpenPositions = 2. Current live behaviour.'
                          : 'Stop opening new pairs entirely when regime = LOW_PARTICIPATION. Zero new exposure until regime changes.';
                        return (
                          <label key={mode} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: selected ? `2px solid ${mode === 'ENTRY_BLOCK' ? '#ef4444' : '#0e7490'}` : '1px solid #e5e7eb', background: selected ? (mode === 'ENTRY_BLOCK' ? '#fff1f2' : '#f0fdff') : '#fafafa' }}>
                            <input
                              type="radio"
                              name="lowParticipationMode"
                              value={mode}
                              checked={selected}
                              onChange={() => setTPEditForm({ ...tpEditForm, ...(tpEditForm as any), lowParticipationMode: mode })}
                              style={{ marginTop: 2, accentColor: mode === 'ENTRY_BLOCK' ? '#ef4444' : '#0e7490' }}
                            />
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: selected ? (mode === 'ENTRY_BLOCK' ? '#b91c1c' : '#0e7490') : '#374151' }}>{label}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 2 }}>{desc}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#6b7280' }}>
                      Current: <strong>{config.lowParticipationMode || 'REDUCED_EXPOSURE'}</strong>
                      {isLowParticipationStyleRegime(config.aiAutoTuneLastRegime, selectedStrategyForTPEdit.strategyType)
                        && (tpEditForm as any).lowParticipationMode === 'ENTRY_BLOCK' && (
                        <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 700 }}>
                          ⛔ Entry block is ACTIVE (current regime = {selectedStrategyForTPEdit.strategyType === 'CRYPTO_PAIRS' ? 'LOW_LIQUIDITY' : 'LOW_PARTICIPATION'})
                        </span>
                      )}
                    </p>
                    {/* US-REB-03: auto-escalate after N days */}
                    <div style={{ marginTop: '12px', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                        Auto-escalate to ENTRY_BLOCK after N consecutive LOW_PARTICIPATION days
                        <span style={{ fontWeight: 400, color: '#9ca3af' }}> (0 = disabled)</span>
                      </label>
                      <input type="number" min="0" max="30" step="1"
                        value={(tpEditForm as any).lowParticipationAutoBlockDays || '0'}
                        onChange={e => setTPEditForm({ ...tpEditForm, ...(tpEditForm as any), lowParticipationAutoBlockDays: e.target.value })}
                        style={{ width: '80px', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px' }} />
                      <span style={{ marginLeft: 8, fontSize: '11px', color: '#6b7280' }}>days (AI Tune will propose ENTRY_BLOCK after this streak)</span>
                    </div>
                  </div>

                {/* ── BEARISH_BTC Long-Block (US-RLB-01) ───────────── */}
                <div style={{ marginBottom: '18px', padding: '14px', border: `1px solid ${(tpEditForm as any).aiRegimeLongBlockEnabled ? '#dc2626' : '#e5e7eb'}`, borderRadius: '8px', backgroundColor: (tpEditForm as any).aiRegimeLongBlockEnabled ? '#fff1f2' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#b91c1c' }}>🔒 BEARISH_BTC Long-Block</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>
                        Blocks ALL new pair creation (LONG + SHORT) when BTC is bearish. New pairs historically lose in this regime.
                        Current regime: <strong>{config.aiAutoTuneLastRegime || '—'}</strong>
                      </p>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: (tpEditForm as any).aiRegimeLongBlockEnabled ? '#b91c1c' : '#6b7280', flexShrink: 0, marginLeft: 12 }}>
                      <input type="checkbox" checked={!!(tpEditForm as any).aiRegimeLongBlockEnabled}
                        onChange={e => setTPEditForm({ ...tpEditForm, ...(tpEditForm as any), aiRegimeLongBlockEnabled: e.target.checked })}
                        style={{ width: 16, height: 16, accentColor: '#dc2626', cursor: 'pointer' }} />
                      {(tpEditForm as any).aiRegimeLongBlockEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                  {(tpEditForm as any).aiRegimeLongBlockEnabled && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                        Blocked regimes (comma-separated)
                      </label>
                      <input type="text"
                        value={(tpEditForm as any).aiRegimeLongBlockList || 'BEARISH_BTC'}
                        onChange={e => setTPEditForm({ ...tpEditForm, ...(tpEditForm as any), aiRegimeLongBlockList: e.target.value })}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }} />
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9ca3af' }}>Default: BEARISH_BTC. AI Tune will auto-enable/disable this when regime changes.</p>
                      {config.aiAutoTuneLastRegime === 'BEARISH_BTC' && (tpEditForm as any).aiRegimeLongBlockEnabled && (
                        <p style={{ margin: '6px 0 0', fontSize: '12px', fontWeight: 700, color: '#dc2626' }}>🔒 Long block is ACTIVE — no new pairs while BEARISH_BTC</p>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Scalper Mode (US-SCL-01) ───────────────────── */}
                <div style={{ marginBottom: '18px', padding: '14px', border: `1px solid ${(tpEditForm as any).scalperModeEnabled ? '#d97706' : '#e5e7eb'}`, borderRadius: '8px', backgroundColor: (tpEditForm as any).scalperModeEnabled ? '#fffbeb' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#b45309' }}>⚡ Scalper Mode</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>
                        Override TP/TSL/maxPositions with tight scalper values in HIGH_VOLATILITY. Captures fast moves before reversal.
                        Current regime: <strong>{config.aiAutoTuneLastRegime || '—'}</strong>
                      </p>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: (tpEditForm as any).scalperModeEnabled ? '#b45309' : '#6b7280', flexShrink: 0, marginLeft: 12 }}>
                      <input type="checkbox" checked={!!(tpEditForm as any).scalperModeEnabled}
                        onChange={e => setTPEditForm({ ...tpEditForm, ...(tpEditForm as any), scalperModeEnabled: e.target.checked })}
                        style={{ width: 16, height: 16, accentColor: '#d97706', cursor: 'pointer' }} />
                      {(tpEditForm as any).scalperModeEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                  {(tpEditForm as any).scalperModeEnabled && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                      {[
                        { key: 'scalperTakeProfit', label: 'Take Profit %', min: '0.5', max: '10', step: '0.5' },
                        { key: 'scalperTrailingStopLoss', label: 'Trailing SL %', min: '0.3', max: '5', step: '0.2' },
                        { key: 'scalperMaxOpenPositions', label: 'Max Positions', min: '1', max: '5', step: '1' },
                      ].map(({ key, label, min, max, step }) => (
                        <div key={key}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>{label}</label>
                          <input type="number" min={min} max={max} step={step}
                            value={(tpEditForm as any)[key] || ''}
                            onChange={e => setTPEditForm({ ...tpEditForm, ...(tpEditForm as any), [key]: e.target.value })}
                            style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }} />
                        </div>
                      ))}
                    </div>
                  )}
                  {(tpEditForm as any).scalperModeEnabled && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>Active in regimes (comma-separated)</label>
                      <input type="text"
                        value={(tpEditForm as any).scalperRegimeList || 'HIGH_VOLATILITY'}
                        onChange={e => setTPEditForm({ ...tpEditForm, ...(tpEditForm as any), scalperRegimeList: e.target.value })}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box' }} />
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9ca3af' }}>Default: HIGH_VOLATILITY. AI Tune auto-activates when regime matches.</p>
                      {config.aiAutoTuneLastRegime === 'HIGH_VOLATILITY' && (tpEditForm as any).scalperModeEnabled && (
                        <p style={{ margin: '6px 0 0', fontSize: '12px', fontWeight: 700, color: '#d97706' }}>⚡ Scalper mode is ACTIVE — tight exits in effect</p>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Coin Selection Oracle ─────────────────────── */}
                <div style={{ marginBottom: '18px', padding: '14px', border: `1px solid ${tpEditForm.oracleEnabled ? '#2563eb' : '#e5e7eb'}`, borderRadius: '8px', backgroundColor: tpEditForm.oracleEnabled ? '#eff6ff' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#1d4ed8' }}>🔮 Coin Selection Oracle</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6b7280' }}>
                        Scores every coin on RSI, momentum, volume, funding rate &amp; EMA — chooses best LONG+SHORT pair.
                        Falls back to shuffle if confidence is too low.
                      </p>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: tpEditForm.oracleEnabled ? '#1d4ed8' : '#6b7280', flexShrink: 0, marginLeft: 12 }}>
                      <input
                        type="checkbox"
                        checked={!!tpEditForm.oracleEnabled}
                        onChange={e => setTPEditForm({ ...tpEditForm, oracleEnabled: e.target.checked })}
                        style={{ width: 16, height: 16, accentColor: '#1d4ed8', cursor: 'pointer' }}
                      />
                      {tpEditForm.oracleEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>
                  <div style={{ opacity: tpEditForm.oracleEnabled ? 1 : 0.4, pointerEvents: tpEditForm.oracleEnabled ? 'auto' : 'none' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>
                      Min Confidence Threshold (0.0 – 1.0)
                    </label>
                    <input
                      type="number" step="0.05" min="0.1" max="1.0"
                      value={tpEditForm.oracleConfidenceThreshold ?? 0.5}
                      onChange={e => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0 && v <= 1)
                          setTPEditForm({ ...tpEditForm, oracleConfidenceThreshold: v });
                      }}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #93c5fd', borderRadius: '6px', fontSize: '14px' }}
                      placeholder="0.50"
                    />
                    <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                      Below this score the oracle falls back to shuffle. Default: <strong>0.50</strong>
                    </p>

                    {/* Signal Weights */}
                    <div style={{ marginTop: '14px', borderTop: '1px dashed #bfdbfe', paddingTop: '12px' }}>
                      <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 600, color: '#1e3a8a' }}>
                        Signal Weights <span style={{ fontWeight: 400, color: '#6b7280' }}>(leave blank to use global defaults)</span>
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {[
                          { key: 'oracleWeightMomentum', label: 'Momentum', defaultVal: 0.25 },
                          { key: 'oracleWeightRsi',      label: 'RSI',      defaultVal: 0.30 },
                          { key: 'oracleWeightVolume',   label: 'Volume',   defaultVal: 0.20 },
                          { key: 'oracleWeightFunding',  label: 'Funding Rate', defaultVal: 0.15 },
                          { key: 'oracleWeightEma',      label: 'EMA Trend',    defaultVal: 0.10 },
                        ].map(({ key, label, defaultVal }) => (
                          <div key={key}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#374151', marginBottom: 3 }}>
                              {label}
                            </label>
                            <input
                              type="number" step="0.05" min="0" max="1.0"
                              value={(tpEditForm as any)[key] ?? ''}
                              placeholder={`Global (${defaultVal})`}
                              onChange={e => {
                                const raw = e.target.value;
                                const v = raw === '' ? null : parseFloat(raw);
                                setTPEditForm({ ...tpEditForm, [key]: (v !== null && !isNaN(v) && v >= 0 && v <= 1) ? v : null } as any);
                              }}
                              style={{ width: '100%', padding: '6px 10px', border: '1px solid #93c5fd', borderRadius: '6px', fontSize: '13px' }}
                            />
                          </div>
                        ))}
                      </div>
                      <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#6b7280' }}>
                        Weights should ideally sum to 1.0. When blank, the value from <code>application.properties</code> is used.
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── Minimum Profit Exit ─────────────────────── */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ padding: '14px', border: '1px solid #d1fae5', borderRadius: '8px', background: '#f0fdf4' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 700, color: '#065f46' }}>
                      🛡️ Minimum Profit Exit
                    </h4>
                    <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#374151' }}>
                      TSL will NOT trigger unless the position has reached this profit level first.
                      Set to <strong>0</strong> to disable (default behaviour). Recommended: <strong>1.0%</strong>
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Min Profit Before TSL Exit (%)</label>
                        <input
                          type="number" step="0.1" min="0" max="5"
                          placeholder="e.g. 1.0"
                          value={tpEditForm.minProfitExitPercent}
                          onChange={e => setTPEditForm({...tpEditForm, minProfitExitPercent: e.target.value})}
                          style={{ width: '100%', padding: '8px 12px', border: '1px solid #6ee7b7', borderRadius: '6px', fontSize: '14px' }}
                        />
                        <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                          Current: <strong>{config.minProfitExitPercent != null ? `${config.minProfitExitPercent}%` : 'Disabled (0)'}</strong>
                          {' · '}Set to 0 to disable
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Order Type ──────────────────────────────── */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ padding: '14px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fafafa' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 700, color: '#374151' }}>
                      📋 Order Type
                    </h4>

                    {/* Radio buttons — same design as Create modal */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
                      {/* Market Order */}
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', border: `2px solid ${tpEditForm.orderType === 'MARKET' ? '#e7038e' : '#e5e7eb'}`, borderRadius: '8px', cursor: 'pointer', background: tpEditForm.orderType === 'MARKET' ? '#fdf2f8' : 'white' }}>
                        <input
                          type="radio"
                          name="tpEditOrderType"
                          value="MARKET"
                          checked={tpEditForm.orderType === 'MARKET'}
                          onChange={() => setTPEditForm({ ...tpEditForm, orderType: 'MARKET' })}
                          style={{ marginTop: '2px', accentColor: '#e7038e' }}
                        />
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>Market Order</div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Execute immediately at the current market price. Higher exchange fees (taker fees), best for fast entry/exit.
                          </div>
                        </div>
                        </label>

                      {/* Limit Order */}
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', border: `2px solid ${tpEditForm.orderType === 'LIMIT' ? '#e7038e' : '#e5e7eb'}`, borderRadius: '8px', cursor: 'pointer', background: tpEditForm.orderType === 'LIMIT' ? '#fdf2f8' : 'white' }}>
                        <input
                          type="radio"
                          name="tpEditOrderType"
                          value="LIMIT"
                          checked={tpEditForm.orderType === 'LIMIT'}
                          onChange={() => setTPEditForm({ ...tpEditForm, orderType: 'LIMIT' })}
                          style={{ marginTop: '2px', accentColor: '#e7038e' }}
                        />
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#111' }}>Limit Order <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 500 }}>(Lower Fees)</span></div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            Bot places orders at a calculated price (around the current price) to try to act as a maker. Lower exchange fees, but fills may take longer or occasionally miss.
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Limit-only options */}
                    {tpEditForm.orderType === 'LIMIT' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '4px', padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Price Offset (%)</label>
                          <input
                            type="number" step="0.01" min="0.01" max="1"
                            placeholder="e.g. 0.1"
                            value={tpEditForm.limitOffset}
                            onChange={e => setTPEditForm({ ...tpEditForm, limitOffset: e.target.value })}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                          <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                            Current: <strong>{config.limitOffset != null ? `${(config.limitOffset * 100).toFixed(2)}%` : '0.10% (default)'}</strong>
                          </p>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Fill Timeout (seconds)</label>
                        <input
                            type="number" step="1" min="5" max="120"
                            placeholder="e.g. 30"
                            value={tpEditForm.limitTimeoutSeconds}
                            onChange={e => setTPEditForm({ ...tpEditForm, limitTimeoutSeconds: e.target.value })}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                          <p style={{ margin: '3px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                            Current: <strong>{config.limitTimeoutSeconds ?? '30 (default)'}s</strong>
                          </p>
                        </div>
                      </div>
                    )}

                    <p style={{ margin: '10px 0 0 0', fontSize: '11px', color: '#6b7280' }}>
                      Current: <strong>{config.orderType || 'MARKET'}</strong>
                      {config.orderType === 'LIMIT' && config.limitOffset != null && (
                        <> · Offset: {(config.limitOffset * 100).toFixed(2)}% · Timeout: {config.limitTimeoutSeconds ?? 30}s</>
                      )}
                    </p>
                      </div>
                </div>

                {/* ── Margin Mode ─────────────────────────────── */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ padding: '14px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fafafa' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 700, color: '#374151' }}>
                      📐 Margin Mode (Futures Only)
                    </h4>
                    <select
                      value={tpEditForm.marginMode}
                      onChange={e => setTPEditForm({ ...tpEditForm, marginMode: e.target.value as 'CROSS' | 'ISOLATED' })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', background: 'white' }}
                    >
                      <option value="CROSS">Cross Margin (Default)</option>
                      <option value="ISOLATED">Isolated Margin</option>
                    </select>
                    <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                      <strong style={{ color: '#086afb' }}>Cross:</strong> Shared margin across positions.{' '}
                      <strong style={{ color: '#e7038e' }}>Isolated:</strong> Each position has its own margin (safer for pairs trading).
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#9ca3af' }}>
                      Current: <strong>{config.marginMode || 'CROSS'}</strong>
                    </p>
                  </div>
                </div>
                
                {/* Preview Changes */}
                {!isNaN(newTP) && newTP !== currentTP && (
                  <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600 }}>Preview Changes</h4>
                    <table style={{ width: '100%', fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '4px' }}>Field</th>
                          <th style={{ textAlign: 'right', padding: '4px' }}>Current</th>
                          <th style={{ textAlign: 'right', padding: '4px' }}>New</th>
                          <th style={{ textAlign: 'right', padding: '4px' }}>Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '4px' }}>Take Profit</td>
                          <td style={{ textAlign: 'right', padding: '4px' }}>{currentTP}%</td>
                          <td style={{ textAlign: 'right', padding: '4px' }}>{newTP}%</td>
                          <td style={{ textAlign: 'right', padding: '4px', color: newTP > currentTP ? '#10b981' : '#ef4444' }}>
                            {newTP > currentTP ? '⬆' : '⬇'} {Math.abs(newTP - currentTP)}%
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              <div className="modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={closeTPEditModal}
                  className="action-btn"
                  style={{ background: '#6b7280', color: 'white', border: 'none' }}
                >
                  Cancel
                </button>
                {isActive && (
                  <button
                    onClick={() => {
                      pauseStrategy(selectedStrategyForTPEdit.id);
                      closeTPEditModal();
                    }}
                    className="action-btn"
                    style={{ background: '#fbbf24', color: 'white', border: 'none' }}
                  >
                    Pause Strategy First
                  </button>
                )}
                <button
                  onClick={handleTPSave}
                  className="action-btn"
                  style={{ background: '#086afb', color: 'white', border: 'none' }}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Update Anyway'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit Entry Filters Modal */}
      {showFilterEditModal && selectedStrategyForFilterEdit && (
        <div className="modal-overlay" onClick={() => setShowFilterEditModal(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">
                🔍 Edit Entry Filters — {selectedStrategyForFilterEdit.name}
              </h3>
              <button onClick={() => setShowFilterEditModal(false)} className="modal-close">×</button>
            </div>

            <div className="modal-body">
              {/* Strategy Info */}
              <div style={{ marginBottom: 20, padding: 12, backgroundColor: '#f9fafb', borderRadius: 8 }}>
                <p style={{ margin: '4px 0', fontSize: 14 }}>
                  <strong>Strategy:</strong> {selectedStrategyForFilterEdit.name}
                </p>
                <p style={{ margin: '4px 0', fontSize: 14 }}>
                  <strong>Status:</strong> {selectedStrategyForFilterEdit.status}
                </p>
              </div>

              <div style={{ marginBottom: 24, padding: 14, border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: '#f8fafc' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>
                  Shared filter profile (optional overlay)
                </div>
                <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b' }}>
                  Adds market gates from a reusable profile on top of this strategy&apos;s JSON config. Leave empty to use strategy filters only.
                </p>
                <select
                  value={strategyFilterProfileId}
                  onChange={e => setStrategyFilterProfileId(e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
                >
                  <option value="">None (strategy JSON only)</option>
                  {strategyFilterProfiles.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleStrategyFilterProfileSave}
                  disabled={loading}
                  style={{ marginTop: 10, padding: '8px 14px', borderRadius: 8, border: 'none', background: '#086afb', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Save filter profile link
                </button>
              </div>

              {/* Master Toggle */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', backgroundColor: filterEditForm.entryFiltersEnabled ? '#ede9fe' : '#f9fafb', border: filterEditForm.entryFiltersEnabled ? '1px solid #7c3aed' : '1px solid #e5e7eb', borderRadius: 8 }}>
                  <input
                    type="checkbox"
                    id="filterEnabled"
                    checked={filterEditForm.entryFiltersEnabled}
                    onChange={e => setFilterEditForm({ ...filterEditForm, entryFiltersEnabled: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#7c3aed' }}
                  />
                  <label htmlFor="filterEnabled" style={{ fontSize: 14, fontWeight: 600, color: filterEditForm.entryFiltersEnabled ? '#5b21b6' : '#374151', cursor: 'pointer' }}>
                    {filterEditForm.entryFiltersEnabled ? '✅ Entry Filters ENABLED' : '⭕ Entry Filters DISABLED'}
                  </label>
                </div>
                <p style={{ margin: '6px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                  When OFF, all three filters are bypassed and orders proceed as normal.
                </p>
              </div>

              {/* Filter Parameter Fields — always visible so user can pre-configure before enabling */}
              <div style={{ display: 'grid', gap: 16 }}>
                {/* Funding Rate Guard */}
                <div style={{ padding: '14px 16px', border: `1px solid ${filterEditForm.fundingRateFilterEnabled ? '#e5e7eb' : '#fca5a5'}`, borderRadius: 8, backgroundColor: filterEditForm.fundingRateFilterEnabled ? '#fafafa' : '#fff5f5' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    💰 Funding Rate Guard (Longs only)
                  </h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: filterEditForm.fundingRateFilterEnabled ? '#16a34a' : '#dc2626' }}>
                      <input
                        type="checkbox"
                        checked={filterEditForm.fundingRateFilterEnabled}
                        onChange={e => setFilterEditForm({ ...filterEditForm, fundingRateFilterEnabled: e.target.checked })}
                        style={{ width: 15, height: 15, accentColor: '#16a34a' }}
                      />
                      {filterEditForm.fundingRateFilterEnabled ? 'ON' : 'OFF'}
                    </label>
                  </div>
                  <div style={{ opacity: filterEditForm.fundingRateFilterEnabled ? 1 : 0.4, pointerEvents: filterEditForm.fundingRateFilterEnabled ? 'auto' : 'none' }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>
                      Max Funding Rate for Longs (%)
                    </label>
                    <input
                      type="number"
                      min={0.001}
                      max={1}
                      step={0.001}
                      value={filterEditForm.maxFundingRateLong}
                      onChange={e => setFilterEditForm({ ...filterEditForm, maxFundingRateLong: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    />
                    <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                      Block LONG entry if KuCoin funding rate &gt; this value (e.g. 0.05 for 0.05%). Stored as decimal (÷100) in backend.
                    </p>
                  </div>
                </div>

                {/* RSI Filter */}
                <div style={{ padding: '14px 16px', border: `1px solid ${filterEditForm.rsiFilterEnabled ? '#e5e7eb' : '#fca5a5'}`, borderRadius: 8, backgroundColor: filterEditForm.rsiFilterEnabled ? '#fafafa' : '#fff5f5' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    📈 RSI Filter (Longs &amp; Shorts)
                  </h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: filterEditForm.rsiFilterEnabled ? '#16a34a' : '#dc2626' }}>
                      <input
                        type="checkbox"
                        checked={filterEditForm.rsiFilterEnabled}
                        onChange={e => setFilterEditForm({ ...filterEditForm, rsiFilterEnabled: e.target.checked })}
                        style={{ width: 15, height: 15, accentColor: '#16a34a' }}
                      />
                      {filterEditForm.rsiFilterEnabled ? 'ON' : 'OFF'}
                    </label>
                  </div>
                  <div style={{ opacity: filterEditForm.rsiFilterEnabled ? 1 : 0.4, pointerEvents: filterEditForm.rsiFilterEnabled ? 'auto' : 'none', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>RSI Period</label>
                      <input
                        type="number"
                        min={5}
                        max={30}
                        step={1}
                        value={filterEditForm.rsiPeriod}
                        onChange={e => setFilterEditForm({ ...filterEditForm, rsiPeriod: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Candles (5-min)</label>
                      <input
                        type="number"
                        min={15}
                        max={200}
                        step={1}
                        value={filterEditForm.rsiCandleLimit}
                        onChange={e => setFilterEditForm({ ...filterEditForm, rsiCandleLimit: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Min RSI — Long Entry</label>
                      <input
                        type="number"
                        min={30}
                        max={70}
                        step={1}
                        value={filterEditForm.rsiLongThreshold}
                        onChange={e => setFilterEditForm({ ...filterEditForm, rsiLongThreshold: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Max RSI — Short Entry</label>
                      <input
                        type="number"
                        min={30}
                        max={70}
                        step={1}
                        value={filterEditForm.rsiShortThreshold}
                        onChange={e => setFilterEditForm({ ...filterEditForm, rsiShortThreshold: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      />
                    </div>
                  </div>
                </div>

                {/* EMA Trend Filter */}
                <div style={{ padding: '14px 16px', border: `1px solid ${filterEditForm.emaFilterEnabled ? '#e5e7eb' : '#fca5a5'}`, borderRadius: 8, backgroundColor: filterEditForm.emaFilterEnabled ? '#fafafa' : '#fff5f5' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    📉 EMA Trend Filter (Longs only)
                  </h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: filterEditForm.emaFilterEnabled ? '#16a34a' : '#dc2626' }}>
                      <input
                        type="checkbox"
                        checked={filterEditForm.emaFilterEnabled}
                        onChange={e => setFilterEditForm({ ...filterEditForm, emaFilterEnabled: e.target.checked })}
                        style={{ width: 15, height: 15, accentColor: '#16a34a' }}
                      />
                      {filterEditForm.emaFilterEnabled ? 'ON' : 'OFF'}
                    </label>
                  </div>
                  {!filterEditForm.emaFilterEnabled && (
                    <div style={{ padding: '8px 12px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, marginBottom: 10 }}>
                      <p style={{ margin: 0, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                        ⚠️ EMA filter is OFF — longs will enter even when price is below the EMA (e.g. post-crash recovery mode).
                      </p>
                    </div>
                  )}
                  <div style={{ opacity: filterEditForm.emaFilterEnabled ? 1 : 0.4, pointerEvents: filterEditForm.emaFilterEnabled ? 'auto' : 'none' }}>
                    <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>EMA Period</label>
                    <input
                      type="number"
                      min={3}
                      max={50}
                      step={1}
                      value={filterEditForm.emaPeriod}
                      onChange={e => setFilterEditForm({ ...filterEditForm, emaPeriod: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    />
                    <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                      For LONG entries, current close must be above EMA. Uses the same candle dataset as RSI.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowFilterEditModal(false)}
                className="action-btn"
                style={{ background: '#6b7280', color: 'white', border: 'none' }}
              >
                Cancel
              </button>
              <button
                onClick={handleFilterSave}
                className="action-btn"
                style={{ background: '#7c3aed', color: 'white', border: 'none' }}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Filters'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Auto-Tune Modal (Phase 2) */}
      {showAiAutoTuneModal && selectedStrategyForAiAutoTune && (
        <div
          className="modal-overlay ai-auto-tune-modal-overlay"
          onClick={() => setShowAiAutoTuneModal(false)}
        >
          <div
            className="modal-content ai-auto-tune-modal-content"
            style={{ maxWidth: aiAutoTuneModalTab === 'review' ? '920px' : aiAutoTuneModalTab === 'history' ? '720px' : aiAutoTuneModalTab === 'performance' ? '680px' : '560px' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <h3 className="modal-title">
                🤖 AI Auto-Tune — {selectedStrategyForAiAutoTune.name}
              </h3>
              <button onClick={() => setShowAiAutoTuneModal(false)} className="modal-close">×</button>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '0 20px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setAiAutoTuneModalTab('settings')}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: aiAutoTuneModalTab === 'settings' ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                  background: aiAutoTuneModalTab === 'settings' ? '#faf5ff' : '#fff',
                  color: aiAutoTuneModalTab === 'settings' ? '#6d28d9' : '#374151',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Settings
              </button>
              <button
                type="button"
                onClick={() => setAiAutoTuneModalTab('performance')}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: aiAutoTuneModalTab === 'performance' ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                  background: aiAutoTuneModalTab === 'performance' ? '#faf5ff' : '#fff',
                  color: aiAutoTuneModalTab === 'performance' ? '#6d28d9' : '#374151',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Performance
              </button>
              <button
                type="button"
                onClick={() => {
                  setAiAutoTuneModalTab('history');
                  if (selectedStrategyForAiAutoTune) {
                    loadAiAutoTuneHistory(selectedStrategyForAiAutoTune.id, aiAutoTuneHistoryPage);
                  }
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: aiAutoTuneModalTab === 'history' ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                  background: aiAutoTuneModalTab === 'history' ? '#faf5ff' : '#fff',
                  color: aiAutoTuneModalTab === 'history' ? '#6d28d9' : '#374151',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                History
              </button>
              <button
                type="button"
                onClick={() => setAiAutoTuneModalTab('review')}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: aiAutoTuneModalTab === 'review' ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                  background: aiAutoTuneModalTab === 'review' ? '#faf5ff' : '#fff',
                  color: aiAutoTuneModalTab === 'review' ? '#6d28d9' : '#374151',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Review
              </button>
            </div>
            <div
              className="modal-body ai-auto-tune-modal-body"
              style={{ flex: 1, minHeight: 0, overflowY: 'auto', maxHeight: 'none' }}
            >
              {aiAutoTuneModalTab === 'review' ? (
                <AiTuneRegimeReview
                  strategyId={selectedStrategyForAiAutoTune.id}
                  active={aiAutoTuneModalTab === 'review'}
                />
              ) : aiAutoTuneModalTab === 'history' ? (
                <div>
                  <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
                    Before/after values for each AI auto-tune event on this strategy.
                  </p>
                  {aiAutoTuneHistoryLoading && (
                    <p style={{ fontSize: 13, color: '#6b7280' }}>Loading history...</p>
                  )}
                  {!aiAutoTuneHistoryLoading && aiAutoTuneHistory.length === 0 && (
                    <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, color: '#6b7280', fontSize: 13 }}>
                      No AI tune history yet. Run AI Tune or wait for the scheduler after enabling auto-tune.
                    </div>
                  )}
                  {!aiAutoTuneHistoryLoading && aiAutoTuneHistory.map((entry) => {
                    const isExpanded = expandedAiTuneHistoryId === entry.id;
                    const changes = entry.changes || [];
                    return (
                      <div
                        key={entry.id}
                        style={{
                          marginBottom: 12,
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          overflow: 'hidden',
                          background: '#fff',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedAiTuneHistoryId(isExpanded ? null : entry.id)}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '12px 14px',
                            border: 'none',
                            background: '#f8fafc',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: 13, color: '#111827' }}>
                              {entry.appliedAt ? new Date(entry.appliedAt).toLocaleString() : '—'}
                            </strong>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                              {entry.marketRegime || '—'} · {entry.outcome || '—'} · {entry.trigger || '—'}
                              {entry.runTriggerReason ? ` · ${entry.runTriggerReason}` : ''}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                            Actor: {entry.actor || 'SYSTEM'} · {changes.length} change(s)
                            {entry.performanceAtApply?.netPnl != null && entry.performanceAfter?.netPnl != null && (
                              <span>
                                {' '}· At apply net: {String(entry.performanceAtApply.netPnl)} → after: {String(entry.performanceAfter.netPnl)}
                              </span>
                            )}
                          </div>
                        </button>
                        {isExpanded && (
                          <div style={{ padding: 12 }}>
                            <AiTunePerformanceMetaPanels meta={entry.performanceMeta} />
                            <AiTuneChangeTable changes={changes} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {aiAutoTuneHistoryTotalPages > 1 && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                      <button
                        type="button"
                        className="action-btn"
                        style={{ background: '#6b7280', color: '#fff', border: 'none' }}
                        disabled={aiAutoTuneHistoryLoading || aiAutoTuneHistoryPage <= 0}
                        onClick={() => selectedStrategyForAiAutoTune && loadAiAutoTuneHistory(selectedStrategyForAiAutoTune.id, aiAutoTuneHistoryPage - 1)}
                      >
                        Prev
                      </button>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        Page {aiAutoTuneHistoryPage + 1} / {Math.max(aiAutoTuneHistoryTotalPages, 1)}
                      </span>
                      <button
                        type="button"
                        className="action-btn"
                        style={{ background: '#6b7280', color: '#fff', border: 'none' }}
                        disabled={aiAutoTuneHistoryLoading || aiAutoTuneHistoryPage + 1 >= aiAutoTuneHistoryTotalPages}
                        onClick={() => selectedStrategyForAiAutoTune && loadAiAutoTuneHistory(selectedStrategyForAiAutoTune.id, aiAutoTuneHistoryPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : aiAutoTuneModalTab === 'performance' ? (
                <AiTunePerformanceSnapshot
                  strategyId={selectedStrategyForAiAutoTune.id}
                  active={aiAutoTuneModalTab === 'performance'}
                />
              ) : (
              <>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
                Applies only to this strategy. Other bots are not affected unless you enable it on each one.
                Leverage and order size are never auto-changed.
              </p>
              {(() => {
                const saved = parseStrategyConfig(selectedStrategyForAiAutoTune.config);
                return (
                  <div style={{
                    marginBottom: 16,
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: saved.aiAutoTuneEnabled ? '#f0fdf4' : '#f9fafb',
                    border: `1px solid ${saved.aiAutoTuneEnabled ? '#86efac' : '#e5e7eb'}`,
                  }}>
                    <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700 }}>
                      Saved state: {saved.aiAutoTuneEnabled ? '✅ Auto-tune ON' : '⭕ Auto-tune OFF'}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: 13 }}><strong>Strategy status:</strong> {selectedStrategyForAiAutoTune.status}</p>
                    {saved.aiAutoTuneEnabled && (
                      <>
                        <p style={{ margin: '4px 0', fontSize: 12, color: '#444' }}>
                          Last regime: <strong>{saved.aiAutoTuneLastRegime || '—'}</strong> · Last applied: <strong>{formatAiAutoTuneLastApplied(saved.aiAutoTuneLastAppliedAt)}</strong>
                        </p>
                        {hasAiAutoTunePending(saved) && (
                          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#b45309', fontWeight: 600 }}>⏳ Pending approval required</p>
                        )}
                      </>
                    )}
                    {!saved.aiAutoTuneEnabled && (
                      <p style={{ margin: '6px 0 0', fontSize: 11, color: '#64748b' }}>
                        Enable below and click <strong>Save Settings</strong> — the action button turns green (✅ AI On).
                      </p>
                    )}
                  </div>
                );
              })()}
              <div
                style={{
                  marginBottom: 12,
                  padding: '10px 14px',
                  borderRadius: 8,
                  fontSize: 12,
                  backgroundColor: aiAutoTuneGlobal?.schedulerEnabled ? '#ecfdf5' : '#f8fafc',
                  border: `1px solid ${aiAutoTuneGlobal?.schedulerEnabled ? '#86efac' : '#e2e8f0'}`,
                  color: '#334155',
                }}
              >
                <strong>Automatic re-runs (server scheduler):</strong>{' '}
                {aiAutoTuneGlobal?.schedulerEnabled
                  ? `ON — evaluates enabled bots every ~${formatAiAutoTuneInterval(aiAutoTuneGlobal.schedulerIntervalMs)}`
                  : 'OFF — does not mean AI Tune is disabled; use Run Now below or set trading.ai.auto-tune.scheduler.enabled=true'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', marginBottom: 12, backgroundColor: aiAutoTuneForm.aiAutoTuneEnabled ? '#ede9fe' : '#f9fafb', border: aiAutoTuneForm.aiAutoTuneEnabled ? '1px solid #7c3aed' : '1px solid #e5e7eb', borderRadius: 8 }}>
                <input
                  type="checkbox"
                  id="aiAutoTuneEnabled"
                  checked={aiAutoTuneForm.aiAutoTuneEnabled}
                  onChange={e => setAiAutoTuneForm({ ...aiAutoTuneForm, aiAutoTuneEnabled: e.target.checked })}
                  style={{ width: 18, height: 18, accentColor: '#7c3aed' }}
                />
                <label htmlFor="aiAutoTuneEnabled" style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Enable live AI auto-tune for this bot
                </label>
              </div>
              {aiAutoTuneForm.aiAutoTuneEnabled && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 8px' }}>Apply mode (per bot — choose one)</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => setAiAutoTuneForm({ ...aiAutoTuneForm, aiAutoTuneRequireApproval: true })}
                      style={{
                        textAlign: 'left',
                        padding: 12,
                        borderRadius: 8,
                        cursor: 'pointer',
                        border: aiAutoTuneForm.aiAutoTuneRequireApproval ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                        background: aiAutoTuneForm.aiAutoTuneRequireApproval ? '#faf5ff' : '#fff',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#5b21b6' }}>🛡️ Manual approve</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
                        Run Now or scheduler → <strong>AI Pending</strong> → you Apply Pending. Best for live money.
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAiAutoTuneForm({ ...aiAutoTuneForm, aiAutoTuneRequireApproval: false })}
                      style={{
                        textAlign: 'left',
                        padding: 12,
                        borderRadius: 8,
                        cursor: 'pointer',
                        border: !aiAutoTuneForm.aiAutoTuneRequireApproval ? '2px solid #059669' : '1px solid #e5e7eb',
                        background: !aiAutoTuneForm.aiAutoTuneRequireApproval ? '#ecfdf5' : '#fff',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#047857' }}>⚡ Auto-apply</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
                        Run Now or scheduler → config updates immediately. No Apply step.
                      </div>
                    </button>
                  </div>
                </div>
              )}
              {aiAutoTuneForm.aiAutoTuneEnabled && (
                <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc' }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 8px' }}>
                    Trade performance feedback (KuCoin Live)
                  </p>
                  {!aiAutoTuneGlobal?.performanceTuneEnabled && (
                    <p style={{ fontSize: 11, color: '#b45309', margin: '0 0 8px', lineHeight: 1.4 }}>
                      Server master switch is OFF (<code>trading.ai.performance-tune.enabled=false</code>).
                      Snapshot tab still works; parameter nudges from performance are disabled until ops enables it.
                    </p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <input
                      type="checkbox"
                      id="aiPerformanceTuneEnabled"
                      checked={aiAutoTuneForm.aiPerformanceTuneEnabled}
                      disabled={!aiAutoTuneGlobal?.performanceTuneEnabled}
                      onChange={e => setAiAutoTuneForm({ ...aiAutoTuneForm, aiPerformanceTuneEnabled: e.target.checked })}
                      style={{ width: 18, height: 18, accentColor: '#0891b2' }}
                    />
                    <label htmlFor="aiPerformanceTuneEnabled" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      Use trade performance feedback when tuning parameters
                    </label>
                  </div>
                  {aiAutoTuneForm.aiPerformanceTuneEnabled && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontWeight: 600, color: '#475569' }}>Window (last N closes)</span>
                        <select
                          value={aiAutoTuneForm.aiPerformanceTuneWindow}
                          onChange={e => setAiAutoTuneForm({
                            ...aiAutoTuneForm,
                            aiPerformanceTuneWindow: Number(e.target.value),
                          })}
                          style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                        >
                          {[5, 10, 12, 20].map(w => (
                            <option key={w} value={w}>{w}</option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontWeight: 600, color: '#475569' }}>Min trades required</span>
                        <input
                          type="number"
                          min={3}
                          max={50}
                          value={aiAutoTuneForm.aiPerformanceTuneMinTrades}
                          onChange={e => setAiAutoTuneForm({
                            ...aiAutoTuneForm,
                            aiPerformanceTuneMinTrades: Math.min(50, Math.max(3, Number(e.target.value) || 8)),
                          })}
                          style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #cbd5e1' }}
                        />
                      </label>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        id="aiPerformanceCoinTuneEnabled"
                        checked={aiAutoTuneForm.aiPerformanceCoinTuneEnabled}
                        disabled={!aiAutoTuneGlobal?.performanceTuneEnabled}
                        onChange={e => setAiAutoTuneForm({ ...aiAutoTuneForm, aiPerformanceCoinTuneEnabled: e.target.checked })}
                        style={{ width: 18, height: 18, accentColor: '#0891b2' }}
                      />
                      <label htmlFor="aiPerformanceCoinTuneEnabled" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        Adjust coin list from performance (TOP_N / MANUAL / ALL_COINS — swap remove + add)
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        id="aiPerformanceSideBlockEnabled"
                        checked={aiAutoTuneForm.aiPerformanceSideBlockEnabled}
                        disabled={!aiAutoTuneGlobal?.performanceTuneEnabled}
                        onChange={e => setAiAutoTuneForm({ ...aiAutoTuneForm, aiPerformanceSideBlockEnabled: e.target.checked })}
                        style={{ width: 18, height: 18, accentColor: '#0891b2' }}
                      />
                      <label htmlFor="aiPerformanceSideBlockEnabled" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        Block losing LONG/SHORT per coin
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <input
                        type="checkbox"
                        id="aiPerformanceTuneLlmEnabled"
                        checked={aiAutoTuneForm.aiPerformanceTuneLlmEnabled}
                        disabled={!aiAutoTuneGlobal?.performanceLlmEnabled || !aiAutoTuneForm.aiPerformanceTuneEnabled}
                        onChange={e => setAiAutoTuneForm({ ...aiAutoTuneForm, aiPerformanceTuneLlmEnabled: e.target.checked })}
                        style={{ width: 16, height: 16 }}
                      />
                      <label htmlFor="aiPerformanceTuneLlmEnabled" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        LLM advisor for richer performance reasoning + coin suggestions (optional)
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <input
                        type="checkbox"
                        id="aiPerformanceTuneHistoryLearningEnabled"
                        checked={aiAutoTuneForm.aiPerformanceTuneHistoryLearningEnabled}
                        disabled={!aiAutoTuneGlobal?.performanceHistoryLearningEnabled || !aiAutoTuneForm.aiPerformanceTuneEnabled}
                        onChange={e => setAiAutoTuneForm({ ...aiAutoTuneForm, aiPerformanceTuneHistoryLearningEnabled: e.target.checked })}
                        style={{ width: 16, height: 16 }}
                      />
                      <label htmlFor="aiPerformanceTuneHistoryLearningEnabled" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        Learn from tune history (LLM context + skip repeated ineffective changes)
                      </label>
                    </div>
                    {aiAutoTuneForm.aiPerformanceTuneHistoryLearningEnabled ? (
                      <p style={{ fontSize: 11, color: '#64748b', margin: '6px 0 0', lineHeight: 1.4 }}>
                        Feeds recent tune outcomes into the LLM prompt and blocks repeats that did not improve net PnL.
                      </p>
                    ) : null}
                    {!aiAutoTuneGlobal?.performanceLlmEnabled ? (
                      <p style={{ fontSize: 11, color: '#b45309', margin: '6px 0 0' }}>
                        Server LLM off — set <code>trading.ai.performance-tune.llm.enabled=true</code> and API key to use.
                      </p>
                    ) : aiAutoTuneGlobal?.performanceLlmConfigured === false ? (
                      <p style={{ fontSize: 11, color: '#b45309', margin: '6px 0 0' }}>
                        LLM enabled on server but API key missing ({aiAutoTuneGlobal?.performanceLlmProvider ?? 'openai'} / {aiAutoTuneGlobal?.performanceLlmModel ?? '—'}).
                      </p>
                    ) : null}
                  </div>
                  <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 0', lineHeight: 1.4 }}>
                    Nudges trailing SL, take profit, RSI, and activity filters from recent closed positions.
                    Coin list and side blocks require approval when manual approve mode is on.
                  </p>
                </div>
              )}

              {/* ── Regime Gates (v1.22) ── */}
              {aiAutoTuneModalTab === 'settings' && (
                <div style={{ marginTop: 20, padding: 16, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafafa' }}>
                  <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#374151' }}>🚦 Regime Gates</p>
                  <p style={{ margin: '0 0 14px', fontSize: 11, color: '#6b7280' }}>
                    Per-bot runtime guards applied at pair creation. Never force-closes open positions.
                    Saved together with <strong>Save Settings</strong>.
                  </p>

                  {/* Gate 1 — LOW_PARTICIPATION */}
                  <div style={{ marginBottom: 14, padding: 12, border: `1px solid ${aiRegimeGatesForm.lowParticipationMode === 'ENTRY_BLOCK' ? '#fca5a5' : '#e5e7eb'}`, borderRadius: 8, background: aiRegimeGatesForm.lowParticipationMode === 'ENTRY_BLOCK' ? '#fff1f2' : '#fff' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>LOW_PARTICIPATION behaviour</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(['REDUCED_EXPOSURE', 'ENTRY_BLOCK'] as const).map(mode => {
                        const sel = aiRegimeGatesForm.lowParticipationMode === mode;
                        return (
                          <label key={mode} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '8px 10px', borderRadius: 7, border: sel ? `2px solid ${mode === 'ENTRY_BLOCK' ? '#ef4444' : '#0e7490'}` : '1px solid #e5e7eb', background: sel ? (mode === 'ENTRY_BLOCK' ? '#fff1f2' : '#f0fdff') : '#fafafa' }}>
                            <input type="radio" name="aiTune_lowParticipationMode" value={mode} checked={sel}
                              onChange={() => setAiRegimeGatesForm({ ...aiRegimeGatesForm, lowParticipationMode: mode })}
                              style={{ marginTop: 2, accentColor: mode === 'ENTRY_BLOCK' ? '#ef4444' : '#0e7490' }} />
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: sel ? (mode === 'ENTRY_BLOCK' ? '#b91c1c' : '#0e7490') : '#374151' }}>
                                {mode === 'REDUCED_EXPOSURE' ? 'Reduced exposure (2 positions, tighter filters) — default' : 'Full entry block (no new pairs, monitor only)'}
                              </div>
                              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                {mode === 'REDUCED_EXPOSURE' ? 'Keeps opening pairs with strict gates, max 2 positions. Current live behaviour.' : 'Stops all new pair creation while regime = LOW_PARTICIPATION.'}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Auto-escalate to ENTRY_BLOCK after</label>
                      <input type="number" min={0} max={30} step={1}
                        value={aiRegimeGatesForm.lowParticipationAutoBlockDays}
                        onChange={e => setAiRegimeGatesForm({ ...aiRegimeGatesForm, lowParticipationAutoBlockDays: parseInt(e.target.value, 10) || 0 })}
                        style={{ width: 60, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }} />
                      <span style={{ fontSize: 11, color: '#6b7280' }}>consecutive days (0 = disabled)</span>
                    </div>
                  </div>

                  {/* Gate 2 — BEARISH_BTC Long Block */}
                  <div style={{ marginBottom: 14, padding: 12, border: `1px solid ${aiRegimeGatesForm.aiRegimeLongBlockEnabled ? '#dc2626' : '#e5e7eb'}`, borderRadius: 8, background: aiRegimeGatesForm.aiRegimeLongBlockEnabled ? '#fff1f2' : '#fff' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#374151' }}>🔒 BEARISH_BTC — Short-only mode (Long Block)</p>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
                      <input type="checkbox" checked={aiRegimeGatesForm.aiRegimeLongBlockEnabled}
                        onChange={e => setAiRegimeGatesForm({ ...aiRegimeGatesForm, aiRegimeLongBlockEnabled: e.target.checked })}
                        style={{ accentColor: '#dc2626' }} />
                      <span style={{ fontWeight: 600, color: aiRegimeGatesForm.aiRegimeLongBlockEnabled ? '#b91c1c' : '#374151' }}>
                        {aiRegimeGatesForm.aiRegimeLongBlockEnabled ? 'Enabled — no new LONG pairs in blocked regimes' : 'Disabled'}
                      </span>
                    </label>
                    {aiRegimeGatesForm.aiRegimeLongBlockEnabled && (
                      <div style={{ marginTop: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Blocked regimes (comma-separated)</label>
                        <input type="text"
                          value={aiRegimeGatesForm.aiRegimeLongBlockList}
                          onChange={e => setAiRegimeGatesForm({ ...aiRegimeGatesForm, aiRegimeLongBlockList: e.target.value })}
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12 }} />
                      </div>
                    )}
                  </div>

                  {/* Gate 3 — HIGH_VOLATILITY Scalper Mode */}
                  <div style={{ padding: 12, border: `1px solid ${aiRegimeGatesForm.scalperModeEnabled ? '#7c3aed' : '#e5e7eb'}`, borderRadius: 8, background: aiRegimeGatesForm.scalperModeEnabled ? '#faf5ff' : '#fff' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#374151' }}>⚡ HIGH_VOLATILITY — Scalper Mode</p>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
                      <input type="checkbox" checked={aiRegimeGatesForm.scalperModeEnabled}
                        onChange={e => setAiRegimeGatesForm({ ...aiRegimeGatesForm, scalperModeEnabled: e.target.checked })}
                        style={{ accentColor: '#7c3aed' }} />
                      <span style={{ fontWeight: 600, color: aiRegimeGatesForm.scalperModeEnabled ? '#6d28d9' : '#374151' }}>
                        {aiRegimeGatesForm.scalperModeEnabled ? 'Enabled — tighter TP/TSL in high-volatility' : 'Disabled'}
                      </span>
                    </label>
                    {aiRegimeGatesForm.scalperModeEnabled && (
                      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        {[
                          { key: 'scalperTakeProfit', label: 'Take profit %', placeholder: '2.0' },
                          { key: 'scalperTrailingStopLoss', label: 'Trailing SL %', placeholder: '1.0' },
                          { key: 'scalperMaxOpenPositions', label: 'Max positions', placeholder: '2' },
                          { key: 'scalperRegimeList', label: 'Active regimes', placeholder: 'HIGH_VOLATILITY' },
                        ].map(({ key, label, placeholder }) => (
                          <div key={key}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>{label}</label>
                            <input type="text" placeholder={placeholder}
                              value={(aiRegimeGatesForm as any)[key]}
                              onChange={e => setAiRegimeGatesForm({ ...aiRegimeGatesForm, [key]: e.target.value })}
                              style={{ width: '100%', padding: '5px 8px', border: '1px solid #c4b5fd', borderRadius: 6, fontSize: 12 }} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {aiAutoTunePending != null ? (
                <div style={{ padding: 12, marginBottom: 16, background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, fontSize: 13 }}>
                  <strong>Pending AI suggestion</strong>
                  <p style={{ margin: '8px 0 0' }}>Regime: {String(aiAutoTunePending.regimeLabel ?? aiAutoTunePending.marketRegime ?? '—')}</p>
                  <AiTunePerformanceMetaPanels
                    meta={{
                      triggeredPerformanceRules: aiAutoTunePending.triggeredPerformanceRules,
                      performanceSnapshot: aiAutoTunePending.performanceSnapshot,
                      performanceReasoning: aiAutoTunePending.performanceReasoning,
                      llmUsed: aiAutoTunePending.llmUsed,
                      llmFallback: aiAutoTunePending.llmFallback,
                      llmSkipReason: aiAutoTunePending.llmSkipReason,
                      llmReasoning: aiAutoTunePending.llmReasoning,
                      llmFieldAdjustments: aiAutoTunePending.llmFieldAdjustments,
                      llmCoinAdjustments: aiAutoTunePending.llmCoinAdjustments,
                      llmProvider: aiAutoTunePending.llmProvider,
                    }}
                  />
                  {aiAutoTunePending.coinChangeDetail != null &&
                    typeof aiAutoTunePending.coinChangeDetail === 'object' ? (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      <strong>Coin list change</strong>
                      <p style={{ margin: '4px 0 0' }}>
                        Removed: {String((aiAutoTunePending.coinChangeDetail as Record<string, unknown>).removed ?? '—')}
                        {' · '}
                        Added: {String((aiAutoTunePending.coinChangeDetail as Record<string, unknown>).added ?? '—')}
                      </p>
                      <p style={{ margin: '2px 0 0', color: '#78350f' }}>
                        {String((aiAutoTunePending.coinChangeDetail as Record<string, unknown>).reason ?? '')}
                      </p>
                    </div>
                  ) : null}
                  {aiAutoTunePending.sideBlockChangeDetail != null &&
                    typeof aiAutoTunePending.sideBlockChangeDetail === 'object' ? (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      <strong>Side blocks</strong>
                      <pre style={{ margin: '4px 0 0', fontSize: 11, whiteSpace: 'pre-wrap', color: '#78350f' }}>
                        {JSON.stringify((aiAutoTunePending.sideBlockChangeDetail as Record<string, unknown>).after ?? {}, null, 0)}
                      </pre>
                    </div>
                  ) : null}
                  {Array.isArray(aiAutoTunePending.reasoning) ? (
                    <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
                      {(aiAutoTunePending.reasoning as string[]).slice(0, 6).map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
              </>
              )}
            </div>
            <div
              className="modal-footer ai-auto-tune-modal-footer"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                justifyContent: 'flex-end',
                flexShrink: 0,
                background: '#fff',
              }}
            >
              <button onClick={() => setShowAiAutoTuneModal(false)} className="action-btn ai-auto-tune-btn-close" style={{ background: '#6b7280', color: 'white', border: 'none' }}>Close</button>
              <button onClick={handleAiAutoTuneRunNow} className="action-btn ai-auto-tune-btn-run" style={{ background: '#0891b2', color: 'white', border: 'none' }} disabled={aiAutoTuneRunning || loading}>
                {aiAutoTuneRunning ? 'Running...' : 'Run Now'}
              </button>
              <button onClick={handleAiAutoTuneSave} className="action-btn ai-auto-tune-btn-save" style={{ background: '#7c3aed', color: 'white', border: 'none' }} disabled={loading}>
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
              {aiAutoTunePending && (
                <>
                  <button onClick={handleAiAutoTuneDismissPending} className="action-btn ai-auto-tune-btn-dismiss" style={{ background: '#dc2626', color: 'white', border: 'none' }} disabled={loading}>Dismiss</button>
                  <button onClick={handleAiAutoTuneApplyPending} className="action-btn ai-auto-tune-btn-apply" style={{ background: '#16a34a', color: 'white', border: 'none' }} disabled={loading}>Apply Pending</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* Clone Strategy Modal                                            */}
      {/* ================================================================ */}
      {showCloneModal && strategyToClone && (
        <div
          onClick={() => setShowCloneModal(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '16px', overflow: 'hidden', boxSizing: 'border-box',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12,
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
              width: '100%', maxWidth: 520,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden', padding: '24px', boxSizing: 'border-box',
              maxHeight: `${viewportH - 32}px`,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>📋 Clone Strategy</h3>
              <button
                onClick={() => setShowCloneModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280', padding: '0 4px' }}
              >×</button>
            </div>

            {/* Source info */}
            <div style={{ padding: '10px 14px', background: '#f9fafb', borderRadius: 8, marginBottom: 20, fontSize: 13, color: '#374151', flexShrink: 0 }}>
              <span>Cloning: </span><strong>{strategyToClone.name}</strong>
              <span style={{ marginLeft: 12, color: strategyToClone.status === 'ACTIVE' ? '#16a34a' : '#6b7280', fontWeight: 600 }}>
                {strategyToClone.status}
              </span>
            </div>

            {/* Scrollable form body */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>

              {/* Error */}
              {cloneError && (
                <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
                  <span>⚠️</span><span>{cloneError}</span>
                </div>
              )}

              {/* New name */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#374151' }}>
                  New Strategy Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={cloneForm.name}
                  onChange={e => setCloneForm(prev => ({ ...prev, name: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="e.g. Aristos-New2 - Copy"
                />
              </div>

              {/* Take Profit */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#374151' }}>Take Profit %</label>
                <input
                  type="number"
                  min="0" step="0.1"
                  value={cloneForm.takeProfit}
                  onChange={e => setCloneForm(prev => ({ ...prev, takeProfit: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Leave blank to keep original"
                />
              </div>

              {/* Fixed Order Amount */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#374151' }}>Fixed Order Amount (USDT)</label>
                <input
                  type="number"
                  min="1" step="1"
                  value={cloneForm.fixedOrderAmount}
                  onChange={e => setCloneForm(prev => ({ ...prev, fixedOrderAmount: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Leave blank to keep original"
                />
              </div>

              {/* Max Open Positions */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6, color: '#374151' }}>Max Open Positions</label>
                <input
                  type="number"
                  min="1" step="1"
                  value={cloneForm.maxOpenPositions}
                  onChange={e => setCloneForm(prev => ({ ...prev, maxOpenPositions: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Leave blank to keep original"
                />
              </div>

              {/* Coin Selection Mode */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#374151' }}>Coin Selection Mode</label>
                <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                  {(['TOP_N', 'MANUAL'] as const).map(mode => (
                    <button
                      key={mode}
                      onClick={() => setCloneForm(prev => ({ ...prev, coinSelectionMode: mode }))}
                      style={{
                        flex: 1, padding: '10px 0', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                        background: cloneForm.coinSelectionMode === mode ? '#0d9488' : '#fff',
                        color: cloneForm.coinSelectionMode === mode ? '#fff' : '#374151',
                      }}
                    >
                      {mode === 'TOP_N' ? '📊 Top N by Volume' : '✋ Manual Selection'}
                    </button>
                  ))}
                </div>
                {cloneForm.coinSelectionMode === 'TOP_N' && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Number of top coins</label>
                    <input
                      type="number" min="1" max="200"
                      value={cloneForm.coinLimit}
                      onChange={e => setCloneForm(prev => ({ ...prev, coinLimit: parseInt(e.target.value) || 20 }))}
                      style={{ width: 100, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {[5, 10, 20, 30, 50].map(n => (
                        <button key={n} onClick={() => setCloneForm(prev => ({ ...prev, coinLimit: n }))}
                          style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            background: cloneForm.coinLimit === n ? '#0d9488' : '#f3f4f6',
                            color: cloneForm.coinLimit === n ? '#fff' : '#374151',
                            border: cloneForm.coinLimit === n ? 'none' : '1px solid #e5e7eb' }}>
                          Top {n}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {cloneForm.coinSelectionMode === 'MANUAL' && (
                  <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                    ✏️ Coins will be copied from the original strategy. You can edit them after saving using the 🪙 Coins button.
                  </p>
                )}
              </div>

              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                The cloned strategy will start in <strong>STOPPED</strong> status. All other settings are copied from the original.
              </p>

              <div style={{
                marginTop: 12,
                padding: '10px 12px',
                background: '#fffbeb',
                border: '1px solid #f59e0b',
                borderRadius: 8,
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
                <p style={{ fontSize: 12, color: '#92400e', margin: 0, lineHeight: 1.5 }}>
                  <strong>Trading Account not copied.</strong>{' '}
                  {canUseSharedKucoin
                    ? 'The clone will use the primary/shared KuCoin account by default. Once saved, assign a dedicated Trading Account via the Trading Account column before starting it — to avoid two strategies trading on the same account simultaneously.'
                    : 'You do not have permission to use the shared KuCoin (System) account. You must assign a personal trading account via the Trading Account column before starting this clone.'}
                </p>
              </div>

            </div>{/* end scrollable body */}

            {/* Footer */}
            <div style={{
              display: 'flex', gap: 12, justifyContent: 'flex-end',
              paddingTop: 16, paddingBottom: 'env(safe-area-inset-bottom, 8px)',
              borderTop: '1px solid #e5e7eb', flexShrink: 0, background: '#fff',
            }}>
              <button
                onClick={() => setShowCloneModal(false)}
                style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#6b7280', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCloneSave}
                disabled={cloningInProgress}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: cloningInProgress ? '#9ca3af' : '#f59e0b',
                  color: '#fff', fontWeight: 600, fontSize: 14,
                  cursor: cloningInProgress ? 'not-allowed' : 'pointer', minWidth: 140,
                }}
              >
                {cloningInProgress ? 'Cloning…' : '📋 Create Clone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Coins Modal                                                */}
      {/* ================================================================ */}
      {showEditCoinsModal && selectedStrategyForCoinEdit && (
        /* Overlay: fixed to the real viewport via inset:0.
           alignItems/justifyContent center the modal.
           The 16px padding IS the gap between modal edge and screen edge.
           overflow:hidden ensures nothing escapes the overlay bounds. */
        <div
          onClick={() => setShowEditCoinsModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {/* Modal box: maxHeight 100% means "fill the overlay's padded interior".
              This completely avoids the iOS Safari 100vh > visible-height bug. */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
              width: '100%',
              maxWidth: 680,
              height: '100%',        /* stretch to fill available overlay space */
              maxHeight: '100%',     /* never exceed the overlay's padded area */
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              padding: '24px',
              boxSizing: 'border-box',
            }}
          >
            {/* Header — fixed, never scrolls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                🪙 Edit Coin Selection — {selectedStrategyForCoinEdit.name}
              </h3>
              <button
                onClick={() => setShowEditCoinsModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#6b7280', padding: '0 4px' }}
              >×</button>
            </div>

            {/* Scrollable body — takes all remaining height */}
            {/* minHeight:0 is required: flex children default to min-height:auto which prevents shrinking */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>

            {/* Strategy info bar */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 20, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, fontSize: 13, color: '#374151' }}>
              <span><strong>Strategy:</strong> {selectedStrategyForCoinEdit.name}</span>
              <span><strong>Status:</strong> <span style={{ color: selectedStrategyForCoinEdit.status === 'ACTIVE' ? '#16a34a' : '#6b7280', fontWeight: 600 }}>{selectedStrategyForCoinEdit.status}</span></span>
            </div>

            {/* Active warning */}
            {selectedStrategyForCoinEdit.status === 'ACTIVE' && (
              <div style={{ display: 'flex', gap: 10, padding: '10px 14px', background: '#fefce8', border: '1px solid #fbbf24', borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
                <span>⚠️</span>
                <span style={{ color: '#92400e' }}>Strategy is <strong>ACTIVE</strong>. Changes are safe and take effect on the next execution tick (~60 seconds).</span>
              </div>
            )}

            {/* Mode switcher */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#374151' }}>Selection Mode</label>
              <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                {(['TOP_N', 'MANUAL'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setEditCoinsForm(prev => ({ ...prev, coinSelectionMode: mode, coinSearch: '' }))}
                    style={{
                      flex: 1,
                      padding: '10px 0',
                      background: editCoinsForm.coinSelectionMode === mode ? '#0d9488' : '#fff',
                      color: editCoinsForm.coinSelectionMode === mode ? '#fff' : '#374151',
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                  >
                    {mode === 'TOP_N' ? '📊 Top N by Volume' : '✋ Manual Selection'}
                  </button>
                ))}
              </div>
            </div>

            {/* TOP_N mode controls */}
            {editCoinsForm.coinSelectionMode === 'TOP_N' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#374151' }}>
                  Number of Top Coins by 24h Volume
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={editCoinsForm.coinLimit}
                    onChange={e => setEditCoinsForm(prev => ({ ...prev, coinLimit: parseInt(e.target.value, 10) || 1 }))}
                    style={{ width: 90, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 15, fontWeight: 600, textAlign: 'center' }}
                  />
                  <span style={{ fontSize: 13, color: '#6b7280' }}>coins (1 – 200)</span>
                </div>
                {/* Quick presets */}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  {[5, 10, 20, 30, 50, 100].map(n => (
                    <button
                      key={n}
                      onClick={() => setEditCoinsForm(prev => ({ ...prev, coinLimit: n }))}
                      style={{
                        padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: editCoinsForm.coinLimit === n ? '#0d9488' : '#f3f4f6',
                        color: editCoinsForm.coinLimit === n ? '#fff' : '#374151',
                        border: editCoinsForm.coinLimit === n ? 'none' : '1px solid #e5e7eb',
                      }}
                    >
                      Top {n}
                    </button>
                  ))}
                </div>
                <p style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                  {selectedStrategyForCoinEdit.strategyType === 'CRYPTO_PAIRS'
                    ? `The bot will automatically pick the top ${editCoinsForm.coinLimit} major crypto coins (BTC, ETH, SOL, etc.) ranked by 24h volume on KuCoin at each execution tick.`
                    : `The bot will automatically pick the top ${editCoinsForm.coinLimit} meme coins ranked by 24h trading volume on KuCoin at each execution tick.`}
                </p>
              </div>
            )}

            {/* MANUAL mode coin picker */}
            {editCoinsForm.coinSelectionMode === 'MANUAL' && (
              <div>

                {selectedStrategyForCoinEdit.strategyType === 'CRYPTO_PAIRS' && (
                  <div style={{ marginBottom: 12, padding: '10px 14px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, fontSize: 12, color: '#1e40af' }}>
                    Major crypto + stock-index perps (MU, TSLA, …) — meme token bases excluded from crypto list only.
                  </div>
                )}

                {/* ── Smart Predict bar ─────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', background: 'linear-gradient(135deg,#eef2ff,#f0fdf4)', border: '1px solid #c7d2fe', borderRadius: 8 }}>
                  <span style={{ fontSize: 18 }}>🔮</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#3730a3' }}>Smart Predict</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>
                      Ranks coins using historical P&amp;L, win-rate, volume &amp; volatility
                    </div>
                  </div>
                  <button
                    onClick={handlePredictCoins}
                    disabled={predictingCoins}
                    style={{
                      padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                      background: predictingCoins ? '#a5b4fc' : '#4f46e5',
                      color: '#fff', border: 'none', cursor: predictingCoins ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                    }}
                  >
                    {predictingCoins ? (
                      <><span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Analysing…</>
                    ) : '🔮 Predict'}
                  </button>
                  {showPredictionPanel && (
                    <button
                      onClick={() => setShowPredictionPanel(false)}
                      style={{ padding: '7px 12px', borderRadius: 6, fontSize: 12, background: '#f3f4f6', border: '1px solid #e5e7eb', cursor: 'pointer', color: '#374151', fontWeight: 600 }}
                    >
                      Hide
                    </button>
                  )}
                </div>

                {/* ── Prediction Results Panel ──────────────────────────── */}
                {showPredictionPanel && !predictingCoins && predictedCoins.length > 0 && (
                  <div style={{ marginBottom: 16, border: '1px solid #e0e7ff', borderRadius: 8, overflow: 'hidden' }}>
                    {/* Panel header */}
                    <div style={{ padding: '10px 14px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e7ff' }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 13, color: '#3730a3' }}>📊 Prediction Results</span>
                        {predictionMessage && (
                          <span style={{ marginLeft: 10, fontSize: 11, color: '#6b7280' }}>{predictionMessage}</span>
                        )}
                      </div>
                      {/* Quick-apply buttons */}
                      <div style={{ display: 'flex', gap: 6 }}>
                        {[5, 10, 20].map(n => (
                          <button
                            key={n}
                            onClick={() => handleApplyPredictions(n)}
                            style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: '#4f46e5', color: '#fff', border: 'none' }}
                          >
                            ✓ Top {n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Ranked coin rows — show top 30 */}
                    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                      {predictedCoins.slice(0, 30).map((coin, i) => {
                        const isSelected = editCoinsForm.selectedCoins.includes(normalizeCoinSymbol(coin.coinName));
                        const pnlColor = coin.totalPnl > 0 ? '#16a34a' : coin.totalPnl < 0 ? '#dc2626' : '#6b7280';
                        return (
                          <div
                            key={coin.coinName}
                            onClick={() => handleCoinEditToggle(coin.coinName)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '7px 14px', cursor: 'pointer',
                              background: isSelected ? '#f0fdf4' : i % 2 === 0 ? '#fff' : '#fafafa',
                              borderLeft: isSelected ? '3px solid #16a34a' : '3px solid transparent',
                              borderBottom: '1px solid #f3f4f6',
                              transition: 'background 0.1s',
                            }}
                          >
                            {/* Rank */}
                            <span style={{ width: 22, textAlign: 'right', fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>#{coin.predictedRank}</span>

                            {/* Selected tick */}
                            <span style={{ width: 16, fontSize: 12, color: '#16a34a', fontWeight: 700 }}>{isSelected ? '✓' : ''}</span>

                            {/* Coin name */}
                            <span style={{ fontWeight: 700, fontSize: 13, minWidth: 52, color: '#111827' }}>{coin.coinName}</span>

                            {/* Tier badge */}
                            <span style={{ fontSize: 11, fontWeight: 700, color: coin.tierColor, minWidth: 80 }}>{coin.tier}</span>

                            {/* Score bar */}
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${coin.score}%`, height: '100%', background: coin.score >= 70 ? '#dc2626' : coin.score >= 50 ? '#16a34a' : coin.score >= 30 ? '#d97706' : '#9ca3af', borderRadius: 3 }} />
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', minWidth: 34, textAlign: 'right' }}>{coin.score}</span>
                            </div>

                            {/* Trade history badge */}
                            {coin.hasHistory ? (
                              <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#6b7280', minWidth: 140 }}>
                                <span title="Trades">{coin.tradeCount}T</span>
                                <span title="Win Rate" style={{ color: coin.winRate >= 50 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{coin.winRate.toFixed(0)}%W</span>
                                <span title="Total P&L" style={{ color: pnlColor, fontWeight: 600 }}>
                                  {coin.totalPnl >= 0 ? '+' : ''}{(coin.totalPnl as number).toFixed(2)}$
                                </span>
                              </div>
                            ) : (
                              <span style={{ fontSize: 10, color: '#9ca3af', minWidth: 140 }}>No trade history</span>
                            )}

                            {/* Volume rank chip */}
                            <span style={{ fontSize: 10, color: '#6b7280', minWidth: 42 }}>Vol #{coin.volumeRank}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {showPredictionPanel && predictingCoins && (
                  <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 13, color: '#6b7280' }}>
                    ⏳ Scoring coins using live market data and your trade history…
                  </div>
                )}

                {/* Search + select-all bar */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Search coins…"
                    value={editCoinsForm.coinSearch}
                    onChange={e => setEditCoinsForm(prev => ({ ...prev, coinSearch: e.target.value }))}
                    style={{ flex: 1, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                  />
                  <button
                    onClick={handleCoinEditSelectAll}
                    style={{ padding: '7px 14px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap' }}
                  >
                    {editCoinsAvailable
                      .filter(c => c.toLowerCase().includes(editCoinsForm.coinSearch.toLowerCase()))
                      .every(c => editCoinsForm.selectedCoins.includes(c))
                      ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {/* Selected count + chips */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 8, fontSize: 12, color: '#0d9488', fontWeight: 600 }}>
                    {editCoinsForm.selectedCoins.length} coin{editCoinsForm.selectedCoins.length !== 1 ? 's' : ''} selected
                    {editCoinsForm.selectedCoins.length > 0 && (
                      <button
                        onClick={() => setEditCoinsForm(prev => ({ ...prev, selectedCoins: [] }))}
                        style={{ marginLeft: 10, fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  {editCoinsForm.selectedCoins.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {editCoinsForm.selectedCoins.map(coin => (
                        <button
                          key={`selected-${coin}`}
                          type="button"
                          onClick={() => handleCoinEditToggle(coin)}
                          title="Click to remove"
                          style={{
                            padding: '5px 10px',
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: '#0d9488',
                            color: '#fff',
                            border: 'none',
                          }}
                        >
                          ✓ {coin} ×
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>No coins selected yet.</div>
                  )}
                </div>

                {/* Coin grid */}
                {loadingEditCoins ? (
                  <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>Loading coins…</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 300, overflowY: 'auto', padding: '4px 0' }}>
                    {editCoinsAvailable
                      .filter(c => c.toLowerCase().includes(editCoinsForm.coinSearch.toLowerCase()))
                      .map(coin => {
                        const selected = editCoinsForm.selectedCoins.includes(coin);
                        return (
                          <button
                            key={coin}
                            onClick={() => handleCoinEditToggle(coin)}
                            style={{
                              padding: '4px 10px',
                              borderRadius: 20,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              background: selected ? '#0d9488' : '#f3f4f6',
                              color: selected ? '#fff' : '#374151',
                              border: selected ? 'none' : '1px solid #e5e7eb',
                              transition: 'all 0.1s',
                            }}
                          >
                            {selected ? '✓ ' : ''}{coin}
                          </button>
                        );
                      })}
                    {editCoinsAvailable.filter(c => c.toLowerCase().includes(editCoinsForm.coinSearch.toLowerCase())).length === 0 && (
                      <p style={{ color: '#9ca3af', fontSize: 13 }}>No coins match your search.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            </div>{/* end scrollable body */}

            {/* Footer — pinned at bottom, never scrolls off screen */}
            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'flex-end',
              paddingTop: 16,
              paddingBottom: 'env(safe-area-inset-bottom, 8px)',
              borderTop: '1px solid #e5e7eb',
              flexShrink: 0,
              backgroundColor: '#fff',
            }}>
              <button
                onClick={() => setShowEditCoinsModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#6b7280',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCoinEditSave}
                disabled={savingEditCoins}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: savingEditCoins ? '#5eaaa3' : '#0d9488',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: savingEditCoins ? 'not-allowed' : 'pointer',
                  minWidth: 130,
                }}
              >
                {savingEditCoins ? 'Saving…' : '💾 Save Coins'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meme Blacklist Modal */}
      <MemeBlacklistModal
        isOpen={showBlacklistModal}
        onClose={() => setShowBlacklistModal(false)}
      />

      {showSubaccountWizard && (
        <KucoinSubaccountProvisioningWizard
          strategyId={subaccountWizardStrategyId}
          fixedOrderAmount={createForm.fixedOrderAmount}
          leverage={createForm.leverage}
          maxOpenPositions={createForm.maxOpenPositions}
          marketType={createForm.marketType}
          onClose={() => {
            setShowSubaccountWizard(false);
            setSubaccountWizardStrategyId(undefined);
            adminApiService.getAllKucoinAccounts().then((res) => {
              if (res.success && res.data) {
                setAllKucoinAccounts(res.data);
                setPersonalAccounts(res.data.filter((a: { shared: boolean }) => !a.shared));
              }
            });
          }}
        />
      )}
    </div>
  );
};

// ── US-RDX-02: 7-day regime history bar ─────────────────────────────────────
const REGIME_COLORS_BAR: Record<string, string> = {
  HOT_MEME: '#059669',
  RISK_ON: '#059669',
  MODERATE: '#2563eb',
  HIGH_VOLATILITY: '#d97706',
  LOW_PARTICIPATION: '#f59e0b',
  LOW_LIQUIDITY: '#f59e0b',
  BEARISH_BTC: '#dc2626',
  UNKNOWN: '#d1d5db',
};

function isLowParticipationStyleRegime(regime?: string | null, strategyType?: string) {
  if (!regime) return false;
  return strategyType === 'CRYPTO_PAIRS'
    ? regime === 'LOW_LIQUIDITY'
    : regime === 'LOW_PARTICIPATION';
}

function isCryptoPairsStrategy(strategyType?: string | null) {
  return strategyType === 'CRYPTO_PAIRS';
}

function participationFilterTitle(strategyType?: string | null, createModalCrypto?: boolean) {
  return isCryptoPairsStrategy(strategyType) || createModalCrypto
    ? '₿ Major Coin Participation Filter'
    : '🪙 Meme Participation Filter';
}

function participationFilterEnableLabel(strategyType?: string | null, createModalCrypto?: boolean) {
  return isCryptoPairsStrategy(strategyType) || createModalCrypto
    ? 'Enable Major Coin Participation Filter for this strategy'
    : 'Enable Meme Participation Filter for this strategy';
}

function participationFilterDescription(strategyType?: string | null, createModalCrypto?: boolean) {
  if (isCryptoPairsStrategy(strategyType) || createModalCrypto) {
    return (
      'Blocks new pair creation when your selected major coins have low participation (weak follow-through). ' +
      'A coin is considered active if it has a positive 24 h price change and above-average volume. ' +
      'Evaluates your strategy coin list (e.g. BNB, XRP) — not the meme basket.'
    );
  }
  return (
    'Blocks new pair creation when the meme universe is asleep. A coin is considered active if it has a ' +
    'positive 24 h price change and above-average volume. Ticker data is cached for 2 minutes — no extra API calls per cycle.'
  );
}

function participationFilterDisabledHint(strategyType?: string | null) {
  return isCryptoPairsStrategy(strategyType)
    ? '⭕ Disabled — trades open regardless of major-coin participation'
    : '⭕ Disabled — trades open regardless of meme participation';
}

function participationFilterEditHint(strategyType?: string | null) {
  return isCryptoPairsStrategy(strategyType)
    ? 'Blocks entries when selected major coins are inactive (low participation / liquidity).'
    : 'Blocks entries when the meme universe is asleep (low participation).';
}

function RegimeHistoryBar({ strategyId }: { strategyId: number }) {
  const [days, setDays] = React.useState<Array<{ date: string; regime: string; regimeLabel: string; netPnl: number; closeCount: number }> | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    adminApiService.getRegimeReview(strategyId, undefined, undefined).then((res) => {
      if (cancelled || !res.success || !res.data) return;
      const sorted = [...(res.data.pnlByRegimeByDay ?? [])].sort((a, b) => a.date.localeCompare(b.date));
      const last7 = sorted.slice(-7);
      // Fill missing days with UNKNOWN
      const all7: Array<{ date: string; regime: string; regimeLabel: string; netPnl: number; closeCount: number }> = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const key = d.toISOString().slice(0, 10);
        const found = last7.find((r) => r.date === key);
        all7.push(found
          ? { date: found.date, regime: found.regime, regimeLabel: found.regimeLabel ?? '', netPnl: Number(found.netPnl), closeCount: found.closeCount }
          : { date: key, regime: 'UNKNOWN', regimeLabel: 'No data', netPnl: 0, closeCount: 0 }
        );
      }
      if (!cancelled) setDays(all7);
    });
    return () => { cancelled = true; };
  }, [strategyId]);

  if (!days) return <span style={{ fontSize: 10, color: '#9ca3af' }}>Loading regime history…</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <span style={{ fontSize: 10, color: '#6b7280', marginRight: 4, whiteSpace: 'nowrap' }}>7d regime:</span>
      {days.map((d) => {
        const color = REGIME_COLORS_BAR[d.regime] ?? REGIME_COLORS_BAR.UNKNOWN;
        const pnl = d.netPnl;
        const tooltip = `${d.date}\n${d.regimeLabel}\nCloses: ${d.closeCount}\nNet PnL: ${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`;
        return (
          <div
            key={d.date}
            title={tooltip}
            style={{
              width: 10,
              height: 22,
              borderRadius: 3,
              background: d.regime === 'UNKNOWN' ? color : color + 'cc',
              border: `1px solid ${color}`,
              cursor: 'default',
              position: 'relative',
            }}
          />
        );
      })}
      <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 4 }}>← 7 days</span>
    </div>
  );
}

// ── US-RDX-01: Regime Status Dashboard Panel ─────────────────────────────────
const REGIME_COLORS: Record<string, string> = {
  HOT_MEME: '#059669',
  RISK_ON: '#059669',
  MODERATE: '#2563eb',
  HIGH_VOLATILITY: '#d97706',
  LOW_PARTICIPATION: '#b45309',
  LOW_LIQUIDITY: '#b45309',
  BEARISH_BTC: '#dc2626',
  UNKNOWN: '#9ca3af',
};

function RegimeDashboardPanel() {
  const [status, setStatus] = React.useState<RegimeStatusResult | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [lastRefresh, setLastRefresh] = React.useState<Date | null>(null);

  const load = React.useCallback(async () => {
    const res = await adminApiService.getRegimeStatus();
    if (res.success && res.data) {
      setStatus(res.data);
      setLastRefresh(new Date());
    }
  }, []);

  React.useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [load]);

  if (!status) return null;

  const dominated = status.strategies.filter(
    (s) => s.entryBlocked || s.entryBlockEnabled || s.longBlocked || s.longBlockEnabled || s.scalperActive || s.scalperEnabled
  );
  const regimeColor = REGIME_COLORS[status.currentRegime] || REGIME_COLORS.UNKNOWN;

  return (
    <div style={{ marginBottom: 20, border: `1px solid ${regimeColor}`, borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: `${regimeColor}12`, border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: regimeColor, boxShadow: `0 0 0 3px ${regimeColor}30` }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: regimeColor }}>Crypto market regime: {status.regimeLabel}</span>
          {dominated.length > 0 && (
            <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 999, background: '#fef3c7', color: '#b45309', fontWeight: 600, border: '1px solid #fde68a' }}>
              {dominated.length} bot{dominated.length > 1 ? 's' : ''} with gates configured
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {lastRefresh && (
            <span style={{ fontSize: 10, color: '#9ca3af' }}>refreshed {lastRefresh.toLocaleTimeString()}</span>
          )}
          <button onClick={(e) => { e.stopPropagation(); load(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#6b7280' }} title="Refresh now">↻</button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '12px 16px', background: '#fff' }}>
          {status.strategies.length === 0 ? (
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>No active MEME_COIN_PAIRS strategies found.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    {['Strategy', 'Last Regime', 'Entry Block', 'Long Block', 'Scalper', 'AI Tune'].map((h) => (
                      <th key={h} style={{ padding: '5px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {status.strategies.map((s: RegimeStatusStrategyRow) => (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', background: (s.entryBlocked || s.longBlocked || s.scalperActive) ? '#fafafa' : undefined }}>
                      <td style={{ padding: '6px 10px', fontWeight: 600, color: '#1f2937' }}>{s.name}</td>
                      <td style={{ padding: '6px 10px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 999, background: `${REGIME_COLORS[s.lastRegime] || REGIME_COLORS.UNKNOWN}15`, color: REGIME_COLORS[s.lastRegime] || REGIME_COLORS.UNKNOWN, fontSize: 11, fontWeight: 600 }}>
                          {s.lastRegime?.replace(/_/g, ' ') || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                        {s.entryBlocked
                          ? <span style={{ color: '#dc2626', fontWeight: 700 }}>⛔ ON</span>
                          : s.entryBlockEnabled
                            ? <span style={{ color: '#dc2626', fontWeight: 600, opacity: 0.5 }} title="Configured — activates in LOW_PARTICIPATION">⛔ Armed</span>
                            : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                        {s.longBlocked
                          ? <span style={{ color: '#b91c1c', fontWeight: 700 }}>🔒 ON</span>
                          : s.longBlockEnabled
                            ? <span style={{ color: '#b91c1c', fontWeight: 600, opacity: 0.5 }} title="Configured — activates in BEARISH_BTC">🔒 Armed</span>
                            : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                        {s.scalperActive
                          ? <span style={{ color: '#d97706', fontWeight: 700 }}>⚡ ON</span>
                          : s.scalperEnabled
                            ? <span style={{ color: '#d97706', fontWeight: 600, opacity: 0.5 }} title="Configured — activates in HIGH_VOLATILITY">⚡ Armed</span>
                            : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'center' }}>{s.aiAutoTuneEnabled ? <span style={{ color: '#059669' }}>✓</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminTradingStrategies;
